import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Bell,
  Boxes,
  Building2,
  ChevronRight,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Download,
  ExternalLink,
  Factory,
  FileSpreadsheet,
  Filter,
  Gauge,
  Globe2,
  Heart,
  Info,
  Inbox,
  LockKeyhole,
  LogOut,
  MapPin,
  Menu,
  MessageSquare,
  Minus,
  PackageCheck,
  Plus,
  RadioTower,
  RotateCcw,
  Search,
  Send,
  ShieldCheck,
  ShoppingCart,
  Store,
  TrendingUp,
  Truck,
  UploadCloud,
  Users,
  X,
  Zap,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  localizationRules,
  permissionRoles,
} from "./config";
import {
  behaviorActionFor,
  behaviorElapsedSeconds,
  hasRecordedBehaviorEvent,
  type BehaviorEventInput,
} from "./features/analytics/behaviorMetrics";
import {
  formatMoney,
  normalizeQuickUnit,
  parseQuickInvoiceLineItems,
  parseQuickOrderText,
  parseQuickDecimal,
  type QuickInvoiceInput,
} from "./features/restaurant/restaurantFirstRunUtils";
import type {
  QuickOrderItemInput,
  QuickQuoteRequestInput,
} from "./features/restaurant/restaurantFirstRunUtils";
import type { RestaurantSupplierContactInput } from "./features/restaurant/RestaurantOwnerStart";
import type { MarketplaceMode } from "./features/restaurant/RestaurantMarketplace";
import type {
  ManualSupplierProductInput,
  SupplierInviteRequest,
} from "./features/supplier/SupplierFirstRunStart";
import {
  acceptQuoteOffer,
  fetchAppBootstrap,
  fetchSupplierOrderConfirmation,
  respondToSupplierOrderConfirmation,
  submitApprovalRequest,
  submitAuditEvent,
  submitBranchBudget,
  submitCatalogProduct,
  submitDeliveryArea,
  submitDoneForYouSetupRequest,
  convertDoneForYouSetupRequest,
  submitFulfillmentUpdate,
  submitImportBatch,
  submitInquiry,
  submitIntegrationRequest,
  submitInvoice,
  submitOrder,
  submitExternalSupplierStatus,
  submitPublicSupplierSource,
  submitQuoteResponse,
  submitQuoteRequest,
  submitMessageThread,
  submitCoverageRecord,
  submitDemandGap,
  submitDiscoverySourceRun,
  submitLocalizationRule,
  submitPreferredSupplier,
  submitSourcingSprint,
  submitRestaurantSupplierContact,
  submitSupplierOrderRequest,
  submitSupplierDemand,
  submitWorkspaceUser,
  updateInvoiceStatus,
  updateIntegrationConnection,
  updateInquiryStatus,
  updateDoneForYouSetupRequest,
} from "./lib/api";
import type {
  SessionAccount,
  ConvertDoneForYouSetupRequestInput,
  SubmitDoneForYouSetupRequestInput,
  SubmitIntegrationRequestInput,
  SubmitOrderResult,
  SubmitPublicSupplierSourceInput,
} from "./lib/api";
import {
  bootstrapAccount,
  consumeUrlSession,
  isAuthConfigured,
  onAuthChange,
  signInWithMagicLink,
  signInWithPassword,
  signOut as authSignOut,
  signUpWithPassword,
} from "./lib/supabaseAuth";
import { parseRestaurantOrderGuideFile, parseSupplierCatalogFile } from "./lib/imports";
import {
  defaultPermissionRoleId,
  initialPermissionRoleId,
  permissionCapabilitySummary,
  permissionOptionsForRole,
  permissionRoleStorageKey,
  permissionTouchedStorageKey,
  roleLabels,
  roleSwitchLabels,
  searchPlaceholderForRole,
  sidebarWorkspaceCopy,
} from "./platform/workspaceAccess";
import { navDisplayLabel, navItems, navItemsForRole, navMobileDisplayLabel } from "./platform/workspaceNavigation";
import {
  buildLocalWorkspaceFallbackHydration,
  buildWorkspaceBootstrapHydration,
  localFulfillmentOrders,
  mergeByKey,
  orderGuideMergeKey,
  orderToFulfillmentOrder,
  productMergeKey,
} from "./platform/workspaceBootstrap";
import { workspaceRoleEntryFor } from "./platform/workspaceRoleEntry";
import { clearLegacySessionRole, resolveWorkspaceSession } from "./platform/workspaceSession";
import {
  ACCOUNT_BUSINESS_NAME_KEY,
  ACCOUNT_EMAIL_KEY,
  ALLOW_LOCAL_WORKSPACE_FALLBACK,
  PENDING_SIGNUP_KEY,
  REAL_RESTAURANT_DATA_KEY,
  REAL_SUPPLIER_DATA_KEY,
  isLocalOrderId,
  readLocalCatalogProducts,
  readLocalConnectorRequests,
  readLocalDoneForYouSetupRequests,
  readLocalImportRuns,
  readLocalInvoices,
  readLocalOrderGuideItems,
  readLocalOrders,
  readLocalQuoteRequests,
  readLocalSupplierOrderRequests,
  readStoredJson,
  saveLocalCatalogProducts,
  saveLocalConnectorRequests,
  saveLocalDoneForYouSetupRequests,
  saveLocalImportRuns,
  saveLocalInvoices,
  saveLocalOrderGuideItems,
  saveLocalOrders,
  saveLocalQuoteRequests,
  saveLocalSupplierOrderRequests,
  storageGet,
  storageRemove,
  storageSet,
} from "./platform/workspaceStorage";
import type { ConnectorRequestRecord, ImportRunRecord } from "./platform/workspaceStorage";
import type {
  AutomationTask,
  ApprovalRequest,
  AuditEvent,
  BranchAccount,
  BosniaCoverageTarget,
  ColdStartPricingRule,
  CustomerAccount,
  DemandGap,
  DiscoveryRunRecord,
  DiscoverySource,
  DoneForYouSetupRequest,
  DeliveryArea,
  DiscrepancyRecord,
  ExternalInquiryRecord,
  ExternalSupplierRecord,
  InvoiceLineItem,
  InvoiceLineMatch,
  InvoiceMismatchType,
  InvoicePaymentReadiness,
  InvoiceRecord,
  IntegrationStatus,
  LiveIncident,
  LocalizationRule,
  MarketLane,
  MarketCoverageRecord,
  MarketplaceProduct,
  MarketplaceSupplyState,
  MessageThread,
  NetworkOperatingSnapshot,
  NetworkStats,
  Order,
  OrderItem,
  OrderStatus,
  OperatingCountry,
  PriceBenchmark,
  PriceBenchmarkRecord,
  Product,
  PurchaseInsight,
  QuoteRequest,
  RestaurantSupplierContactRecord,
  ReplenishmentSignal,
  RegionHealth,
  Role,
  StockStatus,
  PermissionRole,
  SupplierOffer,
  SupplierConfirmationTrustAction,
  SupplierDeliveryAvailability,
  SupplierOrderMessageChannel,
  SupplierOpportunity,
  SupplierOrderMessageDeliveryStatus,
  SupplierOrderRequest,
  SupplierOrderRequestStatus,
  SupplierSearchDemand,
  SupplierConversionTask,
  SupplierFulfillmentOrder,
  SourcingSprint,
  TrustControl,
  WorkflowSignal,
  WorkspaceUser,
} from "./types";

const RestaurantOwnerStart = lazy(() =>
  import("./features/restaurant/RestaurantOwnerStart").then((module) => ({
    default: module.RestaurantOwnerStart,
  })),
);
const RestaurantTodayRail = lazy(() =>
  import("./features/restaurant/RestaurantWorkspace").then((module) => ({
    default: module.RestaurantTodayRail,
  })),
);
const RestaurantBuyingCommand = lazy(() =>
  import("./features/restaurant/RestaurantWorkspace").then((module) => ({
    default: module.RestaurantBuyingCommand,
  })),
);
const BuyerOperationsCenter = lazy(() =>
  import("./features/restaurant/RestaurantWorkspace").then((module) => ({
    default: module.BuyerOperationsCenter,
  })),
);
const BuyerPurchaseControlCenter = lazy(() =>
  import("./features/restaurant/RestaurantWorkspace").then((module) => ({
    default: module.BuyerPurchaseControlCenter,
  })),
);
const MarketplaceWorkbench = lazy(() =>
  import("./features/restaurant/RestaurantMarketplace").then((module) => ({
    default: module.MarketplaceWorkbench,
  })),
);
const QuoteRequestRow = lazy(() =>
  import("./features/restaurant/RestaurantMarketplace").then((module) => ({
    default: module.QuoteRequestRow,
  })),
);
const InvoiceWorkspace = lazy(() =>
  import("./features/invoices/InvoiceWorkspace").then((module) => ({
    default: module.InvoiceWorkspace,
  })),
);
const SupplierFirstRunStart = lazy(() =>
  import("./features/supplier/SupplierFirstRunStart").then((module) => ({
    default: module.SupplierFirstRunStart,
  })),
);
const DataImportAndConnectorPanel = lazy(() =>
  import("./features/setup/DataImportAndConnectorPanel").then((module) => ({
    default: module.DataImportAndConnectorPanel,
  })),
);

type DraftLookupContext = {
  catalogProducts: Product[];
  marketplaceProducts: MarketplaceProduct[];
  supplierContacts: RestaurantSupplierContact[];
};

type RestaurantSupplierContact = RestaurantSupplierContactRecord;

const RESTAURANT_SUPPLIER_CONTACTS_KEY = "veleo.restaurantSupplierContacts";

function supplierConfirmationRouteId() {
  if (typeof window === "undefined") {
    return "";
  }

  const slug = window.location.hash.replace(/^#\/?/, "");
  const match = slug.match(/^supplier-confirm\/([^/?#]+)/i);
  return match ? decodeURIComponent(match[1]) : "";
}

function supplierConfirmationUrl(requestId: string) {
  if (typeof window === "undefined") {
    return `#/supplier-confirm/${encodeURIComponent(requestId)}`;
  }

  return `${window.location.origin}${window.location.pathname}#/supplier-confirm/${encodeURIComponent(requestId)}`;
}

function matchesSupplierConfirmationRequest(request: SupplierOrderRequest, token: string) {
  return request.publicToken === token || request.id === token;
}

function createPublicConfirmationToken() {
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    const bytes = new Uint8Array(18);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
}

function cleanSupplierContact(value?: string) {
  return (value ?? "").trim();
}

function supplierPhoneDigits(value?: string) {
  const contact = cleanSupplierContact(value);
  const digits = contact.replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (digits.startsWith("00")) {
    return digits.slice(2);
  }

  if (digits.startsWith("387")) {
    return digits;
  }

  if (digits.startsWith("0") && digits.length >= 8) {
    return `387${digits.slice(1)}`;
  }

  return digits;
}

function likelyEmail(value?: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanSupplierContact(value));
}

function preferredChannelForContact(value?: string): SupplierOrderRequest["preferredChannel"] {
  const contact = cleanSupplierContact(value).toLowerCase();
  if (!contact) {
    return "copy";
  }
  if (likelyEmail(contact)) {
    return "email";
  }
  if (contact.includes("viber")) {
    return "viber";
  }
  if (contact.includes("sms")) {
    return "sms";
  }

  return "whatsapp";
}

function supplierPrimaryContactValue(contact?: RestaurantSupplierContactRecord) {
  if (!contact) {
    return "";
  }

  const byChannel: Record<NonNullable<RestaurantSupplierContactRecord["preferredChannel"]>, string | undefined> = {
    copy: contact.contact || contact.whatsapp || contact.viber || contact.phone || contact.email,
    email: contact.email,
    sms: contact.phone || contact.whatsapp || contact.viber,
    viber: contact.viber || contact.phone || contact.whatsapp,
    whatsapp: contact.whatsapp || contact.phone || contact.viber,
  };
  return cleanSupplierContact(byChannel[contact.preferredChannel ?? "copy"] || contact.contact || contact.phone || contact.whatsapp || contact.viber || contact.email);
}

function supplierMessageUnitLabel(unit: string, quantity: number) {
  const normalized = unit.trim();
  const labels: Record<string, string> = {
    bale: "bala",
    bottle: "flasa",
    box: "kutija",
    bucket: "kanta",
    bunch: "vezica",
    head: quantity === 1 ? "glava" : "glave",
    pack: "pak",
    pcs: "kom",
  };

  return labels[normalized] ?? normalized;
}

function supplierMessageProductName(item: OrderItem) {
  if (item.name === "Usual item") {
    return item.unit === "unit" ? "dogovorena stavka" : "po dogovoru";
  }

  return item.name;
}

function supplierRequestItemLine(item: OrderItem) {
  const quantity = Number.isInteger(item.quantity) ? item.quantity : Number(item.quantity.toFixed(2));
  const isGenericUnit = item.unit === "unit";
  const unitLabel = isGenericUnit ? "" : supplierMessageUnitLabel(item.unit, quantity);
  const productName = supplierMessageProductName(item);
  const quantityAndUnit = `${quantity}${unitLabel ? ` ${unitLabel}` : ""}`;
  const price = item.unitPrice > 0 ? ` po ${formatMoney(item.unitPrice)} KM/${unitLabel || "stavka"}` : "";
  const note = item.note ? `; napomena: ${item.note}` : "";
  return `- ${quantityAndUnit} ${productName}${price}${note}`;
}

function buildSupplierOrderMessage({
  confirmationUrl,
  deliveryWindow,
  items,
  market,
  restaurantName,
  supplierName,
  totalBam,
}: {
  confirmationUrl: string;
  deliveryWindow: string;
  items: OrderItem[];
  market: string;
  restaurantName: string;
  supplierName: string;
  totalBam: number;
}) {
  const lines = items.map(supplierRequestItemLine).join("\n");
  const totalLine = totalBam > 0 ? `Procjena ukupno: ${formatMoney(totalBam)} KM\n` : "";

  return `Pozdrav ${supplierName},\n\nMoze narudzba za ${restaurantName}:\n${lines}\n\nDostava: ${deliveryWindow}\nLokacija: ${market}\n${totalLine}\nPotvrdite, izmijenite ili odbijte bez naloga:\n${confirmationUrl}\n\nHvala puno. VELEO link cuva odgovor uz narudzbu.`;
}

function isoAfterMinutes(minutes: number, from: Date = new Date()) {
  return new Date(from.getTime() + minutes * 60 * 1000).toISOString();
}

function supplierFollowUpMessage(request: SupplierOrderRequest, followUpCount = 1) {
  const lineSummary = request.items
    .slice(0, 4)
    .map((item) => supplierRequestItemLine(item).replace(/^- /, ""))
    .join(", ");
  const moreLines = request.items.length > 4 ? ` + ${request.items.length - 4} more` : "";

  return [
    `Pozdrav ${request.supplierName}, kratka provjera za narudzbu ${request.orderId} od ${request.restaurantName}.`,
    `Dostava: ${request.deliveryWindow}.`,
    `Stavke: ${lineSummary}${moreLines}.`,
    `Ukupno: ${formatMoney(request.totalBam)} ${request.currency}.`,
    followUpCount > 1 ? `Ovo je follow-up #${followUpCount}.` : "Molimo potvrdite kada stignete.",
    `Potvrda/izmjene bez naloga: ${request.confirmationUrl}`,
  ].join("\n");
}

function supplierFollowUpIsDue(request: SupplierOrderRequest, now = new Date()) {
  if (request.status !== "sent" || !request.followUpDueAt) {
    return false;
  }

  const dueAt = new Date(request.followUpDueAt);
  return !Number.isNaN(dueAt.getTime()) && dueAt.getTime() <= now.getTime();
}

function supplierRequestShareLinks(request: SupplierOrderRequest) {
  const encodedMessage = encodeURIComponent(request.messageBody);
  const phoneDigits = supplierPhoneDigits(request.supplierContact);
  const emailTarget = likelyEmail(request.supplierContact) ? cleanSupplierContact(request.supplierContact) : "";
  const subject = encodeURIComponent(`Narudzba ${request.orderId} / ${request.restaurantName}`);

  return {
    copy: request.messageBody,
    email: emailTarget ? `mailto:${emailTarget}?subject=${subject}&body=${encodedMessage}` : "",
    sms: phoneDigits.length >= 8 ? `sms:${phoneDigits}?&body=${encodedMessage}` : "",
    viber: cleanSupplierContact(request.supplierContact) ? `viber://forward?text=${encodedMessage}` : "",
    whatsapp: phoneDigits.length >= 8 ? `https://wa.me/${phoneDigits}?text=${encodedMessage}` : "",
  };
}

function supplierRequestShareTargets(request: SupplierOrderRequest) {
  const links = supplierRequestShareLinks(request);
  const contact = cleanSupplierContact(request.supplierContact);
  const phoneDigits = supplierPhoneDigits(contact);
  const hasPhoneTarget = phoneDigits.length >= 8;
  const hasEmailTarget = likelyEmail(contact);
  const hasViberTarget = hasPhoneTarget || contact.toLowerCase().includes("viber");
  const missingPhone = "Add a phone, WhatsApp, or Viber number before sending this way.";

  return [
    {
      available: hasPhoneTarget,
      channel: "whatsapp" as const,
      href: links.whatsapp,
      label: "WhatsApp",
      reason: missingPhone,
    },
    {
      available: hasViberTarget,
      channel: "viber" as const,
      href: links.viber,
      label: "Viber",
      reason: "Add a Viber contact or phone number before sending this way.",
    },
    {
      available: hasPhoneTarget,
      channel: "sms" as const,
      href: links.sms,
      label: "SMS",
      reason: missingPhone,
    },
    {
      available: hasEmailTarget,
      channel: "email" as const,
      href: links.email,
      label: "Email",
      reason: "Add a supplier email before sending this way.",
    },
  ];
}

function supplierRequestDeliveryReadiness(request: SupplierOrderRequest): Pick<
  SupplierOrderRequest,
  "messageDeliveryNote" | "messageDeliveryStatus"
> {
  const shareTargets = supplierRequestShareTargets(request);
  const hasDirectShareTarget = shareTargets.some((target) => target.available);
  const preferredShareTarget = shareTargets.find((target) => target.channel === request.preferredChannel);
  const preferredChannelUnavailable =
    Boolean(request.preferredChannel && request.preferredChannel !== "copy" && preferredShareTarget) &&
    !preferredShareTarget?.available;

  if (!hasDirectShareTarget) {
    return {
      messageDeliveryNote: "Missing supplier phone, Viber, WhatsApp, or email. Copy message manually and update contact.",
      messageDeliveryStatus: "needs_follow_up",
    };
  }

  if (preferredChannelUnavailable && request.preferredChannel) {
    return {
      messageDeliveryNote: `Preferred ${supplierMessageChannelLabel(request.preferredChannel)} missing. Use another available channel or update supplier contact.`,
      messageDeliveryStatus: "needs_follow_up",
    };
  }

  return {
    messageDeliveryStatus: "ready",
  };
}

function supplierRequestStatusLabel(status: SupplierOrderRequestStatus) {
  const labels: Record<SupplierOrderRequestStatus, string> = {
    changes_requested: "Changes requested",
    confirmed: "Confirmed",
    declined: "Declined",
    issue: "Issue",
    sent: "Sent",
  };

  return labels[status];
}

function supplierDeliveryAvailabilityLabel(status: SupplierDeliveryAvailability) {
  const labels: Record<SupplierDeliveryAvailability, string> = {
    available: "Can deliver",
    partial: "Partial delivery",
    unavailable: "Cannot deliver",
  };

  return labels[status];
}

function supplierMessageChannelLabel(channel?: SupplierOrderMessageChannel) {
  const labels: Record<SupplierOrderMessageChannel, string> = {
    copy: "Copy",
    email: "Email",
    sms: "SMS",
    viber: "Viber",
    whatsapp: "WhatsApp",
  };

  return channel ? labels[channel] : "No channel";
}

function doneForYouSetupChannelLabel(channel?: DoneForYouSetupRequest["contactChannel"]) {
  const labels: Record<DoneForYouSetupRequest["contactChannel"], string> = {
    copy: "Copy",
    email: "Email",
    in_person: "In person",
    phone: "Phone",
    sms: "SMS",
    viber: "Viber",
    whatsapp: "WhatsApp",
  };

  return channel ? labels[channel] : "No channel";
}

function supplierMessageDeliveryLabel(status?: SupplierOrderMessageDeliveryStatus) {
  const labels: Record<SupplierOrderMessageDeliveryStatus, string> = {
    failed: "Delivery failed",
    needs_follow_up: "Needs follow-up",
    ready: "Ready to share",
    shared: "Shared",
  };

  return labels[status ?? "ready"];
}

function supplierContactRelationshipLabel(status: RestaurantSupplierContactRecord["relationshipStatus"]) {
  const labels: Record<RestaurantSupplierContactRecord["relationshipStatus"], string> = {
    active: "Active",
    needs_review: "Needs review",
    opted_out: "Opted out",
    removed: "Removed",
  };

  return labels[status];
}

function supplierContactRepairReason(contact: RestaurantSupplierContactRecord) {
  if (contact.relationshipStatus === "opted_out") {
    return "Supplier opted out; confirm consent or remove before next order.";
  }

  if (contact.relationshipStatus === "removed") {
    return "Supplier removed; verify whether this restaurant still orders here.";
  }

  if (contact.relationshipStatus === "needs_review") {
    return "Wrong contact or unverified relationship; confirm with restaurant before sending.";
  }

  return "Supplier relationship is active.";
}

function supplierOrderMessageStateLabel(request: SupplierOrderRequest) {
  if (request.responseAt || request.status !== "sent") {
    return "Supplier responded";
  }

  return supplierMessageDeliveryLabel(request.messageDeliveryStatus);
}

function supplierLineResponseLabel(status: NonNullable<OrderItem["supplierResponse"]>) {
  const labels: Record<NonNullable<OrderItem["supplierResponse"]>, string> = {
    available: "Available",
    changed: "Changed",
    rejected: "Unavailable",
  };

  return labels[status];
}

function supplierRequestReviewStats(request?: SupplierOrderRequest) {
  const changedLineCount = request?.items.filter((item) => item.supplierResponse === "changed").length ?? 0;
  const unavailableLineCount = request?.items.filter((item) => item.supplierResponse === "rejected").length ?? 0;
  const deliveryPartial = request?.supplierDeliveryAvailability === "partial";
  const deliveryUnavailable = request?.supplierDeliveryAvailability === "unavailable";
  const needsReview = Boolean(
    request &&
      (request.status === "changes_requested" ||
        request.status === "declined" ||
        request.status === "issue" ||
        changedLineCount ||
        unavailableLineCount ||
        deliveryPartial ||
        deliveryUnavailable ||
        request.supplierNote),
  );
  const canAccept =
    needsReview &&
    Boolean(request) &&
    request?.status !== "declined" &&
    request?.status !== "issue" &&
    !deliveryUnavailable;

  return {
    canAccept,
    changedLineCount,
    deliveryNeedsReview: deliveryPartial || deliveryUnavailable,
    deliveryPartial,
    deliveryUnavailable,
    needsReview,
    unavailableLineCount,
  };
}

const emptyNetworkStats: NetworkStats = {
  customers: 0,
  suppliers: 0,
  monthlyGmvEur: 0,
  countries: 0,
  apiUptime: 0,
  automatedOrders: 0,
  enterpriseAccounts: 0,
  supportSla: 0,
};

const emptyNetworkSnapshot: NetworkOperatingSnapshot = {
  stats: emptyNetworkStats,
  regionHealth: [],
  operatingCountries: [],
  customerAccounts: [],
};

type NotificationAction =
  | "use_quote"
  | "request_approval"
  | "send_supplier_quote"
  | "advance_fulfillment"
  | "mark_catalog_ready"
  | "toggle_delivery_capacity"
  | "advance_incident"
  | "open_coverage_sprint"
  | "advance_external_record"
  | "approve_request";

type DraftSupplierPacket = {
  supplierName: string;
  items: OrderItem[];
  total: number;
  categories: string[];
  sources: Array<NonNullable<OrderItem["source"]>>;
};

type DraftPreflightCheck = {
  id: string;
  title: string;
  value: string;
  detail: string;
  status: "ready" | "watch" | "blocked";
  icon: React.ElementType;
};

type OrderSlaSignal = {
  order: Order;
  title: string;
  detail: string;
  owner: string;
  metric: string;
  severity: "critical" | "watch" | "ready";
  button: string;
};

type SupplierQuoteSlaSignal = {
  request: QuoteRequest;
  title: string;
  detail: string;
  owner: string;
  metric: string;
  severity: "critical" | "watch" | "ready";
  button: string;
};

type OperationalNotification = {
  id: string;
  title: string;
  detail: string;
  meta: string;
  severity: "critical" | "warning" | "info" | "success";
  nav: string;
  icon: React.ElementType;
  action?: NotificationAction;
  actionLabel?: string;
  productId?: string;
  category?: string;
  mode?: MarketplaceMode;
  targetId?: string;
};

type GlobalSearchResult = {
  id: string;
  eyebrow: string;
  label: string;
  detail: string;
  meta: string;
  nav: string;
  icon: React.ElementType;
  productId?: string;
  category?: string;
  mode?: MarketplaceMode;
};

type ExternalInquiryDraft = {
  buyerName: string;
  productName: string;
  quantity: string;
  neededBy: string;
  contactDetails: string;
};

type ProductBranchFit = {
  label: string;
  detail: string;
  status: "local" | "route" | "backup" | "unverified" | "out_of_area";
  score: number;
};

type MarketplaceDecision = {
  action: string;
  detail: string;
  severity: "ready" | "watch" | "external";
};

type ProcurementBriefItem = {
  label: string;
  value: string;
  detail: string;
  status: "ready" | "watch" | "critical";
};

type BuyingSpeedLane = {
  id: "ready" | "quote" | "backup" | "saved";
  label: string;
  count: number;
  detail: string;
  mode: MarketplaceMode;
  button: string;
  severity: "ready" | "watch" | "external";
  icon: React.ElementType;
  product?: MarketplaceProduct;
};

type InquiryOperationSignal = {
  id: string;
  title: string;
  detail: string;
  metric: string;
  status: "waiting" | "ready" | "done";
  button: string;
};

type ExternalVerificationAction = {
  record: ExternalSupplierRecord;
  title: string;
  detail: string;
  metric: string;
  secondary: string;
  severity: "critical" | "watch" | "ready";
  button: string;
  inquiryButton: string;
  icon: React.ElementType;
};

type MessageOperationSignal = {
  thread: MessageThread;
  title: string;
  detail: string;
  metric: string;
  severity: "high" | "watch" | "ready";
  primaryAction: string;
  closeAction: string;
};

type SupplierHealthDriver = {
  id: string;
  label: string;
  value: string;
  detail: string;
  status: "ready" | "watch" | "critical";
  button: string;
  disabled?: boolean;
  icon: React.ElementType;
  onAction?: () => void;
};

type BranchGovernanceAction = {
  id: string;
  title: string;
  detail: string;
  metric: string;
  severity: "high" | "watch" | "ready";
  button: string;
  icon: React.ElementType;
  onAction: () => void;
};

type NetworkHealthAction = {
  id: string;
  title: string;
  detail: string;
  metric: string;
  severity: "critical" | "watch" | "ready";
  button: string;
  icon: React.ElementType;
  onAction: () => void;
};

type NetworkSlaAction = {
  id: string;
  title: string;
  detail: string;
  metric: string;
  secondary: string;
  severity: "critical" | "watch" | "ready";
  button: string;
  icon: React.ElementType;
  disabled?: boolean;
  onAction: () => void;
};

type OperatorWorkItem = {
  id: string;
  title: string;
  detail: string;
  metric: string;
  severity: "critical" | "watch" | "ready";
  button: string;
  icon: React.ElementType;
  onAction: () => void;
};

type OperatorCallSheetItem = {
  id: string;
  title: string;
  detail: string;
  channel: string;
  target: string;
  reason: string;
  severity: "critical" | "watch" | "ready";
  button: string;
  icon: React.ElementType;
  onAction: () => void;
};

type RestaurantSetupRow = {
  id: string;
  name: string;
  supplierCount: number;
  messageCount: number;
  invoiceCount: number;
  status: "ready" | "watch" | "critical";
  nextStep: string;
};

type MarketExpansionAction = {
  id: string;
  title: string;
  detail: string;
  metric: string;
  secondary: string;
  severity: "critical" | "watch" | "ready";
  button: string;
  country: OperatingCountry;
  icon: React.ElementType;
  onAction: () => void;
};

type SupplierRevenueAction = {
  id: string;
  title: string;
  detail: string;
  metric: string;
  severity: "high" | "watch" | "ready";
  button: string;
  icon: React.ElementType;
  onAction: () => void;
};

type SupplierDiscountCandidate = {
  request: QuoteRequest;
  baseline: number;
  discountPercent: number;
  discountPrice: number;
  savings: number;
  currency: string;
  unit: string;
};

type SupplierQuoteOverride = {
  discountPercent?: number;
  listPrice?: number;
};

type SupplierOrderResponseSaveResult = { ok: true } | { ok: false; error: string };

const supplierDiscountPresets = [0, 6, 10, 15];

type AnalyticsAction = {
  id: string;
  title: string;
  detail: string;
  metric: string;
  severity: "high" | "watch" | "ready";
  button: string;
  icon: React.ElementType;
  onAction: () => void | Promise<void>;
};

type BranchCommerceProfile = {
  currencyCode: "BAM" | "EUR" | "AED";
  currencyLabel: string;
  exchangeRateFromBam: number;
  locale: string;
  taxMode: string;
  taxRatePct: number;
  language: string;
  marketRule: string;
};

const emptyBranchAccount: BranchAccount = {
  id: "local-restaurant",
  name: "My restaurant",
  market: "Sarajevo",
  tier: "standard",
  weeklyBudget: 1500,
  spentThisWeek: 0,
  approvalLimit: 500,
  users: 1,
  status: "healthy",
};

function updateStoredLocalOrder(orderId: string, updateOrder: (order: Order) => Order) {
  const localOrders = readLocalOrders();

  if (!localOrders.some((order) => order.id === orderId)) {
    return;
  }

  saveLocalOrders(localOrders.map((order) => (order.id === orderId ? updateOrder(order) : order)));
}

function upsertLocalInvoice(invoice: InvoiceRecord) {
  saveLocalInvoices(mergeByKey([invoice], readLocalInvoices(), (item) => item.id));
}

function upsertLocalQuoteRequest(request: QuoteRequest) {
  saveLocalQuoteRequests(mergeByKey([request], readLocalQuoteRequests(), (item) => item.id));
}

function updateLocalQuoteRequest(requestId: string, updateRequest: (request: QuoteRequest) => QuoteRequest) {
  const localRequests = readLocalQuoteRequests();

  if (!localRequests.some((request) => request.id === requestId)) {
    return;
  }

  saveLocalQuoteRequests(
    localRequests.map((request) => (request.id === requestId ? updateRequest(request) : request)),
  );
}

function realDataSeenStorageKey(role: Role) {
  return role === "supplier" ? REAL_SUPPLIER_DATA_KEY : REAL_RESTAURANT_DATA_KEY;
}

function storedRealDataSeen(role: Role) {
  if (role === "admin") {
    return true;
  }

  if (storageGet(realDataSeenStorageKey(role)) === "true") {
    return true;
  }

  const localImports = readLocalImportRuns();
  const localConnectors = readLocalConnectorRequests();
  const localDoneForYouSetupRequests = readLocalDoneForYouSetupRequests();

  if (role === "supplier") {
    return Boolean(
      readLocalCatalogProducts().length ||
        localImports.some((run) => run.importType === "supplier_catalog") ||
        localConnectors.some((request) => request.role === "supplier"),
    );
  }

  return Boolean(
    readLocalOrderGuideItems().length ||
      readLocalOrders().length ||
      readLocalQuoteRequests().length ||
      readLocalInvoices().length ||
      localDoneForYouSetupRequests.length ||
      localImports.some((run) => run.importType === "restaurant_order_guide") ||
      localConnectors.some((request) => request.role === "restaurant"),
  );
}

function makeLocalConnectorRequest(input: SubmitIntegrationRequestInput): ConnectorRequestRecord {
  const timestamp = new Date().toISOString();
  const requestId = `local-connector-${Date.now()}`;

  return {
    ...input,
    id: requestId,
    requestId,
    mode: "local",
    status: "pending_setup",
    statusNote: "Saved locally. Verify credentials and a test sync before marking it connected.",
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

function makeLocalDoneForYouSetupRequest(
  input: SubmitDoneForYouSetupRequestInput,
  organizationName: string,
): DoneForYouSetupRequest {
  const timestamp = new Date().toISOString();
  const requestId = `local-done-for-you-${Date.now()}`;

  return {
    ...input,
    id: requestId,
    requestId,
    organizationName,
    status: "new",
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

type DoneForYouConversionContact = ConvertDoneForYouSetupRequestInput["supplierContacts"][number];

function doneForYouCategoryForText(value: string) {
  const normalized = normalizeMarketText(value);

  if (/\b(meso|mesara|piletina|junetina|teletina|riba)\b/.test(normalized)) {
    return "Meat and fish";
  }
  if (/\b(povrce|voce|paradajz|luk|salata|krompir|paprika)\b/.test(normalized)) {
    return "Produce";
  }
  if (/\b(pice|sok|voda|cola|pivo|kafa|vino)\b/.test(normalized)) {
    return "Beverages";
  }
  if (/\b(ambalaza|kutije|kese|salvete|case|folija)\b/.test(normalized)) {
    return "Packaging";
  }
  if (/\b(mlijeko|sir|mlijec|jogurt|kajmak)\b/.test(normalized)) {
    return "Dairy";
  }

  return "Current supplier";
}

function doneForYouUsualDays(value: string) {
  const matches = value.match(
    /\b(ponedjeljak|utorak|srijeda|cetvrtak|petak|subota|nedjelja|mon|tue|wed|thu|fri|sat|sun|dnevno|svaki dan)\b/gi,
  );

  return matches ? Array.from(new Set(matches.map((item) => item.trim()))).join(" / ") : "";
}

function doneForYouEmail(value: string) {
  return value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] ?? "";
}

function doneForYouPhone(value: string) {
  return value.match(/(?:\+?\s*387|0)\s*\d{2}\s*\d{3}\s*\d{3,4}/)?.[0]?.replace(/\s+/g, " ").trim() ?? "";
}

function doneForYouPreferredChannel(
  value: string,
  email: string,
  phone: string,
): DoneForYouConversionContact["preferredChannel"] {
  const normalized = value.toLowerCase();

  if (normalized.includes("viber")) {
    return "viber";
  }
  if (normalized.includes("whatsapp") || normalized.includes("wa ")) {
    return "whatsapp";
  }
  if (email) {
    return "email";
  }
  if (phone) {
    return "whatsapp";
  }

  return "copy";
}

function doneForYouSupplierNameFromLine(line: string, email: string, phone: string) {
  const withoutContact = line
    .replace(email, "")
    .replace(phone, "")
    .replace(/\b(?:whatsapp|viber|sms|telefon|phone|mob|kontakt|email|mail|broj)\b/gi, "")
    .replace(/[+@()[\]{}]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  const beforeSeparator = withoutContact.split(/\s[-:|]\s|[,;]/)[0]?.trim() || withoutContact;

  return beforeSeparator.replace(/^\d+[.)]\s*/, "").slice(0, 80).trim();
}

function doneForYouHabitTextForSupplier(request: DoneForYouSetupRequest, supplierName: string) {
  const cleanHabitText = request.orderHabitsText.trim();
  if (!cleanHabitText) {
    return undefined;
  }

  const normalizedSupplier = normalizeMarketText(supplierName);
  const matchingLines = cleanHabitText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => normalizeMarketText(line).includes(normalizedSupplier));
  const habitText = matchingLines.length ? matchingLines.join("\n") : cleanHabitText;

  return habitText.slice(0, 1200);
}

function doneForYouSupplierContactsForRequest(request: DoneForYouSetupRequest): DoneForYouConversionContact[] {
  const seen = new Set<string>();

  return request.supplierListText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .reduce<DoneForYouConversionContact[]>((contacts, line) => {
      const email = doneForYouEmail(line);
      const phone = doneForYouPhone(line);
      const name = doneForYouSupplierNameFromLine(line, email, phone);

      if (!name || name.length < 2) {
        return contacts;
      }

      const key = normalizeMarketText(name);

      if (seen.has(key)) {
        return contacts;
      }

      seen.add(key);

      const preferredChannel = doneForYouPreferredChannel(line, email, phone);
      const contactValue = preferredChannel === "email" ? email : phone || email;

      contacts.push({
        category: doneForYouCategoryForText(line),
        contactPerson: request.contactName || undefined,
        contactValue,
        deliveryArea: request.city,
        email: email || undefined,
        name,
        phone: phone || undefined,
        preferredChannel,
        usualDays: doneForYouUsualDays(line) || request.preferredSetupWindow,
        usualOrderText: doneForYouHabitTextForSupplier(request, name),
        viber: preferredChannel === "viber" ? phone || undefined : undefined,
        whatsapp: preferredChannel === "whatsapp" ? phone || undefined : undefined,
      });

      return contacts;
    }, []);
}

function doneForYouOrderItemsForRequest(request: DoneForYouSetupRequest): OrderItem[] {
  const sourceText = request.orderHabitsText.trim() || request.supplierListText.trim();

  return parseQuickOrderText(sourceText).map((item, index) => {
    const cleanName = item.name.trim();
    const cleanSupplier = item.supplier.trim() || "Supplier pending";
    const cleanUnit = item.unit.trim() || "unit";

    return {
      productId: `done-for-you-${request.requestId}-${slugForNav(cleanSupplier)}-${slugForNav(cleanName)}-${index}`,
      name: cleanName,
      quantity: Number(item.quantity.toFixed(2)),
      unit: cleanUnit,
      unitPrice: Number(item.unitPrice.toFixed(2)),
      supplier: cleanSupplier,
      category: item.category?.trim() || doneForYouCategoryForText(cleanName),
      note: item.note?.trim() || undefined,
      source: "reorder",
    };
  });
}

function currentActionTimestamp() {
  return new Date().toLocaleString(undefined, {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  });
}

function readableSaveError(error: string) {
  const errors: Record<string, string> = {
    empty_import: "The file has no product rows below the header.",
    bootstrap_not_loaded: "Live workspace data could not be loaded.",
    branch_budget_not_saved: "Branch budget changes could not be saved.",
    catalog_product_not_saved: "Catalog changes could not be saved.",
    coverage_not_saved: "Coverage changes could not be saved.",
    delivery_area_not_saved: "Delivery capacity could not be saved.",
    external_supplier_not_saved: "Supplier record changes could not be saved.",
    fulfillment_not_saved: "Fulfillment changes could not be saved.",
    inquiry_not_saved: "The supplier inquiry could not be saved.",
    import_batch_not_saved: "The file was read, but the import batch could not be saved.",
    import_not_saved: "The import service could not be reached.",
    integration_request_not_saved: "The connection setup could not be saved.",
    invoice_not_saved: "The invoice could not be saved.",
    invoice_already_paid: "This invoice is already marked paid.",
    invoice_not_blocked: "This invoice is not currently blocked.",
    invoice_not_found: "The invoice could not be found for this workspace.",
    invoice_not_prepared: "Prepare this invoice for bank transfer before marking it paid.",
    invoice_not_ready: "This invoice is not ready to prepare yet.",
    localization_not_saved: "Market localization changes could not be saved.",
    message_not_saved: "Message changes could not be saved.",
    missing_invoice_amount: "Enter the invoice amount before saving.",
    network_intelligence_not_saved: "Market intelligence could not be saved.",
    network_state_not_saved: "Network state changes could not be saved.",
    order_not_saved: "The order could not be saved.",
    preferred_supplier_not_saved: "The preferred supplier could not be saved.",
    quote_request_not_saved: "The price request could not be saved.",
    quote_acceptance_not_saved: "The accepted offer could not be saved.",
    quote_not_ready: "This quote is not ready to accept yet.",
    quote_offer_not_found: "The selected supplier offer could not be found.",
    quote_response_not_saved: "The quote response could not be saved.",
    source_not_saved: "The supplier source could not be saved.",
    sprint_not_saved: "The sourcing sprint could not be saved.",
    missing_organization_name: "Enter the business name before saving.",
    missing_provider_name: "Enter the software provider before saving.",
    supabase_not_configured: "Live workspace storage is not configured.",
    workspace_user_not_saved: "Workspace access could not be saved.",
  };

  return errors[error] ?? error.replace(/_/g, " ");
}

function importReadErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "The file could not be read.";
}

function slugForNav(label: string) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function navFromHash() {
  if (typeof window === "undefined") {
    return "Dashboard";
  }

  const slug = window.location.hash.replace(/^#\/?/, "").replace(/^app\//, "");
  if (slug === "external-discovery") {
    return "Supplier Finder";
  }
  if (slug === "reports") {
    return "Analytics";
  }

  const match = navItems.find((item) => slugForNav(item.label) === slug);
  return match?.label ?? "Dashboard";
}

function initialNav() {
  const hashNav = navFromHash();

  if (typeof window !== "undefined" && window.location.hash.replace(/^#\/?/, "")) {
    return hashNav;
  }

  const stored = storageGet("veleo.activeNav");
  if (stored === "External Discovery") {
    return "Supplier Finder";
  }
  if (stored === "Reports") {
    return "Analytics";
  }

  return navItems.some((item) => item.label === stored) ? stored ?? "Dashboard" : "Dashboard";
}

function publicPageFromHash(): "landing" | "login" {
  if (typeof window === "undefined") {
    return "landing";
  }

  const slug = window.location.hash.replace(/^#\/?/, "").toLowerCase();
  const workspaceSlug = slug.replace(/^app\//, "");
  const workspaceRoutes = new Set([
    ...navItems.map((item) => slugForNav(item.label)),
    "external-discovery",
    "reports",
  ]);

  if (slug === "login" || slug === "signin" || slug === "sign-in") {
    return "login";
  }

  if (workspaceRoutes.has(workspaceSlug)) {
    return "login";
  }

  return "landing";
}

function initialRole(): Role {
  const routeRole = roleFromUrl();
  if (routeRole) {
    return routeRole;
  }

  const stored = storageGet("veleo.role");
  return stored === "restaurant" || stored === "admin" || stored === "supplier" ? stored : "supplier";
}

function initialSessionRole(): Role | null {
  return null;
}

function roleFromUrl(): Role | null {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const value = params.get("workspace") ?? params.get("role");
  return value === "restaurant" || value === "supplier" || value === "admin" ? value : null;
}

function isMarketplaceMode(value: string | null): value is MarketplaceMode {
  return value === "All" || value === "Ready" || value === "Limited" || value === "Backup" || value === "Saved";
}

const marketplaceSupplyStateLabels: Record<MarketplaceSupplyState, string> = {
  unverified: "Unverified",
  restaurant_provided: "Restaurant-provided",
  contacted: "Contacted",
  price_list_uploaded: "Price list uploaded",
  delivery_confirmed: "Delivery confirmed",
  active: "Active",
};

function marketplaceSupplyStateLabel(state: MarketplaceSupplyState) {
  return marketplaceSupplyStateLabels[state];
}

function marketplaceSupplyStateDetail(product: MarketplaceProduct) {
  const details: Record<MarketplaceSupplyState, string> = {
    unverified: "VELEO has not verified contact, price, or delivery. Request first.",
    restaurant_provided: "The restaurant supplied this supplier. Confirm terms before ordering.",
    contacted: "Supplier has been contacted, but price and delivery are not fully confirmed.",
    price_list_uploaded: "A price list exists, but delivery still needs confirmation.",
    delivery_confirmed: "Delivery is confirmed; ask once more for price and stock before sending.",
    active: "Price, stock, and delivery are confirmed enough for a clean order message.",
  };

  return `${marketplaceSupplyStateLabel(product.supplyState)}: ${details[product.supplyState]}`;
}

function marketplaceProductCanOrder(product: MarketplaceProduct) {
  return (
    product.supplyState === "active" &&
    product.supplierType === "verified" &&
    product.availability === "in_stock" &&
    parseMarketplacePrice(product.priceRange) > 0
  );
}

function supplierOptionScore(product: MarketplaceProduct, savedSupplierNames: string[] = []) {
  const availabilityScore =
    product.availability === "in_stock" ? 30 : product.availability === "limited" ? 16 : 6;
  const supplyStateScore: Record<MarketplaceSupplyState, number> = {
    active: 34,
    delivery_confirmed: 26,
    price_list_uploaded: 20,
    contacted: 14,
    restaurant_provided: 12,
    unverified: 4,
  };
  const verificationScore = supplyStateScore[product.supplyState];
  const savedScore = product.saved || savedSupplierNames.includes(product.supplierName) ? 16 : 0;

  return verificationScore + availabilityScore + savedScore + product.rating * 5;
}

function marketplaceDecisionScore(
  product: MarketplaceProduct,
  branch: BranchAccount,
  savedSupplierNames: string[] = [],
) {
  return supplierOptionScore(product, savedSupplierNames) + productBranchFit(product, branch).score;
}

function marketplaceDecisionFor(product: MarketplaceProduct, fit: ProductBranchFit): MarketplaceDecision {
  if (product.supplierType === "external") {
    return {
      action: "Verify supplier",
      detail: `${fit.label}. ${marketplaceSupplyStateDetail(product)} Send the item, amount, timing, and contact details before using this supplier.`,
      severity: "external",
    };
  }

  if (marketplaceProductCanOrder(product) && fit.score >= 38) {
    return {
      action: "Add to list",
      detail: `${fit.label}. ${marketplaceSupplyStateDetail(product)}`,
      severity: "ready",
    };
  }

  if (product.supplyState === "delivery_confirmed") {
    return {
      action: "Confirm stock",
      detail: `${fit.label}. ${marketplaceSupplyStateDetail(product)}`,
      severity: "watch",
    };
  }

  if (product.supplyState === "price_list_uploaded") {
    return {
      action: "Confirm delivery",
      detail: `${fit.label}. ${marketplaceSupplyStateDetail(product)}`,
      severity: "watch",
    };
  }

  return {
    action: product.supplyState === "contacted" ? "Ask supplier" : "Verify supplier",
    detail: `${fit.label}. ${marketplaceSupplyStateDetail(product)}`,
    severity: "watch",
  };
}

function procurementBriefFor(
  product: MarketplaceProduct,
  branch: BranchAccount,
  isSaved: boolean,
): ProcurementBriefItem[] {
  const fit = productBranchFit(product, branch);
  const decision = marketplaceDecisionFor(product, fit);
  const parsedPrice = parseMarketplacePrice(product.priceRange);
  const parsedQuantity = parseMarketplaceQuantity(product.minOrder);
  const estimatedLineTotal = parsedPrice * parsedQuantity.quantity;
  const branchRemaining = branch.weeklyBudget - branch.spentThisWeek;
  const budgetAfterLine = branchRemaining - estimatedLineTotal;
  const hasCommercialPrice = parsedPrice > 0 && product.availability !== "quote_only";
  const budgetStatus: ProcurementBriefItem["status"] =
    !hasCommercialPrice || !marketplaceProductCanOrder(product) || estimatedLineTotal > branch.approvalLimit || budgetAfterLine < 0
      ? "critical"
      : budgetAfterLine < branch.weeklyBudget * 0.08
        ? "watch"
        : "ready";
  const routeStatus: ProcurementBriefItem["status"] =
    fit.status === "local" || fit.status === "route"
      ? "ready"
      : fit.status === "backup" || fit.status === "unverified"
        ? "watch"
        : "critical";
  const trustStatus: ProcurementBriefItem["status"] =
    product.supplyState === "active"
      ? "ready"
      : product.supplyState === "unverified" || product.supplierType === "external"
        ? "watch"
        : product.rating >= 4.5 || isSaved
          ? "ready"
          : "watch";
  const nextStatus: ProcurementBriefItem["status"] =
    decision.severity === "ready" ? "ready" : product.supplierType === "external" ? "watch" : "critical";

  return [
    {
      label: "Line estimate",
      value: hasCommercialPrice ? formatBranchMoney(estimatedLineTotal, branch) : "Quote required",
      detail: hasCommercialPrice
        ? `${parsedQuantity.quantity} ${parsedQuantity.unit} at ${formatBranchUnitPrice(parsedPrice, parsedQuantity.unit, branch)}. ${marketplaceProductCanOrder(product) ? "Ready to send." : "Supplier confirmation still required."}`
        : "Supplier must confirm price, minimum order, and tax treatment before this can become an order line.",
      status: hasCommercialPrice && marketplaceProductCanOrder(product) ? "ready" : "watch",
    },
    {
      label: "Budget impact",
      value: hasCommercialPrice ? `${formatBranchMoney(Math.max(0, budgetAfterLine), branch)} left` : "Approval pending",
      detail:
        !hasCommercialPrice
          ? "Budget check waits for confirmed commercial terms."
          : estimatedLineTotal > branch.approvalLimit
            ? `Line is above this branch approval limit of ${formatBranchMoney(branch.approvalLimit, branch)}.`
            : budgetAfterLine < 0
              ? "This order would exceed the branch weekly budget."
              : "Line fits current branch budget controls.",
      status: budgetStatus,
    },
    {
      label: "Route confidence",
      value: fit.label,
      detail: fit.detail,
      status: routeStatus,
    },
    {
      label: "Supplier check",
      value: marketplaceSupplyStateLabel(product.supplyState),
      detail: isSaved
        ? "Supplier is already saved for this restaurant."
        : product.supplyState === "active"
          ? `${product.responseSpeed}. Save the supplier if terms should be used again.`
          : marketplaceSupplyStateDetail(product),
      status: trustStatus,
    },
    {
      label: "Next action",
      value: decision.action,
      detail: decision.detail,
      status: nextStatus,
    },
  ];
}

function normalizeMarketText(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function branchCity(branch: BranchAccount) {
  return branch.market.split(",")[0]?.trim() || branch.market;
}

function productBranchFit(product: MarketplaceProduct, branch: BranchAccount): ProductBranchFit {
  const city = branchCity(branch);
  const normalizedCity = normalizeMarketText(city);
  const normalizedBranchMarket = normalizeMarketText(branch.market);
  const normalizedProductMarket = normalizeMarketText(product.market);
  const bosniaMarkets = ["sarajevo", "mostar", "banja luka", "tuzla", "zenica", "bosnia", "bih"];
  const branchInBosnia =
    normalizedBranchMarket.includes("ba") ||
    normalizedBranchMarket.includes("bosnia") ||
    bosniaMarkets.some((market) => normalizedBranchMarket.includes(market));
  const productInBosnia = bosniaMarkets.some((market) => normalizedProductMarket.includes(market));
  const servesCity = normalizedProductMarket.includes(normalizedCity);
  const servesCountry = (branchInBosnia && productInBosnia) || normalizedProductMarket.includes("balkans");
  const routeLike =
    normalizedProductMarket.includes("lane") ||
    normalizedProductMarket.includes("/") ||
    normalizedProductMarket.includes("metro") ||
    normalizedProductMarket.includes(normalizedBranchMarket);

  if (servesCity && product.supplyState === "active") {
    return {
      label: "Branch route",
      detail: `${product.supplierName} serves ${city} for this branch.`,
      status: routeLike ? "route" : "local",
      score: routeLike ? 56 : 62,
    };
  }

  if (servesCity && product.supplyState === "delivery_confirmed") {
    return {
      label: "Delivery confirmed",
      detail: `${product.supplierName} delivers to ${city}; confirm stock and price before sending.`,
      status: "route",
      score: 48,
    };
  }

  if (servesCity) {
    return {
      label: "Ask first",
      detail: `${product.supplierName} appears to serve ${city}; confirm price and delivery before ordering.`,
      status: "unverified",
      score: 38,
    };
  }

  if (servesCountry) {
    return {
      label: "Regional backup",
      detail: `${product.supplierName} may cover ${branch.market}; confirm delivery terms.`,
      status: product.supplierType === "external" ? "unverified" : "backup",
      score: product.supplierType === "external" ? 24 : 32,
    };
  }

  return {
    label: "Check delivery",
    detail: `${product.supplierName} is outside the branch's normal supplier route.`,
    status: "out_of_area",
    score: product.supplierType === "external" ? 6 : 14,
  };
}

function marketplaceModeForProduct(product: MarketplaceProduct): MarketplaceMode {
  if (product.supplierType === "external" || product.tags.includes("backup supplier needed")) {
    return "Backup";
  }

  if (!marketplaceProductCanOrder(product) || product.availability === "limited") {
    return "Limited";
  }

  return "Ready";
}

function parseMarketplacePrice(priceRange: string) {
  const matches = priceRange.match(/\d+(?:\.\d+)?/g)?.map(Number) ?? [];

  if (!matches.length) {
    return 0;
  }

  return matches.length > 1 ? (matches[0] + matches[1]) / 2 : matches[0];
}

function parseMarketplaceQuantity(minOrder: string) {
  const quantity = Number(minOrder.match(/\d+(?:\.\d+)?/)?.[0] ?? 1);
  const unit = minOrder.match(/\b(kg|case|cases|pcs|pc|packs|pack|L|box|boxes)\b/i)?.[0] ?? "unit";

  return {
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    unit: unit.toLowerCase() === "cases" ? "case" : unit.toLowerCase() === "boxes" ? "box" : unit,
  };
}

function initialsForWorkspace(name: string) {
  const letters = name
    .split(/\s+/)
    .map((part) => part.replace(/[^a-z0-9]/gi, ""))
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return letters || "V";
}

function marketplaceProductToDraftItem(product: MarketplaceProduct): OrderItem {
  const { quantity, unit } = parseMarketplaceQuantity(product.minOrder);

  return {
    productId: `market-${product.id}`,
    name: product.name,
    quantity,
    unit,
    unitPrice: parseMarketplacePrice(product.priceRange),
    supplier: product.supplierName,
    category: product.category,
    source: "marketplace",
  };
}

function quoteTextNeedsPricing(value?: string) {
  return !value || /quote|open|supplier to quote|avg budget|demand signal/i.test(value);
}

function quoteCatalogMatch(request: QuoteRequest, catalogProducts: Product[]) {
  const requestName = normalizeMarketText(request.product);
  const requestCategory = request.category ? normalizeMarketText(request.category) : "";
  const requestSupplier = request.supplierName ? normalizeMarketText(request.supplierName) : "";

  return catalogProducts
    .map((product) => {
      const productName = normalizeMarketText(product.name);
      const productSupplier = normalizeMarketText(product.supplier);
      const productCategory = normalizeMarketText(marketplaceCategoryForCatalogProduct(product));
      let score = 0;

      if (requestSupplier && productSupplier === requestSupplier) {
        score += 50;
      }

      if (productName === requestName) {
        score += 40;
      } else if (productName.includes(requestName) || requestName.includes(productName)) {
        score += 26;
      }

      if (requestCategory && (productCategory.includes(requestCategory) || requestCategory.includes(productCategory))) {
        score += 20;
      }

      return { product, score };
    })
    .filter((match) => match.score > 0)
    .sort((left, right) => right.score - left.score)[0]?.product;
}

function fallbackQuoteUnitPrice(request: QuoteRequest) {
  const normalized = normalizeMarketText(`${request.category ?? ""} ${request.product}`);

  if (/(meat|beef|butcher|poultry)/.test(normalized)) {
    return 16.5;
  }

  if (/(seafood|fish|salmon|shrimp|calamari)/.test(normalized)) {
    return 24;
  }

  if (/(dairy|butter|milk|cheese|yogurt|cream)/.test(normalized)) {
    return 7.8;
  }

  if (/(packaging|box|bag|cup|napkin|paper)/.test(normalized)) {
    return 0.42;
  }

  if (/(pantry|oil|dry goods|flour)/.test(normalized)) {
    return 9.5;
  }

  if (/(cleaning|detergent|non-food)/.test(normalized)) {
    return 44;
  }

  if (/(frozen|berries)/.test(normalized)) {
    return 8.4;
  }

  return 2.4;
}

function resolveSupplierQuoteTerms(request: QuoteRequest, catalogProducts: Product[]) {
  const catalogMatch = quoteCatalogMatch(request, catalogProducts);
  const parsedPrice =
    quoteTextNeedsPricing(request.priceRange) && quoteTextNeedsPricing(request.budget)
      ? 0
      : parseMarketplacePrice(request.priceRange ?? request.budget);
  const resolvedUnitPrice = catalogMatch?.price ?? (parsedPrice || fallbackQuoteUnitPrice(request));
  const unitPrice = Number(resolvedUnitPrice.toFixed(2));
  const quantity = parseMarketplaceQuantity(request.quantity);
  const quoteUnit = catalogMatch?.unit ?? quantity.unit;
  const estimatedTotal = quantity.quantity > 1 ? unitPrice * quantity.quantity : unitPrice;
  const deliveryWindow =
    request.deliveryWindow && !/quote|supplier/i.test(request.deliveryWindow)
      ? request.deliveryWindow
      : request.neededBy;

  return {
    budget: `${formatCurrencyAmount(estimatedTotal, "BAM")} est.`,
    priceRange: `${formatCurrencyAmount(unitPrice, "BAM")} / ${quoteUnit}`,
    unitPrice,
    deliveryWindow,
    termsNote: catalogMatch
      ? `Priced from live catalog SKU ${catalogMatch.name}.`
      : "Priced from supplier quote template; confirm final terms before dispatch.",
  };
}

function marketplaceAvailabilityForStock(status: StockStatus): MarketplaceProduct["availability"] {
  if (status === "available") {
    return "in_stock";
  }

  return status === "low" ? "limited" : "quote_only";
}

function marketplaceSupplyStateForCatalogProduct(
  product: Product,
  deliveryArea?: DeliveryArea,
): MarketplaceSupplyState {
  if (deliveryArea?.status === "active" && product.stockStatus === "available") {
    return "active";
  }

  if (deliveryArea?.status === "active" || deliveryArea?.status === "capacity_watch") {
    return "delivery_confirmed";
  }

  if (product.price > 0) {
    return "price_list_uploaded";
  }

  return "contacted";
}

function marketplaceCategoryForCatalogProduct(product: Product) {
  const normalized = `${product.category} ${product.name}`.toLowerCase();

  if (/(citrus|herb|microgreen|orange|lemon|lime|produce|berries)/.test(normalized)) {
    return "Fresh produce";
  }

  if (/(oil|dry goods|pantry)/.test(normalized)) {
    return "Pantry";
  }

  return product.category;
}

function catalogProductToMarketplaceProduct(
  product: Product,
  deliveryAreas: DeliveryArea[],
  savedSupplierNames: string[],
): MarketplaceProduct {
  const activeDeliveryAreas = deliveryAreas.filter((area) => area.status === "active");
  const primaryDeliveryArea = activeDeliveryAreas[0] ?? deliveryAreas[0];
  const deliveryWindow = primaryDeliveryArea
    ? `${primaryDeliveryArea.market} / ${primaryDeliveryArea.days}`
    : "Supplier route pending";
  const supplierMinimum = primaryDeliveryArea?.minimumOrder
    ? `${primaryDeliveryArea.minimumOrder} supplier minimum`
    : "Supplier minimum on request";
  const saved = savedSupplierNames.includes(product.supplier);
  const rating =
    primaryDeliveryArea?.performance
      ? Math.min(4.9, Math.max(3.5, 3.6 + primaryDeliveryArea.performance / 100))
      : product.stockStatus === "available"
        ? 4.4
        : 4.1;

  return {
    id: `catalog-${product.id}`,
    name: product.name,
    category: marketplaceCategoryForCatalogProduct(product),
    supplierName: product.supplier,
    supplierType: "verified",
    market: primaryDeliveryArea?.market ?? "Supplier catalog route",
    priceRange: `${formatCurrencyAmount(product.price, "BAM")} / ${product.unit}`,
    minOrder: `${Math.max(1, Math.round(product.lastOrderedKg))} ${product.unit}`,
    deliveryWindow,
    availability: marketplaceAvailabilityForStock(product.stockStatus),
    supplyState: marketplaceSupplyStateForCatalogProduct(product, primaryDeliveryArea),
    rating,
    responseSpeed: "Supplier catalog live",
    saved,
    tags: [
      "live supplier catalog",
      stockLabels[product.stockStatus].toLowerCase(),
      supplierMinimum,
      product.category,
    ],
  };
}

function catalogProductsToMarketplaceProducts(
  items: Product[],
  deliveryAreas: DeliveryArea[],
  savedSupplierNames: string[],
) {
  return items.map((product) =>
    catalogProductToMarketplaceProduct(product, deliveryAreas, savedSupplierNames),
  );
}

function marketplaceProductKey(product: MarketplaceProduct) {
  return `${normalizeMarketText(product.supplierName)}::${normalizeMarketText(product.name)}`;
}

function marketplaceProductPriority(product: MarketplaceProduct) {
  if (product.tags.includes("live supplier catalog")) {
    return 3;
  }

  return product.supplierType === "verified" ? 2 : 1;
}

function dedupeMarketplaceProducts(items: MarketplaceProduct[]) {
  const productMap = new Map<string, MarketplaceProduct>();

  items.forEach((product) => {
    const key = marketplaceProductKey(product);
    const existing = productMap.get(key);

    if (!existing) {
      productMap.set(key, product);
      return;
    }

    const keepProduct =
      marketplaceProductPriority(product) >= marketplaceProductPriority(existing) ? product : existing;
    const mergeFrom =
      keepProduct.id === product.id ? existing : product;

    productMap.set(key, {
      ...keepProduct,
      saved: keepProduct.saved || mergeFrom.saved,
      tags: Array.from(new Set([...mergeFrom.tags, ...keepProduct.tags])),
    });
  });

  return Array.from(productMap.values());
}

function titleCaseProductName(value: string) {
  return value
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function inferExternalCategory(productName: string) {
  const normalized = productName.toLowerCase();

  if (/(butter|cream|yogurt|cheese|dairy|milk)/.test(normalized)) {
    return "Dairy";
  }

  if (/(fish|shrimp|calamari|seafood)/.test(normalized)) {
    return "Seafood";
  }

  if (/(box|bag|cup|napkin|packaging|paper)/.test(normalized)) {
    return "Packaging";
  }

  if (/(berries|frozen)/.test(normalized)) {
    return "Frozen";
  }

  if (/(olive oil|dry goods|flour|pantry)/.test(normalized)) {
    return "Pantry";
  }

  if (/(herbs|produce|citrus|berries)/.test(normalized)) {
    return "Fresh produce";
  }

  return "External supply";
}

function unitForExternalProduct(productName: string, category: string) {
  const normalized = normalizeMarketText(`${category} ${productName}`);

  if (/(packaging|box|bag|cup|napkin|paper|cleaning|detergent)/.test(normalized)) {
    return "case";
  }

  if (/(olive oil|oil|dry goods|pantry)/.test(normalized)) {
    return "case";
  }

  if (/(herb|produce|citrus|berries|fish|seafood|dairy|butter|cheese|meat)/.test(normalized)) {
    return "kg";
  }

  return "unit";
}

function minimumQuantityForExternalProduct(unit: string) {
  if (unit === "case") {
    return 3;
  }

  if (unit === "kg") {
    return 12;
  }

  return 1;
}

function externalRecordToCatalogProducts(record: ExternalSupplierRecord): Product[] {
  return record.products.map((productName) => {
    const name = titleCaseProductName(productName);
    const category = inferExternalCategory(productName);
    const unit = unitForExternalProduct(productName, category);
    const price = fallbackQuoteUnitPrice({
      id: `quote-template-${record.id}-${slugForNav(productName)}`,
      buyer: "Converted supplier onboarding",
      product: name,
      quantity: `1 ${unit}`,
      neededBy: "First verified catalog sync",
      status: "sent",
      suppliersMatched: 1,
      budget: "Open quote",
      supplierName: record.name,
      category,
      deliveryWindow: record.deliveryHint,
    });

    return {
      id: `converted-${record.id}-${slugForNav(productName)}`,
      name,
      supplier: record.name,
      category,
      unit,
      price,
      currency: "BAM" as const,
      stockStatus: "low" as const,
      lastOrderedKg: minimumQuantityForExternalProduct(unit),
    };
  });
}

function externalRecordToMarketplaceProducts(record: ExternalSupplierRecord): MarketplaceProduct[] {
  const isConverted = record.status === "converted";
  const supplyState: MarketplaceSupplyState =
    record.status === "converted"
      ? "contacted"
      : record.status === "invited"
        ? "contacted"
        : "unverified";

  return record.products.map((productName) => ({
    id: `${isConverted ? "converted" : "external"}-${record.id}-${slugForNav(productName)}`,
    name: titleCaseProductName(productName),
    category: inferExternalCategory(productName),
    supplierName: record.name,
    supplierType: isConverted ? "verified" : "external",
    market: record.market,
    priceRange: isConverted ? "Supplier quote ready" : "Quote required",
    minOrder: isConverted ? "Minimum order in supplier catalog" : "Confirm minimum order",
    deliveryWindow: isConverted ? "Verified route; confirm slot" : record.deliveryHint,
    availability: "quote_only",
    supplyState,
    rating: Math.min(4.8, Math.max(3.4, record.confidence / 20)),
    responseSpeed: isConverted ? "New supplier response pending" : "Ask first",
    saved: false,
    tags: isConverted
      ? ["converted supplier", "verified onboarding", record.source]
      : ["external/unverified", record.status.replace(/_/g, " "), record.source],
  }));
}

function externalRecordsToMarketplaceProducts(records: ExternalSupplierRecord[]) {
  return records.flatMap(externalRecordToMarketplaceProducts);
}

function quoteRequestUnitPrice(request: QuoteRequest) {
  if (request.unitPrice && request.unitPrice > 0) {
    return request.unitPrice;
  }

  if (request.priceRange) {
    const price = parseMarketplacePrice(request.priceRange);
    if (price > 0) {
      return price;
    }
  }

  const { quantity } = parseMarketplaceQuantity(request.quantity);
  const total = parseMarketplacePrice(request.budget);

  return total > 0 && quantity > 0 ? total / quantity : 0;
}

function quoteRequestToDraftItem(request: QuoteRequest): OrderItem {
  const { quantity, unit } = parseMarketplaceQuantity(request.quantity);

  return {
    productId: `quote-${request.id}`,
    name: request.product,
    quantity,
    unit,
    unitPrice: quoteRequestUnitPrice(request),
    supplier: request.supplierName ?? "Quoted supplier",
    category: request.category ?? "Quoted product",
    source: "quote",
  };
}

function supplierOfferToDraftItem(offer: SupplierOffer): OrderItem {
  const { quantity, unit } = parseMarketplaceQuantity(offer.minimumOrder);

  return {
    productId: `offer-${offer.id}`,
    name: offer.productName,
    quantity: Math.max(1, quantity || 1),
    unit: unit || "unit",
    unitPrice: offer.landedPrice,
    supplier: offer.supplierName,
    category: offer.offerType === "catalog_baseline" ? "Catalog offer" : "Supplier offer",
    source: offer.offerType === "catalog_baseline" ? "marketplace" : "quote",
  };
}

function canonicalSupplierName(supplierName: string, lookup: DraftLookupContext) {
  const cleanSupplierName = supplierName.trim();
  if (!cleanSupplierName) {
    return "";
  }

  const normalizedSupplier = normalizeMarketText(cleanSupplierName);
  if (!normalizedSupplier) {
    return cleanSupplierName;
  }

  const weakSupplierWords = new Set([
    "doo",
    "d.o.o",
    "market",
    "trade",
    "trading",
    "supply",
    "supplier",
    "company",
    "group",
    "promet",
  ]);
  const supplierWords = normalizedSupplier
    .split(" ")
    .filter((word) => word.length >= 3 && !weakSupplierWords.has(word));
  const contactMatch = lookup.supplierContacts.find((contact) => {
    const normalizedContact = normalizeMarketText(contact.name);
    return (
      normalizedContact === normalizedSupplier ||
      normalizedContact.includes(normalizedSupplier) ||
      normalizedSupplier.includes(normalizedContact) ||
      supplierWords.some((word) => normalizedContact.split(" ").includes(word))
    );
  });

  return contactMatch?.name ?? cleanSupplierName;
}

function supplierForDraftItem(item: OrderItem, lookup: DraftLookupContext) {
  if (item.supplier) {
    return canonicalSupplierName(item.supplier, lookup);
  }

  const catalogMatch = lookup.catalogProducts.find((product) => product.id === item.productId);
  if (catalogMatch) {
    return catalogMatch.supplier;
  }

  const marketplaceMatch = lookup.marketplaceProducts.find((product) => `market-${product.id}` === item.productId);
  return marketplaceMatch?.supplierName ?? "Supplier pending";
}

function categoryForDraftItem(item: OrderItem, lookup: DraftLookupContext) {
  if (item.category) {
    return item.category;
  }

  const catalogMatch = lookup.catalogProducts.find((product) => product.id === item.productId);
  if (catalogMatch) {
    return catalogMatch.category;
  }

  const marketplaceMatch = lookup.marketplaceProducts.find((product) => `market-${product.id}` === item.productId);
  return marketplaceMatch?.category ?? "Uncategorized";
}

function sourceForDraftItem(item: OrderItem) {
  if (item.source) {
    return item.source;
  }

  return item.productId.startsWith("market-") ? "marketplace" : "reorder";
}

function draftItemNeedsUsualReview(item: Pick<OrderItem, "name" | "note">) {
  return (
    item.name === "Usual item" ||
    /\b(?:Quantity-only repeat order|Original quantity shorthand)\b/i.test(item.note ?? "")
  );
}

function supplierContactForDraftSupplier(supplierName: string, lookup: DraftLookupContext) {
  const canonicalName = canonicalSupplierName(supplierName, lookup);
  const normalizedCanonical = normalizeMarketText(canonicalName);

  if (!normalizedCanonical) {
    return undefined;
  }

  return lookup.supplierContacts.find((contact) => {
    const normalizedContact = normalizeMarketText(contact.name);
    return (
      normalizedContact === normalizedCanonical ||
      normalizedContact.includes(normalizedCanonical) ||
      normalizedCanonical.includes(normalizedContact)
    );
  });
}

function draftItemSavedSupplierHabit(item: OrderItem, lookup: DraftLookupContext) {
  if (!draftItemNeedsUsualReview(item)) {
    return "";
  }

  const supplierName = supplierForDraftItem(item, lookup);
  if (supplierName === "Supplier pending") {
    return "";
  }

  return supplierContactForDraftSupplier(supplierName, lookup)?.usualOrderText?.trim() ?? "";
}

function enrichDraftItem(
  item: OrderItem,
  lookup: DraftLookupContext,
  fallback?: Pick<OrderItem, "supplier" | "category" | "source">,
): OrderItem {
  const supplier = item.supplier ?? fallback?.supplier ?? supplierForDraftItem(item, lookup);
  return {
    ...item,
    supplier: canonicalSupplierName(supplier, lookup),
    category: item.category ?? fallback?.category ?? categoryForDraftItem(item, lookup),
    source: item.source ?? fallback?.source ?? sourceForDraftItem(item),
  };
}

function draftSuppliersFor(items: OrderItem[], lookup: DraftLookupContext) {
  return Array.from(new Set(items.map((item) => supplierForDraftItem(item, lookup))));
}

function draftSupplierLabelFor(items: OrderItem[], lookup: DraftLookupContext) {
  const suppliers = draftSuppliersFor(items, lookup);

  if (!suppliers.length) {
    return "No supplier selected";
  }

  if (suppliers.length === 1) {
    return suppliers[0];
  }

  return `${suppliers.length} suppliers`;
}

function draftSupplierPacketsFor(items: OrderItem[], lookup: DraftLookupContext): DraftSupplierPacket[] {
  const packetMap = new Map<string, OrderItem[]>();

  items.map((item) => enrichDraftItem(item, lookup)).forEach((item) => {
    const supplierName = supplierForDraftItem(item, lookup);
    packetMap.set(supplierName, [...(packetMap.get(supplierName) ?? []), item]);
  });

  return Array.from(packetMap.entries()).map(([supplierName, packetItems]) => ({
    supplierName,
    items: packetItems,
    total: packetItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0),
    categories: Array.from(new Set(packetItems.map((item) => categoryForDraftItem(item, lookup)))),
    sources: Array.from(new Set(packetItems.map((item) => sourceForDraftItem(item)))),
  }));
}

function draftSourceName(source: NonNullable<OrderItem["source"]>) {
  const labels: Record<NonNullable<OrderItem["source"]>, string> = {
    catalog: "Catalog",
    marketplace: "Marketplace",
    reorder: "Reorder",
    forecast: "Forecast",
    quote: "Quote",
    manual: "Manual",
  };

  return labels[source];
}

function draftPacketSourceLabel(packet: DraftSupplierPacket) {
  return packet.sources.map(draftSourceName).join(" + ");
}

function draftSourceLabel(item: OrderItem) {
  return draftSourceName(sourceForDraftItem(item));
}

function approvalForDraft(
  approvals: ApprovalRequest[],
  branch: BranchAccount,
  draftTotal: number,
) {
  if (!draftTotal) {
    return undefined;
  }

  return approvals
    .filter(
      (request) =>
        request.branch === branch.name &&
        request.amountBam >= draftTotal &&
        request.status !== "blocked",
    )
    .sort((left, right) => {
      if (left.status === right.status) {
        return right.amountBam - left.amountBam;
      }

      return left.status === "approved" ? -1 : 1;
    })[0];
}

function orderStatusForFulfillmentStatus(status: SupplierFulfillmentOrder["status"]): OrderStatus {
  const statusMap: Record<SupplierFulfillmentOrder["status"], OrderStatus> = {
    needs_confirmation: "sent",
    confirmed: "confirmed",
    packed: "packed",
    in_transit: "in_transit",
    delivered: "delivered",
    issue: "issue",
  };

  return statusMap[status];
}

function buyerOrderSlaPriority(order: Order) {
  const rank: Record<OrderStatus, number> = {
    issue: 0,
    sent: 1,
    packed: 2,
    in_transit: 3,
    confirmed: 4,
    delivered: 5,
  };

  return rank[order.status];
}

function buyerOrderSlaSignalFor(order: Order): OrderSlaSignal {
  if (order.status === "issue") {
    return {
      order,
      title: `Recover ${order.id}`,
      detail: `${order.supplier} has an issue open. Create a replacement list or resolve it once terms are agreed.`,
      owner: "Buyer recovery",
      metric: order.deliveryAt,
      severity: "critical",
      button: "Replace",
    };
  }

  if (order.status === "sent") {
    return {
      order,
      title: `Confirm ${order.id}`,
      detail: `${order.supplier} still needs to confirm quantities, pricing, and delivery slot.`,
      owner: "Supplier",
      metric: order.deliveryAt,
      severity: "watch",
      button: "Nudge",
    };
  }

  if (order.status === "confirmed") {
    return {
      order,
      title: `Pack ${order.id}`,
      detail: "Order is confirmed. Packing and delivery paperwork should move before dispatch.",
      owner: "Supplier",
      metric: order.deliveryAt,
      severity: "ready",
      button: "Nudge",
    };
  }

  if (order.status === "packed") {
    return {
      order,
      title: `Dispatch ${order.id}`,
      detail: "Order is packed. The buyer should see route status before the delivery window.",
      owner: "Dispatch",
      metric: order.deliveryAt,
      severity: "watch",
      button: "Nudge",
    };
  }

  if (order.status === "in_transit") {
    return {
      order,
      title: `Track ${order.id}`,
      detail: "Driver is on route. Keep ETA and receiving notes visible until delivery closes.",
      owner: "Route",
      metric: order.deliveryAt,
      severity: "watch",
      button: "Nudge",
    };
  }

  return {
    order,
    title: `Repeat ${order.id}`,
    detail: "Delivered order can be repeated as a clean buying list for the same branch.",
    owner: "Closed",
    metric: `${formatMoney(order.total)} KM`,
    severity: "ready",
    button: "Repeat",
  };
}

function supplierQuoteSlaPriority(request: QuoteRequest) {
  const rank: Record<QuoteRequest["status"], number> = {
    waiting: 0,
    sent: 1,
    draft: 2,
    quoted: 3,
    accepted: 4,
    closed: 5,
  };

  return rank[request.status];
}

function isOpenQuoteStatus(status: QuoteRequest["status"]) {
  return status === "draft" || status === "sent" || status === "waiting";
}

function isReadyQuoteStatus(status: QuoteRequest["status"]) {
  return status === "quoted";
}

function supplierSafeQuoteMetric(request: QuoteRequest) {
  if (isOpenQuoteStatus(request.status)) {
    return "Private until quote";
  }

  return request.priceRange ?? request.budget;
}

function supplierQuoteSlaSignalFor(request: QuoteRequest): SupplierQuoteSlaSignal {
  if (request.status === "waiting") {
    return {
      request,
      title: `Follow up ${request.buyer}`,
      detail: `${request.product} is waiting on clear supplier terms before the restaurant can decide.`,
      owner: "Sales",
      metric: supplierSafeQuoteMetric(request),
      severity: "critical",
      button: "Send price",
    };
  }

  if (request.status === "sent") {
    return {
      request,
      title: `Price ${request.buyer}`,
      detail: `${request.quantity} needed ${request.neededBy}. Confirm price, delivery window, and terms.`,
      owner: "Sales",
      metric: supplierSafeQuoteMetric(request),
      severity: "critical",
      button: "Send price",
    };
  }

  if (request.status === "draft") {
    return {
      request,
      title: `Price ${request.product}`,
      detail: "Demand lead is prepared. Add supplier terms so the restaurant can compare it.",
      owner: "Pricing",
      metric: supplierSafeQuoteMetric(request),
      severity: "watch",
      button: "Send price",
    };
  }

  return {
    request,
    title: `Watch ${request.buyer}`,
    detail: `${request.product} terms were sent. Watch the restaurant decision and convert quickly if they accept.`,
    owner: "Restaurant decision",
    metric: request.quotedAt ?? request.deliveryWindow ?? request.neededBy,
    severity: "ready",
    button: "Quoted",
  };
}

function fulfillmentNeedsActionFor(order: SupplierFulfillmentOrder) {
  return order.status !== "delivered";
}

function nextFulfillmentState(order: SupplierFulfillmentOrder): SupplierFulfillmentOrder {
  if (order.status === "issue") {
    return { ...order, nextAction: "Issue is open with the buyer; agree recovery terms before moving fulfillment." };
  }

  if (order.status === "needs_confirmation") {
    return { ...order, status: "confirmed", nextAction: "Pack items and attach delivery paperwork" };
  }

  if (order.status === "confirmed") {
    return { ...order, status: "packed", nextAction: "Dispatch order and share route status" };
  }

  if (order.status === "packed") {
    return { ...order, status: "in_transit", nextAction: "Driver is on route; monitor SLA" };
  }

  if (order.status === "in_transit") {
    return { ...order, status: "delivered", nextAction: "Delivered; invoice can move to finance handoff" };
  }

  return { ...order, nextAction: "Delivery is complete; finance handoff is ready" };
}

function orderDraftPreflightFor(
  items: OrderItem[],
  packets: DraftSupplierPacket[],
  branch: BranchAccount,
  lookup: DraftLookupContext,
  approval?: ApprovalRequest,
): DraftPreflightCheck[] {
  const draftTotal = packets.reduce((sum, packet) => sum + packet.total, 0);
  const remainingBudget = branch.weeklyBudget - branch.spentThisWeek;
  const remainingAfterDraft = remainingBudget - draftTotal;
  const needsBudgetApproval = remainingAfterDraft < 0 || draftTotal > branch.approvalLimit;
  const approvalReady = approval?.status === "approved";
  const zeroPriceItems = items.filter((item) => item.unitPrice <= 0);
  const pendingSupplierItems = items.filter((item) => supplierForDraftItem(item, lookup) === "Supplier pending");
  const usualReviewItems = items.filter(draftItemNeedsUsualReview);
  const usualReviewItemsWithSavedHabits = usualReviewItems.filter((item) => draftItemSavedSupplierHabit(item, lookup));
  const usualReviewItemsMissingHabits = usualReviewItems.length - usualReviewItemsWithSavedHabits.length;
  const largestPacket = packets.slice().sort((left, right) => right.total - left.total)[0];
  const largestPacketShare = draftTotal > 0 && largestPacket ? (largestPacket.total / draftTotal) * 100 : 0;

  return [
    {
      id: "budget",
      title: "Budget check",
      value:
        approvalReady
          ? "Approved"
          : remainingAfterDraft < 0
          ? `${formatBranchMoney(Math.abs(remainingAfterDraft), branch)} over`
          : `${formatBranchMoney(remainingAfterDraft, branch)} left`,
      detail:
        approvalReady
          ? `${approval.requester} approved ${formatBranchMoney(approval.amountBam, branch, { includeBase: true })} for ${branch.name}.`
          : approval?.status === "needs_approval"
            ? `${branch.name} approval is pending before the order can be sent.`
            : remainingAfterDraft < 0
          ? `${branch.name} will exceed weekly budget. Send only if approval is ready.`
          : draftTotal > branch.approvalLimit
            ? `This order is above the ${formatBranchMoney(branch.approvalLimit, branch)} approval limit for ${branch.name}.`
          : `${branch.name} stays inside the weekly budget.`,
      status: needsBudgetApproval && !approvalReady ? "blocked" : "ready",
      icon: LockKeyhole,
    },
    {
      id: "suppliers",
      title: "Suppliers",
      value: `${packets.length} ${packets.length === 1 ? "supplier" : "suppliers"}`,
      detail:
        packets.length > 1
          ? "Veleo will send each supplier only the items they need to confirm."
          : "One supplier receives the full order.",
      status: packets.length > 1 ? "watch" : "ready",
      icon: Boxes,
    },
    {
      id: "pricing",
      title: "Price readiness",
      value: zeroPriceItems.length ? `${zeroPriceItems.length} quote lines` : "Priced",
      detail: zeroPriceItems.length
        ? "Supplier can confirm missing prices from the VELEO link."
        : "Every item has a unit price for the estimated total.",
      status: zeroPriceItems.length ? "watch" : "ready",
      icon: FileSpreadsheet,
    },
    {
      id: "usual-shorthand",
      title: "Usual shorthand",
      value: usualReviewItems.length
        ? usualReviewItemsMissingHabits
          ? `${usualReviewItemsMissingHabits} review`
          : `${usualReviewItemsWithSavedHabits.length} learned`
        : "Clear",
      detail: usualReviewItems.length
        ? usualReviewItemsMissingHabits
          ? usualReviewItemsWithSavedHabits.length
            ? `${usualReviewItemsWithSavedHabits.length} shorthand line${usualReviewItemsWithSavedHabits.length === 1 ? "" : "s"} match saved supplier habits. ${usualReviewItemsMissingHabits} still need a saved habit or manual confirmation.`
            : "Some lines came from quantity-only supplier habits. Confirm the supplier knows the usual item before sending."
          : "Saved supplier habits cover these shorthand lines. The VELEO link still lets the supplier correct them."
        : "No quantity-only repeat-template lines need review.",
      status: usualReviewItemsMissingHabits ? "watch" : "ready",
      icon: usualReviewItemsMissingHabits ? AlertTriangle : ShieldCheck,
    },
    {
      id: "supplier",
      title: "Supplier attribution",
      value: pendingSupplierItems.length ? `${pendingSupplierItems.length} pending` : "Ready",
      detail: pendingSupplierItems.length
        ? "Some items do not have a supplier attached. Assign a current supplier contact on the list before sending."
        : "Every item has a supplier.",
      status: pendingSupplierItems.length ? "blocked" : "ready",
      icon: ShieldCheck,
    },
    {
      id: "concentration",
      title: "Supplier concentration",
      value: largestPacket ? `${largestPacketShare.toFixed(0)}% largest` : "No lines",
      detail:
        largestPacketShare > 70
          ? `${largestPacket?.supplierName} holds most of the order value. Keep a backup supplier ready for critical categories.`
          : "The order is not overly dependent on one supplier.",
      status: largestPacketShare > 70 ? "watch" : "ready",
      icon: Gauge,
    },
  ];
}

const statusLabels: Record<OrderStatus, string> = {
  sent: "Sent",
  confirmed: "Confirmed",
  packed: "Packed",
  in_transit: "In transit",
  delivered: "Delivered",
  issue: "Issue",
};

const stockLabels: Record<StockStatus, string> = {
  available: "Available",
  low: "Low stock",
  unavailable: "Out of stock",
};

function displayCurrencyLabel(currency?: string) {
  return currency === "BAM" ? "KM" : currency || "KM";
}

function formatCurrencyAmount(amount: number, currency?: string) {
  return `${formatMoney(amount)} ${displayCurrencyLabel(currency)}`;
}

function displayMoneyCopy(value?: string) {
  return value?.replace(/\bBAM\b/g, "KM") ?? "";
}

function formatCount(amount: number) {
  return new Intl.NumberFormat("en-US").format(amount);
}

function formatEur(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount);
}

const BAM_PER_EUR = 1.95583;

const coldStartPricingRules: ColdStartPricingRule[] = [
  {
    id: "restaurant-free-setup",
    audience: "restaurant",
    title: "Restaurants start free",
    amount: 0,
    currency: "BAM",
    cadence: "month",
    freeTrialMonths: 3,
    basis: "first 3 months free for done-for-you setup, supplier contacts, repeat orders, and invoice matching.",
    trigger: "Charge only after the restaurant has at least one supplier contact, one supplier-ready message, and one repeatable order path.",
    status: "free_now",
  },
  {
    id: "restaurant-habit-plan",
    audience: "restaurant",
    title: "Restaurant habit plan",
    amount: 10,
    currency: "BAM",
    cadence: "month",
    freeTrialMonths: 3,
    basis: "10 KM/month after trial so the normal ordering habit moves into VELEO without procurement-suite friction.",
    trigger: "Starts after the free period, only when the restaurant keeps using supplier messages, confirmations, or invoice checks.",
    status: "future_paid",
  },
  {
    id: "supplier-confirm-free",
    audience: "supplier",
    title: "Suppliers confirm free",
    amount: 0,
    currency: "BAM",
    cadence: "event",
    freeTrialMonths: 0,
    basis: "Existing suppliers can confirm, edit, decline, and add delivery notes from a VELEO link before any catalog or admin setup.",
    trigger: "No supplier billing for simply serving their existing restaurant customer through a confirmation link.",
    status: "free_now",
  },
  {
    id: "supplier-demand-growth",
    audience: "supplier",
    title: "Supplier growth pricing",
    amount: 0,
    currency: "BAM",
    cadence: "volume",
    freeTrialMonths: 0,
    basis: "Supplier monetization is based on confirmed demand, qualified leads, order volume, visibility in covered cities/categories, and automation.",
    trigger: "Charge later when VELEO can prove buyer demand, lead quality, confirmed order volume, or automation value.",
    status: "future_paid",
  },
];

let runtimeLocalizationRules = localizationRules;

function bamToEur(amountBam: number) {
  return amountBam / BAM_PER_EUR;
}

const INVOICE_AMOUNT_TOLERANCE_EUR = 0.75;

function orderTotalAsEur(order: Order) {
  return order.currency === "EUR" ? order.total : bamToEur(order.total);
}

function invoiceOrderMatch(orderId: string, orders: Order[]) {
  const normalizedOrderId = normalizeMarketText(orderId);

  if (!normalizedOrderId) {
    return undefined;
  }

  return orders.find((order) => {
    const normalizedId = normalizeMarketText(order.id);
    return normalizedId === normalizedOrderId || normalizedId.includes(normalizedOrderId) || normalizedOrderId.includes(normalizedId);
  });
}

function invoiceReadinessLabel(readiness?: InvoicePaymentReadiness) {
  switch (readiness) {
    case "needs_order":
      return "Needs order";
    case "needs_review":
      return "Needs review";
    case "blocked":
      return "Blocked";
    case "prepared":
      return "Prepared";
    case "paid":
      return "Paid";
    case "ready":
      return "Ready";
    default:
      return "Needs review";
  }
}

function invoiceMismatchLabel(type: InvoiceMismatchType) {
  switch (type) {
    case "missing_order":
      return "order missing";
    case "supplier_mismatch":
      return "supplier mismatch";
    case "price_mismatch":
      return "price mismatch";
    case "line_mismatch":
      return "line mismatch";
    case "missing_goods":
      return "missing goods";
    case "due_date_missing":
      return "due date missing";
  }
}

function invoiceMismatchCopy(invoice: InvoiceRecord) {
  if (!invoice.mismatchTypes?.length) {
    return "No mismatch logged";
  }

  return invoice.mismatchTypes.map(invoiceMismatchLabel).join(", ");
}

function invoiceCaptureLabel(invoice: InvoiceRecord) {
  if (invoice.captureMethod === "ocr_manual_review") {
    return "Photo/PDF + manual review";
  }

  if (invoice.captureMethod === "photo_pdf") {
    return "Photo/PDF";
  }

  return "Manual";
}

function invoiceLineItemsFromQuick(items: QuickOrderItemInput[] = []): InvoiceLineItem[] {
  return items
    .map((item) => {
      const quantity = Math.max(0, Number(item.quantity) || 0);
      const unitPrice = Math.max(0, Number(item.unitPrice) || 0);

      if (!item.name.trim() || !quantity) {
        return null;
      }

      return {
        name: item.name.trim(),
        quantity,
        unit: item.unit || "unit",
        unitPrice: Number(unitPrice.toFixed(2)),
        lineTotal: Number((quantity * unitPrice).toFixed(2)),
      };
    })
    .filter((item): item is InvoiceLineItem => Boolean(item));
}

function normalizeInvoiceLineName(value: string) {
  return normalizeMarketText(value).replace(/[^a-z0-9]+/g, " ").trim();
}

function normalizedInvoiceUnit(value: string) {
  return normalizeQuickUnit(value).toLowerCase();
}

function findInvoiceOrderLineIndex(invoiceLine: InvoiceLineItem, candidates: OrderItem[]) {
  const invoiceName = normalizeInvoiceLineName(invoiceLine.name);
  const invoiceUnit = normalizedInvoiceUnit(invoiceLine.unit);
  const exactIndex = candidates.findIndex(
    (candidate) =>
      normalizeInvoiceLineName(candidate.name) === invoiceName &&
      normalizedInvoiceUnit(candidate.unit) === invoiceUnit,
  );

  if (exactIndex >= 0) {
    return exactIndex;
  }

  return candidates.findIndex((candidate) => {
    const candidateName = normalizeInvoiceLineName(candidate.name);
    return Boolean(candidateName && (candidateName.includes(invoiceName) || invoiceName.includes(candidateName)));
  });
}

function compareInvoiceLinesToOrder(invoiceLines: InvoiceLineItem[], order?: Order): InvoiceLineMatch[] {
  if (!invoiceLines.length || !order?.items.length) {
    return [];
  }

  const unmatchedOrderItems = [...order.items];
  const matches: InvoiceLineMatch[] = [];

  invoiceLines.forEach((invoiceLine) => {
    const orderIndex = findInvoiceOrderLineIndex(invoiceLine, unmatchedOrderItems);

    if (orderIndex < 0) {
      matches.push({
        name: invoiceLine.name,
        status: "extra_invoice_line",
        detail: "Invoice line is not on the attached order.",
        invoiceQuantity: invoiceLine.quantity,
        invoiceUnitPrice: invoiceLine.unitPrice,
        deltaBam: invoiceLine.lineTotal,
      });
      return;
    }

    const orderLine = unmatchedOrderItems.splice(orderIndex, 1)[0];
    const quantityMismatch =
      Math.abs(orderLine.quantity - invoiceLine.quantity) > 0.01 ||
      normalizedInvoiceUnit(orderLine.unit) !== normalizedInvoiceUnit(invoiceLine.unit);
    const priceDelta = Number((invoiceLine.unitPrice - orderLine.unitPrice).toFixed(2));
    const priceMismatch = invoiceLine.unitPrice > 0 && Math.abs(priceDelta) > 0.05;
    const status: InvoiceLineMatch["status"] = quantityMismatch
      ? "quantity_mismatch"
      : priceMismatch
        ? "price_mismatch"
        : "matched";

    matches.push({
      name: invoiceLine.name,
      status,
      detail:
        status === "matched"
          ? "Invoice line matches the attached order."
          : quantityMismatch
            ? "Invoice quantity or unit differs from the attached order."
            : "Invoice unit price differs from the attached order.",
      orderQuantity: orderLine.quantity,
      invoiceQuantity: invoiceLine.quantity,
      orderUnitPrice: orderLine.unitPrice,
      invoiceUnitPrice: invoiceLine.unitPrice,
      deltaBam: priceMismatch ? priceDelta : undefined,
    });
  });

  unmatchedOrderItems.forEach((orderLine) => {
    matches.push({
      name: orderLine.name,
      status: "missing_from_invoice",
      detail: "Ordered item is missing from the invoice lines.",
      orderQuantity: orderLine.quantity,
      orderUnitPrice: orderLine.unitPrice,
      deltaBam: Number((orderLine.quantity * orderLine.unitPrice).toFixed(2)),
    });
  });

  return matches;
}

function invoiceMismatchTypesFromLineMatches(lineMatches: InvoiceLineMatch[]): InvoiceMismatchType[] {
  const mismatchTypes: InvoiceMismatchType[] = [];

  if (lineMatches.some((match) => match.status === "price_mismatch")) {
    mismatchTypes.push("price_mismatch");
  }

  if (lineMatches.some((match) => match.status === "quantity_mismatch" || match.status === "extra_invoice_line")) {
    mismatchTypes.push("line_mismatch");
  }

  if (lineMatches.some((match) => match.status === "missing_from_invoice")) {
    mismatchTypes.push("missing_goods");
  }

  return mismatchTypes;
}

function invoiceReviewFor({
  amountEur,
  due,
  fileUrl,
  invoiceLines = [],
  orderId,
  orders,
  status,
  supplierName,
}: {
  amountEur: number;
  due: string;
  fileUrl?: string;
  invoiceLines?: InvoiceLineItem[];
  orderId?: string;
  orders: Order[];
  status?: InvoiceRecord["status"];
  supplierName?: string;
}): Pick<
  InvoiceRecord,
  | "amountDeltaEur"
  | "captureMethod"
  | "expectedAmountEur"
  | "invoiceLines"
  | "lineMatches"
  | "matchStatus"
  | "mismatchTypes"
  | "orderAttachmentStatus"
  | "paymentReadiness"
> {
  if (status === "paid") {
    return {
      captureMethod: fileUrl ? "ocr_manual_review" : "manual",
      invoiceLines,
      lineMatches: [],
      matchStatus: "paid",
      mismatchTypes: [],
      orderAttachmentStatus: orderId ? "attached" : "missing",
      paymentReadiness: "paid",
    };
  }

  if (status === "exported") {
    return {
      captureMethod: fileUrl ? "ocr_manual_review" : "manual",
      invoiceLines,
      lineMatches: [],
      matchStatus: "matched",
      mismatchTypes: [],
      orderAttachmentStatus: orderId ? "attached" : "missing",
      paymentReadiness: "prepared",
    };
  }

  const mismatchTypes: InvoiceMismatchType[] = [];
  const matchedOrder = orderId ? invoiceOrderMatch(orderId, orders) : undefined;
  const expectedAmountEur = matchedOrder ? Number(orderTotalAsEur(matchedOrder).toFixed(2)) : undefined;
  const amountDeltaEur =
    expectedAmountEur === undefined ? undefined : Number((amountEur - expectedAmountEur).toFixed(2));
  const lineMatches = compareInvoiceLinesToOrder(invoiceLines, matchedOrder);

  if (!orderId || !matchedOrder) {
    mismatchTypes.push("missing_order");
  }

  if (
    matchedOrder &&
    supplierName &&
    normalizeMarketText(supplierName) !== normalizeMarketText(matchedOrder.supplier)
  ) {
    mismatchTypes.push("supplier_mismatch");
  }

  if (amountDeltaEur !== undefined && Math.abs(amountDeltaEur) > INVOICE_AMOUNT_TOLERANCE_EUR) {
    mismatchTypes.push("price_mismatch");
  }

  if (matchedOrder?.status === "issue") {
    mismatchTypes.push("line_mismatch");
  }

  if (matchedOrder?.status === "issue" && matchedOrder.items.some((item) => item.supplierResponse === "rejected")) {
    mismatchTypes.push("missing_goods");
  }

  if (!due.trim() || /needs matching|match today/i.test(due)) {
    mismatchTypes.push("due_date_missing");
  }

  const uniqueMismatchTypes = Array.from(new Set([...mismatchTypes, ...invoiceMismatchTypesFromLineMatches(lineMatches)]));
  const hasBlockingMismatch = uniqueMismatchTypes.some((type) => type !== "due_date_missing");
  const paymentReadiness: InvoicePaymentReadiness =
    status === "blocked" || hasBlockingMismatch
      ? orderId && matchedOrder
        ? "blocked"
        : "needs_order"
      : "ready";

  return {
    amountDeltaEur,
    captureMethod: fileUrl ? "ocr_manual_review" : "manual",
    expectedAmountEur,
    invoiceLines,
    lineMatches,
    matchStatus: status === "blocked" || hasBlockingMismatch ? "disputed" : matchedOrder ? "matched" : "needs_match",
    mismatchTypes: uniqueMismatchTypes,
    orderAttachmentStatus: matchedOrder ? "attached" : "missing",
    paymentReadiness,
  };
}

function invoiceStatusFromReadiness(readiness?: InvoicePaymentReadiness): InvoiceRecord["status"] {
  if (readiness === "ready") {
    return "ready";
  }

  if (readiness === "prepared") {
    return "exported";
  }

  if (readiness === "paid") {
    return "paid";
  }

  return "blocked";
}

function localizationRuleFor(ruleId: string) {
  return (
    runtimeLocalizationRules.find((item) => item.id === ruleId) ??
    localizationRules.find((item) => item.id === ruleId)
  );
}

function liveLocalizationValue(
  rule: LocalizationRule | undefined,
  key: "taxMode" | "language" | "market",
  fallback: string,
) {
  return rule?.status === "live" ? rule[key] : fallback;
}

function branchCommerceProfileFor(branch: BranchAccount): BranchCommerceProfile {
  const normalizedMarket = normalizeMarketText(branch.market);

  if (/(hr|croatia|zagreb|split|dubrovnik|eu)/.test(normalizedMarket)) {
    const rule = localizationRuleFor("loc-eu");

    return {
      currencyCode: "EUR",
      currencyLabel: "EUR",
      exchangeRateFromBam: 1 / BAM_PER_EUR,
      locale: "hr-HR",
      taxMode: liveLocalizationValue(rule, "taxMode", "EU localization pending"),
      taxRatePct: 25,
      language: liveLocalizationValue(rule, "language", "en"),
      marketRule: liveLocalizationValue(rule, "market", "EU markets review"),
    };
  }

  if (/(ae|uae|gcc|dubai|riyadh|doha)/.test(normalizedMarket)) {
    const rule = localizationRuleFor("loc-gcc");

    return {
      currencyCode: "AED",
      currencyLabel: "AED",
      exchangeRateFromBam: 2.04,
      locale: "en-AE",
      taxMode: liveLocalizationValue(rule, "taxMode", "GCC localization review pending"),
      taxRatePct: 5,
      language: liveLocalizationValue(rule, "language", "en"),
      marketRule: liveLocalizationValue(rule, "market", "GCC review"),
    };
  }

  const rule = localizationRuleFor("loc-ba");

  return {
    currencyCode: "BAM",
    currencyLabel: "KM",
    exchangeRateFromBam: 1,
    locale: "bs-BA",
    taxMode: liveLocalizationValue(rule, "taxMode", "BA localization pending"),
    taxRatePct: 17,
    language: liveLocalizationValue(rule, "language", "bs / en"),
    marketRule: liveLocalizationValue(rule, "market", "Bosnia and Herzegovina"),
  };
}

function formatBranchMoney(
  amountBam: number,
  branch: BranchAccount,
  options: { includeBase?: boolean; compact?: boolean } = {},
) {
  const profile = branchCommerceProfileFor(branch);
  const localizedAmount = amountBam * profile.exchangeRateFromBam;
  if (profile.currencyCode === "BAM") {
    return `${formatMoney(localizedAmount)} ${profile.currencyLabel}`;
  }

  const formatted = new Intl.NumberFormat(profile.locale, {
    style: "currency",
    currency: profile.currencyCode,
    notation: options.compact ? "compact" : "standard",
    maximumFractionDigits: options.compact ? 1 : 2,
  }).format(localizedAmount);

  if (!options.includeBase) {
    return formatted;
  }

  return `${formatted} / ${formatCurrencyAmount(amountBam, "BAM")} base`;
}

function formatBranchUnitPrice(amountBam: number, unit: string, branch: BranchAccount) {
  return `${formatBranchMoney(amountBam, branch)} / ${unit}`;
}

function marketplacePriceDisplay(product: MarketplaceProduct, branch: BranchAccount) {
  const parsedPrice = parseMarketplacePrice(product.priceRange);

  if (!parsedPrice || /quote|terms pending/i.test(product.priceRange)) {
    return product.priceRange;
  }

  const formattedPrice = formatBranchMoney(parsedPrice, branch);
  return marketplaceProductCanOrder(product) ? formattedPrice : `${formattedPrice} list / confirm`;
}

function branchTaxSummary(branch: BranchAccount) {
  const profile = branchCommerceProfileFor(branch);
  return `${profile.currencyLabel} / ${profile.taxMode} / ${profile.language}`;
}

function branchInquiryContact(branch: BranchAccount) {
  return `${branch.name}, ${branchCity(branch)}, contact through Veleo`;
}

function defaultExternalInquiryDraft(record: ExternalSupplierRecord | undefined, branch: BranchAccount): ExternalInquiryDraft {
  return {
    buyerName: branch.name,
    productName: record?.products[0] ?? "requested products",
    quantity: "Confirm quantity",
    neededBy: "Next available delivery slot",
    contactDetails: branchInquiryContact(branch),
  };
}

function externalInquirySignalFor(item: ExternalInquiryRecord): InquiryOperationSignal {
  if (item.status === "replied") {
    return {
      id: item.id,
      title: `Review reply from ${item.supplierName}`,
      detail: "A price check is open. Confirm price, minimum order, delivery terms, and whether this supplier should become usual or backup.",
      metric: item.productName,
      status: "done",
      button: "Reply captured",
    };
  }

  return {
    id: item.id,
    title: `Waiting for ${item.supplierName}`,
    detail: `${item.buyerName} needs ${item.quantity} of ${item.productName}. Capture price, delivery, and minimum order when the supplier replies.`,
    metric: item.neededBy,
    status: "waiting",
    button: "Capture reply",
  };
}

function missingExternalInquiryFields(draft: ExternalInquiryDraft) {
  const requiredFields: Array<[keyof ExternalInquiryDraft, string]> = [
    ["buyerName", "business"],
    ["productName", "product"],
    ["quantity", "quantity"],
    ["neededBy", "needed by"],
    ["contactDetails", "contact"],
  ];

  return requiredFields
    .filter(([field]) => !draft[field].trim() || draft[field].toLowerCase().startsWith("confirm "))
    .map(([, label]) => label);
}

function deliveryAreaMatchesMarket(area: DeliveryArea, market = "") {
  const normalizedArea = normalizeMarketText(area.market);
  const normalizedMarket = normalizeMarketText(market);
  const city = normalizedMarket.split(",")[0]?.trim() ?? normalizedMarket;

  return Boolean(
    city &&
      (normalizedArea.includes(city) ||
        normalizedMarket.includes(normalizedArea) ||
        (city === "sarajevo" && normalizedArea.includes("sarajevo")) ||
        (city === "zagreb" && normalizedArea.includes("zagreb")) ||
        (city === "dubai" && normalizedArea.includes("dubai"))),
  );
}

function deliveryAreaForMarket(areas: DeliveryArea[], market: string) {
  return areas.find((area) => deliveryAreaMatchesMarket(area, market));
}

function createDeliveryAreaFromMarket(market: string): DeliveryArea {
  const slug = slugForNav(market || "new-market");

  return {
    id: `area-${slug}`,
    market: market || "New delivery market",
    days: "Supplier route pending",
    minimumOrder: "Confirm minimum",
    performance: 92,
    status: "capacity_watch",
    activeOrders: 0,
    lastUpdated: "Created just now",
  };
}

function applyDeliveryAreaPressure(areas: DeliveryArea[], market: string, orderCount: number) {
  const existingArea = deliveryAreaForMarket(areas, market);
  const nextAreas = existingArea ? areas : [createDeliveryAreaFromMarket(market), ...areas];
  const pressureArea = existingArea ?? nextAreas[0];

  return nextAreas.map((area) => {
    if (area.id !== pressureArea.id) {
      return area;
    }

    const activeOrders = (area.activeOrders ?? 0) + orderCount;
    const performance = Math.max(82, Number((area.performance - orderCount * 1.1).toFixed(1)));
    const status: DeliveryArea["status"] =
      activeOrders >= 3 || performance < 94 ? "capacity_watch" : area.status;

    return {
      ...area,
      activeOrders,
      performance,
      status,
      lastUpdated: `${orderCount} order${orderCount === 1 ? "" : "s"} added just now`,
    };
  });
}

function releaseDeliveryAreaPressure(areas: DeliveryArea[], market = "") {
  const area = deliveryAreaForMarket(areas, market);

  if (!area) {
    return areas;
  }

  return areas.map((item) => {
    if (item.id !== area.id) {
      return item;
    }

    const activeOrders = Math.max(0, (item.activeOrders ?? 0) - 1);
    const performance = Math.min(99.3, Number((item.performance + 0.4).toFixed(1)));
    const status: DeliveryArea["status"] =
      activeOrders >= 3 || performance < 94 ? "capacity_watch" : "active";

    return {
      ...item,
      activeOrders,
      performance,
      status,
      lastUpdated: "Delivery completion synced just now",
    };
  });
}

function messageFollowUpTemplate(thread: MessageThread, role: Role) {
  if (role === "supplier") {
    return `Hello ${thread.participant}, thank you for the request. Please confirm final quantity, delivery window, invoice details, and any substitution limits so we can keep the order timeline clean in Veleo.`;
  }

  if (role === "admin") {
    return `Hello ${thread.participant}, Veleo operations is checking this thread. Please confirm owner, next step, market impact, and whether this should become a supplier, sourcing, or fulfillment action.`;
  }

  if (thread.role === "external") {
    return `Hello ${thread.participant}, we are following up through Veleo. Can you confirm availability, minimum order, pricing, delivery window, and the best contact for future restaurant requests?`;
  }

  return `Hello ${thread.participant}, can you confirm price, minimum order, delivery window, and whether there are any substitutions before we move this order forward in Veleo?`;
}

function messageOperationFor(thread: MessageThread): MessageOperationSignal {
  const needsReply = thread.status === "needs_reply";
  const isExternal = thread.role === "external";
  const subjectLabel = needsReply ? "Reply required" : "Follow-up pending";

  return {
    thread,
    title: `${subjectLabel}: ${thread.subject}`,
    detail: `${thread.participant} / ${thread.market}. ${thread.preview}`,
    metric: needsReply ? "Needs reply" : thread.updatedAt,
    severity: needsReply ? "high" : isExternal ? "watch" : "ready",
    primaryAction: needsReply ? (isExternal ? "Send external follow-up" : "Send reply") : "Send follow-up",
    closeAction: needsReply ? "Resolve request" : "Close thread",
  };
}

function branchStatusFor(branch: Pick<BranchAccount, "weeklyBudget" | "spentThisWeek">): BranchAccount["status"] {
  return branchSpendRatio(branch) > 0.85 ? "watch" : "healthy";
}

function branchSpendRatio(branch: Pick<BranchAccount, "weeklyBudget" | "spentThisWeek">) {
  if (branch.weeklyBudget <= 0) {
    return branch.spentThisWeek > 0 ? 1 : 0;
  }

  return branch.spentThisWeek / branch.weeklyBudget;
}

function branchSpendPercent(branch: Pick<BranchAccount, "weeklyBudget" | "spentThisWeek">) {
  return Math.min(100, Math.max(0, Math.round(branchSpendRatio(branch) * 100)));
}

function demandSkuUnitFor(demand: SupplierSearchDemand) {
  const normalized = normalizeMarketText(`${demand.category} ${demand.product}`);

  if (/napkin|packaging|non food|cups|boxes/.test(normalized)) {
    return "pack";
  }

  if (/oil|juice|milk|liquid/.test(normalized)) {
    return "L";
  }

  if (/crate|greens|herbs/.test(normalized)) {
    return "crate";
  }

  return "kg";
}

function demandSkuPriceFor(demand: SupplierSearchDemand) {
  const normalized = normalizeMarketText(`${demand.category} ${demand.product}`);

  if (/butter/.test(normalized)) {
    return 10.9;
  }

  if (/napkin|packaging/.test(normalized)) {
    return 24;
  }

  if (/berries|frozen/.test(normalized)) {
    return 8.6;
  }

  if (/flour/.test(normalized)) {
    return 2.15;
  }

  if (/lemon|citrus/.test(normalized)) {
    return 2.65;
  }

  const estimatedBudgetBam = demand.avgBudgetEur * BAM_PER_EUR;
  return Number(Math.max(1, Math.min(96, estimatedBudgetBam / Math.max(24, demand.buyerCount * 4))).toFixed(2));
}

function sprintPotentialForCoverage(record: MarketCoverageRecord) {
  const missingSuppliers = Math.max(1, record.supplierTarget - record.suppliersLive);
  const coverageGap = Math.max(0, 100 - record.fillRate);

  return Math.round(missingSuppliers * 28000 + coverageGap * 1400);
}

function coverageRecordMatches(record: MarketCoverageRecord, market: string, category: string) {
  const recordMarket = normalizeMarketText(record.market);
  const targetMarket = normalizeMarketText(market);
  const recordCategory = normalizeMarketText(record.category);
  const targetCategory = normalizeMarketText(category);

  return (
    recordCategory === targetCategory &&
    (recordMarket.includes(targetMarket) || targetMarket.includes(recordMarket))
  );
}

function externalRecordMatchesDemand(record: ExternalSupplierRecord, gap: DemandGap) {
  const recordMarket = normalizeMarketText(record.market);
  const gapMarket = normalizeMarketText(gap.market);
  const gapCategory = normalizeMarketText(gap.category);
  const productText = normalizeMarketText(record.products.join(" "));

  return (
    (productText.includes(gapCategory) ||
      gapCategory.includes(productText.split(" ")[0] ?? gapCategory) ||
      record.products.some((product) => gapCategory.includes(normalizeMarketText(product)))) &&
    (recordMarket.includes(gapMarket) ||
      gapMarket.includes(recordMarket) ||
      recordMarket.includes("bosnia") ||
      gapMarket.includes("bosnia") ||
      gapMarket.includes("balkans"))
  );
}

function countryMarketMatches(country: OperatingCountry, market: string) {
  const normalizedMarket = normalizeMarketText(market);
  const normalizedCountry = normalizeMarketText(country.country);
  const normalizedRegion = normalizeMarketText(country.region);
  const countryLead = normalizedCountry.split(" ")[0] ?? normalizedCountry;
  const code = normalizeMarketText(country.code);

  return (
    normalizedMarket.includes(code) ||
    normalizedMarket.includes(countryLead) ||
    normalizedMarket.includes(normalizedRegion) ||
    (country.code === "BA" && (normalizedMarket.includes("bosnia") || normalizedMarket.includes("sarajevo"))) ||
    (country.code === "HR" && (normalizedMarket.includes("croatia") || normalizedMarket.includes("coastal"))) ||
    (country.code === "RS" && (normalizedMarket.includes("serbia") || normalizedMarket.includes("belgrade"))) ||
    (country.code === "ME" && (normalizedMarket.includes("montenegro") || normalizedMarket.includes("coastal")))
  );
}

function coverageRiskFor(suppliersLive: number, supplierTarget: number, fillRate: number): MarketCoverageRecord["risk"] {
  const supplierGap = supplierTarget - suppliersLive;

  if (supplierGap <= 0 || fillRate >= 94) {
    return "healthy";
  }

  if (supplierGap <= 2 && fillRate >= 88) {
    return "watch";
  }

  return "critical";
}

const bosniaCoverageBaselineTargets: Array<
  Pick<
    BosniaCoverageTarget,
    "city" | "category" | "supplierTarget" | "nextSupplierAction" | "acquisitionOwner" | "primaryChannel"
  >
> = [
  {
    city: "Sarajevo",
    category: "Produce",
    supplierTarget: 8,
    nextSupplierAction: "Call produce wholesalers restaurants already use, then verify delivery days and WhatsApp/Viber contacts.",
    acquisitionOwner: "VELEO supply desk",
    primaryChannel: "WhatsApp/Viber call",
  },
  {
    city: "Mostar",
    category: "Meat",
    supplierTarget: 5,
    nextSupplierAction: "Collect butcher and poultry contacts from Mostar restaurants, then ask suppliers to confirm one real order.",
    acquisitionOwner: "Restaurant onboarding",
    primaryChannel: "Phone + Viber",
  },
  {
    city: "Tuzla",
    category: "Packaging",
    supplierTarget: 4,
    nextSupplierAction: "Verify packaging distributors and delivery minimums before showing any packaging item as buyable.",
    acquisitionOwner: "Supplier research",
    primaryChannel: "WhatsApp/email",
  },
  {
    city: "Banja Luka",
    category: "Beverages",
    supplierTarget: 5,
    nextSupplierAction: "Turn beverage distributor leads into confirmation-link pilots for cafes and bars.",
    acquisitionOwner: "VELEO supply desk",
    primaryChannel: "Viber/SMS",
  },
];

const bosniaCategorySignals: Record<string, string[]> = {
  beverages: ["beverage", "beverages", "coffee", "pice", "piće", "piva", "beer", "cola", "juice", "sok"],
  meat: ["meat", "butcher", "poultry", "beef", "chicken", "meso", "piletina", "junetina", "butcher"],
  packaging: ["packaging", "ambalaza", "boxes", "box", "takeaway", "cups", "case", "kraft", "pakovanje"],
  produce: ["produce", "fresh produce", "voce", "voće", "povrce", "povrće", "paradajz", "citrus", "lemon", "limun"],
};

function bosniaCategoryMatches(value: string, category: string) {
  const normalizedValue = normalizeMarketText(value);
  const normalizedCategory = normalizeMarketText(category);
  const categoryLead = normalizedCategory.split(" ")[0] ?? normalizedCategory;
  const hints = bosniaCategorySignals[categoryLead] ?? [categoryLead];

  return (
    normalizedValue.includes(normalizedCategory) ||
    normalizedValue.includes(categoryLead) ||
    hints.some((hint) => normalizedValue.includes(normalizeMarketText(hint)))
  );
}

function bosniaCityMatches(value: string, city: string) {
  const normalizedValue = normalizeMarketText(value);
  const normalizedCity = normalizeMarketText(city);
  const hasCountryToken = /(^|[\s,/-])(ba|bih)($|[\s,/-])/.test(normalizedValue);

  return (
    normalizedValue.includes(normalizedCity) ||
    normalizedValue.includes("bosnia") ||
    hasCountryToken
  );
}

function bosniaTargetRecordMatches(record: MarketCoverageRecord | DemandGap | SourcingSprint, target: Pick<BosniaCoverageTarget, "city" | "category">) {
  return bosniaCityMatches(record.market, target.city) && bosniaCategoryMatches(record.category, target.category);
}

function deriveBosniaCoverageTargets({
  coverageRecords,
  demandGaps,
  externalRecords,
  restaurantSupplierContacts,
  sourcingSprints,
}: {
  coverageRecords: MarketCoverageRecord[];
  demandGaps: DemandGap[];
  externalRecords: ExternalSupplierRecord[];
  restaurantSupplierContacts: RestaurantSupplierContactRecord[];
  sourcingSprints: SourcingSprint[];
}): BosniaCoverageTarget[] {
  return bosniaCoverageBaselineTargets
    .map((target) => {
      const matchingCoverage =
        coverageRecords.find((record) => bosniaTargetRecordMatches(record, target)) ??
        coverageRecords.find((record) => bosniaCategoryMatches(record.category, target.category) && bosniaCityMatches(record.market, "Bosnia"));
      const matchingDemand = demandGaps.find((gap) => bosniaTargetRecordMatches(gap, target));
      const matchingSprint = sourcingSprints.find((sprint) => bosniaTargetRecordMatches(sprint, target));
      const restaurantMatches = restaurantSupplierContacts.filter(
        (contact) =>
          contact.relationshipStatus !== "removed" &&
          bosniaCityMatches(`${contact.deliveryArea} ${contact.branchName}`, target.city) &&
          bosniaCategoryMatches(contact.category, target.category),
      );
      const externalMatches = externalRecords.filter(
        (record) =>
          record.status !== "converted" &&
          bosniaCityMatches(`${record.market} ${record.deliveryHint}`, target.city) &&
          bosniaCategoryMatches(`${record.products.join(" ")} ${record.source} ${record.name}`, target.category),
      );
      const suppliersLive = Math.max(
        matchingCoverage?.suppliersLive ?? 0,
        restaurantMatches.length,
        externalRecords.filter(
          (record) =>
            record.status === "converted" &&
            bosniaCityMatches(`${record.market} ${record.deliveryHint}`, target.city) &&
            bosniaCategoryMatches(`${record.products.join(" ")} ${record.name}`, target.category),
        ).length,
      );
      const supplierTarget = Math.max(matchingCoverage?.supplierTarget ?? 0, target.supplierTarget);
      const demandSignals =
        (matchingDemand?.failedSearches ?? 0) + (matchingDemand?.unavailableOrders ?? 0) + externalMatches.length;
      const estimatedFillRate = Math.min(
        100,
        ((suppliersLive + restaurantMatches.length * 0.35 + externalMatches.length * 0.2) / Math.max(1, supplierTarget)) *
          100,
      );
      const fillRate = Number((matchingCoverage?.fillRate ?? estimatedFillRate).toFixed(1));
      const risk = matchingCoverage?.risk ?? coverageRiskFor(suppliersLive, supplierTarget, fillRate);
      const sourceSignals = [
        `${restaurantMatches.length} restaurant-provided contacts`,
        `${externalMatches.length} external candidates`,
        matchingDemand
          ? `${matchingDemand.failedSearches} failed searches / ${matchingDemand.unavailableOrders} unavailable orders`
          : "No saved demand gap yet",
        matchingSprint ? `${matchingSprint.status} sourcing sprint` : "No sourcing sprint yet",
      ];

      return {
        ...target,
        id: `bosnia-${slugForNav(target.city)}-${slugForNav(target.category)}`,
        suppliersLive,
        supplierTarget,
        fillRate,
        risk,
        restaurantContacts: restaurantMatches.length,
        externalCandidates: externalMatches.length,
        demandSignals,
        sourceSignals,
        nextSupplierAction: matchingCoverage?.nextMove ?? matchingDemand?.recommendedAction ?? target.nextSupplierAction,
        acquisitionOwner: matchingSprint?.owner ?? target.acquisitionOwner,
        primaryChannel: target.primaryChannel,
        sprintStatus: matchingSprint?.status,
      };
    })
    .sort((left, right) => {
      const rank: Record<BosniaCoverageTarget["risk"], number> = { critical: 0, watch: 1, healthy: 2 };
      return (
        rank[left.risk] - rank[right.risk] ||
        right.supplierTarget - right.suppliersLive - (left.supplierTarget - left.suppliersLive) ||
        right.demandSignals - left.demandSignals
      );
    });
}

function countryCoverageRecordFor(country: OperatingCountry): MarketCoverageRecord {
  const supplierGap = country.priority === "healthy" ? 0 : Math.max(2, Math.ceil((100 - country.fillRate) / 2.8));
  const suppliersLive = country.suppliers;
  const supplierTarget = suppliersLive + supplierGap;
  const risk: MarketCoverageRecord["risk"] =
    country.priority === "priority" ? "critical" : country.priority === "watch" ? "watch" : "healthy";

  return {
    id: `cov-${slugForNav(country.country)}-${slugForNav(country.topGap)}`,
    market: country.country,
    category: country.topGap,
    suppliersLive,
    supplierTarget,
    fillRate: country.fillRate,
    risk,
    nextMove: country.nextMove,
  };
}

type LiveOrderItemObservation = {
  order: Order;
  item: OrderItem;
  catalogProduct?: Product;
  supplier: string;
  category: string;
  quantity: number;
  unitPrice: number;
  unit: string;
  totalBam: number;
};

function positiveFinite(value: number, fallback = 0) {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function average(values: number[]) {
  const usableValues = values.filter((value) => Number.isFinite(value) && value > 0);

  if (!usableValues.length) {
    return 0;
  }

  return usableValues.reduce((sum, value) => sum + value, 0) / usableValues.length;
}

function percentile(values: number[], ratio: number) {
  const usableValues = values
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((left, right) => left - right);

  if (!usableValues.length) {
    return 0;
  }

  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const index = (usableValues.length - 1) * clampedRatio;
  const lowerIndex = Math.floor(index);
  const upperIndex = Math.ceil(index);
  const weight = index - lowerIndex;
  const lower = usableValues[lowerIndex] ?? usableValues[0];
  const upper = usableValues[upperIndex] ?? usableValues[usableValues.length - 1];

  return lower + (upper - lower) * weight;
}

function mostCommonText(values: string[], fallback: string) {
  const counts = values.reduce<Record<string, number>>((totals, value) => {
    const cleanValue = value.trim();

    if (cleanValue) {
      totals[cleanValue] = (totals[cleanValue] ?? 0) + 1;
    }

    return totals;
  }, {});

  return (
    Object.entries(counts).sort((left, right) => right[1] - left[1])[0]?.[0] ??
    fallback
  );
}

function normalizedOrderProductId(productId: string) {
  return productId
    .replace(/^market-catalog-/, "")
    .replace(/^catalog-/, "")
    .replace(/^market-/, "");
}

function categoryTextsMatch(left: string, right: string) {
  const normalizedLeft = normalizeMarketText(left);
  const normalizedRight = normalizeMarketText(right);
  const leftLead = normalizedLeft.split(" ")[0] ?? normalizedLeft;
  const rightLead = normalizedRight.split(" ")[0] ?? normalizedRight;

  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.includes(normalizedRight) ||
    normalizedRight.includes(normalizedLeft) ||
    leftLead === rightLead
  );
}

function catalogProductForOrderItem(item: OrderItem, catalogProducts: Product[]) {
  const normalizedProductId = normalizedOrderProductId(item.productId);
  const directMatch = catalogProducts.find(
    (product) => product.id === item.productId || product.id === normalizedProductId,
  );

  if (directMatch) {
    return directMatch;
  }

  const itemName = normalizeMarketText(item.name);
  const itemSupplier = item.supplier ? normalizeMarketText(item.supplier) : "";
  const itemCategory = item.category ? normalizeMarketText(item.category) : "";

  return catalogProducts
    .map((product) => {
      const productName = normalizeMarketText(product.name);
      const productSupplier = normalizeMarketText(product.supplier);
      const productCategory = normalizeMarketText(product.category);
      let score = 0;

      if (itemSupplier && itemSupplier === productSupplier) {
        score += 36;
      }

      if (itemName === productName) {
        score += 42;
      } else if (itemName.includes(productName) || productName.includes(itemName)) {
        score += 24;
      }

      if (itemCategory && categoryTextsMatch(itemCategory, productCategory)) {
        score += 18;
      }

      return { product, score };
    })
    .filter((match) => match.score >= 36)
    .sort((left, right) => right.score - left.score)[0]?.product;
}

function liveOrderItemObservations(orders: Order[], catalogProducts: Product[]): LiveOrderItemObservation[] {
  return orders.flatMap((order) =>
    order.items.map((item) => {
      const catalogProduct = catalogProductForOrderItem(item, catalogProducts);
      const supplier = item.supplier ?? catalogProduct?.supplier ?? order.supplier;
      const category = item.category ?? catalogProduct?.category ?? "Uncategorized";
      const quantity = positiveFinite(item.quantity, 1);
      const unitPrice = positiveFinite(item.unitPrice, catalogProduct?.price ?? 0);
      const unit = item.unit || catalogProduct?.unit || "unit";
      const rawLineTotal = item.quantity * item.unitPrice;
      const fallbackLineTotal = order.items.length ? order.total / order.items.length : order.total;
      const totalBam = positiveFinite(rawLineTotal, positiveFinite(fallbackLineTotal, 0));

      return {
        order,
        item,
        catalogProduct,
        supplier,
        category,
        quantity,
        unitPrice,
        unit,
        totalBam,
      };
    }),
  );
}

function observationMatchesProduct(observation: LiveOrderItemObservation, product: Product) {
  const productName = normalizeMarketText(product.name);
  const itemName = normalizeMarketText(observation.item.name);
  const sameProduct =
    observation.catalogProduct?.id === product.id ||
    observation.item.productId === product.id ||
    normalizedOrderProductId(observation.item.productId) === product.id;
  const sameSupplier = normalizeMarketText(observation.supplier) === normalizeMarketText(product.supplier);
  const sameCategory = categoryTextsMatch(observation.category, product.category);
  const sameName = itemName === productName || itemName.includes(productName) || productName.includes(itemName);

  return sameProduct || (sameSupplier && sameCategory && sameName);
}

function deriveReplenishmentSignals(
  catalogProducts: Product[],
  orders: Order[],
  branches: BranchAccount[],
): ReplenishmentSignal[] {
  const observations = liveOrderItemObservations(orders, catalogProducts);
  const riskRank: Record<ReplenishmentSignal["risk"], number> = {
    stockout: 0,
    price_drift: 1,
    seasonal: 2,
  };

  return catalogProducts
    .flatMap((product) => {
      const matchedObservations = observations.filter((observation) => observationMatchesProduct(observation, product));
      const paidUnitPrice = average(matchedObservations.map((observation) => observation.unitPrice));
      const highestQuantity = Math.max(0, ...matchedObservations.map((observation) => observation.quantity));
      const averageQuantity = average(matchedObservations.map((observation) => observation.quantity));
      const forecastQuantity = Math.ceil(Math.max(positiveFinite(product.lastOrderedKg, 0), highestQuantity, averageQuantity, 1));
      const priceDriftPct =
        product.price > 0 && paidUnitPrice > 0 ? ((paidUnitPrice - product.price) / product.price) * 100 : 0;
      const risk: ReplenishmentSignal["risk"] | null =
        product.stockStatus === "unavailable" || product.stockStatus === "low"
          ? "stockout"
          : Math.abs(priceDriftPct) >= 8
            ? "price_drift"
            : matchedObservations.length >= 2 && averageQuantity > positiveFinite(product.lastOrderedKg, 1) * 1.15
              ? "seasonal"
              : null;

      if (!risk) {
        return [];
      }

      const demandPerDay = Math.max(averageQuantity || product.lastOrderedKg || 1, 1) / 7;
      const currentCoverageDays =
        product.stockStatus === "unavailable"
          ? 0.3
          : product.stockStatus === "low"
            ? Math.max(0.8, Math.min(3.5, (product.lastOrderedKg || forecastQuantity * 0.3) / demandPerDay))
            : Math.max(4, Math.min(14, (product.lastOrderedKg || forecastQuantity) / demandPerDay));
      const branch = matchedObservations[0]?.order.buyer ?? branches[0]?.name ?? "Unassigned restaurant";
      const unitPrice = paidUnitPrice || product.price;
      const dueBy =
        risk === "stockout"
          ? product.stockStatus === "unavailable"
            ? "Before next service"
            : "Next order cycle"
          : risk === "price_drift"
            ? "Before approval"
            : "This week";
      const action =
        risk === "stockout"
          ? "Repeat confirmed supply or request a backup supplier before the next order."
          : risk === "price_drift"
            ? "Request a backup quote before approving the repeat quantity."
            : "Confirm supplier allocation for the upcoming order cycle.";

      return [
        {
          id: `rep-live-${slugForNav(product.id)}-${risk}`,
          productId: product.id,
          productName: product.name,
          branch,
          supplier: product.supplier,
          currentCoverageDays,
          forecastQuantity,
          unit: product.unit,
          unitPrice,
          risk,
          dueBy,
          action,
        },
      ];
    })
    .sort(
      (left, right) =>
        riskRank[left.risk] - riskRank[right.risk] ||
        right.forecastQuantity * right.unitPrice - left.forecastQuantity * left.unitPrice,
    )
    .slice(0, 12);
}

function benchmarkConfidenceRank(confidence: PriceBenchmark["confidence"]) {
  return confidence === "strong" ? 0 : confidence === "building" ? 1 : 2;
}

function networkPriceBenchmarkFor(
  category: string,
  unit: string,
  networkPriceBenchmarks: PriceBenchmarkRecord[],
) {
  return networkPriceBenchmarks
    .filter(
      (benchmark) =>
        categoryTextsMatch(benchmark.category, category) &&
        normalizeQuickUnit(benchmark.unit) === normalizeQuickUnit(unit),
    )
    .sort(
      (left, right) =>
        benchmarkConfidenceRank(left.confidence) - benchmarkConfidenceRank(right.confidence) ||
        right.sampleCount - left.sampleCount ||
        right.lastUpdated.localeCompare(left.lastUpdated),
    )[0];
}

function derivePurchaseInsights(
  orders: Order[],
  catalogProducts: Product[],
  networkPriceBenchmarks: PriceBenchmarkRecord[] = [],
): PurchaseInsight[] {
  const observations = liveOrderItemObservations(orders, catalogProducts);
  const grouped = new Map<
    string,
    {
      supplier: string;
      category: string;
      spendBam: number;
      lineCount: number;
      paidUnitPrices: number[];
      catalogPrices: number[];
      units: string[];
    }
  >();

  observations.forEach((observation) => {
    const key = `${normalizeMarketText(observation.supplier)}::${normalizeMarketText(observation.category)}`;
    const current =
      grouped.get(key) ??
      {
        supplier: observation.supplier,
        category: observation.category,
        spendBam: 0,
        lineCount: 0,
        paidUnitPrices: [],
        catalogPrices: [],
        units: [],
      };

    current.spendBam += observation.totalBam;
    current.lineCount += 1;
    current.paidUnitPrices.push(observation.unitPrice);
    current.units.push(observation.unit);
    if (observation.catalogProduct?.price) {
      current.catalogPrices.push(observation.catalogProduct.price);
    }

    grouped.set(key, current);
  });

  return Array.from(grouped.values())
    .map((group) => {
      const catalogMatches = catalogProducts.filter(
        (product) =>
          normalizeMarketText(product.supplier) === normalizeMarketText(group.supplier) &&
          categoryTextsMatch(product.category, group.category),
      );
      const catalogBaseline = average([
        ...group.catalogPrices,
        ...catalogMatches.map((product) => product.price),
      ]);
      const paidAverage = average(group.paidUnitPrices);
      const benchmarkUnit = mostCommonText(group.units, catalogMatches[0]?.unit ?? "unit");
      const networkBenchmark = networkPriceBenchmarkFor(group.category, benchmarkUnit, networkPriceBenchmarks);
      const benchmarkObservations = observations.filter(
        (observation) =>
          categoryTextsMatch(observation.category, group.category) &&
          normalizeQuickUnit(observation.unit) === normalizeQuickUnit(benchmarkUnit),
      );
      const benchmarkPrices = benchmarkObservations.map((observation) => observation.unitPrice);
      const localBenchmarkMedian = percentile(benchmarkPrices, 0.5);
      const benchmarkMedian = networkBenchmark?.medianBam ?? localBenchmarkMedian;
      const benchmarkDeltaPct =
        benchmarkMedian > 0 && paidAverage > 0 ? ((paidAverage - benchmarkMedian) / benchmarkMedian) * 100 : 0;
      const variancePct =
        benchmarkMedian > 0
          ? benchmarkDeltaPct
          : catalogBaseline > 0 && paidAverage > 0
            ? ((paidAverage - catalogBaseline) / catalogBaseline) * 100
            : 0;
      const benchmarkConfidence: PriceBenchmark["confidence"] =
        benchmarkObservations.length >= 8 && new Set(benchmarkObservations.map((observation) => observation.supplier)).size >= 3
          ? "strong"
          : benchmarkObservations.length >= 3
            ? "building"
            : "low";
      const status: PurchaseInsight["status"] =
        variancePct > 12 ? "over_budget" : variancePct > 5 ? "price_watch" : "inside_band";
      const priceBenchmark: PriceBenchmark | undefined = networkBenchmark
        ? {
            lowBam: networkBenchmark.lowBam,
            medianBam: networkBenchmark.medianBam,
            highBam: networkBenchmark.highBam,
            unit: networkBenchmark.unit,
            sampleCount: networkBenchmark.sampleCount,
            restaurantCount: networkBenchmark.restaurantCount,
            supplierCount: networkBenchmark.supplierCount,
            confidence: networkBenchmark.confidence,
            source: "restaurant_reported",
            negotiationCue: networkBenchmark.negotiationCue,
          }
        : benchmarkPrices.length
          ? {
              lowBam: Number(percentile(benchmarkPrices, 0.15).toFixed(2)),
              medianBam: Number(localBenchmarkMedian.toFixed(2)),
              highBam: Number(percentile(benchmarkPrices, 0.85).toFixed(2)),
              unit: benchmarkUnit,
              sampleCount: benchmarkObservations.length,
              restaurantCount: new Set(benchmarkObservations.map((observation) => observation.order.buyer)).size,
              supplierCount: new Set(benchmarkObservations.map((observation) => observation.supplier)).size,
              confidence: benchmarkConfidence,
              source: "restaurant_reported",
              negotiationCue:
                benchmarkMedian > 0 && paidAverage > benchmarkMedian * 1.05
                  ? "Ask the supplier to explain the premium or quote closer to the restaurant-reported median."
                  : benchmarkMedian > 0 && paidAverage < benchmarkMedian * 0.95
                    ? "This supplier is already below the restaurant-reported median; protect the terms before switching."
                    : "Use the range as a negotiation anchor before the next repeat order.",
            }
          : undefined;
      const benchmarkDetail = priceBenchmark
        ? `${networkBenchmark ? "Network " : ""}Restaurant-reported range ${formatMoney(priceBenchmark.lowBam)}-${formatMoney(priceBenchmark.highBam)} KM/${priceBenchmark.unit}; median ${formatMoney(priceBenchmark.medianBam)} KM from ${priceBenchmark.sampleCount} self-reported line${priceBenchmark.sampleCount === 1 ? "" : "s"} across ${priceBenchmark.restaurantCount} restaurant${priceBenchmark.restaurantCount === 1 ? "" : "s"}.`
        : "No restaurant-reported range yet.";
      const detail = catalogBaseline
        ? `${formatMoney(group.spendBam)} KM saved order spend across ${group.lineCount} line${group.lineCount === 1 ? "" : "s"}; paid avg ${formatMoney(paidAverage)} KM vs catalog avg ${formatMoney(catalogBaseline)} KM. ${benchmarkDetail}`
        : `${formatMoney(group.spendBam)} KM saved order spend across ${group.lineCount} line${group.lineCount === 1 ? "" : "s"}. ${benchmarkDetail}`;

      return {
        id: `pi-live-${slugForNav(group.supplier)}-${slugForNav(group.category)}`,
        supplier: group.supplier,
        category: group.category,
        monthSpendEur: bamToEur(group.spendBam),
        variancePct,
        status,
        detail,
        paidAverageBam: Number(paidAverage.toFixed(2)),
        priceBenchmark,
      };
    })
    .filter((insight) => insight.monthSpendEur > 0)
    .sort((left, right) => {
      const statusRank: Record<PurchaseInsight["status"], number> = {
        over_budget: 0,
        price_watch: 1,
        inside_band: 2,
      };

      return statusRank[left.status] - statusRank[right.status] || right.monthSpendEur - left.monthSpendEur;
    })
    .slice(0, 10);
}

function deriveSupplierOffers({
  catalogProducts,
  quoteRequests,
  savedSupplierNames,
}: {
  catalogProducts: Product[];
  quoteRequests: QuoteRequest[];
  savedSupplierNames: string[];
}): SupplierOffer[] {
  const quotedOffers = quoteRequests
    .filter((request) => isReadyQuoteStatus(request.status))
    .map((request) => {
      const catalogMatch = quoteCatalogMatch(request, catalogProducts);
      const quotedPrice = request.unitPrice || parseMarketplacePrice(request.priceRange || request.budget);
      const landedPrice = positiveFinite(
        quotedPrice,
        positiveFinite(catalogMatch?.price, fallbackQuoteUnitPrice(request)),
      );
      const supplierName = request.supplierName || catalogMatch?.supplier || "Quoted supplier";
      const isUsualSupplier = savedSupplierNames.includes(supplierName);
      const baselinePrice = positiveFinite(
        catalogMatch && catalogMatch.price > landedPrice ? catalogMatch.price : 0,
        Number((landedPrice * (isUsualSupplier ? 1.03 : 1.08)).toFixed(2)),
      );
      const savingsAmount = Math.max(0, Number((baselinePrice - landedPrice).toFixed(2)));
      const discountPercent = baselinePrice > 0 ? Math.round((savingsAmount / baselinePrice) * 100) : 0;
      const offerType: SupplierOffer["offerType"] =
        discountPercent >= 3
          ? "competitive_discount"
          : isUsualSupplier
            ? "usual_supplier"
            : "backup_offer";
      const reliability = Math.min(
        99,
        Math.max(
          60,
          Math.round(
            (catalogMatch?.stockStatus === "available" ? 18 : catalogMatch?.stockStatus === "low" ? 8 : 0) +
              (savedSupplierNames.includes(supplierName) ? 12 : 0) +
              (request.deliveryWindow ? 16 : 8) +
              58,
          ),
        ),
      );

      return {
        id: `offer-${request.id}`,
        quoteRequestId: request.id,
        supplierName,
        productName: request.product,
        landedPrice,
        listPrice: baselinePrice,
        discountPercent,
        savingsAmount,
        currency: request.budget.includes("EUR") ? "EUR" : "BAM",
        minimumOrder: request.termsNote?.match(/MOQ[^/,.]*/i)?.[0]?.replace(/MOQ/i, "Minimum order") || "Minimum order in quote",
        deliveryWindow: request.deliveryWindow || request.neededBy,
        substitution: request.termsNote?.includes("substitution") ? request.termsNote : undefined,
        validity: request.quotedAt ? `Quoted ${request.quotedAt}` : "Valid until supplier updates terms",
        reliability,
        offerType,
        rankReason: discountPercent >= 3
          ? `${discountPercent}% below baseline; supplier is competing for this order`
          : isUsualSupplier
            ? "Usual supplier with private terms on file"
            : "Backup offer ranked by true total cost, timing, and reliability",
      };
    });

  const catalogOffers = catalogProducts
    .filter((product) => product.stockStatus !== "unavailable")
    .slice(0, 8)
    .map((product) => {
      const isUsualSupplier = savedSupplierNames.includes(product.supplier);
      const baselinePrice = Number((product.price * (isUsualSupplier ? 1 : 1.06)).toFixed(2));
      const savingsAmount = Math.max(0, Number((baselinePrice - product.price).toFixed(2)));
      const discountPercent = baselinePrice > product.price ? Math.round((savingsAmount / baselinePrice) * 100) : 0;
      const offerType: SupplierOffer["offerType"] = isUsualSupplier
        ? "catalog_baseline"
        : discountPercent >= 3
          ? "competitive_discount"
          : "backup_offer";

      return {
        id: `offer-catalog-${product.id}`,
        quoteRequestId: "",
        supplierName: product.supplier,
        productName: product.name,
        landedPrice: product.price,
        listPrice: baselinePrice,
        discountPercent,
        savingsAmount,
        currency: product.currency,
        minimumOrder: product.lastOrderedKg ? `${product.lastOrderedKg} ${product.unit}` : "Supplier minimum",
        deliveryWindow: product.stockStatus === "available" ? "Available from catalog" : "Confirm before service",
        validity: "Catalog price, confirm before order",
        reliability: product.stockStatus === "available" ? 86 : 72,
        offerType,
        rankReason: isUsualSupplier
          ? "Usual supplier baseline; compete this item when price moves"
          : discountPercent >= 3
            ? `${discountPercent}% below current baseline for this item`
            : "Backup offer available for comparison",
      };
    });

  return [...quotedOffers, ...catalogOffers]
    .sort((left, right) => {
      const leftSaved = savedSupplierNames.includes(left.supplierName) ? 1 : 0;
      const rightSaved = savedSupplierNames.includes(right.supplierName) ? 1 : 0;

      return (
        left.landedPrice - right.landedPrice ||
        right.reliability - left.reliability ||
        rightSaved - leftSaved
      );
    })
    .slice(0, 10);
}

function orderFeeFor(totalBam: number) {
  if (totalBam <= 0) {
    return 0;
  }

  const band = Math.ceil(totalBam / 100);
  return Math.max(0.5, band * 0.5);
}

function invoiceMatchLabel(invoice: InvoiceRecord) {
  if (invoice.paymentReadiness) {
    return invoiceReadinessLabel(invoice.paymentReadiness);
  }

  if (invoice.matchStatus === "needs_match") {
    return "Needs order";
  }

  if (invoice.matchStatus === "disputed") {
    return "Blocked";
  }

  if (invoice.status === "paid") {
    return "Paid";
  }

  if (invoice.status === "blocked") {
    return "Disputed";
  }

  if (invoice.status === "exported") {
    return "Matched";
  }

  return invoice.orderId ? "Needs match" : "Upload order link";
}

function moneyTextToEur(value?: string) {
  const amount = parseMarketplacePrice(value ?? "");

  if (!amount) {
    return 0;
  }

  const normalized = normalizeMarketText(value ?? "");
  return normalized.includes("eur") || (value ?? "").includes("€") ? amount : bamToEur(amount);
}

function quoteRequestValueEur(request: QuoteRequest, catalogProducts: Product[]) {
  const budgetValue = moneyTextToEur(request.budget);

  if (budgetValue > 0) {
    return budgetValue;
  }

  const { quantity } = parseMarketplaceQuantity(request.quantity);
  const catalogMatch = quoteCatalogMatch(request, catalogProducts);
  const unitPriceBam = positiveFinite(request.unitPrice ?? 0, positiveFinite(catalogMatch?.price ?? 0, 0));

  return unitPriceBam > 0 ? bamToEur(unitPriceBam * Math.max(quantity, 1)) : 0;
}

function deriveSupplierOpportunities({
  catalogProducts,
  demandGaps: activeDemandGaps,
  deliveryAreas: activeDeliveryAreas,
  fulfillmentOrders,
  quoteRequests: activeQuoteRequests,
  supplierSearchDemand: activeSupplierSearchDemand,
}: {
  catalogProducts: Product[];
  demandGaps: DemandGap[];
  deliveryAreas: DeliveryArea[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  quoteRequests: QuoteRequest[];
  supplierSearchDemand: SupplierSearchDemand[];
}): SupplierOpportunity[] {
  const opportunities: SupplierOpportunity[] = [];
  const openQuoteRequests = activeQuoteRequests.filter((request) => isOpenQuoteStatus(request.status));

  openQuoteRequests.forEach((request) => {
    opportunities.push({
      id: `opp-live-quote-${request.id}`,
      title: `Quote ${request.product}`,
      detail: `${request.buyer} needs ${request.quantity} by ${request.neededBy}.`,
      potentialRevenueEur: Math.round(quoteRequestValueEur(request, catalogProducts)),
      urgency: request.status === "waiting" ? "this_week" : "now",
      action: "Send price, minimum order, delivery window, and substitution terms.",
    });
  });

  activeSupplierSearchDemand.forEach((demand) => {
    opportunities.push({
      id: `opp-live-demand-${demand.id}`,
      title: `Serve ${demand.product}`,
      detail: `${formatCount(demand.failedSearches)} failed searches from ${demand.buyerCount} buyer${demand.buyerCount === 1 ? "" : "s"} in ${demand.market}.`,
      potentialRevenueEur: Math.round(demand.avgBudgetEur * Math.max(1, demand.buyerCount)),
      urgency: demand.urgency,
      action: demand.recommendedAction,
    });
  });

  activeDemandGaps.forEach((gap) => {
    opportunities.push({
      id: `opp-live-gap-${gap.id}`,
      title: `Cover ${gap.category} in ${gap.market}`,
        detail: `${formatCount(gap.failedSearches)} failed restaurant searches and ${gap.unavailableOrders} unavailable orders are recorded.`,
      potentialRevenueEur: Math.round(gap.revenueAtRiskEur),
      urgency: gap.revenueAtRiskEur > 100000 || gap.unavailableOrders > 50 ? "now" : "this_week",
      action: gap.recommendedAction,
    });
  });

  catalogProducts
    .filter((product) => product.stockStatus !== "available")
    .forEach((product) => {
      const matchingQuoteValue = openQuoteRequests
        .filter(
          (request) =>
            (request.supplierName ? normalizeMarketText(request.supplierName) === normalizeMarketText(product.supplier) : true) &&
            (categoryTextsMatch(request.category ?? product.category, product.category) ||
              normalizeMarketText(request.product).includes(normalizeMarketText(product.name))),
        )
        .reduce((sum, request) => sum + quoteRequestValueEur(request, catalogProducts), 0);
      const matchingDemandValue = activeSupplierSearchDemand
        .filter((demand) => categoryTextsMatch(demand.category, product.category))
        .reduce((sum, demand) => sum + demand.avgBudgetEur * Math.max(1, demand.buyerCount), 0);

      opportunities.push({
        id: `opp-live-catalog-${product.id}`,
        title: `Refresh ${product.name}`,
        detail: `${product.category} is ${stockLabels[product.stockStatus].toLowerCase()}; buyers need current availability before ordering.`,
        potentialRevenueEur: Math.round(matchingQuoteValue + matchingDemandValue),
        urgency: product.stockStatus === "unavailable" ? "now" : "this_week",
        action: "Update stock, price, and minimum order so buyers can route demand.",
      });
    });

  fulfillmentOrders
    .filter((order) => order.status === "issue" || order.status === "needs_confirmation")
    .forEach((order) => {
      opportunities.push({
        id: `opp-live-fulfillment-${order.id}`,
        title: `Protect ${order.buyer} fulfillment`,
        detail: `${order.deliveryWindow}: ${order.nextAction}`,
        potentialRevenueEur: Math.round(order.valueEur),
        urgency: order.status === "issue" ? "now" : "this_week",
        action: "Move the order before the buyer has to source elsewhere.",
      });
    });

  activeDeliveryAreas
    .filter((area) => area.status === "capacity_watch")
    .forEach((area) => {
      const activeOrders = area.activeOrders ?? 0;
      const minimumOrderEur = moneyTextToEur(area.minimumOrder);

      opportunities.push({
        id: `opp-live-route-${area.id}`,
        title: `Reopen ${area.market} route capacity`,
        detail: `${area.days} / ${area.performance.toFixed(1)}% delivery performance.`,
        potentialRevenueEur: Math.round(minimumOrderEur * Math.max(1, activeOrders)),
        urgency: "this_week",
        action: "Adjust minimum order and delivery windows before accepting more demand.",
      });
    });

  const urgencyRank: Record<SupplierOpportunity["urgency"], number> = {
    now: 0,
    this_week: 1,
    monitor: 2,
  };
  const deduped = new Map<string, SupplierOpportunity>();

  opportunities.forEach((opportunity) => {
    const key = `${normalizeMarketText(opportunity.title)}::${normalizeMarketText(opportunity.action)}`;
    const existing = deduped.get(key);

    if (
      !existing ||
      urgencyRank[opportunity.urgency] < urgencyRank[existing.urgency] ||
      opportunity.potentialRevenueEur > existing.potentialRevenueEur
    ) {
      deduped.set(key, opportunity);
    }
  });

  return Array.from(deduped.values())
    .sort(
      (left, right) =>
        urgencyRank[left.urgency] - urgencyRank[right.urgency] ||
        right.potentialRevenueEur - left.potentialRevenueEur,
    )
    .slice(0, 12);
}

function percentValue(numerator: number, denominator: number) {
  if (!denominator) {
    return 100;
  }

  return Math.max(0, Math.min(100, Math.round((numerator / denominator) * 100)));
}

function deriveAutomationTasks({
  catalogProducts,
  connectorRequests,
  conversionTasks,
  deliveryAreas,
  discoveryRuns,
  externalRecords,
  fulfillmentOrders,
  quoteRequests,
}: {
  catalogProducts: Product[];
  connectorRequests: ConnectorRequestRecord[];
  conversionTasks: SupplierConversionTask[];
  deliveryAreas: DeliveryArea[];
  discoveryRuns: DiscoveryRunRecord[];
  externalRecords: ExternalSupplierRecord[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  quoteRequests: QuoteRequest[];
}): AutomationTask[] {
  const openQuotes = quoteRequests.filter((request) => isOpenQuoteStatus(request.status));
  const weakProducts = catalogProducts.filter((product) => product.stockStatus !== "available");
  const actionFulfillment = fulfillmentOrders.filter(fulfillmentNeedsActionFor);
  const capacityRoutes = deliveryAreas.filter((area) => area.status === "capacity_watch");
  const pendingConnectors = connectorRequests.filter((request) => request.status !== "connected");
  const activeDiscoveryRuns = discoveryRuns.filter((run) => run.status === "running");
  const openConversions = conversionTasks.filter((task) => task.stage !== "converted");
  const unverifiedExternal = externalRecords.filter((record) => record.status !== "converted");
  const tasks: AutomationTask[] = [
    {
      id: "auto-quotes",
      title: "Quote response monitor",
      scope: "Restaurant price requests",
      volume: `${openQuotes.length} open / ${quoteRequests.length} total`,
      status: openQuotes.length ? "needs_review" : "running",
    },
    {
      id: "auto-catalog-stock",
      title: "Catalog availability monitor",
      scope: "Supplier product rows",
      volume: `${weakProducts.length} low or unavailable / ${catalogProducts.length} SKUs`,
      status: weakProducts.length ? "needs_review" : "running",
    },
    {
      id: "auto-fulfillment",
      title: "Fulfillment SLA monitor",
      scope: "Supplier orders",
      volume: `${actionFulfillment.length} need action / ${fulfillmentOrders.length} tracked`,
      status: actionFulfillment.length ? "needs_review" : "running",
    },
    {
      id: "auto-discovery",
      title: "Supplier discovery monitor",
      scope: "External supplier records",
      volume: `${unverifiedExternal.length} reviewable / ${openConversions.length} conversion tasks`,
      status: activeDiscoveryRuns.length ? "running" : unverifiedExternal.length ? "queued" : "running",
    },
    {
      id: "auto-routes",
      title: "Route capacity monitor",
      scope: "Delivery areas",
      volume: `${capacityRoutes.length} watches / ${deliveryAreas.length} routes`,
      status: capacityRoutes.length ? "needs_review" : "running",
    },
  ];

  if (pendingConnectors.length) {
    tasks.push({
      id: "auto-connectors",
      title: "Connector setup monitor",
      scope: "Import and ERP requests",
      volume: `${pendingConnectors.length} pending setup / ${connectorRequests.length} saved`,
      status: "queued",
    });
  }

  return tasks.filter((task) => task.volume);
}

function deriveMarketLanes(
  networkSnapshot: NetworkOperatingSnapshot,
  coverageRecords: MarketCoverageRecord[],
  demandGaps: DemandGap[],
): MarketLane[] {
  const { operatingCountries, stats } = networkSnapshot;
  const totalCustomers = operatingCountries.reduce((sum, country) => sum + country.customers, 0);

  return operatingCountries
    .map((country) => {
      const normalizedCountry = normalizeMarketText(`${country.country} ${country.code}`);
      const matchedCoverage = coverageRecords.find(
        (record) =>
          normalizeMarketText(record.market).includes(normalizedCountry.split(" ")[0] ?? normalizedCountry) ||
          normalizedCountry.includes(normalizeMarketText(record.market).split(" ")[0] ?? ""),
      );
      const matchedDemand = demandGaps.find(
        (gap) =>
          categoryTextsMatch(gap.category, country.topGap) ||
          normalizeMarketText(gap.market).includes(normalizedCountry.split(" ")[0] ?? normalizedCountry),
      );
      const customerShare = totalCustomers ? country.customers / totalCustomers : 0;
      const gmvEur = Math.round(stats.monthlyGmvEur * customerShare);
      const fillRate = matchedCoverage?.fillRate ?? country.fillRate;
      const status: MarketLane["status"] =
        country.priority === "priority" || fillRate < 84
          ? "watch"
          : country.demandIndex >= 78 || (matchedDemand?.revenueAtRiskEur ?? 0) > 100000
            ? "accelerating"
            : "stable";

      return {
        id: `lane-live-${country.id}`,
        market: country.country,
        segment: country.topGap,
        customers: country.customers,
        gmvEur,
        fillRate,
        marginLift: Math.max(0, Math.round((100 - fillRate) / 2 + country.demandIndex / 20)),
        status,
        nextMove: matchedCoverage?.nextMove ?? matchedDemand?.recommendedAction ?? country.nextMove,
      };
    })
    .sort((left, right) => right.gmvEur - left.gmvEur)
    .slice(0, 8);
}

function deriveWorkflowSignals({
  conversionTasks,
  fulfillmentOrders,
  messageThreads,
  orders,
  quoteRequests,
}: {
  conversionTasks: SupplierConversionTask[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  messageThreads: MessageThread[];
  orders: Order[];
  quoteRequests: QuoteRequest[];
}): WorkflowSignal[] {
  const quotedRequests = quoteRequests.filter((request) => isReadyQuoteStatus(request.status)).length;
  const resolvedMessages = messageThreads.filter((thread) => thread.status === "resolved").length;
  const completedConversions = conversionTasks.filter((task) => task.stage === "converted").length;
  const movedFulfillment = fulfillmentOrders.filter((order) => !fulfillmentNeedsActionFor(order)).length;
  const nonIssueOrders = orders.filter((order) => order.status !== "issue").length;

  return [
    {
      id: "workflow-quotes",
      name: "Request to quote",
      stage: "Supplier pricing",
      automation: percentValue(quotedRequests, quoteRequests.length),
      volume: `${quoteRequests.length} requests`,
      outcome: `${quoteRequests.length - quotedRequests} waiting on supplier terms`,
    },
    {
      id: "workflow-orders",
      name: "Order control",
      stage: "Buyer approval and dispatch",
      automation: percentValue(nonIssueOrders, orders.length),
      volume: `${orders.length} orders`,
      outcome: `${orders.length - nonIssueOrders} orders need recovery`,
    },
    {
      id: "workflow-fulfillment",
      name: "Fulfillment movement",
      stage: "Supplier operations",
      automation: percentValue(movedFulfillment, fulfillmentOrders.length),
      volume: `${fulfillmentOrders.length} fulfillment records`,
      outcome: `${fulfillmentOrders.length - movedFulfillment} supplier actions open`,
    },
    {
      id: "workflow-messages",
      name: "Message resolution",
      stage: "Buyer-supplier comms",
      automation: percentValue(resolvedMessages, messageThreads.length),
      volume: `${messageThreads.length} threads`,
      outcome: `${messageThreads.length - resolvedMessages} threads still open`,
    },
    {
      id: "workflow-conversions",
      name: "External supplier conversion",
      stage: "Network growth",
      automation: percentValue(completedConversions, conversionTasks.length),
      volume: `${conversionTasks.length} conversion tasks`,
      outcome: `${conversionTasks.length - completedConversions} suppliers still in motion`,
    },
  ];
}

function deriveTrustControls({
  approvalRequests,
  auditEvents,
  connectorRequests,
  localizationRules,
  workspaceUsers,
}: {
  approvalRequests: ApprovalRequest[];
  auditEvents: AuditEvent[];
  connectorRequests: ConnectorRequestRecord[];
  localizationRules: LocalizationRule[];
  workspaceUsers: WorkspaceUser[];
}): TrustControl[] {
  const pendingApprovals = approvalRequests.filter((request) => request.status === "needs_approval").length;
  const activeUsers = workspaceUsers.filter((user) => user.status === "active").length;
  const invitedUsers = workspaceUsers.filter((user) => user.status === "invited").length;
  const connectedRequests = connectorRequests.filter((request) => request.status === "connected").length;
  const failedRequests = connectorRequests.filter((request) => request.status === "failed").length;
  const liveLocalization = localizationRules.filter((rule) => rule.status === "live").length;

  return [
    {
      id: "trust-audit",
      label: "Audit trail",
      value: `${auditEvents.length} events`,
      detail: "Every saved order, supplier, connector, and control action should leave an operator-readable event.",
      status: auditEvents.length ? "ready" : "monitor",
    },
    {
      id: "trust-approvals",
      label: "Budget approvals",
      value: `${pendingApprovals} pending`,
      detail: `${approvalRequests.length} approval records are tied to branch spend controls.`,
      status: pendingApprovals ? "monitor" : "ready",
    },
    {
      id: "trust-access",
      label: "Role access",
      value: `${activeUsers} active`,
      detail: `${invitedUsers} invited users still need confirmation before they can buy or approve orders.`,
      status: invitedUsers ? "monitor" : "ready",
    },
    {
      id: "trust-connectors",
      label: "Connector verification",
      value: `${connectedRequests}/${connectorRequests.length} connected`,
      detail: failedRequests
        ? `${failedRequests} software connection needs repair before automation can rely on it.`
        : "ERP/POS/accounting connections remain clearly staged until verified.",
      status: failedRequests ? "monitor" : connectorRequests.length ? "ready" : "planned",
    },
    {
      id: "trust-localization",
      label: "Localization controls",
      value: `${liveLocalization}/${localizationRules.length} live`,
      detail: "Currency, tax, and market rules keep regional buying records comparable.",
      status: liveLocalization === localizationRules.length && localizationRules.length ? "ready" : "planned",
    },
  ];
}

function deriveIntegrationStatus({
  connectorRequests,
  importRuns,
  networkActionState,
}: {
  connectorRequests: ConnectorRequestRecord[];
  importRuns: ImportRunRecord[];
  networkActionState: string;
}): IntegrationStatus[] {
  const apiLoaded = networkActionState.startsWith("Live data loaded");
  const statuses: IntegrationStatus[] = [
    {
      name: "Live workspace storage",
      category: "Data",
      status: apiLoaded ? "connected" : "degraded",
      lastSync: apiLoaded ? networkActionState : "Waiting for live workspace data",
    },
    {
      name: "Catalog and order-guide imports",
      category: "Imports",
      status: importRuns.length ? "connected" : "planned",
      lastSync: importRuns.length
        ? `${importRuns.length} saved batch${importRuns.length === 1 ? "" : "es"}`
        : "No saved import batch yet",
    },
  ];

  const connectorMap = new Map<string, ConnectorRequestRecord[]>();
  connectorRequests.forEach((request) => {
    const key = normalizeMarketText(`${request.providerName} ${request.systemType}`);
    connectorMap.set(key, [...(connectorMap.get(key) ?? []), request]);
  });

  connectorMap.forEach((requests) => {
    const newest = requests
      .slice()
      .sort((left, right) => (right.updatedAt ?? right.createdAt).localeCompare(left.updatedAt ?? left.createdAt))[0];
    const connected = requests.filter((request) => request.status === "connected").length;
    const failed = requests.filter((request) => request.status === "failed").length;
    const pending = requests.filter((request) => request.status === "pending_setup").length;

    statuses.push({
      name: newest.providerName,
      category: newest.systemType,
      status: failed ? "degraded" : connected ? "connected" : "planned",
      lastSync:
        newest.statusNote ??
        (connected
          ? `${connected}/${requests.length} verified connection${requests.length === 1 ? "" : "s"}`
          : `${pending} pending setup request${pending === 1 ? "" : "s"}`),
    });
  });

  if (!connectorRequests.length) {
    statuses.push({
      name: "ERP/accounting connectors",
      category: "Integrations",
      status: "planned",
      lastSync: "Create software connections from Settings",
    });
  }

  return statuses;
}

function deriveLiveIncidents({
  connectorRequests,
  coverageRecords,
  deliveryAreas,
  fulfillmentOrders,
  importedIncidents,
  quoteRequests,
}: {
  connectorRequests: ConnectorRequestRecord[];
  coverageRecords: MarketCoverageRecord[];
  deliveryAreas: DeliveryArea[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  importedIncidents: LiveIncident[];
  quoteRequests: QuoteRequest[];
}): LiveIncident[] {
  const derived: LiveIncident[] = [];

  fulfillmentOrders
    .filter((order) => order.status === "issue")
    .forEach((order) => {
      derived.push({
        id: `incident-fulfillment-${order.id}`,
        title: `Fulfillment issue: ${order.buyer}`,
        market: order.market ?? "Supplier operations",
        severity: "high",
        status: "active",
        impact: `${formatEur(order.valueEur)} order at risk`,
        owner: order.supplierName ?? "Supplier ops",
        eta: order.deliveryWindow,
        action: order.nextAction,
      });
    });

  coverageRecords
    .filter((record) => record.risk === "critical")
    .forEach((record) => {
      derived.push({
        id: `incident-coverage-${record.id}`,
        title: `Critical coverage gap: ${record.category}`,
        market: record.market,
        severity: "critical",
        status: "active",
        impact: `${record.suppliersLive}/${record.supplierTarget} suppliers live`,
        owner: "Network ops",
        eta: "Next sourcing cycle",
        action: record.nextMove,
      });
    });

  deliveryAreas
    .filter((area) => area.status === "capacity_watch" && area.performance < 90)
    .forEach((area) => {
      derived.push({
        id: `incident-route-${area.id}`,
        title: `Route capacity watch: ${area.market}`,
        market: area.market,
        severity: area.performance < 82 ? "high" : "medium",
        status: "active",
        impact: `${area.performance.toFixed(1)}% delivery performance`,
        owner: "Delivery ops",
        eta: area.days,
        action: `Recheck ${area.minimumOrder} minimum and active orders before accepting more demand.`,
      });
    });

  connectorRequests
    .filter((request) => request.status === "failed")
    .forEach((request) => {
      derived.push({
        id: `incident-connector-${request.requestId}`,
        title: `Connector setup failed: ${request.providerName}`,
        market: request.organizationName,
        severity: "medium",
        status: "active",
        impact: `${request.systemType} sync unavailable`,
        owner: "Integrations",
        eta: "Setup review",
        action: request.statusNote ?? "Repair credentials and run a test sync.",
      });
    });

  const staleQuotes = quoteRequests.filter((request) => request.status === "waiting");
  if (staleQuotes.length) {
    derived.push({
      id: "incident-quote-waiting",
      title: "Price response queue waiting",
      market: "Supplier response",
      severity: staleQuotes.length > 3 ? "high" : "medium",
      status: "active",
      impact: `${staleQuotes.length} restaurant price request${staleQuotes.length === 1 ? "" : "s"} waiting`,
      owner: "Sales",
      eta: "Today",
      action: "Follow up with suppliers or route restaurants to backup options.",
    });
  }

  const merged = new Map<string, LiveIncident>();

  derived.forEach((incident) => merged.set(incident.id, incident));
  importedIncidents.forEach((incident) => merged.set(incident.id, incident));

  const severityRank: Record<LiveIncident["severity"], number> = {
    critical: 0,
    high: 1,
    medium: 2,
  };
  const statusRank: Record<LiveIncident["status"], number> = {
    active: 0,
    mitigating: 1,
    resolved: 2,
  };

  return Array.from(merged.values()).sort(
    (left, right) =>
      statusRank[left.status] - statusRank[right.status] ||
      severityRank[left.severity] - severityRank[right.severity],
  );
}

function App() {
  const [sessionRole, setSessionRole] = useState<Role | null>(() => initialSessionRole());
  const [role, setRole] = useState<Role>(() => initialSessionRole() ?? initialRole());
  const [publicPage, setPublicPage] = useState<"landing" | "login">(() => publicPageFromHash());
  const [permissionRoleId, setPermissionRoleId] = useState(
    () => initialPermissionRoleId(initialSessionRole() ?? initialRole()),
  );
  const [activeNav, setActiveNav] = useState(() => initialNav());
  const [orderFilter, setOrderFilter] = useState<"all" | OrderStatus>("all");
  const [orderQueue, setOrderQueue] = useState<Order[]>(() => readLocalOrders());
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<OrderItem[]>([]);
  const [submitState, setSubmitState] = useState(() => storageGet("veleo.submitState") ?? "Ready");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(
    () => storageGet("veleo.selectedBranchId") ?? "",
  );
  const [branchBook, setBranchBook] = useState<BranchAccount[]>([]);
  const [workspaceUserQueue, setWorkspaceUserQueue] = useState<WorkspaceUser[]>([]);
  const [marketplaceCategory, setMarketplaceCategory] = useState(
    () => storageGet("veleo.marketplaceCategory") ?? "All",
  );
  const [marketplaceMode, setMarketplaceMode] = useState<MarketplaceMode>(() => {
    const stored = storageGet("veleo.marketplaceMode");
    return isMarketplaceMode(stored) ? stored : "All";
  });
  const [selectedMarketProductId, setSelectedMarketProductId] = useState(
    () => storageGet("veleo.selectedMarketProductId") ?? "",
  );
  const [externalInquiry, setExternalInquiry] = useState(
    () => storageGet("veleo.externalInquiry") ?? "",
  );
  const [externalInquiryLog, setExternalInquiryLog] = useState<ExternalInquiryRecord[]>([]);
  const [quoteQueue, setQuoteQueue] = useState<QuoteRequest[]>(() => readLocalQuoteRequests());
  const [savedSupplierNames, setSavedSupplierNames] = useState<string[]>([]);
  const [restaurantSupplierContacts, setRestaurantSupplierContacts] = useState<RestaurantSupplierContact[]>(() =>
    readStoredJson<RestaurantSupplierContact[]>(RESTAURANT_SUPPLIER_CONTACTS_KEY, []),
  );
  const [supplierOrderRequests, setSupplierOrderRequests] = useState<SupplierOrderRequest[]>(() =>
    readLocalSupplierOrderRequests(),
  );
  const [recentSupplierRequestIds, setRecentSupplierRequestIds] = useState<string[]>([]);
  const [messageQueue, setMessageQueue] = useState<MessageThread[]>([]);
  const [approvalQueue, setApprovalQueue] = useState<ApprovalRequest[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
  const [fulfillmentQueue, setFulfillmentQueue] = useState<SupplierFulfillmentOrder[]>(() =>
    localFulfillmentOrders(),
  );
  const [deliveryAreaQueue, setDeliveryAreaQueue] = useState<DeliveryArea[]>([]);
  const [invoiceQueue, setInvoiceQueue] = useState<InvoiceRecord[]>(() => readLocalInvoices());
  const [discrepancyQueue, setDiscrepancyQueue] = useState<DiscrepancyRecord[]>([]);
  const [externalRecords, setExternalRecords] = useState<ExternalSupplierRecord[]>([]);
  const [discoveryRuns, setDiscoveryRuns] = useState<DiscoveryRunRecord[]>([]);
  const [conversionTasks, setConversionTasks] = useState<SupplierConversionTask[]>([]);
  const [demandGapQueue, setDemandGapQueue] = useState<DemandGap[]>([]);
  const [sourcingSprintQueue, setSourcingSprintQueue] = useState<SourcingSprint[]>([]);
  const [coverageQueue, setCoverageQueue] = useState<MarketCoverageRecord[]>([]);
  const [incidentQueue, setIncidentQueue] = useState<LiveIncident[]>([]);
  const [supplierDemandQueue, setSupplierDemandQueue] = useState<SupplierSearchDemand[]>([]);
  const [auditQueue, setAuditQueue] = useState<AuditEvent[]>([]);
  const [reorderStartedAt, setReorderStartedAt] = useState<number | null>(null);
  const [activatedSupplierOpportunityIds, setActivatedSupplierOpportunityIds] = useState<string[]>(() =>
    readStoredJson<string[]>("veleo.activatedSupplierOpportunities", []),
  );
  const [localizationQueue, setLocalizationQueue] = useState<LocalizationRule[]>([]);
  const [discoverySourceQueue, setDiscoverySourceQueue] = useState<DiscoverySource[]>([]);
  const [networkSnapshot, setNetworkSnapshot] = useState<NetworkOperatingSnapshot>(emptyNetworkSnapshot);
  runtimeLocalizationRules = localizationQueue;
  const [quoteActionState, setQuoteActionState] = useState("Ready");
  const [replenishmentState, setReplenishmentState] = useState("Forecast live");
  const [catalogActionState, setCatalogActionState] = useState("Catalog live");
  const [discoveryActionState, setDiscoveryActionState] = useState("Sources live");
  const [networkActionState, setNetworkActionState] = useState("Network ready");
  const [priceBenchmarkQueue, setPriceBenchmarkQueue] = useState<PriceBenchmarkRecord[]>([]);
  const [importStatus, setImportStatus] = useState("No import processed yet");
  const [connectorStatus, setConnectorStatus] = useState("No software connection saved yet");
  const [importRuns, setImportRuns] = useState<ImportRunRecord[]>([]);
  const [connectorRequests, setConnectorRequests] = useState<ConnectorRequestRecord[]>([]);
  const [doneForYouSetupRequests, setDoneForYouSetupRequests] = useState<DoneForYouSetupRequest[]>(() =>
    readLocalDoneForYouSetupRequests(),
  );
  const [realDataSeen, setRealDataSeen] = useState<Record<"restaurant" | "supplier", boolean>>(() => ({
    restaurant: storedRealDataSeen("restaurant"),
    supplier: storedRealDataSeen("supplier"),
  }));
  const [account, setAccount] = useState<SessionAccount | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [supplierConfirmRequestId, setSupplierConfirmRequestId] = useState(() => supplierConfirmationRouteId());
  const [publicConfirmationRequest, setPublicConfirmationRequest] = useState<SupplierOrderRequest | null>(null);
  const [publicConfirmationLoading, setPublicConfirmationLoading] = useState(false);

  function restaurantActivationReady(signal: { orderOrMessage?: boolean; supplier?: boolean } = {}) {
    const hasSupplierSignal =
      signal.supplier ?? Boolean(restaurantSupplierContacts.length || savedSupplierNames.length);
    const hasOrderOrMessageSignal =
      signal.orderOrMessage ??
      Boolean(
        draft.length ||
          orderQueue.length ||
          quoteQueue.length ||
          messageQueue.some((thread) => thread.role === "supplier" || thread.role === "external"),
      );

    return hasSupplierSignal && hasOrderOrMessageSignal;
  }

  function markRoleDataSeen(nextRole: Role, restaurantSignal?: { orderOrMessage?: boolean; supplier?: boolean }) {
    if (nextRole === "admin") {
      return;
    }

    if (nextRole === "restaurant" && !restaurantActivationReady(restaurantSignal)) {
      return;
    }

    storageSet(realDataSeenStorageKey(nextRole), "true");
    setRealDataSeen((current) => ({ ...current, [nextRole]: true }));
  }

  function applyAccount(next: SessionAccount) {
    setAccount(next);
    setRole(next.role);
    setSessionRole(next.role);
    setPermissionRoleId(defaultPermissionRoleId(next.role));
    if (next.organizationName) {
      storageSet(ACCOUNT_BUSINESS_NAME_KEY, next.organizationName);
    }
  }

  function openLocalProofWorkspace(nextRole: Role) {
    if (!ALLOW_LOCAL_WORKSPACE_FALLBACK || isAuthConfigured()) {
      setAuthNotice("Local proof mode is available only in development before workspace sign-in is configured.");
      setPublicPage("login");
      window.history.replaceState(null, "", "#/login");
      return;
    }

    clearLegacySessionRole();
    const organizationName =
      nextRole === "restaurant"
        ? "Local Restaurant"
        : nextRole === "admin"
          ? "VELEO Ops"
          : storageGet(ACCOUNT_BUSINESS_NAME_KEY)?.trim() || "Local Supplier";

    applyAccount({
      organizationId: `local-proof-${nextRole}`,
      organizationName,
      type: nextRole,
      role: nextRole,
      memberRole: "owner",
      fullName: "VELEO local operator",
    });
    setActiveNav("Dashboard");
    setAuthNotice(null);
    setPublicPage("landing");
    setMobileNavOpen(false);
    window.history.replaceState(null, "", "#/dashboard");
  }

  async function refreshSession() {
    const result = await resolveWorkspaceSession();

    if (result.status === "signed_in") {
      applyAccount(result.account);
      setAuthNotice(null);
    } else {
      setAccount(null);
      setSessionRole(null);
      setAuthNotice(result.authNotice);
    }

    setAuthReady(true);
  }

  useEffect(() => {
    consumeUrlSession();
    let cancelled = false;

    void refreshSession();

    const unsubscribe = onAuthChange((session) => {
      if (cancelled) {
        return;
      }
      if (!session) {
        setAccount(null);
        setSessionRole(null);
      } else {
        void refreshSession();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const nextSupplierConfirmRequestId = supplierConfirmationRouteId();
      setSupplierConfirmRequestId(nextSupplierConfirmRequestId);
      if (nextSupplierConfirmRequestId) {
        setPublicPage("landing");
        setMobileNavOpen(false);
        return;
      }
      if (!sessionRole) {
        setPublicPage(publicPageFromHash());
        return;
      }

      setActiveNav(navFromHash());
      setMobileNavOpen(false);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [sessionRole]);

  useEffect(() => {
    if (!sessionRole) {
      return;
    }

    let cancelled = false;

    fetchAppBootstrap().then((result) => {
      if (cancelled) {
        return;
      }

      if (!result.ok) {
        setNetworkActionState(`Live data not loaded: ${readableSaveError(result.error)}`);
        // Demo fallback is a local-development convenience only. In production we
        // never surface fake records; an empty/failed load stays empty.
        if (import.meta.env.DEV) {
          const fallback = buildLocalWorkspaceFallbackHydration();
          setOrderQueue(fallback.orders);
          setDraft(fallback.draftItems);
          setCatalogProducts(fallback.catalogProducts);
          setQuoteQueue(fallback.quoteRequests);
          setFulfillmentQueue(fallback.fulfillmentOrders);
          setInvoiceQueue(fallback.invoices);
          setImportRuns(fallback.importRuns);
          setConnectorRequests(fallback.connectorRequests);
          setDoneForYouSetupRequests(fallback.doneForYouSetupRequests);
          setRestaurantSupplierContacts(fallback.restaurantSupplierContacts);
          setSupplierOrderRequests(fallback.supplierOrderRequests);
        }
        return;
      }

      const hydrated = buildWorkspaceBootstrapHydration(result);
      setOrderQueue(hydrated.orders);
      setDraft(hydrated.draftItems);
      setSavedSupplierNames(hydrated.preferredSupplierNames);
      setCatalogProducts(hydrated.catalogProducts);
      setQuoteQueue(hydrated.quoteRequests);
      setExternalRecords(hydrated.externalSuppliers);
      setExternalInquiryLog(hydrated.externalInquiries);
      setConversionTasks(hydrated.supplierConversionTasks);
      setFulfillmentQueue(hydrated.fulfillmentOrders);
      setDeliveryAreaQueue(hydrated.deliveryAreas);
      setInvoiceQueue(hydrated.invoices);
      setDiscrepancyQueue(hydrated.discrepancies);
      setMessageQueue(hydrated.messageThreads);
      setApprovalQueue(hydrated.approvalRequests);
      setAuditQueue(hydrated.auditEvents);
      setWorkspaceUserQueue(hydrated.workspaceUsers);
      setDemandGapQueue(hydrated.demandGaps);
      setSupplierDemandQueue(hydrated.supplierSearchDemand);
      setCoverageQueue(hydrated.marketCoverageRecords);
      setSourcingSprintQueue(hydrated.sourcingSprints);
      setLocalizationQueue(hydrated.localizationRules);
      setDiscoverySourceQueue(hydrated.discoverySources);
      setDiscoveryRuns(hydrated.discoveryRuns);
      setImportRuns(hydrated.importRuns);
      setConnectorRequests(hydrated.connectorRequests);
      setDoneForYouSetupRequests(hydrated.doneForYouSetupRequests);
      setNetworkSnapshot(hydrated.networkSnapshot);
      setPriceBenchmarkQueue(hydrated.priceBenchmarks);
      setRestaurantSupplierContacts(hydrated.restaurantSupplierContacts);
      setSupplierOrderRequests(hydrated.supplierOrderRequests);

      if (hydrated.branchAccounts.length) {
        setBranchBook(hydrated.branchAccounts);
        setSelectedBranchId((current) => {
          const firstBranch = hydrated.branchAccounts.at(0);
          return hydrated.branchAccounts.some((branch) => branch.id === current)
            ? current
            : firstBranch?.id ?? current;
        });
      }

      setNetworkActionState(hydrated.networkActionState);
      setImportStatus(hydrated.importStatus);
      setConnectorStatus(hydrated.softwareConnectionStatus);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionRole]);

  useEffect(() => {
    if (!sessionRole || supplierConfirmRequestId) {
      return;
    }

    const nextHash = `#/${slugForNav(activeNav)}`;

    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", nextHash);
    }

    storageSet("veleo.activeNav", activeNav);
  }, [activeNav, sessionRole, supplierConfirmRequestId]);

  useEffect(() => storageSet("veleo.role", role), [role]);
  useEffect(() => storageSet(permissionRoleStorageKey(role), permissionRoleId), [permissionRoleId, role]);
  useEffect(() => {
    if (sessionRole && !navItemsForRole(role).some((item) => item.label === activeNav)) {
      setActiveNav("Dashboard");
    }
  }, [activeNav, role, sessionRole]);
  useEffect(() => {
    const allowedPermissions = permissionOptionsForRole(role);

    if (!allowedPermissions.some((permission) => permission.id === permissionRoleId)) {
      setPermissionRoleId(defaultPermissionRoleId(role));
    }
  }, [permissionRoleId, role]);
  useEffect(() => storageSet("veleo.submitState", submitState), [submitState]);
  useEffect(() => storageSet("veleo.selectedBranchId", selectedBranchId), [selectedBranchId]);
  useEffect(() => storageSet("veleo.marketplaceCategory", marketplaceCategory), [marketplaceCategory]);
  useEffect(() => storageSet("veleo.marketplaceMode", marketplaceMode), [marketplaceMode]);
  useEffect(() => storageSet("veleo.selectedMarketProductId", selectedMarketProductId), [selectedMarketProductId]);
  useEffect(() => storageSet("veleo.externalInquiry", externalInquiry), [externalInquiry]);
  useEffect(
    () => storageSet("veleo.activatedSupplierOpportunities", JSON.stringify(activatedSupplierOpportunityIds)),
    [activatedSupplierOpportunityIds],
  );
  useEffect(
    () => storageSet(RESTAURANT_SUPPLIER_CONTACTS_KEY, JSON.stringify(restaurantSupplierContacts)),
    [restaurantSupplierContacts],
  );
  useEffect(() => saveLocalSupplierOrderRequests(supplierOrderRequests), [supplierOrderRequests]);
  useEffect(() => storageSet("veleo.importStatus", importStatus), [importStatus]);
  useEffect(() => storageSet("veleo.connectorStatus", connectorStatus), [connectorStatus]);

  useEffect(() => {
    if (!supplierConfirmRequestId) {
      setPublicConfirmationRequest(null);
      setPublicConfirmationLoading(false);
      return;
    }

    const localRequest =
      supplierOrderRequests.find((request) => matchesSupplierConfirmationRequest(request, supplierConfirmRequestId)) ??
      readLocalSupplierOrderRequests().find((request) =>
        matchesSupplierConfirmationRequest(request, supplierConfirmRequestId),
      ) ??
      null;
    setPublicConfirmationRequest(localRequest);
    setPublicConfirmationLoading(!localRequest);

    let cancelled = false;
    fetchSupplierOrderConfirmation(supplierConfirmRequestId).then((result) => {
      if (cancelled) {
        return;
      }

      setPublicConfirmationLoading(false);

      if (!result.ok) {
        return;
      }

      const request = {
        ...result.request,
        confirmationUrl: supplierConfirmationUrl(result.request.publicToken),
      };
      setPublicConfirmationRequest(request);
      setSupplierOrderRequests((currentRequests) => [
        request,
        ...currentRequests.filter((item) =>
          item.id !== request.id && item.publicToken !== request.publicToken,
        ),
      ]);
    });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierConfirmRequestId]);

  const roleEntry = workspaceRoleEntryFor(role, realDataSeen);
  const { realRestaurantDataActive, realSupplierDataActive, roleHasRealData } = roleEntry;
  const accountOrders = role === "restaurant" && !realRestaurantDataActive ? [] : orderQueue;
  const accountDraft = role === "restaurant" && !realRestaurantDataActive ? [] : draft;
  const accountCatalogProducts = role === "admin" || roleHasRealData ? catalogProducts : [];
  const accountQuoteQueue = role === "admin" || roleHasRealData ? quoteQueue : [];
  const accountFulfillmentQueue = role === "admin" || roleHasRealData ? fulfillmentQueue : [];
  const accountInvoiceQueue = role === "admin" || roleHasRealData ? invoiceQueue : [];
  const accountDiscrepancies = role === "admin" || roleHasRealData ? discrepancyQueue : [];
  const accountApprovalQueue = role === "admin" || roleHasRealData ? approvalQueue : [];
  const accountMessages = role === "admin" || roleHasRealData ? messageQueue : [];
  const accountExternalRecords = role === "admin" || roleHasRealData ? externalRecords : [];
  const accountSavedSupplierNames =
    role === "restaurant" && !realRestaurantDataActive ? [] : savedSupplierNames;
  const restaurantKnownSupplierNames = useMemo(
    () =>
      Array.from(
        new Set([
          ...accountSavedSupplierNames,
          ...restaurantSupplierContacts.map((contact) => contact.name),
        ].filter(Boolean)),
      ),
    [accountSavedSupplierNames, restaurantSupplierContacts],
  );
  const accountImportRuns = role === "admin" || roleHasRealData ? importRuns : readLocalImportRuns();
  const accountConnectorRequests =
    role === "admin" || roleHasRealData ? connectorRequests : readLocalConnectorRequests();
  const accountDoneForYouSetupRequests =
    role === "admin" || role === "restaurant" || roleHasRealData
      ? doneForYouSetupRequests
      : readLocalDoneForYouSetupRequests();

  const filteredOrders = useMemo(() => {
    return accountOrders.filter((order) => orderFilter === "all" || order.status === orderFilter);
  }, [accountOrders, orderFilter]);
  const supplierInviteOrder = useMemo<SupplierInviteRequest | undefined>(() => {
    const inviteOrder = fulfillmentQueue.find(fulfillmentNeedsActionFor);

    if (!inviteOrder) {
      return undefined;
    }

    return {
      buyer: inviteOrder.buyer,
      deliveryWindow: inviteOrder.deliveryWindow,
      id: inviteOrder.id,
      itemCount: inviteOrder.itemCount,
      nextAction: inviteOrder.nextAction,
      status: inviteOrder.status,
      valueEur: inviteOrder.valueEur,
    };
  }, [fulfillmentQueue]);

  const searchableMarketplaceProducts = useMemo(
    () =>
      dedupeMarketplaceProducts([
        ...catalogProductsToMarketplaceProducts(accountCatalogProducts, deliveryAreaQueue, accountSavedSupplierNames),
        ...externalRecordsToMarketplaceProducts(accountExternalRecords),
      ]),
    [accountCatalogProducts, accountExternalRecords, accountSavedSupplierNames, deliveryAreaQueue],
  );
  const draftLookupContext = useMemo<DraftLookupContext>(
    () => ({
      catalogProducts: accountCatalogProducts,
      marketplaceProducts: searchableMarketplaceProducts,
      supplierContacts: restaurantSupplierContacts,
    }),
    [accountCatalogProducts, restaurantSupplierContacts, searchableMarketplaceProducts],
  );
  const visibleAccountDraft = useMemo(
    () => accountDraft.map((item) => enrichDraftItem(item, draftLookupContext)),
    [accountDraft, draftLookupContext],
  );

  const marketplaceCategories = useMemo(
    () => ["All", ...Array.from(new Set(searchableMarketplaceProducts.map((product) => product.category)))],
    [searchableMarketplaceProducts],
  );

  const selectedBranch =
    branchBook.find((branch) => branch.id === selectedBranchId) ?? branchBook[0] ?? emptyBranchAccount;
  const selectedBranchRemaining = selectedBranch.weeklyBudget - selectedBranch.spentThisWeek;
  const selectedBranchSupplierContacts = restaurantSupplierContacts.filter(
    (contact) => contact.branchName === selectedBranch.name || !contact.branchName,
  );
  const liveNetworkStats = networkSnapshot.stats;
  const accountBusinessName =
    account?.organizationName?.trim() || storageGet(ACCOUNT_BUSINESS_NAME_KEY)?.trim() || "";
  const workspaceName =
    role === "restaurant"
      ? accountBusinessName || selectedBranch.name || "My restaurant"
      : role === "supplier"
        ? accountBusinessName || catalogProducts[0]?.supplier || savedSupplierNames[0] || "Supplier account"
        : "Network operations";
  const workspaceInitials = initialsForWorkspace(workspaceName);

  const visibleMarketplaceProducts = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return searchableMarketplaceProducts
      .filter((product) => {
        const matchesCategory =
          marketplaceCategory === "All" || product.category === marketplaceCategory;
        const matchesMode =
          marketplaceMode === "All" ||
          (marketplaceMode === "Ready" &&
            marketplaceProductCanOrder(product)) ||
          (marketplaceMode === "Limited" &&
            product.supplierType === "verified" &&
            !marketplaceProductCanOrder(product)) ||
          (marketplaceMode === "Backup" &&
            (product.supplierType === "external" || product.tags.includes("backup supplier needed"))) ||
          (marketplaceMode === "Saved" && accountSavedSupplierNames.includes(product.supplierName));
        const matchesQuery =
          !normalized ||
          [
            product.name,
            product.category,
            product.supplierName,
            product.market,
            ...product.tags,
          ].some((value) => value.toLowerCase().includes(normalized));

        return matchesCategory && matchesMode && matchesQuery;
      })
      .sort((left, right) => {
        const rightFit = productBranchFit(right, selectedBranch).score;
        const leftFit = productBranchFit(left, selectedBranch).score;
        return (
          rightFit - leftFit ||
          supplierOptionScore(right, accountSavedSupplierNames) - supplierOptionScore(left, accountSavedSupplierNames)
        );
      });
  }, [
    accountSavedSupplierNames,
    marketplaceCategory,
    marketplaceMode,
    query,
    searchableMarketplaceProducts,
    selectedBranch,
  ]);

  const selectedMarketProduct =
    searchableMarketplaceProducts.find((product) => product.id === selectedMarketProductId) ??
    searchableMarketplaceProducts[0];

  const draftTotal = accountDraft.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const draftSuppliers = draftSuppliersFor(accountDraft, draftLookupContext);
  const draftSupplierSummary = draftSupplierLabelFor(accountDraft, draftLookupContext);
  const draftPackets = draftSupplierPacketsFor(accountDraft, draftLookupContext);
  const draftNeedsBudgetApproval =
    draftTotal > selectedBranch.approvalLimit || selectedBranchRemaining - draftTotal < 0;
  const draftApprovalRequest = approvalForDraft(accountApprovalQueue, selectedBranch, draftTotal);
  const draftPreflightChecks = orderDraftPreflightFor(
    accountDraft,
    draftPackets,
    selectedBranch,
    draftLookupContext,
    draftApprovalRequest,
  );
  const draftBlockingChecks = draftPreflightChecks.filter((check) => check.status === "blocked");
  const draftSendDisabled = !accountDraft.length || draftBlockingChecks.length > 0;
  const lowStockItems = accountCatalogProducts.filter((product) => product.stockStatus !== "available");
  const liveReplenishmentSignals = useMemo(
    () => deriveReplenishmentSignals(accountCatalogProducts, accountOrders, branchBook),
    [accountCatalogProducts, accountOrders, branchBook],
  );
  const accountRole = account?.role;
  const accountPriceBenchmarks = useMemo(
    () => (accountRole === "supplier" ? [] : priceBenchmarkQueue),
    [accountRole, priceBenchmarkQueue],
  );
  const livePurchaseInsights = useMemo(
    () => derivePurchaseInsights(accountOrders, accountCatalogProducts, accountPriceBenchmarks),
    [accountCatalogProducts, accountOrders, accountPriceBenchmarks],
  );
  const liveSupplierOffers = useMemo(
    () => deriveSupplierOffers({ catalogProducts: accountCatalogProducts, quoteRequests: accountQuoteQueue, savedSupplierNames: accountSavedSupplierNames }),
    [accountCatalogProducts, accountQuoteQueue, accountSavedSupplierNames],
  );
  const liveSupplierOpportunities = useMemo(
    () =>
      deriveSupplierOpportunities({
        catalogProducts: accountCatalogProducts,
        demandGaps: demandGapQueue,
        deliveryAreas: deliveryAreaQueue,
        fulfillmentOrders: accountFulfillmentQueue,
        quoteRequests: accountQuoteQueue,
        supplierSearchDemand: supplierDemandQueue,
      }),
    [
      accountCatalogProducts,
      accountFulfillmentQueue,
      accountQuoteQueue,
      demandGapQueue,
      deliveryAreaQueue,
      supplierDemandQueue,
    ],
  );
  const { restaurantSimpleDashboard, supplierSetupNeeded } = roleEntry;
  const liveAutomationTasks = useMemo(
    () =>
      deriveAutomationTasks({
        catalogProducts: accountCatalogProducts,
        connectorRequests: accountConnectorRequests,
        conversionTasks,
        deliveryAreas: roleHasRealData ? deliveryAreaQueue : [],
        discoveryRuns,
        externalRecords: accountExternalRecords,
        fulfillmentOrders: accountFulfillmentQueue,
        quoteRequests: accountQuoteQueue,
      }),
    [
      accountCatalogProducts,
      accountConnectorRequests,
      accountExternalRecords,
      accountFulfillmentQueue,
      accountQuoteQueue,
      conversionTasks,
      deliveryAreaQueue,
      discoveryRuns,
      role,
      roleHasRealData,
    ],
  );
  const liveMarketLanes = useMemo(
    () => deriveMarketLanes(networkSnapshot, coverageQueue, demandGapQueue),
    [coverageQueue, demandGapQueue, networkSnapshot],
  );
  const liveBosniaCoverageTargets = useMemo(
    () =>
      deriveBosniaCoverageTargets({
        coverageRecords: coverageQueue,
        demandGaps: demandGapQueue,
        externalRecords: accountExternalRecords,
        restaurantSupplierContacts,
        sourcingSprints: sourcingSprintQueue,
      }),
    [accountExternalRecords, coverageQueue, demandGapQueue, restaurantSupplierContacts, sourcingSprintQueue],
  );
  const liveWorkflowSignals = useMemo(
    () =>
      deriveWorkflowSignals({
        conversionTasks,
        fulfillmentOrders: accountFulfillmentQueue,
        messageThreads: accountMessages,
        orders: accountOrders,
        quoteRequests: accountQuoteQueue,
      }),
    [accountFulfillmentQueue, accountMessages, accountOrders, accountQuoteQueue, conversionTasks],
  );
  const liveTrustControls = useMemo(
    () =>
      deriveTrustControls({
        approvalRequests: accountApprovalQueue,
        auditEvents: auditQueue,
        connectorRequests: accountConnectorRequests,
        localizationRules: localizationQueue,
        workspaceUsers: workspaceUserQueue,
      }),
    [accountApprovalQueue, accountConnectorRequests, auditQueue, localizationQueue, workspaceUserQueue],
  );
  const liveIncidentQueue = useMemo(
    () =>
      deriveLiveIncidents({
        connectorRequests: accountConnectorRequests,
        coverageRecords: coverageQueue,
        deliveryAreas: deliveryAreaQueue,
        fulfillmentOrders: accountFulfillmentQueue,
        importedIncidents: incidentQueue,
        quoteRequests: accountQuoteQueue,
      }),
    [accountConnectorRequests, accountFulfillmentQueue, accountQuoteQueue, coverageQueue, deliveryAreaQueue, incidentQueue],
  );
  const liveIntegrationStatus = useMemo(
    () =>
      deriveIntegrationStatus({
        connectorRequests: accountConnectorRequests,
        importRuns: accountImportRuns,
        networkActionState,
      }),
    [accountConnectorRequests, accountImportRuns, networkActionState],
  );
  const issueCount = accountOrders.filter((order) => order.status === "issue").length;
  const openIncidents = liveIncidentQueue.filter((incident) => incident.status !== "resolved");
  const operationalNotifications = useMemo<OperationalNotification[]>(() => {
    const notifications: OperationalNotification[] = [];
    const openQuotes = accountQuoteQueue.filter((request) => isOpenQuoteStatus(request.status));
    const quotedRequests = accountQuoteQueue.filter((request) => isReadyQuoteStatus(request.status));
    const needsApproval = accountApprovalQueue.filter((request) => request.status === "needs_approval");
    const fulfillmentNeedsAction = accountFulfillmentQueue.filter(fulfillmentNeedsActionFor);
    const deliveryCapacityWatch = deliveryAreaQueue.filter((area) => area.status === "capacity_watch");
    const externalNeedsReview = externalRecords.filter((record) => record.status !== "converted");
    const coverageRisks = coverageQueue.filter((record) => record.risk !== "healthy");

    if (role === "restaurant") {
      draftBlockingChecks.forEach((check) => {
        notifications.push({
          id: `draft-${check.id}`,
          title: check.title,
          detail: check.detail,
          meta: check.value,
          severity: check.status === "blocked" ? "critical" : "warning",
          nav: "Orders",
          icon: check.icon,
          action: check.id === "budget" ? "request_approval" : undefined,
          actionLabel: check.id === "budget" ? "Request approval" : "Open list",
        });
      });

      quotedRequests.slice(0, 2).forEach((request) => {
        notifications.push({
          id: `quote-${request.id}`,
          title: `Quote ready: ${request.product}`,
          detail: `${request.supplierName ?? "Supplier"} offered ${request.budget}. Accept it when the terms fit today's list.`,
          meta: request.deliveryWindow ?? request.neededBy,
          severity: "success",
          nav: "Marketplace",
          icon: Send,
          action: "use_quote",
          actionLabel: "Accept offer",
          targetId: request.id,
        });
      });

      lowStockItems.slice(0, 2).forEach((product) => {
        notifications.push({
          id: `stock-${product.id}`,
          title: `${product.name} needs attention`,
          detail: `${product.supplier} is marked ${stockLabels[product.stockStatus].toLowerCase()}. Repeat confirmed supply or request a backup supplier.`,
          meta: `${product.lastOrderedKg} ${product.unit}`,
          severity: product.stockStatus === "unavailable" ? "critical" : "warning",
          nav: product.stockStatus === "unavailable" ? "Marketplace" : "Orders",
          icon: AlertTriangle,
          category: marketplaceCategoryForCatalogProduct(product),
          mode: "Backup",
          actionLabel: product.stockStatus === "unavailable" ? "Compare backup" : "Open alert",
          targetId: product.id,
        });
      });

      if (issueCount) {
        notifications.push({
          id: "orders-issues",
          title: `${issueCount} order issue${issueCount === 1 ? "" : "s"}`,
          detail: "Review issue orders before the next delivery window closes.",
          meta: "Needs restaurant decision",
          severity: "critical",
          nav: "Orders",
          icon: PackageCheck,
          actionLabel: "Open issues",
        });
      }
    }

    if (role === "supplier") {
      openQuotes.slice(0, 2).forEach((request) => {
        notifications.push({
          id: `supplier-quote-${request.id}`,
          title: `Price request: ${request.product}`,
          detail: `${request.buyer} needs ${request.quantity}. Send pricing, minimum order, and delivery terms.`,
          meta: request.neededBy,
          severity: "warning",
          nav: "Orders",
          icon: Send,
          action: "send_supplier_quote",
          actionLabel: "Send price",
          targetId: request.id,
        });
      });

      fulfillmentNeedsAction.slice(0, 2).forEach((order) => {
        notifications.push({
          id: `fulfillment-${order.id}`,
          title: `Move order ${order.id}`,
          detail: `${order.buyer}: ${order.nextAction}`,
          meta: formatEur(order.valueEur),
          severity: order.status === "needs_confirmation" ? "critical" : "warning",
          nav: "Orders",
          icon: Truck,
          action: "advance_fulfillment",
          actionLabel:
            order.status === "needs_confirmation"
              ? "Confirm"
              : order.status === "packed"
                ? "Dispatch"
                : "Advance",
          targetId: order.id,
        });
      });

      supplierDemandQueue.filter((demand) => demand.urgency !== "monitor").slice(0, 2).forEach((demand) => {
        notifications.push({
          id: `demand-${demand.id}`,
          title: `Restaurant lead: ${demand.product}`,
          detail: `${demand.buyerCount} restaurants searched in ${demand.market}. Create a lead while demand is hot.`,
          meta: formatEur(demand.avgBudgetEur),
          severity: demand.urgency === "now" ? "critical" : "warning",
          nav: "Suppliers",
          icon: TrendingUp,
          actionLabel: "Open lead",
          targetId: demand.id,
        });
      });

      lowStockItems.slice(0, 1).forEach((product) => {
        notifications.push({
          id: `catalog-${product.id}`,
          title: `Catalog status: ${product.name}`,
          detail: "Update supplier availability so buyers stop seeing stale stock.",
          meta: stockLabels[product.stockStatus],
          severity: product.stockStatus === "unavailable" ? "critical" : "warning",
          nav: "Suppliers",
          icon: Boxes,
          action: "mark_catalog_ready",
          actionLabel: "Mark ready",
          targetId: product.id,
        });
      });

      deliveryCapacityWatch.slice(0, 1).forEach((area) => {
        notifications.push({
          id: `delivery-capacity-${area.id}`,
          title: `Delivery capacity: ${area.market}`,
          detail: `${area.performance.toFixed(1)}% SLA${typeof area.activeOrders === "number" ? ` / ${area.activeOrders} active orders` : ""}. Review minimums before accepting more demand.`,
          meta: area.minimumOrder,
          severity: "warning",
          nav: "Suppliers",
          icon: MapPin,
          action: "toggle_delivery_capacity",
          actionLabel: "Update route",
          targetId: area.id,
        });
      });
    }

    if (role === "admin") {
      openIncidents.slice(0, 2).forEach((incident) => {
        notifications.push({
          id: `incident-${incident.id}`,
          title: incident.title,
          detail: `${incident.market}: ${incident.action}`,
          meta: incident.eta,
          severity: incident.severity === "critical" || incident.severity === "high" ? "critical" : "warning",
          nav: "Network Intel",
          icon: AlertTriangle,
          action: "advance_incident",
          actionLabel: incident.status === "active" ? "Mitigate" : "Resolve",
          targetId: incident.id,
        });
      });

      coverageRisks.slice(0, 2).forEach((record) => {
        notifications.push({
          id: `coverage-${record.id}`,
          title: `${record.market} / ${record.category}`,
          detail: record.nextMove,
          meta: `${record.suppliersLive}/${record.supplierTarget} suppliers`,
          severity: record.risk === "critical" ? "critical" : "warning",
          nav: "Network Intel",
          icon: Gauge,
          action: "open_coverage_sprint",
          actionLabel: "Open sprint",
          targetId: record.id,
        });
      });

      externalNeedsReview.slice(0, 2).forEach((record) => {
        notifications.push({
          id: `external-${record.id}`,
          title: `Verify ${record.name}`,
          detail: `${record.products.slice(0, 3).join(", ")} can become marketplace backup supply.`,
          meta: `${record.confidence}% confidence`,
          severity: record.status === "needs_review" ? "warning" : "info",
          nav: "Supplier Finder",
          mode: "Backup",
          icon: RadioTower,
          action: "advance_external_record",
          actionLabel:
            record.status === "invited"
              ? "Convert"
              : record.status === "ready_to_contact"
                ? "Invite"
                : "Review",
          targetId: record.id,
        });
      });
    }

    if (role !== "supplier") {
      needsApproval.slice(0, 1).forEach((request) => {
        notifications.push({
          id: `approval-${request.id}`,
          title: `Approval needed: ${request.branch}`,
          detail: `${formatMoney(request.amountBam)} KM / ${request.policy}`,
          meta: request.due,
          severity: "warning",
          nav: "Orders",
          icon: LockKeyhole,
          action: "approve_request",
          actionLabel: "Approve",
          targetId: request.id,
        });
      });
    }

    return notifications.slice(0, 8);
  }, [
    accountApprovalQueue,
    accountExternalRecords,
    accountFulfillmentQueue,
    accountQuoteQueue,
    coverageQueue,
    deliveryAreaQueue,
    draftBlockingChecks,
    issueCount,
    lowStockItems,
    openIncidents,
    role,
    supplierDemandQueue,
  ]);
  const urgentNotificationCount = operationalNotifications.filter(
    (notification) => notification.severity === "critical" || notification.severity === "warning",
  ).length;
  const activeNavItems = navItemsForRole(role);
  const effectiveNav = activeNavItems.some((item) => item.label === activeNav) ? activeNav : "Dashboard";
  const view = workspaceForNav(effectiveNav, role);
  const activePermissionOptions = permissionOptionsForRole(role);
  const activePermission =
    activePermissionOptions.find((permission) => permission.id === permissionRoleId) ??
    activePermissionOptions[0] ??
    permissionRoles[0];

  const showOverview = effectiveNav === "Dashboard";
  const showMarketplace = effectiveNav === "Marketplace";
  const showSupplierConsole = effectiveNav === "Suppliers";
  const showExternalDiscovery = effectiveNav === "Supplier Finder";
  const showNetworkIntel = effectiveNav === "Network Intel";
  const showMessages = effectiveNav === "Messages";
  const showAnalytics = effectiveNav === "Analytics";
  const showInvoices = effectiveNav === "Invoices";
  const showBuyerOrders = effectiveNav === "Orders" && role === "restaurant";
  const showSupplierOrders = effectiveNav === "Orders" && role === "supplier";
  const showDelivery = showBuyerOrders;
  const showOrders = showBuyerOrders;

  const globalSearchResults = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    if (normalized.length < 2) {
      return [];
    }

    const matches = (...values: Array<string | number | undefined | null>) =>
      values.some((value) => String(value ?? "").toLowerCase().includes(normalized));

    const results: GlobalSearchResult[] = [];

    if (role === "restaurant") {
      searchableMarketplaceProducts.forEach((product) => {
        if (
          matches(
            product.name,
            product.category,
            product.supplierName,
            product.market,
            product.priceRange,
            product.minOrder,
            product.deliveryWindow,
            ...product.tags,
          )
        ) {
          results.push({
            id: `product-${product.id}`,
            eyebrow: product.supplierType === "verified" ? "Product" : "External option",
            label: product.name,
            detail: `${product.supplierName} / ${product.priceRange} / ${product.deliveryWindow}`,
            meta: `${product.market} / ${product.availability.replace(/_/g, " ")}`,
            nav: "Marketplace",
            icon: ShoppingCart,
            productId: product.id,
            category: product.category,
            mode: marketplaceModeForProduct(product),
          });
        }
      });

      accountOrders.forEach((order) => {
        if (matches(order.id, order.buyer, order.supplier, order.city, order.status)) {
          results.push({
            id: `order-${order.id}`,
            eyebrow: "Order",
            label: order.id,
            detail: `${order.supplier} / ${order.buyer} / ${order.deliveryAt}`,
            meta: statusLabels[order.status],
            nav: "Orders",
            icon: PackageCheck,
          });
        }
      });
    }

    if (role === "supplier") {
      accountCatalogProducts.forEach((product) => {
        if (matches(product.name, product.category, product.supplier, product.stockStatus)) {
          results.push({
            id: `catalog-${product.id}`,
            eyebrow: "Catalog",
            label: product.name,
            detail: `${product.category} / ${formatMoney(product.price)} KM / ${product.unit}`,
            meta: stockLabels[product.stockStatus],
            nav: "Suppliers",
            icon: Boxes,
          });
        }
      });

      accountFulfillmentQueue.forEach((order) => {
        if (matches(order.id, order.buyer, order.deliveryWindow, order.nextAction, order.status)) {
          results.push({
            id: `fulfillment-${order.id}`,
            eyebrow: "Fulfillment",
            label: order.buyer,
            detail: `${order.id} / ${order.deliveryWindow} / ${order.nextAction}`,
            meta: order.status.replace(/_/g, " "),
            nav: "Orders",
            icon: Truck,
          });
        }
      });

      demandGapQueue.forEach((gap) => {
        if (matches(gap.category, gap.market, gap.recommendedAction)) {
          results.push({
            id: `demand-${gap.id}`,
            eyebrow: "Demand signal",
            label: gap.category,
            detail: `${gap.market} / ${formatCount(gap.failedSearches)} failed searches`,
            meta: formatEur(gap.revenueAtRiskEur),
            nav: "Supplier Finder",
            icon: TrendingUp,
          });
        }
      });
    }

    if (role === "admin") {
      liveIncidentQueue.forEach((incident) => {
        if (matches(incident.title, incident.market, incident.impact, incident.owner, incident.action, incident.status)) {
          results.push({
            id: `incident-${incident.id}`,
            eyebrow: "Incident",
            label: incident.title,
            detail: `${incident.market} / ${incident.impact}`,
            meta: incident.status,
            nav: "Network Intel",
            icon: AlertTriangle,
          });
        }
      });

      coverageQueue.forEach((record) => {
        if (matches(record.category, record.market, record.nextMove, record.risk)) {
          results.push({
            id: `coverage-${record.id}`,
            eyebrow: "Coverage",
            label: record.category,
            detail: `${record.market} / ${record.suppliersLive} of ${record.supplierTarget} suppliers live`,
            meta: `${record.fillRate.toFixed(1)}% fill rate`,
            nav: "Network Intel",
            icon: Gauge,
          });
        }
      });

      conversionTasks.forEach((task) => {
        if (matches(task.supplierName, task.market, task.owner, task.nextStep, task.stage)) {
          results.push({
            id: `conversion-${task.id}`,
            eyebrow: "Onboarding",
            label: task.supplierName,
            detail: `${task.market} / ${task.nextStep}`,
            meta: formatEur(task.revenuePotentialEur),
            nav: "Supplier Finder",
            icon: Users,
          });
        }
      });
    }

    const supplierSearchCards = new Map<
      string,
      { id: string; name: string; market: string; categories: string[]; meta: string; isExternal: boolean }
    >();

    accountCatalogProducts.forEach((product) => {
      const key = normalizeMarketText(product.supplier);
      const existing = supplierSearchCards.get(key);
      const categories = Array.from(new Set([...(existing?.categories ?? []), product.category]));

      supplierSearchCards.set(key, {
        id: `supplier-live-${slugForNav(product.supplier)}`,
        name: product.supplier,
        market: existing?.market ?? "Saved supplier catalog",
        categories,
        meta: `${accountCatalogProducts.filter((item) => item.supplier === product.supplier).length} live SKUs`,
        isExternal: false,
      });
    });

    accountExternalRecords.forEach((record) => {
      const key = normalizeMarketText(record.name);

      if (!supplierSearchCards.has(key)) {
        supplierSearchCards.set(key, {
          id: `supplier-external-${record.id}`,
          name: record.name,
          market: record.market,
          categories: Array.from(new Set(record.products.map(inferExternalCategory))),
          meta: role === "admin" ? `${record.confidence}% confidence / ${statusPillLabel(record.status)}` : "Ask first / contact found",
          isExternal: true,
        });
      }
    });

    Array.from(supplierSearchCards.values()).forEach((supplier) => {
      if (matches(supplier.name, supplier.market, supplier.categories.join(" "), supplier.meta)) {
        results.push({
          id: supplier.id,
          eyebrow: supplier.isExternal ? (role === "admin" ? "External supplier" : "Backup supplier") : "Supplier",
          label: supplier.name,
          detail: `${supplier.market} / ${supplier.categories.join(", ")}`,
          meta: supplier.meta,
          nav: role === "admin" ? "Supplier Finder" : role === "supplier" ? "Suppliers" : "Marketplace",
          icon: Factory,
        });
      }
    });

    accountExternalRecords.forEach((record) => {
      if (
        matches(
          record.name,
          record.market,
          record.source,
          record.deliveryHint,
          record.contactHint,
          record.status,
          record.products.join(" "),
        )
      ) {
        results.push({
          id: `external-${record.id}`,
          eyebrow: "External record",
          label: record.name,
          detail: `${record.market} / ${record.products.join(", ")}`,
          meta: `${record.confidence}% confidence`,
          nav: "Supplier Finder",
          icon: Search,
        });
      }
    });

    accountMessages.forEach((thread) => {
      if (matches(thread.subject, thread.participant, thread.market, thread.role, thread.preview, thread.status)) {
        results.push({
          id: `message-${thread.id}`,
          eyebrow: "Message",
          label: thread.subject,
          detail: `${thread.participant} / ${thread.market}`,
          meta: thread.status.replace(/_/g, " "),
          nav: "Messages",
          icon: MessageSquare,
        });
      }
    });

    accountQuoteQueue.forEach((request) => {
      if (matches(request.id, request.buyer, request.product, request.quantity, request.neededBy, request.status)) {
        results.push({
          id: `quote-${request.id}`,
          eyebrow: "Quote request",
          label: request.product,
          detail: `${request.buyer} / ${request.quantity} / ${request.neededBy}`,
          meta: request.status,
          nav: role === "supplier" ? "Orders" : "Marketplace",
          icon: ClipboardList,
        });
      }
    });

    return results.slice(0, 8);
  }, [
    accountCatalogProducts,
    accountExternalRecords,
    accountFulfillmentQueue,
    accountMessages,
    accountOrders,
    accountQuoteQueue,
    conversionTasks,
    coverageQueue,
    demandGapQueue,
    deliveryAreaQueue,
    liveIncidentQueue,
    query,
    role,
    searchableMarketplaceProducts,
    supplierDemandQueue,
  ]);

  useEffect(() => {
    if (
      showMarketplace &&
      visibleMarketplaceProducts.length > 0 &&
      !visibleMarketplaceProducts.some((product) => product.id === selectedMarketProductId)
    ) {
      setSelectedMarketProductId(visibleMarketplaceProducts[0].id);
    }
  }, [selectedMarketProductId, showMarketplace, visibleMarketplaceProducts]);

  function navigate(label: string) {
    setActiveNav(label);
    setMobileNavOpen(false);
    setNotificationsOpen(false);
  }

  function selectSearchResult(result: GlobalSearchResult) {
    if (result.category) {
      setMarketplaceCategory(result.category);
    }

    if (result.mode) {
      setMarketplaceMode(result.mode);
    }

    if (result.productId) {
      setSelectedMarketProductId(result.productId);
    }

    navigate(result.nav);
    setQuery("");
  }

  function selectNotification(notification: OperationalNotification) {
    if (notification.category) {
      setMarketplaceCategory(notification.category);
    }

    if (notification.mode) {
      setMarketplaceMode(notification.mode);
    }

    if (notification.productId) {
      setSelectedMarketProductId(notification.productId);
    }

    navigate(notification.nav);
  }

  function runNotificationAction(notification: OperationalNotification) {
    if (notification.category) {
      setMarketplaceCategory(notification.category);
    }

    if (notification.mode) {
      setMarketplaceMode(notification.mode);
    }

    if (notification.productId) {
      setSelectedMarketProductId(notification.productId);
    }

    switch (notification.action) {
      case "use_quote": {
        const request = quoteQueue.find((item) => item.id === notification.targetId);

        if (request) {
          addQuoteRequestToDraft(request);
        }
        break;
      }
      case "request_approval":
        requestDraftApproval();
        break;
      case "send_supplier_quote": {
        const request = quoteQueue.find((item) => item.id === notification.targetId);

        if (request) {
          sendSupplierQuote(request);
        }
        break;
      }
      case "advance_fulfillment":
        if (notification.targetId) {
          advanceFulfillmentOrder(notification.targetId);
        }
        break;
      case "mark_catalog_ready":
        if (notification.targetId) {
          markCatalogProductAvailable(notification.targetId);
        }
        break;
      case "toggle_delivery_capacity":
        if (notification.targetId) {
          toggleDeliveryAreaCapacity(notification.targetId);
        }
        break;
      case "advance_incident":
        if (notification.targetId) {
          advanceIncident(notification.targetId);
        }
        break;
      case "open_coverage_sprint": {
        const record = coverageQueue.find((item) => item.id === notification.targetId);

        if (record) {
          openCoverageSprint(record);
        }
        break;
      }
      case "advance_external_record": {
        const record = externalRecords.find((item) => item.id === notification.targetId);

        if (record) {
          advanceExternalRecord(record);
        }
        break;
      }
      case "approve_request":
        if (notification.targetId) {
          approveRequest(notification.targetId);
        }
        break;
      default:
        selectNotification(notification);
        return;
    }

    if (notification.action !== "use_quote") {
      navigate(notification.nav);
      return;
    }

    setNotificationsOpen(false);
  }

  function recordAudit(event: Omit<AuditEvent, "id" | "createdAt">) {
    void submitAuditEvent(event);
    setAuditQueue((events) => [
      {
        ...event,
        id: `audit-${Date.now()}-${events.length}`,
        createdAt: "Just now",
      },
      ...events,
    ]);
  }

  function recordBehavior(event: BehaviorEventInput, options: { once?: boolean } = {}) {
    if (options.once && hasRecordedBehaviorEvent(auditQueue, event.name)) {
      return;
    }

    recordAudit({
      actor: event.actor ?? roleLabels[event.role],
      action: behaviorActionFor(event.name),
      behaviorEvent: event.name,
      behaviorProperties: Object.fromEntries(
        Object.entries(event.properties ?? {}).filter(([, value]) => value !== undefined),
      ) as AuditEvent["behaviorProperties"],
      behaviorStep: event.step,
      entity: event.entity,
      market: event.market,
    });
  }

  async function importSupplierCatalogFile(file: File, supplierName: string) {
    const fallbackSupplierName = supplierName.trim();
    let result: Awaited<ReturnType<typeof parseSupplierCatalogFile>>;

    try {
      result = await parseSupplierCatalogFile(file, fallbackSupplierName);
    } catch (error) {
      const message = `Import not saved: ${importReadErrorMessage(error)}`;

      setImportStatus(message);
      setCatalogActionState(message);
      recordAudit({
        actor: roleLabels[role],
        action: "Price list import failed",
        entity: `${file.name} / file could not be read`,
        market: fallbackSupplierName || selectedBranch.market,
      });
      return false;
    }

    const organizationName =
      fallbackSupplierName || result.accepted[0]?.supplier || "Price list import";

    if (!result.rowCount) {
      const message = `Import not saved: ${file.name} has no product rows below the header.`;

      setImportStatus(message);
      setCatalogActionState(message);
      recordAudit({
        actor: roleLabels[role],
        action: "Price list import failed",
        entity: `${file.name} / empty file`,
        market: organizationName,
      });
      return false;
    }

    const apiResult = await submitImportBatch({
      fileName: file.name,
      importType: "supplier_catalog",
      market: selectedBranch.market,
      organizationName,
      role,
      acceptedRows: result.accepted,
      rejectedRows: result.rejected,
      rowCount: result.rowCount,
    });

    if (!apiResult.ok) {
      if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
        const message = `Import not saved: ${readableSaveError(apiResult.error)}`;

        setImportStatus(message);
        setCatalogActionState(message);
        recordAudit({
          actor: roleLabels[role],
          action: "Supplier catalog import blocked",
          entity: `${file.name} / ${readableSaveError(apiResult.error)}`,
          market: organizationName,
        });
        return false;
      }

      const savedProducts = result.accepted;
      const localRun: ImportRunRecord = {
        id: `import-run-local-${Date.now()}`,
        batchId: `local-catalog-${Date.now()}`,
        fileName: file.name,
        importType: "supplier_catalog",
        organizationName,
        role,
        mode: "local",
        rowCount: result.rowCount,
        acceptedCount: savedProducts.length,
        rejectedCount: result.rejected.length,
        rejectedRows: result.rejected,
        createdAt: currentActionTimestamp(),
      };
      const reviewNote = result.rejected.length
        ? `; ${result.rejected.length} row${result.rejected.length === 1 ? "" : "s"} need review`
        : "; no rows need review";
      const message = `${savedProducts.length} product${savedProducts.length === 1 ? "" : "s"} imported locally from ${file.name}${reviewNote}; backend sync needs connection`;

      setImportStatus(message);
      setCatalogActionState(message);
      setCatalogProducts((items) => mergeByKey(savedProducts, realSupplierDataActive ? items : [], productMergeKey));
      setImportRuns((runs) => mergeByKey([localRun], runs, (run) => run.batchId || run.id));
      saveLocalCatalogProducts(mergeByKey(savedProducts, readLocalCatalogProducts(), productMergeKey));
      saveLocalImportRuns(mergeByKey([localRun], readLocalImportRuns(), (run) => run.batchId || run.id));
      markRoleDataSeen("supplier");
      recordAudit({
        actor: roleLabels[role],
        action: "Imported supplier catalog locally",
        entity: `${file.name} / ${savedProducts.length} products / ${readableSaveError(apiResult.error)}`,
        market: organizationName,
      });
      recordBehavior(
        {
          entity: `${file.name} / ${savedProducts.length} products`,
          market: organizationName,
          name: "first_import",
          properties: {
            acceptedRows: savedProducts.length,
            importType: "supplier_catalog",
            rejectedRows: result.rejected.length,
            source: "local",
          },
          role: "supplier",
          step: "supplier_catalog_import",
        },
        { once: true },
      );
      return true;
    }

    const savedProducts = apiResult.products ?? [];

    if (savedProducts.length) {
      setCatalogProducts((items) => {
        const baseItems = realSupplierDataActive ? items : [];
        const importedKeys = new Set(
          savedProducts.map(
            (product) => `${normalizeMarketText(product.supplier)}::${normalizeMarketText(product.name)}`,
          ),
        );
        const existingItems = baseItems.filter(
          (product) =>
            !importedKeys.has(
              `${normalizeMarketText(product.supplier)}::${normalizeMarketText(product.name)}`,
            ),
        );

        return [...savedProducts, ...existingItems];
      });
    }

    const savedCatalogNote =
      typeof apiResult.catalogProductsSaved === "number"
        ? `; ${apiResult.catalogProductsSaved} saved to supplier catalog`
        : "";
    const reviewNote = result.rejected.length
      ? `; ${result.rejected.length} row${result.rejected.length === 1 ? "" : "s"} need review`
      : "; no rows need review";
    const message = `${savedProducts.length} product${savedProducts.length === 1 ? "" : "s"} saved from ${file.name}${reviewNote}${savedCatalogNote}`;

    setImportRuns((runs) => [
      {
        id: `import-run-${Date.now()}`,
        batchId: apiResult.batchId,
        fileName: file.name,
        importType: "supplier_catalog",
        organizationName,
        role,
        mode: apiResult.mode,
        rowCount: result.rowCount,
        acceptedCount: apiResult.acceptedCount,
        rejectedCount: apiResult.rejectedCount,
        rejectedRows: result.rejected,
        createdAt: currentActionTimestamp(),
      },
      ...runs,
    ]);
    setImportStatus(message);
    setCatalogActionState(message);
    markRoleDataSeen("supplier");
    recordAudit({
      actor: roleLabels[role],
      action: "Imported supplier catalog",
      entity: `${file.name} / ${savedProducts.length} products`,
      market: organizationName,
    });
    recordBehavior(
      {
        entity: `${file.name} / ${savedProducts.length} products`,
        market: organizationName,
        name: "first_import",
        properties: {
          acceptedRows: savedProducts.length,
          importType: "supplier_catalog",
          rejectedRows: result.rejected.length,
          source: apiResult.mode,
        },
        role: "supplier",
        step: "supplier_catalog_import",
      },
      { once: true },
    );
    return true;
  }

  async function saveManualSupplierProduct(input: ManualSupplierProductInput) {
    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot add supplier products`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked manual supplier product add",
        entity: input.name || "Manual product",
        market: input.supplierName || workspaceName,
      });
      return false;
    }

    const cleanName = input.name.trim();
    const cleanSupplier = input.supplierName.trim() || workspaceName || "Supplier account";
    const cleanUnit = input.unit.trim() || "unit";
    const cleanCategory = input.category.trim() || "General";
    const price = Math.max(0, Number(input.price.toFixed(2)) || 0);
    const stockQuantity = Math.max(0, Number(input.stockQuantity.toFixed(2)) || 0);

    if (!cleanName || !price) {
      setCatalogActionState("Add product needs a name and price.");
      return false;
    }

    const product: Product = {
      id: `manual-sku-${cleanSupplier.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${cleanName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}-${Date.now().toString().slice(-5)}`,
      name: cleanName,
      supplier: cleanSupplier,
      category: cleanCategory,
      unit: cleanUnit,
      price,
      currency: "BAM",
      stockStatus: stockQuantity <= 0 ? "unavailable" : stockQuantity < 10 ? "low" : "available",
      lastOrderedKg: stockQuantity || 1,
    };
    const result = await submitCatalogProduct(catalogProductInput(product));
    if (!result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      const message = `Product not saved: ${readableSaveError(result.error)}`;

      setCatalogActionState(message);
      setImportStatus(message);
      recordAudit({
        actor: roleLabels[role],
        action: "Supplier product save blocked",
        entity: `${cleanName} / ${readableSaveError(result.error)}`,
        market: cleanSupplier,
      });
      return false;
    }

    const savedProduct = result.ok ? result.product : product;
    const message = result.ok
      ? `${savedProduct.name} saved. Restaurants can now ask for price or order it.`
      : `${savedProduct.name} saved locally. Backend sync needs connection.`;

    setCatalogProducts((items) => {
      const nextItems = mergeByKey([savedProduct], realSupplierDataActive ? items : [], productMergeKey);
      saveLocalCatalogProducts(mergeByKey([savedProduct], readLocalCatalogProducts(), productMergeKey));
      return nextItems;
    });
    setCatalogActionState(message);
    setImportStatus(message);
    markRoleDataSeen("supplier");
    recordAudit({
      actor: roleLabels[role],
      action: result.ok ? "Added supplier product" : "Added supplier product locally",
      entity: `${savedProduct.name} / ${formatCurrencyAmount(savedProduct.price, savedProduct.currency)} / ${savedProduct.unit}`,
      market: cleanSupplier,
    });
    recordBehavior(
      {
        entity: `${savedProduct.name} / ${cleanSupplier}`,
        market: cleanSupplier,
        name: "first_item",
        properties: {
          category: savedProduct.category,
          source: result.ok ? "supabase" : "local",
        },
        role: "supplier",
        step: "supplier_catalog_item",
      },
      { once: true },
    );
    return true;
  }

  async function importBuyerOrderGuideFile(file: File) {
    let result: Awaited<ReturnType<typeof parseRestaurantOrderGuideFile>>;

    try {
      result = await parseRestaurantOrderGuideFile(file);
    } catch (error) {
      const message = `Import not saved: ${importReadErrorMessage(error)}`;

      setImportStatus(message);
      setSubmitState(message);
      recordAudit({
        actor: roleLabels[role],
        action: "Restaurant order guide import failed",
        entity: `${file.name} / file could not be read`,
        market: selectedBranch.market,
      });
      return false;
    }

    if (!result.rowCount) {
      const message = `Import not saved: ${file.name} has no rows below the header.`;

      setImportStatus(message);
      setSubmitState(message);
      recordAudit({
        actor: roleLabels[role],
        action: "Restaurant order guide import failed",
        entity: `${file.name} / empty file`,
        market: selectedBranch.market,
      });
      return false;
    }

    const apiResult = await submitImportBatch({
      fileName: file.name,
      importType: "restaurant_order_guide",
      market: selectedBranch.market,
      organizationName: selectedBranch.name,
      role,
      acceptedRows: result.accepted,
      rejectedRows: result.rejected,
      rowCount: result.rowCount,
    });

    if (!apiResult.ok) {
      if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
        const message = `Import not saved: ${readableSaveError(apiResult.error)}`;

        setImportStatus(message);
        setSubmitState(message);
        recordAudit({
          actor: roleLabels[role],
          action: "Restaurant order guide import blocked",
          entity: `${file.name} / ${readableSaveError(apiResult.error)}`,
          market: selectedBranch.market,
        });
        return false;
      }

      const savedGuideItems = result.accepted;
      const localRun: ImportRunRecord = {
        id: `import-run-local-${Date.now()}`,
        batchId: `local-order-guide-${Date.now()}`,
        fileName: file.name,
        importType: "restaurant_order_guide",
        organizationName: selectedBranch.name,
        role,
        mode: "local",
        rowCount: result.rowCount,
        acceptedCount: savedGuideItems.length,
        rejectedCount: result.rejected.length,
        rejectedRows: result.rejected,
        createdAt: currentActionTimestamp(),
      };
      const reviewNote = result.rejected.length
        ? `; ${result.rejected.length} row${result.rejected.length === 1 ? "" : "s"} need review`
        : "; no rows need review";
      const message = `${savedGuideItems.length} line${savedGuideItems.length === 1 ? "" : "s"} imported locally from ${file.name}${reviewNote}; backend sync needs connection`;

      setImportStatus(message);
      setSubmitState(message);
      setDraft((items) => mergeByKey(savedGuideItems, realRestaurantDataActive ? items : [], orderGuideMergeKey));
      setImportRuns((runs) => mergeByKey([localRun], runs, (run) => run.batchId || run.id));
      saveLocalOrderGuideItems(mergeByKey(savedGuideItems, readLocalOrderGuideItems(), orderGuideMergeKey));
      saveLocalImportRuns(mergeByKey([localRun], readLocalImportRuns(), (run) => run.batchId || run.id));
      markRoleDataSeen("restaurant", {
        orderOrMessage: true,
        supplier:
          restaurantKnownSupplierNames.length > 0 ||
          savedGuideItems.some((item) => (item.supplier ?? "").trim() && item.supplier !== "Supplier pending"),
      });
      recordAudit({
        actor: roleLabels[role],
        action: "Imported restaurant order guide locally",
        entity: `${file.name} / ${savedGuideItems.length} lines / ${readableSaveError(apiResult.error)}`,
        market: selectedBranch.market,
      });
      recordBehavior(
        {
          entity: `${file.name} / ${savedGuideItems.length} lines`,
          market: selectedBranch.market,
          name: "first_import",
          properties: {
            acceptedRows: savedGuideItems.length,
            importType: "restaurant_order_guide",
            rejectedRows: result.rejected.length,
            source: "local",
          },
          role: "restaurant",
          step: "restaurant_order_guide_import",
        },
        { once: true },
      );
      if (savedGuideItems.length) {
        recordBehavior(
          {
            entity: `${savedGuideItems[0]?.name ?? "Imported item"} / ${file.name}`,
            market: selectedBranch.market,
            name: "first_item",
            properties: {
              itemCount: savedGuideItems.length,
              source: "import",
            },
            role: "restaurant",
            step: "restaurant_first_buying_list_item",
          },
          { once: true },
        );
      }
      return true;
    }

    const savedGuideItems = apiResult.orderGuideItems ?? [];

    if (savedGuideItems.length) {
      setDraft((items) => {
        const baseItems = realRestaurantDataActive ? items : [];
        const importedKeys = new Set(
          savedGuideItems.map(
            (item) => `${normalizeMarketText(item.supplier ?? "")}::${normalizeMarketText(item.name)}`,
          ),
        );
        const existingItems = baseItems.filter(
          (item) =>
            !importedKeys.has(
              `${normalizeMarketText(item.supplier ?? "")}::${normalizeMarketText(item.name)}`,
            ),
        );

        return [...savedGuideItems, ...existingItems];
      });
    }

    const savedGuideNote =
      typeof apiResult.orderGuideItemsSaved === "number"
        ? `; ${apiResult.orderGuideItemsSaved} saved to order guide`
        : "";
    const reviewNote = result.rejected.length
      ? `; ${result.rejected.length} row${result.rejected.length === 1 ? "" : "s"} need review`
      : "; no rows need review";
    const message = `${savedGuideItems.length} line${savedGuideItems.length === 1 ? "" : "s"} saved from ${file.name}${reviewNote}${savedGuideNote}`;

    setImportRuns((runs) => [
      {
        id: `import-run-${Date.now()}`,
        batchId: apiResult.batchId,
        fileName: file.name,
        importType: "restaurant_order_guide",
        organizationName: selectedBranch.name,
        role,
        mode: apiResult.mode,
        rowCount: result.rowCount,
        acceptedCount: apiResult.acceptedCount,
        rejectedCount: apiResult.rejectedCount,
        rejectedRows: result.rejected,
        createdAt: currentActionTimestamp(),
      },
      ...runs,
    ]);
    setImportStatus(message);
    setSubmitState(message);
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier:
        restaurantKnownSupplierNames.length > 0 ||
        savedGuideItems.some((item) => (item.supplier ?? "").trim() && item.supplier !== "Supplier pending"),
    });
    recordAudit({
      actor: roleLabels[role],
      action: "Imported restaurant order guide",
      entity: `${file.name} / ${savedGuideItems.length} lines`,
      market: selectedBranch.market,
    });
    recordBehavior(
      {
        entity: `${file.name} / ${savedGuideItems.length} lines`,
        market: selectedBranch.market,
        name: "first_import",
        properties: {
          acceptedRows: savedGuideItems.length,
          importType: "restaurant_order_guide",
          rejectedRows: result.rejected.length,
          source: apiResult.mode,
        },
        role: "restaurant",
        step: "restaurant_order_guide_import",
      },
      { once: true },
    );
    if (savedGuideItems.length) {
      recordBehavior(
        {
          entity: `${savedGuideItems[0]?.name ?? "Imported item"} / ${file.name}`,
          market: selectedBranch.market,
          name: "first_item",
          properties: {
            itemCount: savedGuideItems.length,
            source: "import",
          },
          role: "restaurant",
          step: "restaurant_first_buying_list_item",
        },
        { once: true },
      );
    }
    return true;
  }

async function saveConnectorRequest(input: SubmitIntegrationRequestInput) {
    const result = await submitIntegrationRequest(input);

    if (!result.ok) {
      if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
        const message = `Connection request not saved: ${readableSaveError(result.error)}`;

        setConnectorStatus(message);
        recordAudit({
          actor: roleLabels[role],
          action: "Connector setup request blocked",
          entity: `${input.providerName} / ${readableSaveError(result.error)}`,
          market: input.organizationName,
        });
        return;
      }

      const localRequest = makeLocalConnectorRequest(input);
      const message = `${input.providerName} connector saved locally. It will stay pending until credentials and a test sync are verified.`;

      setConnectorStatus(message);
      setConnectorRequests((requests) => {
        const nextRequests = mergeByKey([localRequest], requests, (request) => request.requestId || request.id);
        saveLocalConnectorRequests(nextRequests.filter((request) => request.mode === "local"));
        return nextRequests;
      });
      markRoleDataSeen(input.role);
      recordAudit({
        actor: roleLabels[role],
        action: "Saved connector setup request locally",
        entity: `${input.providerName} / ${input.syncTarget}`,
        market: input.organizationName,
      });
      return;
    }

    const status = result.status.replace("_", " ");
    const message = `${input.providerName} ${input.syncTarget} connector saved as ${status}. Mark connected only after credentials and a test sync are verified.`;

    setConnectorStatus(message);
    setConnectorRequests((requests) => [
      result.connectorRequest,
      ...requests,
    ]);
    markRoleDataSeen(input.role);
    recordAudit({
      actor: roleLabels[role],
      action: "Saved connector setup request",
      entity: `${input.providerName} / ${input.syncTarget}`,
      market: input.organizationName,
    });
  }

  async function saveDoneForYouSetupRequest(input: SubmitDoneForYouSetupRequestInput) {
    const result = await submitDoneForYouSetupRequest(input);
    const organizationName = account?.organizationName ?? selectedBranch.name;

    if (!result.ok) {
      if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
        const message = `Done-for-you setup request not saved: ${readableSaveError(result.error)}`;

        setImportStatus(message);
        recordAudit({
          actor: roleLabels[role],
          action: "Done-for-you setup request blocked",
          entity: readableSaveError(result.error),
          market: input.city || selectedBranch.market,
        });
        return;
      }

      const localRequest = makeLocalDoneForYouSetupRequest(input, organizationName);
      setDoneForYouSetupRequests((requests) => {
        const nextRequests = mergeByKey([localRequest], requests, (request) => request.requestId || request.id);
        saveLocalDoneForYouSetupRequests(nextRequests.filter((request) => request.requestId.startsWith("local-")));
        return nextRequests;
      });
      setImportStatus("Done-for-you setup request saved locally for VELEO operator follow-up.");
      markRoleDataSeen("restaurant");
      recordAudit({
        actor: roleLabels[role],
        action: "Saved done-for-you setup request locally",
        entity: input.branchName || organizationName,
        market: input.city || selectedBranch.market,
      });
      return;
    }

    setDoneForYouSetupRequests((requests) => [result.request, ...requests]);
    setImportStatus("Done-for-you setup request saved. VELEO can build supplier contacts and order habits from it.");
    markRoleDataSeen("restaurant");
    recordAudit({
      actor: roleLabels[role],
      action: "Saved done-for-you setup request",
      entity: result.request.branchName || result.request.organizationName,
      market: result.request.city || selectedBranch.market,
    });
  }

  function applyDoneForYouConversionLocally(
    request: DoneForYouSetupRequest,
    supplierContacts: DoneForYouConversionContact[],
    orderItems: OrderItem[],
    status: DoneForYouSetupRequest["status"],
    operatorNote: string,
  ) {
    const branchName = request.branchName || request.organizationName || selectedBranch.name;
    const timestamp = currentActionTimestamp();
    const localContacts: RestaurantSupplierContact[] = supplierContacts.map((contact, index) => ({
      branchName,
      category: contact.category,
      contact: contact.contactValue,
      contactPerson: contact.contactPerson,
      deliveryArea: contact.deliveryArea || request.city || selectedBranch.market,
      email: contact.email,
      id: `supplier-contact-${slugForNav(contact.name)}-${Date.now()}-${index}`,
      name: contact.name,
      phone: contact.phone,
      preferredChannel: contact.preferredChannel,
      relationshipStatus: "needs_review",
      source: "operator_added",
      usualDays: contact.usualDays,
      usualOrderText: contact.usualOrderText,
      viber: contact.viber,
      whatsapp: contact.whatsapp,
      createdAt: timestamp,
      updatedAt: timestamp,
    }));
    const convertedSupplierNames = supplierContacts.map((contact) => contact.name).filter(Boolean);
    const updatedRequest: DoneForYouSetupRequest = {
      ...request,
      status,
      operatorNote,
      updatedAt: new Date().toISOString(),
    };

    if (localContacts.length) {
      setRestaurantSupplierContacts((contacts) =>
        mergeByKey(
          localContacts,
          contacts,
          (contact) => `${normalizeMarketText(contact.branchName)}::${normalizeMarketText(contact.name)}`,
        ),
      );
      setSavedSupplierNames((names) =>
        Array.from(
          new Map(
            [...convertedSupplierNames, ...names]
              .filter(Boolean)
              .map((name) => [normalizeMarketText(name), name] as const),
          ).values(),
        ),
      );
    }

    if (orderItems.length) {
      setDraft((items) => {
        const nextItems = mergeByKey(orderItems, realRestaurantDataActive ? items : [], orderGuideMergeKey);
        saveLocalOrderGuideItems(mergeByKey(orderItems, readLocalOrderGuideItems(), orderGuideMergeKey));
        return nextItems;
      });
    }

    setDoneForYouSetupRequests((requests) => {
      const nextRequests = requests.map((item) => (item.requestId === request.requestId ? updatedRequest : item));
      saveLocalDoneForYouSetupRequests(nextRequests.filter((item) => item.requestId.startsWith("local-")));
      return nextRequests;
    });
    markRoleDataSeen("restaurant", {
      orderOrMessage: orderItems.length > 0 || draft.length > 0,
      supplier: supplierContacts.length > 0 || restaurantKnownSupplierNames.length > 0,
    });
  }

  async function convertDoneForYouSetupIntoWorkspace(request: DoneForYouSetupRequest) {
    const supplierContacts = doneForYouSupplierContactsForRequest(request);
    const orderItems = doneForYouOrderItemsForRequest(request);
    const status: DoneForYouSetupRequest["status"] = orderItems.length ? "orders_created" : "contacts_created";
    const operatorNote = [
      "Converted by VELEO operator from WhatsApp/Viber intake.",
      `${supplierContacts.length} supplier contact${supplierContacts.length === 1 ? "" : "s"} created or updated.`,
      `${orderItems.length} repeat order line${orderItems.length === 1 ? "" : "s"} created.`,
    ].join(" ");

    if (!supplierContacts.length && !orderItems.length) {
      setImportStatus("Nothing converted: add supplier contact lines or usual order lines to the setup intake first.");
      return;
    }

    if (request.requestId.startsWith("local-")) {
      applyDoneForYouConversionLocally(request, supplierContacts, orderItems, status, operatorNote);
      setImportStatus(
        `${request.branchName || request.organizationName} setup converted locally: ${supplierContacts.length} supplier contacts / ${orderItems.length} repeat order lines.`,
      );
      recordAudit({
        actor: roleLabels[role],
        action: "Converted done-for-you setup locally",
        entity: request.branchName || request.organizationName,
        market: request.city || selectedBranch.market,
      });
      return;
    }

    const result = await convertDoneForYouSetupRequest({
      requestId: request.requestId,
      orderItems: orderItems.map((item) => ({
        category: item.category,
        name: item.name,
        note: item.note,
        quantity: item.quantity,
        supplier: item.supplier,
        unit: item.unit,
        unitPrice: item.unitPrice,
      })),
      supplierContacts,
    });

    if (!result.ok) {
      if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
        setImportStatus(`Done-for-you setup not converted: ${readableSaveError(result.error)}`);
        return;
      }

      applyDoneForYouConversionLocally(request, supplierContacts, orderItems, status, operatorNote);
      setImportStatus(
        `${request.branchName || request.organizationName} setup converted locally because live save failed: ${readableSaveError(result.error)}.`,
      );
      recordAudit({
        actor: roleLabels[role],
        action: "Converted done-for-you setup locally after API fallback",
        entity: `${request.branchName || request.organizationName} / ${readableSaveError(result.error)}`,
        market: request.city || selectedBranch.market,
      });
      return;
    }

    applyDoneForYouConversionLocally(
      result.request,
      supplierContacts,
      orderItems,
      result.status,
      result.request.operatorNote ?? operatorNote,
    );
    setDoneForYouSetupRequests((requests) =>
      requests.map((item) => (item.requestId === result.request.requestId ? result.request : item)),
    );
    setImportStatus(
      `${result.request.branchName || result.request.organizationName} setup converted: ${result.contactsCreated ?? supplierContacts.length} supplier contacts / ${result.orderGuideItemsCreated ?? orderItems.length} repeat order lines.`,
    );
    recordAudit({
      actor: roleLabels[role],
      action: "Converted done-for-you setup",
      entity: result.request.branchName || result.request.organizationName,
      market: result.request.city || selectedBranch.market,
    });
  }

  async function advanceDoneForYouSetupRequest(request: DoneForYouSetupRequest) {
    const nextStatusFor: Record<DoneForYouSetupRequest["status"], DoneForYouSetupRequest["status"]> = {
      contacts_created: "orders_created",
      done: "done",
      in_review: "contacts_created",
      new: "in_review",
      orders_created: "done",
    };
    const nextStatus = nextStatusFor[request.status];
    const operatorNote =
      nextStatus === "in_review"
        ? "VELEO operator picked up the WhatsApp/Viber setup intake."
        : nextStatus === "contacts_created"
          ? "Supplier contacts created or updated from the intake."
          : nextStatus === "orders_created"
            ? "Usual order habits created from the intake."
            : "Done-for-you setup completed.";

    if (request.requestId.startsWith("local-")) {
      const nextRequest = {
        ...request,
        status: nextStatus,
        operatorNote,
        updatedAt: new Date().toISOString(),
      };

      setDoneForYouSetupRequests((requests) => {
        const nextRequests = requests.map((item) => (item.requestId === request.requestId ? nextRequest : item));
        saveLocalDoneForYouSetupRequests(nextRequests.filter((item) => item.requestId.startsWith("local-")));
        return nextRequests;
      });
      setImportStatus(`${request.branchName || request.organizationName} setup marked ${nextStatus.replace(/_/g, " ")} locally.`);
      return;
    }

    const result = await updateDoneForYouSetupRequest({
      requestId: request.requestId,
      status: nextStatus,
      operatorNote,
    });

    if (!result.ok) {
      setImportStatus(`Done-for-you setup status not saved: ${readableSaveError(result.error)}`);
      return;
    }

    setDoneForYouSetupRequests((requests) =>
      requests.map((item) => (item.requestId === request.requestId ? result.request : item)),
    );
    setImportStatus(`${result.request.branchName || result.request.organizationName} setup marked ${result.status.replace(/_/g, " ")}.`);
  }

  function signOut() {
    void authSignOut();
    clearLegacySessionRole();
    setAccount(null);
    setSessionRole(null);
    setMobileNavOpen(false);
    setPublicPage("landing");
    window.history.replaceState(null, "", "#/");
  }

  function showLogin() {
    setPublicPage("login");
    window.history.replaceState(null, "", "#/login");
  }

  function showLanding() {
    setPublicPage("landing");
    window.history.replaceState(null, "", "#/");
  }

  async function provisionBranchBuyerSeat() {
    const branch = selectedBranch;

    if (!activePermission.canManageWorkspace) {
      setSubmitState(`${activePermission.role} cannot create workspace seats`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked workspace seat creation",
        entity: branch.name,
        market: branch.market,
      });
      return;
    }

    const branchParts = branch.name.split("/");
    const branchLabel = branchParts[branchParts.length - 1]?.trim() || branch.name;
    const roleId = role === "supplier" ? "supplier-sales" : "buyer";
    const permission = permissionRoles.find((item) => item.id === roleId) ?? permissionRoles[0];
    const suffix = Date.now().toString().slice(-5);
    const emailBranch = branchLabel.toLowerCase().replace(/[^a-z0-9]+/g, ".").replace(/^\.+|\.+$/g, "");
    const nextUser: WorkspaceUser = {
      id: `user-seat-${suffix}`,
      name: `${branchLabel} ${permission.role}`,
      email: `${emailBranch || "branch"}.${suffix}@veleo.access`,
      branchId: branch.id,
      roleId: permission.id,
      status: "invited",
      lastSeen: "Invite created just now",
      focus: "Confirm access before the next supplier packet is sent",
    };
    const userResult = await submitWorkspaceUser({
      branchId: branch.id,
      branchName: branch.name,
      email: nextUser.email,
      focus: nextUser.focus,
      lastSeen: nextUser.lastSeen,
      market: branch.market,
      name: nextUser.name,
      organizationName: branch.name.split("/")[0]?.trim() || branch.name,
      permissionRoleId: nextUser.roleId,
      role,
      status: nextUser.status,
    });

    if (!userResult.ok) {
      setSubmitState(`Workspace access not saved: ${readableSaveError(userResult.error)}`);
      recordAudit({
        actor: "Workspace admin",
        action: "Workspace seat creation failed",
        entity: `${branch.name} / ${nextUser.email} / ${readableSaveError(userResult.error)}`,
        market: branch.market,
      });
      return;
    }

    const savedUser: WorkspaceUser = { ...userResult.user, branchId: branch.id };
    const accessMessage: MessageThread = {
      id: `msg-access-${savedUser.id}-${Date.now()}`,
      participant: savedUser.name,
      role: role === "supplier" ? "supplier" : "buyer",
      market: branch.market,
      subject: `Access prepared: ${branch.name}`,
      preview: `${permission.role} access is ready for ${branch.name}. Confirm the person and permissions before they place or approve orders.`,
      status: "waiting",
      updatedAt: "Just now",
      lastAction: "Workspace seat created",
    };
    void submitMessageThread(accessMessage);

    setWorkspaceUserQueue((users) => [savedUser, ...users]);
    setBranchBook((branches) =>
      branches.map((item) =>
        item.id === branch.id ? { ...item, users: item.users + 1 } : item,
      ),
    );
    setMessageQueue((threads) => [
      accessMessage,
      ...threads,
    ]);
    recordAudit({
      actor: "Workspace admin",
      action: "Prepared branch access",
      entity: `${branch.name} / ${permission.role} / ${savedUser.email}`,
      market: branch.market,
    });
    setSubmitState(`${permission.role} access prepared for ${branchLabel}`);
  }

  async function toggleWorkspaceUserStatus(userId: string) {
    const user = workspaceUserQueue.find((item) => item.id === userId);

    if (!user) {
      return;
    }

    if (!activePermission.canManageWorkspace) {
      setSubmitState(`${activePermission.role} cannot change workspace user access`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked workspace user status change",
        entity: user.name,
        market: "Workspace",
      });
      return;
    }

    const nextStatus: WorkspaceUser["status"] = user.status === "active" ? "suspended" : "active";
    const previousCounts = user.status !== "suspended";
    const nextCounts = nextStatus !== "suspended";
    const branch = branchBook.find((item) => item.id === user.branchId);
    const permission = permissionRoles.find((item) => item.id === user.roleId);
    const nextUser: WorkspaceUser = {
      ...user,
      status: nextStatus,
      lastSeen: nextStatus === "active" ? "Reactivated just now" : "Suspended just now",
      focus:
        nextStatus === "active"
          ? "Can operate within assigned branch and permission scope"
          : "Access is paused until an admin reactivates it",
    };
    const userResult = await submitWorkspaceUser({
      branchId: branch?.id ?? user.branchId,
      branchName: branch?.name ?? user.branchId,
      email: nextUser.email,
      focus: nextUser.focus,
      lastSeen: nextUser.lastSeen,
      market: branch?.market ?? "Workspace",
      name: nextUser.name,
      organizationName: branch?.name.split("/")[0]?.trim() || branch?.name || "Workspace organization",
      permissionRoleId: nextUser.roleId,
      role,
      status: nextUser.status,
    });

    if (!userResult.ok) {
      setSubmitState(`Workspace user not saved: ${readableSaveError(userResult.error)}`);
      recordAudit({
        actor: "Workspace admin",
        action: "Workspace user status save failed",
        entity: `${user.name} / ${readableSaveError(userResult.error)}`,
        market: branch?.market ?? "Workspace",
      });
      return;
    }

    setWorkspaceUserQueue((users) =>
      users.map((item) =>
        item.id === userId
          ? userResult.user
          : item,
      ),
    );

    if (previousCounts !== nextCounts) {
      setBranchBook((branches) =>
        branches.map((item) =>
          item.id === user.branchId
            ? { ...item, users: Math.max(0, item.users + (nextCounts ? 1 : -1)) }
            : item,
        ),
      );
    }

    recordAudit({
      actor: "Workspace admin",
      action: nextStatus === "active" ? "Reactivated workspace user" : "Suspended workspace user",
      entity: `${user.name} / ${permission?.role ?? user.roleId}`,
      market: branch?.market ?? "Workspace",
    });
    setSubmitState(`${user.name} ${nextStatus}`);
  }

  async function adjustBranchBudgetRule(
    branchId: string,
    field: "weeklyBudget" | "approvalLimit",
    delta: number,
  ) {
    const branch = branchBook.find((item) => item.id === branchId);

    if (!branch) {
      return;
    }

    if (!activePermission.canManageBudgetControls) {
      setSubmitState(`${activePermission.role} cannot change branch budget controls`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked branch budget rule change",
        entity: `${branch.name} / ${field}`,
        market: branch.market,
      });
      return;
    }

    const nextWeeklyBudget =
      field === "weeklyBudget"
        ? Math.max(branch.spentThisWeek, branch.weeklyBudget + delta)
        : branch.weeklyBudget;
    const nextApprovalLimit =
      field === "approvalLimit"
        ? Math.max(100, Math.min(nextWeeklyBudget, branch.approvalLimit + delta))
        : Math.min(branch.approvalLimit, nextWeeklyBudget);
    const updatedBranch: BranchAccount = {
      ...branch,
      weeklyBudget: nextWeeklyBudget,
      approvalLimit: nextApprovalLimit,
      status: branchStatusFor({ weeklyBudget: nextWeeklyBudget, spentThisWeek: branch.spentThisWeek }),
    };
    const branchResult = await submitBranchBudget({
      approvalLimit: updatedBranch.approvalLimit,
      branchId: updatedBranch.id,
      branchName: updatedBranch.name,
      market: updatedBranch.market,
      organizationName: updatedBranch.name.split("/")[0]?.trim() || updatedBranch.name,
      role,
      weeklyBudget: updatedBranch.weeklyBudget,
    });

    if (!branchResult.ok) {
      setSubmitState(`Branch budget not saved: ${readableSaveError(branchResult.error)}`);
      recordAudit({
        actor: "Workspace admin",
        action: "Branch budget save failed",
        entity: `${branch.name} / ${field} / ${readableSaveError(branchResult.error)}`,
        market: branch.market,
      });
      return;
    }

    const changedValue =
      field === "weeklyBudget"
        ? formatBranchMoney(updatedBranch.weeklyBudget, updatedBranch, { includeBase: true })
        : formatBranchMoney(updatedBranch.approvalLimit, updatedBranch, { includeBase: true });

    setBranchBook((branches) =>
      branches.map((item) =>
        item.id === branchId
          ? {
              ...updatedBranch,
              id: branchResult.branch.id,
              name: branchResult.branch.name,
              market: branchResult.branch.market,
              weeklyBudget: branchResult.branch.weeklyBudget,
              approvalLimit: branchResult.branch.approvalLimit,
            }
          : item,
      ),
    );
    if (selectedBranchId === branchId) {
      setSelectedBranchId(branchResult.branch.id);
    }
    setWorkspaceUserQueue((users) =>
      users.map((user) => (user.branchId === branchId ? { ...user, branchId: branchResult.branch.id } : user)),
    );
    const branchMessage: MessageThread = {
      id: `msg-branch-budget-${branch.id}-${Date.now()}`,
      participant: "Finance control",
      role: "buyer",
      market: branch.market,
      subject: `Branch rule updated: ${branch.name}`,
      preview: `${field === "weeklyBudget" ? "Weekly budget" : "Approval limit"} changed to ${changedValue}. Buyers will use the updated rule on the next supplier packet.`,
      status: "resolved",
      updatedAt: "Just now",
      lastAction: "Branch budget rule changed",
    };
    void submitMessageThread(branchMessage);
    setMessageQueue((threads) => [
      branchMessage,
      ...threads,
    ]);
    recordAudit({
      actor: "Workspace admin",
      action: field === "weeklyBudget" ? "Updated branch weekly budget" : "Updated branch approval limit",
      entity: `${branch.name} / ${changedValue}`,
      market: branch.market,
    });
    setSubmitState(`${branch.name} ${field === "weeklyBudget" ? "budget" : "approval limit"} updated`);
  }

  function updateQuantity(productId: string, nextQuantity: number) {
    setDraft((items) =>
      items
        .map((item) =>
          item.productId === productId ? { ...item, quantity: Math.max(0, nextQuantity) } : item,
        )
        .filter((item) => item.quantity > 0),
    );
  }

  function assignDraftItemSupplier(productId: string, supplierName: string) {
    const cleanSupplier = supplierName.trim() || "Supplier pending";
    const canonicalSupplier =
      cleanSupplier === "Supplier pending" ? cleanSupplier : canonicalSupplierName(cleanSupplier, draftLookupContext);
    const draftItemName = draft.find((item) => item.productId === productId)?.name ?? "Item";

    setDraft((items) =>
      items.map((item) =>
        item.productId === productId
          ? {
              ...item,
              supplier: canonicalSupplier,
              source: item.source ?? "manual",
            }
          : item,
      ),
    );
    setSubmitState(
      canonicalSupplier === "Supplier pending"
        ? "Assign a supplier contact before sending"
        : `${draftItemName} assigned to ${canonicalSupplier}`,
    );
    markRoleDataSeen("restaurant", {
      orderOrMessage: draft.length > 0,
      supplier: canonicalSupplier !== "Supplier pending" || restaurantKnownSupplierNames.length > 0,
    });
  }

  function addReplenishmentSignal(signal: ReplenishmentSignal) {
    if (!activePermission.canSubmitOrders) {
      setSubmitState(`${activePermission.role} cannot add replenishment items to today's list`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked replenishment draft add",
        entity: `${signal.productName} / ${signal.branch}`,
        market: signal.branch,
      });
      return;
    }

    setDraft((items) => {
      const baseItems = realRestaurantDataActive ? items : [];
      const current = baseItems.find((item) => item.productId === signal.productId);

      if (current) {
        return baseItems.map((item) =>
          item.productId === signal.productId
            ? {
                ...enrichDraftItem(item, draftLookupContext, { supplier: signal.supplier, source: "forecast" }),
                quantity: item.quantity + signal.forecastQuantity,
              }
            : item,
        );
      }

      const catalogMatch = catalogProducts.find((product) => product.id === signal.productId);

      return [
        ...baseItems,
        {
          productId: signal.productId,
          name: signal.productName,
          quantity: signal.forecastQuantity,
          unit: signal.unit,
          unitPrice: signal.unitPrice,
          supplier: signal.supplier,
          category: catalogMatch?.category,
          source: "forecast",
        },
      ];
    });
    setReplenishmentState(`${signal.productName} added to today's list`);
    setSubmitState("List updated from forecast");
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: Boolean(restaurantKnownSupplierNames.length || (signal.supplier && signal.supplier !== "Supplier pending")),
    });
    recordBehavior(
      {
        entity: `${signal.productName} / ${signal.supplier}`,
        market: selectedBranch.market,
        name: "first_item",
        properties: {
          source: "forecast",
          unit: signal.unit,
        },
        role: "restaurant",
        step: "restaurant_first_buying_list_item",
      },
      { once: true },
    );
  }

  function addQuickOrderItem(item: QuickOrderItemInput) {
    if (!activePermission.canSubmitOrders) {
      setSubmitState(`${activePermission.role} cannot add manual items to today's list`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked manual draft add",
        entity: `${item.name} / ${selectedBranch.name}`,
        market: selectedBranch.market,
      });
      return;
    }

    const cleanName = item.name.trim();
    const rawSupplier = item.supplier.trim() || "Supplier pending";
    const cleanSupplier =
      rawSupplier === "Supplier pending" ? rawSupplier : canonicalSupplierName(rawSupplier, draftLookupContext);
    const cleanUnit = item.unit.trim() || "unit";
    const cleanNote = item.note?.trim();

    if (!cleanName || item.quantity <= 0 || item.unitPrice < 0) {
      setSubmitState("Add item needs a name, quantity, and valid price");
      return;
    }

    const productId = `manual-${cleanName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${cleanSupplier
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")}-${cleanUnit.toLowerCase()}`;
    const draftItem: OrderItem = {
      productId,
      name: cleanName,
      quantity: Number(item.quantity.toFixed(2)),
      unit: cleanUnit,
      unitPrice: Number(item.unitPrice.toFixed(2)),
      supplier: cleanSupplier,
      category: item.category?.trim() || "Manual item",
      note: cleanNote || undefined,
      source: "manual",
    };

    setDraft((items) => {
      const baseItems = realRestaurantDataActive ? items : [];
      const current = baseItems.find((existing) => existing.productId === productId);

      if (!current) {
        return [...baseItems, draftItem];
      }

      return baseItems.map((existing) =>
        existing.productId === productId
          ? {
              ...existing,
              quantity: Number((existing.quantity + draftItem.quantity).toFixed(2)),
              unitPrice: draftItem.unitPrice,
              supplier: draftItem.supplier,
              category: draftItem.category,
              note: draftItem.note,
              source: "manual",
            }
          : existing,
      );
    });
    setSubmitState(`${cleanName} added to today's order`);
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: restaurantKnownSupplierNames.length > 0 || cleanSupplier !== "Supplier pending",
    });
    recordBehavior(
      {
        entity: `${cleanName} / ${cleanSupplier}`,
        market: selectedBranch.market,
        name: "first_item",
        properties: {
          source: "manual",
          unit: cleanUnit,
        },
        role: "restaurant",
        step: "restaurant_first_buying_list_item",
      },
      { once: true },
    );
  }

  async function addManualInvoice(input: QuickInvoiceInput) {
    if (!activePermission.canExportFinance) {
      setCatalogActionState(`${activePermission.role} can see invoices but cannot add finance records`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked manual invoice capture",
        entity: `${input.supplierName || "Supplier"} / ${selectedBranch.name}`,
        market: selectedBranch.market,
      });
      return;
    }

    const cleanSupplier = input.supplierName.trim() || "Supplier pending";
    const cleanOrderId = input.orderId.trim();
    const due = input.due.trim() || "Needs matching";
    const cleanFileUrl = input.fileUrl?.trim() || "";
    const amountBam = Math.max(0, Number(input.amountBam) || 0);
    const invoiceLines = invoiceLineItemsFromQuick(input.lineItems ?? parseQuickInvoiceLineItems(input.lineText ?? ""));

    if (!amountBam) {
      setCatalogActionState("Invoice needs an amount before it can be saved");
      return;
    }

    const amountEur = Number(bamToEur(amountBam).toFixed(2));
    const review = invoiceReviewFor({
      amountEur,
      due,
      fileUrl: cleanFileUrl || undefined,
      invoiceLines,
      orderId: cleanOrderId || undefined,
      orders: orderQueue,
      supplierName: cleanSupplier,
    });
    const fallbackInvoice: InvoiceRecord = {
      id: `INV-local-${Date.now()}`,
      orderId: cleanOrderId || undefined,
      buyer: selectedBranch.name,
      supplierName: cleanSupplier,
      market: selectedBranch.market,
      amountEur,
      status: invoiceStatusFromReadiness(review.paymentReadiness),
      due: review.paymentReadiness === "needs_order" ? "Add order number to match" : due,
      fileUrl: cleanFileUrl || undefined,
      issuedAt: "Captured just now",
      ...review,
    };

    const result = await submitInvoice({
      amountBam,
      buyerName: selectedBranch.name,
      captureMethod: review.captureMethod,
      due,
      fileUrl: cleanFileUrl || undefined,
      lineItems: invoiceLines,
      mismatchTypes: review.mismatchTypes,
      orderId: cleanOrderId || undefined,
      supplierName: cleanSupplier,
    });

    if (!result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      setCatalogActionState(`Invoice not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: activePermission.role,
        action: "Manual invoice save failed",
        entity: `${cleanSupplier} / ${formatMoney(amountBam)} KM / ${readableSaveError(result.error)}`,
        market: selectedBranch.market,
      });
      return;
    }

    const invoice: InvoiceRecord = result.ok
      ? {
          ...fallbackInvoice,
          ...result.invoice,
          amountDeltaEur: result.invoice.amountDeltaEur ?? fallbackInvoice.amountDeltaEur,
          captureMethod: result.invoice.captureMethod ?? fallbackInvoice.captureMethod,
          expectedAmountEur: result.invoice.expectedAmountEur ?? fallbackInvoice.expectedAmountEur,
          invoiceLines: result.invoice.invoiceLines ?? fallbackInvoice.invoiceLines,
          lineMatches: result.invoice.lineMatches ?? fallbackInvoice.lineMatches,
          matchStatus: result.invoice.matchStatus ?? fallbackInvoice.matchStatus,
          mismatchTypes: result.invoice.mismatchTypes ?? fallbackInvoice.mismatchTypes,
          orderAttachmentStatus: result.invoice.orderAttachmentStatus ?? fallbackInvoice.orderAttachmentStatus,
          paymentReadiness: result.invoice.paymentReadiness ?? fallbackInvoice.paymentReadiness,
        }
      : fallbackInvoice;

    if (!result.ok) {
      upsertLocalInvoice(invoice);
    }

    setInvoiceQueue((invoices) => [invoice, ...invoices.filter((item) => item.id !== invoice.id)]);
    setCatalogActionState(
      result.ok
        ? `${invoice.id} saved for ${cleanSupplier}`
        : `${invoice.id} saved locally. Backend sync still needs connection.`,
    );
    setMessageQueue((threads) => [
      {
        id: `msg-manual-invoice-${invoice.id}`,
        participant: cleanSupplier,
        role: "buyer",
        market: selectedBranch.market,
        subject: `Invoice captured: ${invoice.id}`,
        preview: cleanOrderId
          ? `${formatMoney(amountBam)} KM was captured for ${cleanOrderId}. ${invoiceReadinessLabel(invoice.paymentReadiness)}${invoice.amountDeltaEur ? `; delta ${formatEur(invoice.amountDeltaEur)}` : ""}.`
          : `${formatMoney(amountBam)} KM was captured. Add an order number before preparing payment.`,
        status: invoice.paymentReadiness === "ready" ? "waiting" : "needs_reply",
        updatedAt: "Just now",
        lastAction: "Restaurant captured invoice manually",
      },
      ...threads,
    ]);
    recordAudit({
      actor: activePermission.role,
      action: "Captured manual invoice",
      entity: `${invoice.id} / ${cleanSupplier} / ${formatMoney(amountBam)} KM`,
      market: selectedBranch.market,
    });
    if (invoice.paymentReadiness === "ready" || invoice.status === "ready") {
      recordBehavior({
        actor: activePermission.role,
        entity: `${invoice.id} / ${invoice.orderId ?? "order pending"}`,
        market: selectedBranch.market,
        name: "invoice_matched",
        properties: {
          amountEur: invoice.amountEur,
          orderId: invoice.orderId ?? null,
          source: "manual_capture",
        },
        role,
        step: "invoice_order_match",
      });
    }
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: restaurantKnownSupplierNames.length > 0 || cleanSupplier !== "Supplier pending",
    });
  }

  async function createSupplierInvoiceFromOrder(orderId: string) {
    const fulfillmentOrder = fulfillmentQueue.find((order) => order.id === orderId);

    if (!fulfillmentOrder) {
      return;
    }

    if (!activePermission.canExportFinance) {
      setCatalogActionState(`${activePermission.role} cannot send supplier invoices`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked supplier invoice creation",
        entity: `${fulfillmentOrder.buyer} / ${fulfillmentOrder.id}`,
        market: fulfillmentOrder.market ?? "Supplier orders",
      });
      return;
    }

    const linkedOrderId = fulfillmentOrder.buyerOrderId ?? fulfillmentOrder.id;
    const existingInvoice = invoiceQueue.find((invoice) => invoice.orderId === linkedOrderId);

    if (existingInvoice) {
      setCatalogActionState(`${existingInvoice.id} is already tied to ${linkedOrderId}`);
      return;
    }

    const amountBam = Number((fulfillmentOrder.valueEur * BAM_PER_EUR).toFixed(2));

    if (!amountBam) {
      setCatalogActionState("Invoice needs an order amount before it can be sent");
      return;
    }

    const fallbackInvoice: InvoiceRecord = {
      id: `INV-local-${linkedOrderId}-${Date.now()}`,
      orderId: linkedOrderId,
      buyer: fulfillmentOrder.buyer,
      supplierName: fulfillmentOrder.supplierName ?? workspaceName,
      market: fulfillmentOrder.market,
      amountEur: fulfillmentOrder.valueEur,
      status: "ready",
      due: "Ready for buyer match",
      amountDeltaEur: 0,
      captureMethod: "manual",
      expectedAmountEur: fulfillmentOrder.valueEur,
      issuedAt: "Sent just now",
      matchStatus: "matched",
      mismatchTypes: [],
      orderAttachmentStatus: "attached",
      paymentReadiness: "ready",
    };

    const result = await submitInvoice({
      amountBam,
      buyerName: fulfillmentOrder.buyer,
      captureMethod: "manual",
      due: "Ready for buyer match",
      mismatchTypes: [],
      orderId: linkedOrderId,
      supplierName: fulfillmentOrder.supplierName ?? workspaceName,
    });

    if (!result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      setCatalogActionState(`Invoice not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: fulfillmentOrder.supplierName ?? "Supplier operator",
        action: "Supplier invoice save failed",
        entity: `${linkedOrderId} / ${formatMoney(amountBam)} KM / ${readableSaveError(result.error)}`,
        market: fulfillmentOrder.market ?? "Supplier orders",
      });
      return;
    }

    const invoice = result.ok ? result.invoice : fallbackInvoice;

    if (!result.ok) {
      upsertLocalInvoice(invoice);
    }

    setInvoiceQueue((invoices) => [invoice, ...invoices.filter((item) => item.id !== invoice.id)]);
    setCatalogActionState(
      result.ok
        ? `${invoice.id} sent to ${fulfillmentOrder.buyer}`
        : `${invoice.id} saved locally. Backend sync still needs connection.`,
    );
    setMessageQueue((threads) => [
      {
        id: `msg-supplier-invoice-${invoice.id}-${Date.now()}`,
        participant: fulfillmentOrder.buyer,
        role: "buyer",
        market: fulfillmentOrder.market ?? "Supplier orders",
        subject: `Invoice sent: ${invoice.id}`,
        preview: `${fulfillmentOrder.supplierName ?? "Supplier"} sent ${formatMoney(amountBam)} KM for ${linkedOrderId}. Buyer can match it against the order before bank transfer.`,
        status: "waiting",
        updatedAt: "Just now",
        lastAction: "Supplier sent invoice from order",
      },
      ...threads,
    ]);
    recordAudit({
      actor: fulfillmentOrder.supplierName ?? "Supplier operator",
      action: "Supplier sent invoice from order",
      entity: `${invoice.id} / ${linkedOrderId} / ${formatMoney(amountBam)} KM`,
      market: fulfillmentOrder.market ?? "Supplier orders",
    });
    markRoleDataSeen("supplier");
  }

  async function addMarketplaceProductToDraft(product: MarketplaceProduct) {
    if (!marketplaceProductCanOrder(product)) {
      await handleCreateQuoteRequest(product);
      setQuoteActionState(`${product.name} needs supplier confirmation before it can be ordered`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Marketplace order converted to supplier request",
        entity: `${product.name} / ${product.supplierName} / ${marketplaceSupplyStateLabel(product.supplyState)}`,
        market: product.market,
      });
      return;
    }

    if (!activePermission.canSubmitOrders) {
      setSubmitState(`${activePermission.role} cannot add marketplace items to today's list`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked marketplace draft add",
        entity: `${product.name} / ${product.supplierName}`,
        market: product.market,
      });
      return;
    }

    const draftItem = marketplaceProductToDraftItem(product);

    setDraft((items) => {
      const baseItems = realRestaurantDataActive ? items : [];
      const current = baseItems.find((item) => item.productId === draftItem.productId);

      if (current) {
        return baseItems.map((item) =>
          item.productId === draftItem.productId
            ? {
                ...enrichDraftItem(item, draftLookupContext, draftItem),
                quantity: item.quantity + draftItem.quantity,
              }
            : item,
        );
      }

      return [...baseItems, draftItem];
    });
    setSubmitState(`${product.name} added from marketplace`);
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: restaurantKnownSupplierNames.length > 0 || Boolean(product.supplierName),
    });
    recordBehavior(
      {
        entity: `${product.name} / ${product.supplierName}`,
        market: product.market,
        name: "first_item",
        properties: {
          availability: product.availability,
          supplyState: product.supplyState,
          source: "marketplace",
        },
        role: "restaurant",
        step: "restaurant_first_buying_list_item",
      },
      { once: true },
    );
    setActiveNav("Orders");
  }

  function addCatalogProductToDraft(product: Product) {
    if (!activePermission.canSubmitOrders) {
      setSubmitState(`${activePermission.role} cannot add stock alerts to today's list`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked stock alert draft add",
        entity: `${product.name} / ${product.supplier}`,
        market: selectedBranch.market,
      });
      return;
    }

    const draftItem = enrichDraftItem(
      {
        productId: product.id,
        name: product.name,
        quantity: Math.max(1, product.lastOrderedKg),
        unit: product.unit,
        unitPrice: product.price,
        supplier: product.supplier,
        category: product.category,
        source: "forecast",
      },
      draftLookupContext,
    );

    setDraft((items) => {
      const baseItems = realRestaurantDataActive ? items : [];
      const current = baseItems.find((item) => item.productId === draftItem.productId);

      if (current) {
        return baseItems.map((item) =>
          item.productId === draftItem.productId
            ? {
                ...enrichDraftItem(item, draftLookupContext, draftItem),
                quantity: item.quantity + draftItem.quantity,
              }
            : item,
        );
      }

      return [...baseItems, draftItem];
    });
    setSubmitState(`${product.name} added from stock alert`);
    setMessageQueue((threads) => [
      {
        id: `msg-stock-alert-${product.id}-${Date.now()}`,
        participant: product.supplier,
        role: "supplier",
        market: selectedBranch.market,
        subject: `Stock alert added to today's list: ${product.name}`,
        preview: `${selectedBranch.name} added ${product.lastOrderedKg} ${product.unit} of ${product.name} from a ${stockLabels[product.stockStatus].toLowerCase()} alert. Supplier terms should be confirmed before dispatch.`,
        status: product.stockStatus === "unavailable" ? "needs_reply" : "waiting",
        updatedAt: "Just now",
        lastAction: "Buyer acted on stock alert",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Added stock alert item to today's list",
      entity: `${product.name} / ${product.supplier} / ${product.lastOrderedKg} ${product.unit}`,
      market: selectedBranch.market,
    });
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: restaurantKnownSupplierNames.length > 0 || Boolean(product.supplier),
    });
    recordBehavior(
      {
        entity: `${product.name} / ${product.supplier}`,
        market: selectedBranch.market,
        name: "first_item",
        properties: {
          source: "stock_alert",
          stockStatus: product.stockStatus,
        },
        role: "restaurant",
        step: "restaurant_first_buying_list_item",
      },
      { once: true },
    );
    setActiveNav("Orders");
  }

  function findBackupForCatalogProduct(product: Product) {
    const category = marketplaceCategoryForCatalogProduct(product);
    const backupMatch = searchableMarketplaceProducts.find(
      (item) =>
        item.id !== product.id &&
        item.category === category &&
        (item.supplierType === "external" ||
          item.tags.includes("backup supplier needed") ||
          item.availability !== "in_stock"),
    );

    setMarketplaceCategory(category);
    setMarketplaceMode("Backup");
    setQuery(category);

    if (backupMatch) {
      setSelectedMarketProductId(backupMatch.id);
    }

    setQuoteActionState(
      backupMatch
        ? `${backupMatch.supplierName} selected as backup for ${product.name}`
        : `Backup search opened for ${category}`,
    );
    recordAudit({
      actor: "Restaurant buyer",
      action: "Opened backup supplier search",
      entity: `${product.name} / ${category}`,
      market: selectedBranch.market,
    });
    navigate("Marketplace");
  }

  async function addQuoteRequestToDraft(request: QuoteRequest) {
    if (!activePermission.canSubmitOrders) {
      setQuoteActionState(`${activePermission.role} cannot accept supplier offers into today's list`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked quote-to-draft action",
        entity: `${request.id} / ${request.product}`,
        market: request.deliveryWindow ?? request.neededBy,
      });
      return;
    }

    if (!isReadyQuoteStatus(request.status)) {
      setQuoteActionState(`${request.id} is not quoted yet`);
      return;
    }

    const draftItem = quoteRequestToDraftItem(request);
    const result = await acceptQuoteOffer({
      quoteRequestId: request.id,
      supplierName: request.supplierName,
    });

    if (!result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      setQuoteActionState(`Offer not accepted: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: activePermission.role,
        action: "Quote acceptance failed",
        entity: `${request.id} / ${request.product} / ${readableSaveError(result.error)}`,
        market: request.deliveryWindow ?? request.neededBy,
      });
      return;
    }

    setDraft((items) => {
      const baseItems = realRestaurantDataActive ? items : [];
      const current = baseItems.find((item) => item.productId === draftItem.productId);

      if (current) {
        return baseItems.map((item) =>
          item.productId === draftItem.productId
            ? {
                ...enrichDraftItem(item, draftLookupContext, draftItem),
                quantity: Math.max(item.quantity, draftItem.quantity),
                unitPrice: draftItem.unitPrice,
              }
            : item,
        );
      }

      return [...baseItems, draftItem];
    });
    if (result.ok) {
      setQuoteQueue((requests) =>
        requests.map((item) => (item.id === request.id ? { ...item, ...result.quoteRequest } : item)),
      );
    }
    setSubmitState(`${request.product} offer added to today's list`);
    setQuoteActionState(
      result.ok
        ? `${request.id} accepted and added to today's list`
        : `${request.id} accepted locally into today's list`,
    );
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: restaurantKnownSupplierNames.length > 0 || Boolean(request.supplierName),
    });
    recordBehavior({
      entity: `${request.id} / ${request.product} / ${request.supplierName ?? "Supplier"}`,
      market: request.deliveryWindow ?? request.neededBy,
      name: "quote_accepted",
      properties: {
        product: request.product,
        quoteRequestId: request.id,
        supplierName: request.supplierName ?? "Supplier",
      },
      role: "restaurant",
      step: "restaurant_quote_acceptance",
    });
    recordBehavior(
      {
        entity: `${request.product} / ${request.supplierName ?? "Supplier"}`,
        market: request.deliveryWindow ?? request.neededBy,
        name: "first_item",
        properties: {
          source: "quote",
        },
        role: "restaurant",
        step: "restaurant_first_buying_list_item",
      },
      { once: true },
    );
    setActiveNav("Orders");
  }

  async function addSupplierOfferToDraft(offer: SupplierOffer) {
    if (!activePermission.canSubmitOrders) {
      setQuoteActionState(`${activePermission.role} cannot accept supplier offers into today's list`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked supplier-offer draft add",
        entity: `${offer.productName} / ${offer.supplierName}`,
        market: selectedBranch.market,
      });
      return;
    }

    const quotedRequest = offer.quoteRequestId
      ? quoteQueue.find((request) => request.id === offer.quoteRequestId && isReadyQuoteStatus(request.status))
      : undefined;
    const result = quotedRequest
      ? await acceptQuoteOffer({
          quoteRequestId: quotedRequest.id,
          supplierName: offer.supplierName,
        })
      : null;

    if (result && !result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      setQuoteActionState(`Offer not accepted: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: activePermission.role,
        action: "Supplier offer acceptance failed",
        entity: `${offer.productName} / ${offer.supplierName} / ${readableSaveError(result.error)}`,
        market: selectedBranch.market,
      });
      return;
    }

    const draftItem = quotedRequest
      ? quoteRequestToDraftItem(quotedRequest)
      : supplierOfferToDraftItem(offer);

    setDraft((items) => {
      const baseItems = realRestaurantDataActive ? items : [];
      const current = baseItems.find((item) => item.productId === draftItem.productId);

      if (current) {
        return baseItems.map((item) =>
          item.productId === draftItem.productId
            ? {
                ...enrichDraftItem(item, draftLookupContext, draftItem),
                quantity: Math.max(item.quantity, draftItem.quantity),
                unitPrice: draftItem.unitPrice,
                supplier: draftItem.supplier,
              }
            : item,
        );
      }

      return [...baseItems, draftItem];
    });
    if (result?.ok) {
      setQuoteQueue((requests) =>
        requests.map((item) => (item.id === quotedRequest?.id ? { ...item, ...result.quoteRequest } : item)),
      );
    }
    setSubmitState(`${offer.productName} from ${offer.supplierName} added to today's list`);
    setQuoteActionState(
      result?.ok
        ? `${offer.supplierName} offer accepted and added to today's list`
        : `${offer.supplierName} offer added to today's list`,
    );
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: restaurantKnownSupplierNames.length > 0 || Boolean(offer.supplierName),
    });
    recordAudit({
      actor: "Restaurant buyer",
      action: "Accepted supplier offer into today's list",
      entity: `${offer.productName} / ${offer.supplierName} / ${formatCurrencyAmount(offer.landedPrice, offer.currency)}`,
      market: selectedBranch.market,
    });
    recordBehavior({
      entity: `${offer.productName} / ${offer.supplierName}`,
      market: selectedBranch.market,
      name: "quote_accepted",
      properties: {
        offerType: offer.offerType,
        quoteRequestId: offer.quoteRequestId,
        savingsAmount: offer.savingsAmount,
      },
      role: "restaurant",
      step: "restaurant_quote_acceptance",
    });
    recordBehavior(
      {
        entity: `${offer.productName} / ${offer.supplierName}`,
        market: selectedBranch.market,
        name: "first_item",
        properties: {
          source: "supplier_offer",
        },
        role: "restaurant",
        step: "restaurant_first_buying_list_item",
      },
      { once: true },
    );
    setActiveNav("Orders");
  }

  function followUpQuoteRequest(request: QuoteRequest) {
    if (!activePermission.canSubmitOrders) {
      setQuoteActionState(`${activePermission.role} cannot send buyer quote follow-ups`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked quote follow-up",
        entity: `${request.id} / ${request.product}`,
        market: request.deliveryWindow ?? request.neededBy,
      });
      return;
    }

    const nextStatus: QuoteRequest["status"] = request.status === "draft" ? "sent" : "waiting";
    const supplierTarget = request.supplierName ?? `${request.suppliersMatched} matched suppliers`;
    const actionLabel = request.status === "draft" ? "Sent price request" : "Sent price follow-up";

    setQuoteQueue((requests) =>
      requests.map((item) =>
        item.id === request.id
          ? {
              ...item,
              status: nextStatus,
              deliveryWindow: item.deliveryWindow ?? item.neededBy,
            }
          : item,
      ),
    );
    setMessageQueue((threads) => [
      {
        id: `msg-quote-follow-up-${request.id}-${Date.now()}`,
        participant: supplierTarget,
        role: "supplier",
        market: request.deliveryWindow ?? request.neededBy,
        subject: `${request.status === "draft" ? "Quote request" : "Quote follow-up"}: ${request.product}`,
        preview: `${request.buyer} needs ${request.quantity} of ${request.product} by ${request.neededBy}. Supplier should confirm price, minimum order, delivery window, and any substitutions.`,
        status: "needs_reply",
        updatedAt: "Just now",
      },
      ...threads,
    ]);
    setQuoteActionState(`${request.id} ${request.status === "draft" ? "sent to" : "followed up with"} ${supplierTarget}`);
    recordAudit({
      actor: "Restaurant buyer",
      action: actionLabel,
      entity: `${request.product} / ${supplierTarget} / ${request.quantity}`,
      market: request.deliveryWindow ?? request.neededBy,
    });
  }

  async function approveRequest(requestId: string) {
    const request = approvalQueue.find((item) => item.id === requestId);

    if (!activePermission.canApproveOrders) {
      setSubmitState(`${activePermission.role} cannot approve branch spend`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked approval attempt",
        entity: request ? `${request.branch} / ${formatCurrencyAmount(request.amountBam, "BAM")}` : requestId,
        market: request?.branch ?? "Approval queue",
      });
      return;
    }

    if (!request) {
      return;
    }

    const branch = branchBook.find((item) => item.name === request.branch);
    const approvalResult = await submitApprovalRequest({
      ...request,
      branchId: branch?.id,
      market: branch?.market,
      organizationName: branch?.name.split("/")[0]?.trim() || branch?.name,
      status: "approved",
    });

    if (!approvalResult.ok) {
      setSubmitState(`Approval not saved: ${readableSaveError(approvalResult.error)}`);
      recordAudit({
        actor: activePermission.role,
        action: "Approval save failed",
        entity: `${request.branch} / ${readableSaveError(approvalResult.error)}`,
        market: branch?.market ?? request.branch,
      });
      return;
    }

    setApprovalQueue((requests) =>
      requests.map((request) =>
        request.id === requestId
          ? { ...approvalResult.approval, status: "approved", due: "Approved just now" }
          : request,
      ),
    );
    recordAudit({
      actor: activePermission.role,
      action: "Approved branch spend request",
      entity: `${request.branch} / ${formatCurrencyAmount(request.amountBam, "BAM")}`,
      market: branch?.market ?? (request.branch.includes("Hotel") ? "Sarajevo hotel group" : "Sarajevo"),
    });
  }

  async function requestDraftApproval() {
    if (!activePermission.canSubmitOrders) {
      setSubmitState(`${activePermission.role} cannot request order approval`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked approval request attempt",
        entity: `${selectedBranch.name} / ${formatBranchMoney(draftTotal, selectedBranch, { includeBase: true })}`,
        market: selectedBranch.market,
      });
      return;
    }

    if (!draft.length) {
      setSubmitState("Add items before requesting approval");
      return;
    }

    if (draftApprovalRequest?.status === "needs_approval") {
      setSubmitState(`${draftApprovalRequest.id} is already waiting for approval`);
      return;
    }

    if (draftApprovalRequest?.status === "approved") {
      setSubmitState(`${draftApprovalRequest.id} is approved for this order`);
      return;
    }

    const remainingAfterDraft = selectedBranchRemaining - draftTotal;
    const policy =
      remainingAfterDraft < 0
        ? `Order exceeds weekly branch budget by ${formatBranchMoney(Math.abs(remainingAfterDraft), selectedBranch, { includeBase: true })}`
        : `Order exceeds ${formatBranchMoney(selectedBranch.approvalLimit, selectedBranch, { includeBase: true })} branch approval limit`;
    const approval: ApprovalRequest = {
      id: `ap-draft-${Date.now()}`,
      branch: selectedBranch.name,
      requester: activePermission.role,
      amountBam: Number(draftTotal.toFixed(2)),
      policy,
          due: "Needed before this order can be sent",
      status: "needs_approval",
    };
    const approvalResult = await submitApprovalRequest({
      ...approval,
      branchId: selectedBranch.id,
      market: selectedBranch.market,
      organizationName: selectedBranch.name.split("/")[0]?.trim() || selectedBranch.name,
    });

    if (!approvalResult.ok) {
      setSubmitState(`Approval request not saved: ${readableSaveError(approvalResult.error)}`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Approval request save failed",
        entity: `${selectedBranch.name} / ${readableSaveError(approvalResult.error)}`,
        market: selectedBranch.market,
      });
      return;
    }

    const savedApproval = approvalResult.approval;
    const approvalMessage: MessageThread = {
      id: `msg-approval-${savedApproval.id}`,
      participant: "Branch approver",
      role: "buyer",
      market: selectedBranch.market,
      subject: `Approval requested: ${selectedBranch.name}`,
      preview: `${formatBranchMoney(savedApproval.amountBam, selectedBranch, { includeBase: true })} order requires approval before Veleo can send ${draftPackets.length} supplier packet${draftPackets.length === 1 ? "" : "s"}.`,
      status: "needs_reply",
      updatedAt: "Just now",
    };
    void submitMessageThread(approvalMessage);

    setApprovalQueue((requests) => [savedApproval, ...requests]);
    setMessageQueue((threads) => [
      approvalMessage,
      ...threads,
    ]);
    setSubmitState(`${savedApproval.id} requested for ${selectedBranch.name}`);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Requested branch spend approval",
      entity: `${selectedBranch.name} / ${formatBranchMoney(savedApproval.amountBam, selectedBranch, { includeBase: true })}`,
      market: selectedBranch.market,
    });
  }

  async function sendSupplierQuote(request: QuoteRequest, override?: SupplierQuoteOverride) {
    if (!activePermission.canManageSupplierDesk) {
      setQuoteActionState(`${activePermission.role} cannot send supplier quotes`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked supplier quote attempt",
        entity: `${request.product} / ${request.buyer}`,
        market: request.deliveryWindow ?? request.neededBy,
      });
      return;
    }

    const quoteTerms = resolveSupplierQuoteTerms(request, catalogProducts);
    const quoteQuantity = parseMarketplaceQuantity(request.quantity);
    const quoteUnit = quoteTerms.priceRange.split("/")[1]?.trim() || quoteQuantity.unit;
    const quoteCurrency = quoteTerms.priceRange.includes("EUR") ? "EUR" : "BAM";
    const quoteListPrice = Number((override?.listPrice ?? quoteTerms.unitPrice * 1.08).toFixed(2));
    const defaultDiscountPercent =
      quoteListPrice > 0 ? Math.round(((quoteListPrice - quoteTerms.unitPrice) / quoteListPrice) * 100) : 0;
    const quoteDiscountPercent = Math.max(
      0,
      Math.min(40, Math.round(override?.discountPercent ?? defaultDiscountPercent)),
    );
    const quotedUnitPrice = Number(
      (override ? quoteListPrice * (1 - quoteDiscountPercent / 100) : quoteTerms.unitPrice).toFixed(2),
    );
    const quoteSavings = Math.max(0, Number((quoteListPrice - quotedUnitPrice).toFixed(2)));
    const quotedPriceRange = `${formatCurrencyAmount(quotedUnitPrice, quoteCurrency)} / ${quoteUnit}`;
    const estimatedTotal = quoteQuantity.quantity > 1 ? quotedUnitPrice * quoteQuantity.quantity : quotedUnitPrice;
    const quotedBudget =
      quoteQuantity.quantity > 1 ? `${formatCurrencyAmount(estimatedTotal, quoteCurrency)} est.` : quotedPriceRange;
    const quotedTermsNote =
      quoteDiscountPercent >= 3
        ? `${quoteTerms.termsNote} Private ${quoteDiscountPercent}% discount versus ${formatCurrencyAmount(quoteListPrice, quoteCurrency)}/${quoteUnit}.`
        : quoteTerms.termsNote;
    setQuoteActionState(`Saving quote response for ${request.id}...`);

    const result = await submitQuoteResponse({
      buyerName: request.buyer,
      currency: quoteCurrency,
      discountPercent: quoteDiscountPercent,
      deliveryWindow: quoteTerms.deliveryWindow,
      listPrice: quoteListPrice,
      market: selectedBranch.market,
      offerType: quoteDiscountPercent >= 3 ? "competitive_discount" : "private_quote",
      priceRange: quotedPriceRange,
      productName: request.product,
      quantity: request.quantity,
      quoteRequestId: request.id,
      quotedPrice: quotedUnitPrice,
      savingsAmount: quoteSavings,
      supplierName: request.supplierName ?? "Supplier",
      termsNote: quotedTermsNote,
    });

    if (!result.ok) {
      if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
        setQuoteActionState(`Quote not saved: ${readableSaveError(result.error)}`);
        recordAudit({
          actor: "Supplier operator",
          action: "Supplier quote response save failed",
          entity: `${request.product} / ${request.buyer} / ${request.id} / ${readableSaveError(result.error)}`,
          market: request.deliveryWindow ?? request.neededBy,
        });
        return;
      }

      setQuoteActionState(`Quote saved locally. Backend sync still needs connection: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Supplier operator",
        action: "Saved local supplier quote response",
        entity: `${request.product} / ${request.buyer} / ${request.id}`,
        market: request.deliveryWindow ?? request.neededBy,
      });
    }

    setQuoteQueue((requests) =>
      requests.map((item) =>
        item.id === request.id
          ? {
              ...item,
              status: "quoted",
              budget: quotedBudget,
              priceRange: quotedPriceRange,
              unitPrice: quotedUnitPrice,
              deliveryWindow: quoteTerms.deliveryWindow,
              quotedAt: "Just now",
              termsNote: quotedTermsNote,
            }
          : item,
      ),
    );
    updateLocalQuoteRequest(request.id, (item) => ({
      ...item,
      status: "quoted",
      budget: quotedBudget,
      priceRange: quotedPriceRange,
      unitPrice: quotedUnitPrice,
      deliveryWindow: quoteTerms.deliveryWindow,
      quotedAt: "Just now",
      termsNote: quotedTermsNote,
    }));
    setMessageQueue((threads) => [
      {
        id: `msg-quote-${Date.now()}`,
        participant: request.buyer,
        role: "buyer",
        market: request.deliveryWindow ?? request.neededBy,
        subject: `Quote sent: ${request.product}`,
        preview: `${request.product} offered at ${quotedPriceRange} for ${request.quantity}. Estimated value ${quotedBudget}; buyer can accept it into today's list.`,
        status: "waiting",
        updatedAt: "Just now",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Supplier operator",
      action: "Sent supplier quote",
      entity: `${request.product} / ${request.buyer} / ${quotedPriceRange}`,
      market: request.deliveryWindow ?? request.neededBy,
    });
    recordBehavior({
      actor: request.supplierName ?? "Supplier operator",
      entity: `${request.id} / ${request.product} / ${request.buyer}`,
      market: request.deliveryWindow ?? request.neededBy,
      name: "supplier_response",
      properties: {
        discountPercent: quoteDiscountPercent,
        quoteRequestId: request.id,
        quotedPrice: quotedUnitPrice,
      },
      role: "supplier",
      step: "supplier_quote_response",
    });
    markRoleDataSeen("supplier");
    setQuoteActionState(`${request.id} quote saved and buyer queue updated`);
  }

  function createDemandQuoteLead(demand: SupplierSearchDemand) {
    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot create supplier price requests`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked supplier price request creation",
        entity: `${demand.product} / ${demand.market}`,
        market: demand.market,
      });
      return;
    }

    const request: QuoteRequest = {
      id: `qr-demand-${Date.now()}`,
      buyer: `${demand.buyerCount} buyers in ${demand.market}`,
      product: demand.product,
      quantity: "Demand signal",
      neededBy: demand.urgency === "now" ? "Today" : demand.urgency === "this_week" ? "This week" : "Monitor",
      status: "draft",
      suppliersMatched: 1,
      budget: `${formatEur(demand.avgBudgetEur)} avg budget`,
      supplierName: demand.matchedSupplier,
      category: demand.category,
      deliveryWindow: demand.recommendedAction,
      priceRange: "Supplier to quote",
    };

    setQuoteQueue((requests) => [request, ...requests]);
    setMessageQueue((threads) => [
      {
        id: `msg-demand-${Date.now()}`,
        participant: demand.matchedSupplier ?? workspaceName,
        role: "supplier",
        market: demand.market,
        subject: `Demand lead opened: ${demand.product}`,
        preview: `${demand.buyerCount} buyers searched for ${demand.product}. Supplier should quote minimum order, delivery window, and price before demand cools.`,
        status: "needs_reply",
        updatedAt: "Just now",
      },
      ...threads,
    ]);
    setCatalogActionState(`${demand.product} demand lead moved to quote queue`);
    markRoleDataSeen("supplier");
  }

  function catalogProductInput(product: Product, overrides: Partial<Product> = {}) {
    const nextProduct = { ...product, ...overrides };

    return {
      category: nextProduct.category,
      currency: nextProduct.currency,
      minimumOrderQuantity: nextProduct.lastOrderedKg,
      name: nextProduct.name,
      price: nextProduct.price,
      productId: nextProduct.id,
      stockStatus: nextProduct.stockStatus,
      supplierName: nextProduct.supplier,
      unit: nextProduct.unit,
    };
  }

  async function publishDemandSku(demand: SupplierSearchDemand) {
    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot publish supplier SKUs`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked demand-backed SKU publish",
        entity: `${demand.product} / ${demand.market}`,
        market: demand.market,
      });
      return;
    }

    const supplierName = demand.matchedSupplier ?? workspaceName;
    const existingProduct = catalogProducts.find(
      (product) =>
        normalizeMarketText(product.name) === normalizeMarketText(demand.product) &&
        normalizeMarketText(product.supplier) === normalizeMarketText(supplierName),
    );

    if (existingProduct) {
      const result = await submitCatalogProduct(
        catalogProductInput(existingProduct, { stockStatus: "available" }),
      );

      if (!result.ok) {
      setCatalogActionState(`Catalog update not saved: ${readableSaveError(result.error)}`);
        recordAudit({
          actor: "Supplier operator",
          action: "Demand-backed SKU publish failed",
          entity: `${existingProduct.name} / ${readableSaveError(result.error)}`,
          market: demand.market,
        });
        return;
      }

      setCatalogProducts((items) =>
        items.map((item) =>
          item.id === existingProduct.id ? { ...item, stockStatus: "available" } : item,
        ),
      );
      setCatalogActionState(`${existingProduct.name} is live and marked available`);
      recordAudit({
        actor: "Supplier operator",
        action: "Refreshed demand-backed SKU",
        entity: `${existingProduct.name} / ${supplierName}`,
        market: demand.market,
      });
      markRoleDataSeen("supplier");
      return;
    }

    const nextProduct: Product = {
      id: `demand-sku-${demand.id}-${Date.now().toString().slice(-5)}`,
      name: demand.product,
      supplier: supplierName,
      category: demand.category,
      unit: demandSkuUnitFor(demand),
      price: demandSkuPriceFor(demand),
      currency: "BAM",
      stockStatus: demand.urgency === "monitor" ? "low" : "available",
      lastOrderedKg: Math.max(1, Math.round(demand.failedSearches / 18)),
    };
    const result = await submitCatalogProduct(catalogProductInput(nextProduct));

    if (!result.ok) {
      setCatalogActionState(`Catalog SKU not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Supplier operator",
        action: "Demand-backed SKU publish failed",
        entity: `${nextProduct.name} / ${supplierName} / ${readableSaveError(result.error)}`,
        market: demand.market,
      });
      return;
    }

    const savedProduct = result.product;

    setCatalogProducts((items) => [savedProduct, ...items]);
    setSupplierDemandQueue((items) =>
      items.map((item) =>
        item.id === demand.id
          ? {
              ...item,
              urgency: "monitor",
              recommendedAction: `${supplierName} published a catalog SKU. Watch restaurant conversion and keep delivery terms current.`,
            }
          : item,
      ),
    );
    setMessageQueue((threads) => [
      {
        id: `msg-demand-sku-${demand.id}-${Date.now()}`,
        participant: "Marketplace buyers",
        role: "buyer",
        market: demand.market,
        subject: `New supply live: ${demand.product}`,
        preview: `${supplierName} published ${demand.product} at ${formatCurrencyAmount(savedProduct.price, savedProduct.currency)} / ${savedProduct.unit}. ${demand.buyerCount} buyers had searched for this demand signal.`,
        status: "waiting",
        updatedAt: "Just now",
        lastAction: "Demand-backed SKU published",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Supplier operator",
      action: "Published demand-backed SKU",
      entity: `${savedProduct.name} / ${supplierName} / ${formatCurrencyAmount(savedProduct.price, savedProduct.currency)}`,
      market: demand.market,
    });
    setCatalogActionState(`${savedProduct.name} published from demand signal`);
    markRoleDataSeen("supplier");
  }

  async function cycleCatalogAvailability(productId: string) {
    const statusOrder: StockStatus[] = ["available", "low", "unavailable"];
    const product = catalogProducts.find((item) => item.id === productId);

    if (!product) {
      return;
    }

    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot change supplier catalog availability`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked catalog availability change",
        entity: product.name,
        market: product.supplier,
      });
      return;
    }

    const nextStatus =
      statusOrder[(statusOrder.indexOf(product.stockStatus) + 1) % statusOrder.length];
    const result = await submitCatalogProduct(catalogProductInput(product, { stockStatus: nextStatus }));

    if (!result.ok) {
      setCatalogActionState(`Catalog availability not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Supplier operator",
        action: "Catalog availability save failed",
        entity: `${product.name} / ${readableSaveError(result.error)}`,
        market: product.supplier,
      });
      return;
    }

    setCatalogActionState(`${product.name} marked ${stockLabels[nextStatus].toLowerCase()}`);
    setCatalogProducts((items) =>
      items.map((item) =>
        item.id === productId ? { ...item, id: result.product.id, stockStatus: nextStatus } : item,
      ),
    );
    markRoleDataSeen("supplier");
    recordAudit({
      actor: "Supplier operator",
      action: "Changed catalog availability",
      entity: `${product.name} / ${stockLabels[nextStatus]}`,
      market: product.supplier,
    });
  }

  async function markCatalogProductAvailable(productId: string) {
    const product = catalogProducts.find((item) => item.id === productId);

    if (!product) {
      return;
    }

    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot change supplier catalog availability`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked catalog availability change",
        entity: product.name,
        market: product.supplier,
      });
      return;
    }

    const result = await submitCatalogProduct(catalogProductInput(product, { stockStatus: "available" }));

    if (!result.ok) {
      setCatalogActionState(`Catalog availability not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Supplier operator",
        action: "Catalog availability save failed",
        entity: `${product.name} / ${readableSaveError(result.error)}`,
        market: product.supplier,
      });
      return;
    }

    setCatalogProducts((items) =>
      items.map((item) => (item.id === productId ? { ...item, id: result.product.id, stockStatus: "available" } : item)),
    );
    setCatalogActionState(`${product.name} marked available`);
    markRoleDataSeen("supplier");
    setMessageQueue((threads) => [
      {
        id: `msg-catalog-ready-${product.id}-${Date.now()}`,
        participant: "Marketplace buyers",
        role: "buyer",
        market: product.supplier,
        subject: `Catalog availability refreshed: ${product.name}`,
        preview: `${product.supplier} marked ${product.name} available at ${formatMoney(product.price)} KM / ${product.unit}. Buyers can now quote or order against live terms.`,
        status: "waiting",
        updatedAt: "Just now",
        lastAction: "Supplier refreshed catalog availability",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Supplier operator",
      action: "Marked catalog SKU available",
      entity: `${product.name} / ${product.supplier}`,
      market: product.supplier,
    });
  }

  async function adjustCatalogPrice(productId: string, delta: number) {
    const product = catalogProducts.find((item) => item.id === productId);

    if (!product) {
      return;
    }

    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot update supplier catalog prices`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked catalog price change",
        entity: product.name,
        market: product.supplier,
      });
      return;
    }

    const nextPrice = Math.max(0.1, Number((product.price + delta).toFixed(2)));
    const result = await submitCatalogProduct(catalogProductInput(product, { price: nextPrice }));

    if (!result.ok) {
      setCatalogActionState(`Catalog price not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Supplier operator",
        action: "Catalog price save failed",
        entity: `${product.name} / ${readableSaveError(result.error)}`,
        market: product.supplier,
      });
      return;
    }

    setCatalogActionState(`${product.name} price updated to ${formatMoney(nextPrice)} KM`);
    setCatalogProducts((items) =>
      items.map((item) => (item.id === productId ? { ...item, id: result.product.id, price: nextPrice } : item)),
    );
    markRoleDataSeen("supplier");
    recordAudit({
      actor: "Supplier operator",
      action: "Updated catalog price",
      entity: `${product.name} / ${formatMoney(nextPrice)} KM`,
      market: product.supplier,
    });
  }

  async function toggleDeliveryAreaCapacity(areaId: string) {
    const area = deliveryAreaQueue.find((item) => item.id === areaId);

    if (!area) {
      return;
    }

    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot change delivery capacity`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked delivery capacity change",
        entity: area.market,
        market: area.market,
      });
      return;
    }

    const nextStatus: DeliveryArea["status"] =
      area.status === "capacity_watch" ? "active" : "capacity_watch";
    const supplierName =
      fulfillmentQueue.find((order) => order.market === area.market)?.supplierName ??
      catalogProducts[0]?.supplier ??
      "Supplier";
    const result = await submitDeliveryArea({
      activeOrders: area.activeOrders,
      areaId: area.id,
      days: area.days,
      market: area.market,
      minimumOrder: area.minimumOrder,
      performance: area.performance,
      status: nextStatus,
      supplierName,
    });

    if (!result.ok) {
      setCatalogActionState(`Delivery capacity not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Supplier operator",
        action: "Delivery capacity save failed",
        entity: `${area.market} / ${readableSaveError(result.error)}`,
        market: area.market,
      });
      return;
    }

    setDeliveryAreaQueue((areas) =>
      areas.map((item) =>
        item.id === areaId
          ? {
              ...item,
              id: result.deliveryArea.id,
              status: nextStatus,
              lastUpdated:
                nextStatus === "active"
                  ? "Capacity reopened just now"
                  : "Capacity watch set just now",
            }
          : item,
      ),
    );
    setCatalogActionState(`${area.market} marked ${nextStatus.replace("_", " ")}`);
    markRoleDataSeen("supplier");
    recordAudit({
      actor: "Supplier operator",
      action: "Updated delivery area capacity",
      entity: `${area.market} / ${nextStatus.replace(/_/g, " ")}`,
      market: area.market,
    });
  }

  async function advanceFulfillmentOrder(orderId: string) {
    const fulfillmentOrder = fulfillmentQueue.find((order) => order.id === orderId);

    if (!fulfillmentOrder) {
      return;
    }

    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot advance supplier fulfillment`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked fulfillment status change",
        entity: orderId,
        market: fulfillmentOrder.market ?? "Supplier route",
      });
      return;
    }

    const nextOrder = nextFulfillmentState(fulfillmentOrder);
    const linkedOrderId = nextOrder.buyerOrderId ?? nextOrder.id;
    const result = await submitFulfillmentUpdate({
      amountEur: nextOrder.valueEur,
      buyerName: nextOrder.buyer,
      market: nextOrder.market,
      orderId: linkedOrderId,
      status: nextOrder.status,
      supplierName: nextOrder.supplierName,
    });

    if (!result.ok && !isLocalOrderId(linkedOrderId)) {
      setCatalogActionState(`Fulfillment update not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: nextOrder.supplierName ?? "Supplier operator",
        action: "Fulfillment status save failed",
        entity: `${linkedOrderId} / ${readableSaveError(result.error)}`,
        market: nextOrder.market ?? "Supplier route",
      });
      return;
    }
    if (!result.ok) {
      setCatalogActionState("Fulfillment updated locally. Backend sync still needs connection.");
      recordAudit({
        actor: nextOrder.supplierName ?? "Supplier operator",
        action: "Advanced local fulfillment status",
        entity: `${linkedOrderId} / ${nextOrder.status.replace(/_/g, " ")}`,
        market: nextOrder.market ?? "Supplier route",
      });
    }

    const existingInvoice = invoiceQueue.find((invoice) => invoice.orderId === linkedOrderId);
    const savedInvoice = result.ok ? result.invoice : null;
    const autoInvoice: InvoiceRecord | null =
      savedInvoice ??
      (nextOrder.status === "delivered" && !existingInvoice
        ? {
            id: `INV-${linkedOrderId.replace(/[^0-9]+/g, "") || Date.now()}`,
            orderId: linkedOrderId,
            buyer: nextOrder.buyer,
            supplierName: nextOrder.supplierName,
            market: nextOrder.market,
            amountEur: nextOrder.valueEur,
            status: "ready",
            due: "Ready just now",
            amountDeltaEur: 0,
            captureMethod: "manual",
            expectedAmountEur: nextOrder.valueEur,
            issuedAt: "Just now",
            matchStatus: "matched",
            mismatchTypes: [],
            orderAttachmentStatus: "attached",
            paymentReadiness: "ready",
          }
        : null);

    if (autoInvoice) {
      upsertLocalInvoice(autoInvoice);
    }

    setFulfillmentQueue((items) => items.map((item) => (item.id === orderId ? nextOrder : item)));
    setOrderQueue((items) =>
      items.map((item) =>
        item.id === linkedOrderId
          ? { ...item, status: orderStatusForFulfillmentStatus(nextOrder.status) }
          : item,
      ),
    );
    updateStoredLocalOrder(linkedOrderId, (order) => ({
      ...order,
      status: orderStatusForFulfillmentStatus(nextOrder.status),
    }));
    if (nextOrder.status === "delivered") {
      setDeliveryAreaQueue((areas) => releaseDeliveryAreaPressure(areas, nextOrder.market));
      setInvoiceQueue((invoices) =>
        autoInvoice && !invoices.some((invoice) => invoice.orderId === linkedOrderId)
          ? [autoInvoice, ...invoices]
          : invoices,
      );
    }
    setMessageQueue((threads) => [
      ...(autoInvoice
        ? [
            {
              id: `msg-auto-invoice-${autoInvoice.id}-${Date.now()}`,
              participant: "Finance control",
              role: "buyer" as const,
              market: autoInvoice.market ?? "Finance",
              subject: `Invoice ready: ${autoInvoice.id}`,
              preview: `${nextOrder.supplierName ?? "Supplier"} delivered ${linkedOrderId}. ${formatEur(autoInvoice.amountEur)} is ready for finance export and reconciliation.`,
              status: "needs_reply" as const,
              updatedAt: "Just now",
              lastAction: "Auto-created invoice from delivered fulfillment",
            },
          ]
        : []),
      {
        id: `msg-fulfillment-${linkedOrderId}-${nextOrder.status}-${Date.now()}`,
        participant: nextOrder.buyer,
        role: "buyer",
        market: nextOrder.market ?? "Supplier route",
        subject: `Fulfillment update: ${linkedOrderId}`,
        preview: `${nextOrder.supplierName ?? "Supplier"} moved the order to ${nextOrder.status.replace(/_/g, " ")}. ${nextOrder.nextAction}`,
        status: nextOrder.status === "delivered" ? "resolved" : "waiting",
        updatedAt: "Just now",
      },
      ...threads,
    ]);
    recordAudit({
      actor: nextOrder.supplierName ?? "Supplier operator",
      action: "Advanced fulfillment status",
      entity: `${linkedOrderId} / ${nextOrder.status.replace(/_/g, " ")}`,
      market: nextOrder.market ?? "Supplier route",
    });
    if (autoInvoice) {
      recordAudit({
        actor: nextOrder.supplierName ?? "Supplier operator",
        action: "Created invoice from delivered fulfillment",
        entity: `${autoInvoice.id} / ${linkedOrderId} / ${formatEur(autoInvoice.amountEur)}`,
        market: autoInvoice.market ?? "Finance",
      });
    }
  }

  async function reportFulfillmentIssue(orderId: string) {
    const fulfillmentOrder = fulfillmentQueue.find((order) => order.id === orderId);

    if (!fulfillmentOrder || fulfillmentOrder.status === "delivered" || fulfillmentOrder.status === "issue") {
      return;
    }

    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot report supplier fulfillment issues`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked fulfillment issue report",
        entity: orderId,
        market: fulfillmentOrder.market ?? "Supplier route",
      });
      return;
    }

    const linkedOrderId = fulfillmentOrder.buyerOrderId ?? fulfillmentOrder.id;
    const issueOrder: SupplierFulfillmentOrder = {
      ...fulfillmentOrder,
      status: "issue",
      nextAction: "Buyer notified. Confirm substitution, shortage, or revised delivery slot before continuing.",
    };
    const result = await submitFulfillmentUpdate({
      amountEur: issueOrder.valueEur,
      buyerName: issueOrder.buyer,
      market: issueOrder.market,
      orderId: linkedOrderId,
      status: "issue",
      supplierName: issueOrder.supplierName,
    });

    if (!result.ok && (!ALLOW_LOCAL_WORKSPACE_FALLBACK || !isLocalOrderId(linkedOrderId))) {
      setCatalogActionState(`Fulfillment issue not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: fulfillmentOrder.supplierName ?? "Supplier operator",
        action: "Fulfillment issue save failed",
        entity: `${linkedOrderId} / ${readableSaveError(result.error)}`,
        market: fulfillmentOrder.market ?? "Supplier route",
      });
      return;
    }
    if (!result.ok) {
      setCatalogActionState("Fulfillment issue saved locally. Backend sync still needs connection.");
    }

    setFulfillmentQueue((items) => items.map((item) => (item.id === orderId ? issueOrder : item)));
    setOrderQueue((items) =>
      items.map((item) =>
        item.id === linkedOrderId
          ? {
              ...item,
              status: "issue",
            }
          : item,
      ),
    );
    updateStoredLocalOrder(linkedOrderId, (order) => ({ ...order, status: "issue" }));
    if (result.ok && result.discrepancyId) {
      const nextDiscrepancy: DiscrepancyRecord = {
        id: result.discrepancyId,
        orderId: linkedOrderId,
        supplierName: fulfillmentOrder.supplierName ?? "Supplier",
        buyerName: fulfillmentOrder.buyer,
        type: "late_delivery",
        status: "open",
        detail: `${fulfillmentOrder.supplierName ?? "Supplier"} reported a fulfillment issue. Confirm substitution, shortage, or revised delivery slot.`,
        createdAt: "Just now",
      };
      setDiscrepancyQueue((items) => [
        nextDiscrepancy,
        ...items.filter((item) => item.id !== nextDiscrepancy.id),
      ]);
    }
    setMessageQueue((threads) => [
      {
        id: `msg-fulfillment-issue-${linkedOrderId}-${Date.now()}`,
        participant: fulfillmentOrder.buyer,
        role: "buyer",
        market: fulfillmentOrder.market ?? "Supplier route",
        subject: `Fulfillment issue: ${linkedOrderId}`,
        preview: `${fulfillmentOrder.supplierName ?? "Supplier"} reported a fulfillment issue on ${linkedOrderId}. Buyer can recover the order with backup suppliers or resolve after revised terms are agreed.`,
        status: "needs_reply",
        updatedAt: "Just now",
        lastAction: "Supplier reported fulfillment issue",
      },
      ...threads,
    ]);
    recordAudit({
      actor: fulfillmentOrder.supplierName ?? "Supplier operator",
      action: "Reported fulfillment issue",
      entity: `${linkedOrderId} / ${fulfillmentOrder.buyer}`,
      market: fulfillmentOrder.market ?? "Supplier route",
    });
    setCatalogActionState(`${linkedOrderId} issue reported to buyer`);
    setSubmitState(`${linkedOrderId} marked as issue`);
  }

  async function resolveOrderIssue(orderId: string) {
    const order = orderQueue.find((item) => item.id === orderId);

    if (!order || order.status !== "issue") {
      return;
    }
    const linkedSupplierRequest = supplierOrderRequests.find((request) => request.orderId === orderId);
    const supplierReviewStats = supplierRequestReviewStats(linkedSupplierRequest);
    if (linkedSupplierRequest && supplierReviewStats.needsReview && !supplierReviewStats.canAccept) {
      setSubmitState(`${order.id} needs a backup supplier or manual follow-up before it can be cleared`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Blocked supplier reply acceptance",
        entity: `${order.id} / ${linkedSupplierRequest.supplierName} / ${supplierRequestStatusLabel(linkedSupplierRequest.status)}`,
        market: order.city,
      });
      return;
    }
    const supplierReviewNeedsAcceptance =
      linkedSupplierRequest && supplierReviewStats.needsReview && supplierReviewStats.canAccept;
    const acceptedSupplierRequest: SupplierOrderRequest | null = supplierReviewNeedsAcceptance
      ? {
          ...linkedSupplierRequest,
          status: "confirmed",
          messageDeliveryNote: [linkedSupplierRequest.messageDeliveryNote, "Restaurant accepted supplier changes"]
            .filter(Boolean)
            .join(" / "),
          updatedAt: new Date().toISOString(),
        }
      : null;

    let savedAcceptedSupplierRequest = acceptedSupplierRequest;

    if (acceptedSupplierRequest) {
      const supplierRequestResult = await submitSupplierOrderRequest(acceptedSupplierRequest);

      if (!supplierRequestResult.ok && (!ALLOW_LOCAL_WORKSPACE_FALLBACK || !isLocalOrderId(orderId))) {
        setSubmitState(`Supplier review acceptance not saved: ${readableSaveError(supplierRequestResult.error)}`);
        recordAudit({
          actor: "Restaurant buyer",
          action: "Supplier review acceptance save failed",
          entity: `${orderId} / ${readableSaveError(supplierRequestResult.error)}`,
          market: order.city,
        });
        return;
      }

      if (supplierRequestResult.ok) {
        savedAcceptedSupplierRequest = {
          ...supplierRequestResult.request,
          confirmationUrl: supplierConfirmationUrl(supplierRequestResult.request.publicToken),
        };
      }
    }

    const result = await submitFulfillmentUpdate({
      amountEur: bamToEur(order.total),
      buyerName: order.buyer,
      market: order.city,
      orderId,
      status: "confirmed",
      supplierName: order.supplier,
    });

    if (!result.ok && (!ALLOW_LOCAL_WORKSPACE_FALLBACK || !isLocalOrderId(orderId))) {
      setSubmitState(`Issue resolution not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Issue resolution save failed",
        entity: `${orderId} / ${readableSaveError(result.error)}`,
        market: order.city,
      });
      return;
    }
    if (!result.ok) {
      setSubmitState("Issue resolved locally. Backend sync still needs connection.");
    }

    if (savedAcceptedSupplierRequest) {
      setSupplierOrderRequests((requests) => {
        const nextRequests = requests.map((request) =>
          request.id === savedAcceptedSupplierRequest.id ||
          request.publicToken === savedAcceptedSupplierRequest.publicToken
            ? savedAcceptedSupplierRequest
            : request,
        );
        saveLocalSupplierOrderRequests(nextRequests);
        return nextRequests;
      });
    }

    setOrderQueue((items) =>
      items.map((item) => (item.id === orderId ? { ...item, status: "confirmed" } : item)),
    );
    updateStoredLocalOrder(orderId, (storedOrder) => ({ ...storedOrder, status: "confirmed" }));
    setFulfillmentQueue((items) =>
      items.map((item) =>
        (item.buyerOrderId ?? item.id) === orderId
          ? {
              ...item,
              status: "confirmed",
              nextAction: "Issue cleared by buyer; confirm corrected delivery timing and substitutions.",
            }
          : item,
      ),
    );
    setDiscrepancyQueue((items) =>
      items.map((item) =>
        item.orderId === orderId && item.status !== "resolved"
          ? { ...item, status: "resolved", detail: `${item.detail} Resolved by buyer.` }
          : item,
      ),
    );
    setMessageQueue((threads) => [
      {
        id: `msg-order-resolved-${order.id}-${Date.now()}`,
        participant: order.supplier,
        role: "supplier",
        market: order.city,
        subject: `Issue resolved: ${order.id}`,
        preview: acceptedSupplierRequest
          ? `${order.buyer} accepted the supplier-confirmed changes on ${order.id}. Use the corrected quantities, prices, delivery window, and notes before dispatch.`
          : `${order.buyer} cleared the issue on ${order.id}. Confirm the corrected delivery timing and any substitutions before dispatch.`,
        status: "waiting",
        updatedAt: "Just now",
        lastAction: acceptedSupplierRequest ? "Buyer accepted supplier changes" : "Buyer cleared issue",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Resolved order issue",
      entity: `${order.id} / ${order.supplier} / ${formatMoney(order.total)} KM`,
      market: order.city,
    });
    recordBehavior({
      actor: "Restaurant buyer",
      entity: `${order.id} / ${order.supplier}`,
      market: order.city,
      name: "issue_resolved",
      properties: {
        issueType: "order",
        orderId: order.id,
        supplierReviewAccepted: Boolean(acceptedSupplierRequest),
        totalBam: Number(order.total.toFixed(2)),
      },
      role: "restaurant",
      step: "order_issue_resolution",
    });
    setSubmitState(
      result.ok && result.resolvedDiscrepancies
        ? `${order.id} issue resolved; ${result.resolvedDiscrepancies} discrepancy closed`
        : acceptedSupplierRequest
          ? `${order.id} supplier changes accepted`
          : `${order.id} issue resolved`,
    );
  }

  function recoverOrderIssue(orderId: string) {
    const order = orderQueue.find((item) => item.id === orderId);

    if (!order || order.status !== "issue") {
      return;
    }

    if (!activePermission.canSubmitOrders) {
      setSubmitState(`${activePermission.role} cannot create replacement lists`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked issue replacement list",
        entity: `${order.id} / ${order.supplier}`,
        market: order.city,
      });
      return;
    }

    const recoveryMatches = order.items.map((item) => {
      const category = categoryForDraftItem(item, draftLookupContext);
      const backup = searchableMarketplaceProducts
        .filter(
          (product) =>
            product.category === category &&
            product.supplierName !== order.supplier &&
            product.availability !== "quote_only",
        )
        .sort((left, right) => supplierOptionScore(right) - supplierOptionScore(left))[0];

      return { item, category, backup };
    });
    const firstBackup = recoveryMatches.find((match) => match.backup)?.backup;
    const recoveryDraft = recoveryMatches.map(({ item, category, backup }) =>
      enrichDraftItem(
        {
          ...item,
          productId: backup ? `market-${backup.id}` : `recovery-${order.id}-${item.productId}`,
          supplier: backup?.supplierName ?? "Supplier pending",
          category,
          unitPrice: backup ? parseMarketplacePrice(backup.priceRange) || item.unitPrice : item.unitPrice,
          source: backup ? "marketplace" : "reorder",
        },
        draftLookupContext,
        {
          supplier: backup?.supplierName ?? "Supplier pending",
          category,
          source: backup ? "marketplace" : "reorder",
        },
      ),
    );
    const primaryCategory = recoveryMatches[0]?.category ?? "All";

    setDraft(recoveryDraft);
    setMarketplaceCategory(primaryCategory);
    setMarketplaceMode("Backup");
    setQuery(primaryCategory);

    if (firstBackup) {
      setSelectedMarketProductId(firstBackup.id);
    }

    setMessageQueue((threads) => [
      {
        id: `msg-order-recovery-${order.id}-${Date.now()}`,
        participant: "Supplier recovery desk",
        role: "supplier",
        market: order.city,
        subject: `Replacement list opened: ${order.id}`,
        preview: `${order.buyer} opened a replacement list for ${order.items.length} line${order.items.length === 1 ? "" : "s"} from ${order.id}. ${firstBackup ? `${firstBackup.supplierName} selected as first backup.` : "Backup supplier still needs selection."}`,
        status: "needs_reply",
        updatedAt: "Just now",
        lastAction: "Issue replacement list created",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Created issue replacement list",
      entity: `${order.id} / ${order.items.length} line${order.items.length === 1 ? "" : "s"} / ${firstBackup?.supplierName ?? "supplier pending"}`,
      market: order.city,
    });
    setSubmitState(`${order.id} replacement list ready`);
    setQuoteActionState(
      firstBackup
        ? `${firstBackup.supplierName} selected as backup for ${order.id}`
        : `${order.id} replacement list needs supplier selection`,
    );
    setActiveNav("Orders");
  }

  function nudgeOrderSupplier(orderId: string) {
    const order = orderQueue.find((item) => item.id === orderId);

    if (!order || order.status === "delivered") {
      return;
    }

    setMessageQueue((threads) => [
      {
        id: `msg-order-nudge-${order.id}-${Date.now()}`,
        participant: order.supplier,
        role: "supplier",
        market: order.city,
        subject: `Status request: ${order.id}`,
        preview: `${order.buyer} requested a supplier update for ${order.id}, delivery ${order.deliveryAt}, current status ${statusLabels[order.status]}.`,
        status: "waiting",
        updatedAt: "Just now",
        lastAction: "Restaurant requested order update",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Sent supplier order follow-up",
      entity: `${order.id} / ${order.supplier}`,
      market: order.city,
    });
    setSubmitState(`${order.id} supplier follow-up sent`);
  }

  async function exportInvoice(invoiceId: string) {
    const invoice = invoiceQueue.find((item) => item.id === invoiceId);

    if (!invoice || invoice.status !== "ready" || (invoice.paymentReadiness && invoice.paymentReadiness !== "ready")) {
      return;
    }

    if (!activePermission.canExportFinance) {
      setCatalogActionState(`${activePermission.role} cannot prepare invoices`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked invoice preparation",
        entity: `${invoice.id} / ${formatEur(invoice.amountEur)}`,
        market: invoice.market ?? "Finance",
      });
      return;
    }

    const fallbackInvoice: InvoiceRecord = {
      ...invoice,
      status: "exported",
      due: "Bank transfer prepared",
      matchStatus: "matched",
      paymentReadiness: "prepared",
    };
    const result = await updateInvoiceStatus({ invoiceId, status: "exported" });

    if (!result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      setCatalogActionState(`Invoice not prepared: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: activePermission.role,
        action: "Invoice preparation failed",
        entity: `${invoice.id} / ${formatEur(invoice.amountEur)} / ${readableSaveError(result.error)}`,
        market: invoice.market ?? "Finance",
      });
      return;
    }

    const updatedInvoice = result.ok ? { ...invoice, ...result.invoice } : fallbackInvoice;
    if (!result.ok) {
      upsertLocalInvoice(updatedInvoice);
    }

    setInvoiceQueue((items) => items.map((item) => (item.id === invoiceId ? updatedInvoice : item)));
    setCatalogActionState(
      result.ok
        ? `${invoice.id} prepared for bank transfer`
        : `${invoice.id} prepared locally. Backend sync still needs connection.`,
    );
    setMessageQueue((threads) => [
      {
        id: `msg-invoice-${invoice.id}-${Date.now()}`,
        participant: invoice.buyer,
        role: "buyer",
        market: invoice.market ?? "Invoices",
        subject: `Invoice prepared: ${invoice.id}`,
        preview: `${invoice.supplierName ?? "Supplier"} prepared ${formatEur(invoice.amountEur)} for ${invoice.orderId ?? invoice.buyer}. Bank-transfer details are ready to check.`,
        status: "resolved",
        updatedAt: "Just now",
      },
      ...threads,
    ]);
    recordAudit({
      actor: invoice.supplierName ?? "Supplier finance",
      action: "Prepared supplier invoice",
      entity: `${invoice.id} / ${formatEur(invoice.amountEur)}`,
      market: invoice.market ?? "Invoices",
    });
    recordBehavior({
      actor: invoice.supplierName ?? "Supplier finance",
      entity: `${invoice.id} / ${invoice.orderId ?? invoice.buyer}`,
      market: invoice.market ?? "Invoices",
      name: "invoice_matched",
      properties: {
        amountEur: invoice.amountEur,
        orderId: invoice.orderId ?? null,
        status: "exported",
      },
      role,
      step: "invoice_order_match",
    });
  }

  async function resolveInvoiceBlocker(invoiceId: string) {
    const invoice = invoiceQueue.find((item) => item.id === invoiceId);

    if (!invoice || invoice.status !== "blocked") {
      return;
    }

    if (!activePermission.canExportFinance) {
      setCatalogActionState(`${activePermission.role} cannot clear invoice issues`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked invoice issue resolution",
        entity: `${invoice.id} / ${formatEur(invoice.amountEur)}`,
        market: invoice.market ?? "Finance",
      });
      return;
    }

    const fallbackInvoice: InvoiceRecord = {
      ...invoice,
      status: "ready",
      due: "Finance mapping cleared",
      issuedAt: "Resolved just now",
      amountDeltaEur: invoice.expectedAmountEur === undefined ? undefined : 0,
      matchStatus: "matched",
      mismatchTypes: [],
      paymentReadiness: "ready",
    };
    const result = await updateInvoiceStatus({ invoiceId, status: "ready" });

    if (!result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      setCatalogActionState(`Invoice issue not cleared: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: activePermission.role,
        action: "Invoice issue clear failed",
        entity: `${invoice.id} / ${formatEur(invoice.amountEur)} / ${readableSaveError(result.error)}`,
        market: invoice.market ?? "Finance",
      });
      return;
    }

    const updatedInvoice = result.ok ? { ...invoice, ...result.invoice } : fallbackInvoice;
    if (!result.ok) {
      upsertLocalInvoice(updatedInvoice);
    }

    setInvoiceQueue((items) => items.map((item) => (item.id === invoiceId ? updatedInvoice : item)));
    setCatalogActionState(
      result.ok
        ? `${invoice.id} issue cleared`
        : `${invoice.id} issue cleared locally. Backend sync still needs connection.`,
    );
    setMessageQueue((threads) => [
      {
        id: `msg-invoice-resolved-${invoice.id}-${Date.now()}`,
        participant: invoice.buyer,
        role: "buyer",
        market: invoice.market ?? "Invoices",
        subject: `Invoice ready: ${invoice.id}`,
        preview: `${invoice.supplierName ?? "Supplier"} cleared the issue for ${invoice.id}. The invoice can now be prepared for ${invoice.orderId ?? invoice.buyer}.`,
        status: "waiting",
        updatedAt: "Just now",
        lastAction: "Invoice issue cleared",
      },
      ...threads,
    ]);
    recordAudit({
      actor: invoice.supplierName ?? "Supplier finance",
      action: "Cleared invoice issue",
      entity: `${invoice.id} / ${formatEur(invoice.amountEur)}`,
      market: invoice.market ?? "Finance",
    });
    recordBehavior({
      actor: invoice.supplierName ?? "Supplier finance",
      entity: `${invoice.id} / ${invoice.orderId ?? invoice.buyer}`,
      market: invoice.market ?? "Finance",
      name: "issue_resolved",
      properties: {
        amountEur: invoice.amountEur,
        issueType: "invoice",
        orderId: invoice.orderId ?? null,
      },
      role,
      step: "invoice_issue_resolution",
    });
    recordBehavior({
      actor: invoice.supplierName ?? "Supplier finance",
      entity: `${invoice.id} / ${invoice.orderId ?? invoice.buyer}`,
      market: invoice.market ?? "Finance",
      name: "invoice_matched",
      properties: {
        amountEur: invoice.amountEur,
        orderId: invoice.orderId ?? null,
        status: "ready",
      },
      role,
      step: "invoice_order_match",
    });
  }

  async function markInvoicePaid(invoiceId: string) {
    const invoice = invoiceQueue.find((item) => item.id === invoiceId);

    if (!invoice || invoice.status !== "exported") {
      return;
    }

    if (!activePermission.canExportFinance) {
      setCatalogActionState(`${activePermission.role} cannot mark invoices paid`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked invoice paid status",
        entity: `${invoice.id} / ${formatEur(invoice.amountEur)}`,
        market: invoice.market ?? "Finance",
      });
      return;
    }

    const fallbackInvoice: InvoiceRecord = {
      ...invoice,
      status: "paid",
      due: "Paid through bank",
      issuedAt: "Paid just now",
      matchStatus: "paid",
      paymentReadiness: "paid",
    };
    const result = await updateInvoiceStatus({ invoiceId, status: "paid" });

    if (!result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      setCatalogActionState(`Invoice payment not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: activePermission.role,
        action: "Invoice paid status save failed",
        entity: `${invoice.id} / ${formatEur(invoice.amountEur)} / ${readableSaveError(result.error)}`,
        market: invoice.market ?? "Finance",
      });
      return;
    }

    const updatedInvoice = result.ok ? { ...invoice, ...result.invoice } : fallbackInvoice;
    if (!result.ok) {
      upsertLocalInvoice(updatedInvoice);
    }

    setInvoiceQueue((items) => items.map((item) => (item.id === invoiceId ? updatedInvoice : item)));
    setCatalogActionState(
      result.ok
        ? `${invoice.id} marked paid`
        : `${invoice.id} marked paid locally. Backend sync still needs connection.`,
    );
    setMessageQueue((threads) => [
      {
        id: `msg-invoice-paid-${invoice.id}-${Date.now()}`,
        participant: invoice.supplierName ?? invoice.buyer,
        role: "supplier",
        market: invoice.market ?? "Invoices",
        subject: `Invoice paid: ${invoice.id}`,
        preview: `${invoice.buyer} marked ${formatEur(invoice.amountEur)} paid for ${invoice.orderId ?? invoice.id}.`,
        status: "resolved",
        updatedAt: "Just now",
        lastAction: "Invoice marked paid",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Finance",
      action: "Marked invoice paid",
      entity: `${invoice.id} / ${formatEur(invoice.amountEur)}`,
      market: invoice.market ?? "Finance",
    });
  }

  async function disputeInvoice(invoiceId: string) {
    const invoice = invoiceQueue.find((item) => item.id === invoiceId);

    if (!invoice || invoice.status === "blocked" || invoice.status === "paid") {
      return;
    }

    if (!activePermission.canExportFinance) {
      setCatalogActionState(`${activePermission.role} cannot flag invoice issues`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked invoice dispute",
        entity: `${invoice.id} / ${formatEur(invoice.amountEur)}`,
        market: invoice.market ?? "Finance",
      });
      return;
    }

    const fallbackInvoice: InvoiceRecord = {
      ...invoice,
      status: "blocked",
      due: "Disputed; supplier correction needed",
      issuedAt: "Disputed just now",
      matchStatus: "disputed",
      mismatchTypes: invoice.mismatchTypes?.length ? invoice.mismatchTypes : ["price_mismatch", "missing_goods"],
      paymentReadiness: "blocked",
    };
    const result = await updateInvoiceStatus({ invoiceId, status: "blocked" });

    if (!result.ok && !ALLOW_LOCAL_WORKSPACE_FALLBACK) {
      setCatalogActionState(`Invoice dispute not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: activePermission.role,
        action: "Invoice dispute save failed",
        entity: `${invoice.id} / ${formatEur(invoice.amountEur)} / ${readableSaveError(result.error)}`,
        market: invoice.market ?? "Finance",
      });
      return;
    }

    const updatedInvoice = result.ok ? { ...invoice, ...result.invoice } : fallbackInvoice;
    if (!result.ok) {
      upsertLocalInvoice(updatedInvoice);
    }

    setInvoiceQueue((items) => items.map((item) => (item.id === invoiceId ? updatedInvoice : item)));
    setCatalogActionState(
      result.ok
        ? `${invoice.id} flagged for correction`
        : `${invoice.id} flagged locally. Backend sync still needs connection.`,
    );
    setMessageQueue((threads) => [
      {
        id: `msg-invoice-dispute-${invoice.id}-${Date.now()}`,
        participant: invoice.supplierName ?? "Supplier",
        role: "supplier",
        market: invoice.market ?? "Invoices",
        subject: `Invoice issue: ${invoice.id}`,
        preview: `${invoice.buyer} flagged ${invoice.id}. Check price, missing goods, or order match before payment.`,
        status: "needs_reply",
        updatedAt: "Just now",
        lastAction: "Invoice flagged for correction",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Finance",
      action: "Flagged invoice issue",
      entity: `${invoice.id} / ${formatEur(invoice.amountEur)}`,
      market: invoice.market ?? "Finance",
    });
  }

  function supplierContactForName(supplierName: string) {
    const normalizedSupplier = supplierName.trim().toLowerCase();
    return restaurantSupplierContacts.find((contact) => {
      const normalizedContact = contact.name.trim().toLowerCase();
      return (
        normalizedContact === normalizedSupplier ||
        normalizedContact.includes(normalizedSupplier) ||
        normalizedSupplier.includes(normalizedContact)
      );
    });
  }

  function requestForSubmittedOrder(order: Order): SupplierOrderRequest {
    const contact = supplierContactForName(order.supplier);
    const supplierContactValue = supplierPrimaryContactValue(contact);
    const id = `sor-${order.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
    const publicToken = createPublicConfirmationToken();
    const confirmationUrl = supplierConfirmationUrl(publicToken);
    const totalBam = order.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
    const now = new Date().toISOString();
    const messageBody = buildSupplierOrderMessage({
      confirmationUrl,
      deliveryWindow: order.deliveryAt,
      items: order.items,
      market: order.city,
      restaurantName: order.buyer,
      supplierName: order.supplier,
      totalBam,
    });
    const request: SupplierOrderRequest = {
      id,
      orderId: order.id,
      publicToken,
      restaurantName: order.buyer,
      supplierName: order.supplier,
      supplierContact: supplierContactValue || undefined,
      preferredChannel: contact?.preferredChannel ?? preferredChannelForContact(supplierContactValue),
      market: order.city,
      deliveryWindow: order.deliveryAt,
      items: order.items,
      totalBam: Number(totalBam.toFixed(2)),
      currency: order.currency,
      messageBody,
      confirmationUrl,
      status: "sent",
      createdAt: now,
      updatedAt: now,
    };
    const deliveryReadiness = supplierRequestDeliveryReadiness(request);

    return {
      ...request,
      ...deliveryReadiness,
    };
  }

  function persistSupplierOrderRequestUpdate(updatedRequest: SupplierOrderRequest) {
    const requestWithUrl = {
      ...updatedRequest,
      confirmationUrl: supplierConfirmationUrl(updatedRequest.publicToken),
    };

    setSupplierOrderRequests((requests) => {
      const nextRequests = requests.map((request) => (request.id === updatedRequest.id ? requestWithUrl : request));
      saveLocalSupplierOrderRequests(nextRequests);
      return nextRequests;
    });

    void submitSupplierOrderRequest(requestWithUrl).then((result) => {
      if (!result.ok) {
        return;
      }

      const savedRequest = {
        ...result.request,
        confirmationUrl: supplierConfirmationUrl(result.request.publicToken),
      };
      setSupplierOrderRequests((requests) => {
        const nextRequests = requests.map((request) =>
          request.id === updatedRequest.id || request.publicToken === savedRequest.publicToken ? savedRequest : request,
        );
        saveLocalSupplierOrderRequests(nextRequests);
        return nextRequests;
      });
    });

    return requestWithUrl;
  }

  function markSupplierMessageShared(requestId: string, channel: SupplierOrderMessageChannel, note?: string) {
    const request = supplierOrderRequests.find((item) => item.id === requestId);
    if (!request) {
      return;
    }

    const now = new Date().toISOString();
    const updatedRequest = persistSupplierOrderRequestUpdate({
      ...request,
      messageDeliveryAt: now,
      messageDeliveryChannel: channel,
      messageDeliveryNote: note ?? `${supplierMessageChannelLabel(channel)} share path opened`,
      messageDeliveryStatus: "shared",
      followUpDueAt: isoAfterMinutes(90, new Date(now)),
      followUpMessage: supplierFollowUpMessage(request, Math.max(1, request.followUpCount ?? 0)),
      updatedAt: now,
    });

    setSubmitState(`${updatedRequest.supplierName} message marked shared via ${supplierMessageChannelLabel(channel)}`);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Shared supplier order message",
      entity: `${updatedRequest.orderId} / ${updatedRequest.supplierName} / ${supplierMessageChannelLabel(channel)}`,
      market: updatedRequest.market,
    });
  }

  async function shareSupplierOrderMessage(requestId: string) {
    const request = supplierOrderRequests.find((item) => item.id === requestId);
    if (!request) {
      return;
    }

    const shareApi = navigator as Navigator & {
      canShare?: (data: { text?: string; title?: string; url?: string }) => boolean;
      share?: (data: { text?: string; title?: string; url?: string }) => Promise<void>;
    };
    const sharePayload = {
      title: `VELEO order ${request.orderId} / ${request.restaurantName}`,
      text: request.messageBody,
      url: request.confirmationUrl,
    };

    if (!shareApi.share || (shareApi.canShare && !shareApi.canShare(sharePayload))) {
      await copySupplierOrderMessage(requestId);
      return;
    }

    try {
      await shareApi.share(sharePayload);
    } catch {
      setSubmitState(`Share failed for ${request.supplierName}. Message copy is still available.`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Supplier message share sheet failed",
        entity: `${request.orderId} / ${request.supplierName}`,
        market: request.market,
      });
      return;
    }

    markSupplierMessageShared(requestId, "copy", "Device share sheet opened");
    setSubmitState(`Share sheet opened for ${request.supplierName}`);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Opened supplier message device share sheet",
      entity: `${request.orderId} / ${request.supplierName}`,
      market: request.market,
    });
  }

  function markSupplierMessageIssue(
    requestId: string,
    deliveryStatus: Extract<SupplierOrderMessageDeliveryStatus, "needs_follow_up" | "failed">,
    note: string,
  ) {
    const request = supplierOrderRequests.find((item) => item.id === requestId);
    if (!request) {
      return;
    }

    const now = new Date().toISOString();
    const nextFollowUpCount = note.toLowerCase().includes("no supplier response")
      ? (request.followUpCount ?? 0) + 1
      : request.followUpCount ?? 0;
    const nextFollowUpMessage = note.toLowerCase().includes("no supplier response")
      ? supplierFollowUpMessage(request, nextFollowUpCount)
      : request.followUpMessage;
    const updatedRequest = persistSupplierOrderRequestUpdate({
      ...request,
      messageDeliveryAt: now,
      messageDeliveryNote: note,
      messageDeliveryStatus: deliveryStatus,
      followUpCount: nextFollowUpCount,
      followUpDueAt: deliveryStatus === "needs_follow_up" ? isoAfterMinutes(60, new Date(now)) : undefined,
      followUpMessage: nextFollowUpMessage,
      lastFollowUpAt: note.toLowerCase().includes("no supplier response") ? now : request.lastFollowUpAt,
      updatedAt: now,
    });

    setMessageQueue((threads) => [
      {
        id: `msg-supplier-delivery-${updatedRequest.id}-${Date.now()}`,
        participant: updatedRequest.supplierName,
        role: "supplier",
        market: updatedRequest.market,
        subject: `Supplier message follow-up: ${updatedRequest.orderId}`,
        preview: `${updatedRequest.supplierName} needs manual follow-up for ${updatedRequest.restaurantName}. ${note}`,
        status: "needs_reply",
        updatedAt: "Just now",
        lastAction: supplierMessageDeliveryLabel(deliveryStatus),
      },
      ...threads,
    ]);
    setSubmitState(`${updatedRequest.supplierName}: ${supplierMessageDeliveryLabel(deliveryStatus).toLowerCase()}`);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Flagged supplier message delivery issue",
      entity: `${updatedRequest.orderId} / ${updatedRequest.supplierName} / ${note}`,
      market: updatedRequest.market,
    });
  }

  async function copySupplierOrderMessage(requestId: string) {
    const request = supplierOrderRequests.find((item) => item.id === requestId);
    if (!request) {
      return;
    }

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("clipboard_unavailable");
      }

      await navigator.clipboard.writeText(request.messageBody);
    } catch {
      setSubmitState(`Copy failed for ${request.supplierName}. Select the message text manually.`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Supplier message copy failed",
        entity: `${request.orderId} / ${request.supplierName} / clipboard unavailable`,
        market: request.market,
      });
      return;
    }

    markSupplierMessageShared(requestId, "copy");
    setSubmitState(`Message copied for ${request.supplierName}`);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Copied supplier order message",
      entity: `${request.orderId} / ${request.supplierName}`,
      market: request.market,
    });
  }

  async function copySupplierFollowUpMessage(requestId: string) {
    const request = supplierOrderRequests.find((item) => item.id === requestId);
    if (!request) {
      return;
    }

    const nextFollowUpCount = Math.max(1, request.followUpCount ?? 1);
    const followUpMessage = request.followUpMessage || supplierFollowUpMessage(request, nextFollowUpCount);

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("clipboard_unavailable");
      }

      await navigator.clipboard.writeText(followUpMessage);
    } catch {
      setSubmitState(`Follow-up copy failed for ${request.supplierName}. Select the generated text manually.`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Supplier follow-up copy failed",
        entity: `${request.orderId} / ${request.supplierName} / clipboard unavailable`,
        market: request.market,
      });
      return;
    }

    const now = new Date().toISOString();
    persistSupplierOrderRequestUpdate({
      ...request,
      followUpCount: nextFollowUpCount,
      followUpDueAt: isoAfterMinutes(60, new Date(now)),
      followUpMessage,
      lastFollowUpAt: now,
      messageDeliveryNote: `Follow-up copied for ${supplierMessageChannelLabel(request.preferredChannel ?? "copy")}`,
      messageDeliveryStatus: "needs_follow_up",
      updatedAt: now,
    });
    setSubmitState(`${request.supplierName} follow-up copied`);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Copied supplier no-response follow-up",
      entity: `${request.orderId} / ${request.supplierName} / follow-up ${nextFollowUpCount}`,
      market: request.market,
    });
  }

  async function applySupplierOrderResponse(
    requestId: string,
    status: SupplierOrderRequestStatus,
    note: string,
    items: OrderItem[],
    deliveryAvailability: SupplierDeliveryAvailability = "available",
    supplierDeliveryWindow?: string,
    trustAction: SupplierConfirmationTrustAction = "response",
  ): Promise<SupplierOrderResponseSaveResult> {
    const currentRequest =
      supplierOrderRequests.find((request) => request.id === requestId) ??
      (publicConfirmationRequest?.id === requestId ? publicConfirmationRequest : undefined) ??
      readLocalSupplierOrderRequests().find((request) => request.id === requestId);
    if (!currentRequest) {
      return { ok: false, error: "supplier_order_request_not_found" };
    }

    const now = new Date().toISOString();
    const totalBam = Number(items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0).toFixed(2));
    const cleanSupplierDeliveryWindow = supplierDeliveryWindow?.trim() || currentRequest.deliveryWindow;
    const response = await respondToSupplierOrderConfirmation({
      deliveryAvailability,
      deliveryWindow: cleanSupplierDeliveryWindow,
      items,
      note,
      status,
      token: currentRequest.publicToken,
      trustAction,
    });
    let updatedRequest: SupplierOrderRequest;

    if (!response.ok) {
      const localSupplierRequests = readLocalSupplierOrderRequests();
      const localSupplierRequest = localSupplierRequests.find(
        (request) => request.id === currentRequest.id || request.publicToken === currentRequest.publicToken,
      );

      if (ALLOW_LOCAL_WORKSPACE_FALLBACK && localSupplierRequest) {
        updatedRequest = {
          ...currentRequest,
          items,
          status,
          totalBam,
          supplierDeliveryAvailability: deliveryAvailability,
          supplierDeliveryWindow: cleanSupplierDeliveryWindow,
          supplierNote: note.trim(),
          responseAt: now,
          followUpDueAt: undefined,
          followUpMessage: undefined,
          updatedAt: now,
        };

        saveLocalSupplierOrderRequests([
          updatedRequest,
          ...localSupplierRequests.filter(
            (request) => request.id !== updatedRequest.id && request.publicToken !== updatedRequest.publicToken,
          ),
        ]);
        recordAudit({
          actor: currentRequest.supplierName,
          action: "Saved local supplier confirmation response",
          entity: `${currentRequest.orderId} / ${supplierRequestStatusLabel(status)} / dev API unavailable`,
          market: currentRequest.market,
        });
      } else {
        const readableError = readableSaveError(response.error);

        setSubmitState(`Supplier response not saved: ${readableError}`);
        recordAudit({
          actor: currentRequest.supplierName,
          action: "Supplier response save failed",
          entity: `${currentRequest.orderId} / ${readableError}`,
          market: currentRequest.market,
        });

        return { ok: false, error: response.error };
      }
    } else {
      updatedRequest = {
        ...response.request,
        confirmationUrl: supplierConfirmationUrl(response.request.publicToken),
      };
    }
    const nextOrderStatus: OrderStatus =
      updatedRequest.status === "confirmed" ? "confirmed" : updatedRequest.status === "sent" ? "sent" : "issue";

    setSupplierOrderRequests((requests) =>
      requests.some((request) => request.id === requestId)
        ? requests.map((request) => (request.id === requestId ? updatedRequest : request))
        : [updatedRequest, ...requests],
    );
    setPublicConfirmationRequest((request) => (request?.id === requestId ? updatedRequest : request));

    setOrderQueue((orders) => {
      const nextOrders = orders.map((order) =>
        order.id === updatedRequest.orderId
          ? {
              ...order,
              items,
              deliveryAt: updatedRequest.supplierDeliveryWindow || order.deliveryAt,
              status: nextOrderStatus,
              total: totalBam,
            }
          : order,
      );
      saveLocalOrders(nextOrders);
      return nextOrders;
    });

    setFulfillmentQueue((orders) =>
      orders.map((order) =>
        order.buyerOrderId === updatedRequest.orderId || order.id === updatedRequest.orderId
          ? {
              ...order,
              itemCount: items.length,
              nextAction:
                deliveryAvailability === "unavailable"
                  ? "Restaurant needs a backup supplier; supplier marked delivery unavailable"
                  : status === "confirmed"
                  ? "Prepare delivery and invoice after dispatch"
                  : status === "declined"
                    ? "Restaurant needs a backup supplier"
                    : deliveryAvailability === "partial"
                      ? "Restaurant needs partial delivery and line changes reviewed"
                      : "Restaurant needs supplier changes reviewed",
              status: status === "confirmed" ? "confirmed" : "issue",
              valueEur: totalBam,
            }
          : order,
      ),
    );

    if (trustAction === "wrong_contact" || trustAction === "opt_out") {
      const normalizedSupplier = normalizeMarketText(updatedRequest.supplierName);
      const relationshipStatus: RestaurantSupplierContactRecord["relationshipStatus"] =
        trustAction === "opt_out" ? "opted_out" : "needs_review";

      setRestaurantSupplierContacts((contacts) =>
        contacts.map((contact) =>
          normalizeMarketText(contact.name) === normalizedSupplier
            ? {
                ...contact,
                relationshipStatus,
                updatedAt: now,
              }
            : contact,
        ),
      );

      if (trustAction === "opt_out") {
        setSavedSupplierNames((names) =>
          names.filter((name) => normalizeMarketText(name) !== normalizedSupplier),
        );
      }
    }

    setMessageQueue((threads) => [
      {
        id: `msg-supplier-response-${updatedRequest.id}-${Date.now()}`,
        participant: updatedRequest.supplierName,
        role: "supplier",
        market: updatedRequest.market,
        subject: `Supplier response: ${updatedRequest.orderId}`,
        preview: `${
          trustAction === "wrong_contact"
            ? `${updatedRequest.supplierName} says this is the wrong supplier/contact for ${updatedRequest.restaurantName}.`
            : trustAction === "opt_out"
              ? `${updatedRequest.supplierName} opted out of future VELEO supplier messages for this contact.`
              : `${updatedRequest.supplierName} marked ${supplierRequestStatusLabel(status).toLowerCase()} for ${updatedRequest.restaurantName}.`
        } ${supplierDeliveryAvailabilityLabel(deliveryAvailability)}${updatedRequest.supplierDeliveryWindow ? ` / ${updatedRequest.supplierDeliveryWindow}` : ""}.${note.trim() ? ` Note: ${note.trim()}` : ""}`,
        status: status === "confirmed" ? "resolved" : "needs_reply",
        updatedAt: "Just now",
        lastAction:
          trustAction === "wrong_contact"
            ? "Wrong contact"
            : trustAction === "opt_out"
              ? "Supplier opted out"
              : supplierRequestStatusLabel(status),
      },
      ...threads,
    ]);
    recordAudit({
      actor: updatedRequest.supplierName,
      action:
        trustAction === "wrong_contact"
          ? "Supplier marked wrong contact"
          : trustAction === "opt_out"
            ? "Supplier opted out from order link"
            : "Supplier responded to order link",
      entity: `${updatedRequest.orderId} / ${supplierRequestStatusLabel(status)}`,
      market: updatedRequest.market,
    });
    recordBehavior(
      {
        actor: updatedRequest.supplierName,
        entity: `${updatedRequest.orderId} / ${updatedRequest.restaurantName}`,
        market: updatedRequest.market,
        name: "supplier_response",
        properties: {
          deliveryAvailability,
          supplierDeliveryWindow: updatedRequest.supplierDeliveryWindow ?? null,
          lineCount: items.length,
          rejectedLineCount: items.filter((item) => item.supplierResponse === "rejected").length,
          responseStatus: status,
          trustAction,
          totalBam,
        },
        role: "supplier",
        step: "supplier_confirmed_from_link",
      },
      { once: true },
    );

    return { ok: true };
  }

  async function handleSubmitOrder() {
    if (!activePermission.canSubmitOrders) {
      setSubmitState(`${activePermission.role} cannot send orders`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked order submission",
        entity: `${selectedBranch.name} / ${formatBranchMoney(draftTotal, selectedBranch, { includeBase: true })}`,
        market: selectedBranch.market,
      });
      return;
    }

    const draftToSubmit = role === "restaurant" && !realRestaurantDataActive ? [] : draft;

    if (!draftToSubmit.length) {
      setSubmitState("Add at least one item");
      return;
    }

    setSubmitState("Sending order...");

    const enrichedDraft = draftToSubmit.map((item) => enrichDraftItem(item, draftLookupContext));
    setDraft(enrichedDraft);

    const packets = draftSupplierPacketsFor(enrichedDraft, draftLookupContext);
    const enrichedDraftTotal = packets.reduce((sum, packet) => sum + packet.total, 0);
    const readyApproval = approvalForDraft(accountApprovalQueue, selectedBranch, enrichedDraftTotal);
    const blockingChecks = orderDraftPreflightFor(
      enrichedDraft,
      packets,
      selectedBranch,
      draftLookupContext,
      readyApproval,
    ).filter((check) => check.status === "blocked");

    if (blockingChecks.length) {
      setSubmitState(
        blockingChecks.some((check) => check.id === "budget")
          ? "Request approval before sending this order"
          : `${blockingChecks.length} issue${blockingChecks.length === 1 ? "" : "s"} to fix before sending`,
      );
      return;
    }

    const results = await Promise.all(
      packets.map((packet) =>
        submitOrder({
          restaurantName: selectedBranch.name,
          branchName: selectedBranch.name,
          supplierName: packet.supplierName,
          city: selectedBranch.market,
          deliveryWindow: "Tomorrow, 08:30-10:30",
          items: packet.items,
        }),
      ),
    );
    const failedResults = results.filter((result) => !result.ok);

    if (failedResults.length) {
      if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
        setSubmitState(`Order not saved: ${failedResults.map((result) => readableSaveError(result.error)).join(", ")}`);
        recordAudit({
          actor: activePermission.role,
          action: "Supplier order save failed",
          entity: `${selectedBranch.name} / ${failedResults.map((result) => readableSaveError(result.error)).join(", ")}`,
          market: selectedBranch.market,
        });
        return;
      }

      setSubmitState("Order saved locally. Backend sync still needs connection.");
      recordAudit({
        actor: activePermission.role,
        action: "Saved local supplier order",
        entity: `${selectedBranch.name} / ${failedResults.map((result) => readableSaveError(result.error)).join(", ")}`,
        market: selectedBranch.market,
      });
    }

    const savedResults: Array<Extract<SubmitOrderResult, { ok: true }>> = failedResults.length
      ? packets.map((packet, index) => ({
          ok: true,
          mode: "local",
          orderNumber: `LO-${Date.now()}-${index + 1}`,
          total: Number(packet.total.toFixed(2)),
        }))
      : (results as Array<Extract<SubmitOrderResult, { ok: true }>>);

    const orderNumbers = savedResults.map((result) => result.orderNumber).join(", ");
    const submittedOrders: Order[] = savedResults.map((result, index) => {
      const packet = packets[index];

      return {
        id: result.orderNumber,
        buyer: selectedBranch.name,
        supplier: packet.supplierName,
        city: selectedBranch.market,
        deliveryAt: "Tomorrow, 08:30-10:30",
        status: "sent",
        total: result.total,
        currency: "BAM",
        items: packet.items,
      };
    });
    const submittedSupplierRequests = submittedOrders.map((order) => requestForSubmittedOrder(order));
    const savedSupplierRequestResults = await Promise.all(
      submittedSupplierRequests.map((request) => submitSupplierOrderRequest(request)),
    );
    const supplierRequestPersistenceFailures = savedSupplierRequestResults
      .map((result, index) =>
        result.ok
          ? null
          : {
              error: readableSaveError(result.error),
              order: submittedOrders[index],
              request: submittedSupplierRequests[index],
            },
      )
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
    const supplierRequestSaveFailures = ALLOW_LOCAL_WORKSPACE_FALLBACK
      ? []
      : supplierRequestPersistenceFailures;
    const persistedSupplierRequests = savedSupplierRequestResults.flatMap((result, index) => {
      if (result.ok) {
        return [
          {
            ...result.request,
            confirmationUrl: supplierConfirmationUrl(result.request.publicToken),
          },
        ];
      }

      if (ALLOW_LOCAL_WORKSPACE_FALLBACK) {
        return [submittedSupplierRequests[index]];
      }

      return [];
    });
    const submittedFulfillmentOrders = submittedOrders.map((order) => orderToFulfillmentOrder(order));
    const submittedTotal = submittedOrders.reduce((sum, order) => sum + order.total, 0);
    const remainingAfterSubmit = selectedBranchRemaining - submittedTotal;
    const policyNeedsApproval =
      submittedTotal > selectedBranch.approvalLimit || remainingAfterSubmit < 0;

    if (failedResults.length) {
      const storedOrders = readLocalOrders();
      saveLocalOrders([
        ...submittedOrders,
        ...storedOrders.filter((order) => !submittedOrders.some((submittedOrder) => submittedOrder.id === order.id)),
      ]);
    }

    setOrderQueue((currentOrders) => [
      ...submittedOrders,
      ...currentOrders.filter((order) => !submittedOrders.some((submittedOrder) => submittedOrder.id === order.id)),
    ]);
    setFulfillmentQueue((currentOrders) => [
      ...submittedFulfillmentOrders,
      ...currentOrders.filter((order) =>
        !submittedFulfillmentOrders.some((submittedOrder) => submittedOrder.id === order.id),
      ),
    ]);
    if (persistedSupplierRequests.length) {
      setSupplierOrderRequests((currentRequests) => [
        ...persistedSupplierRequests,
        ...currentRequests.filter((request) =>
          !persistedSupplierRequests.some((submittedRequest) =>
            submittedRequest.id === request.id || submittedRequest.publicToken === request.publicToken,
          ),
        ),
      ]);
    }
    setDeliveryAreaQueue((areas) =>
      applyDeliveryAreaPressure(areas, selectedBranch.market, submittedOrders.length),
    );
    setBranchBook((branches) =>
      branches.map((branch) => {
        if (branch.id !== selectedBranch.id) {
          return branch;
        }

        const updatedBranch = {
          ...branch,
          spentThisWeek: Number((branch.spentThisWeek + submittedTotal).toFixed(2)),
        };

        return { ...updatedBranch, status: branchStatusFor(updatedBranch) };
      }),
    );
    if (policyNeedsApproval && !readyApproval) {
      setApprovalQueue((requests) => [
        {
          id: `ap-order-${Date.now()}`,
          branch: selectedBranch.name,
          requester: activePermission.role,
          amountBam: Number(submittedTotal.toFixed(2)),
          policy:
            remainingAfterSubmit < 0
              ? "Weekly branch budget exceeded by submitted order"
              : `Above ${formatBranchMoney(selectedBranch.approvalLimit, selectedBranch, { includeBase: true })} branch approval limit`,
          due: "Review before next dispatch",
          status: "needs_approval",
        },
        ...requests,
      ]);
    } else if (policyNeedsApproval && readyApproval) {
      setApprovalQueue((requests) =>
        requests.map((request) =>
          request.id === readyApproval.id
            ? { ...request, due: `Used for ${orderNumbers}` }
            : request,
        ),
      );
    }
    setMessageQueue((threads) => [
      ...submittedOrders.map((order) => {
        const savedSupplierRequest = persistedSupplierRequests.find(
          (request) => request.orderId === order.id && request.supplierName === order.supplier,
        );
        const supplierRequestFailure = supplierRequestSaveFailures.find(
          (failure) => failure.order?.id === order.id && failure.request.supplierName === order.supplier,
        );
        const needsChannelFollowUp = savedSupplierRequest?.messageDeliveryStatus === "needs_follow_up";

        return {
          id: `msg-order-${order.id.replace(/[^a-z0-9]+/gi, "-")}`,
          participant: order.supplier,
          role: "supplier" as const,
          market: order.city,
          subject: savedSupplierRequest
            ? needsChannelFollowUp
              ? `Supplier channel needed: ${order.id}`
              : `Order sent: ${order.id}`
            : `Supplier link failed: ${order.id}`,
          preview: savedSupplierRequest
            ? needsChannelFollowUp
              ? `${order.buyer} generated the VELEO confirmation link, but ${order.supplier} needs a usable phone, Viber, WhatsApp, or email before direct sharing. Copy remains available. ${savedSupplierRequest.messageDeliveryNote ?? ""}`.trim()
              : `${order.buyer} sent ${order.items.length} line${order.items.length === 1 ? "" : "s"} for ${order.deliveryAt}. VELEO confirmation link is ready for WhatsApp/Viber/SMS/email.`
            : `${order.buyer} order was saved, but the VELEO supplier confirmation link was not saved${supplierRequestFailure ? `: ${supplierRequestFailure.error}` : ""}. Contact the supplier manually and retry the link save.`,
          status: savedSupplierRequest ? needsChannelFollowUp ? "needs_reply" as const : "waiting" as const : "needs_reply" as const,
          updatedAt: "Just now",
          lastAction: savedSupplierRequest
            ? needsChannelFollowUp
              ? "Supplier channel needs update"
              : "Supplier message generated"
            : "Supplier link save failed",
        };
      }),
      ...threads,
    ]);
    setDraft([]);
    if (persistedSupplierRequests.length) {
      setRecentSupplierRequestIds(persistedSupplierRequests.map((request) => request.id));
      setOrderFilter("all");
      navigate("Orders");
    } else {
      setRecentSupplierRequestIds([]);
    }
    setSubmitState(
      supplierRequestSaveFailures.length
        ? `${orderNumbers} saved, but ${supplierRequestSaveFailures.length} supplier confirmation link${supplierRequestSaveFailures.length === 1 ? "" : "s"} failed`
        : savedResults.length === 1
        ? `${orderNumbers} accepted and saved`
        : `${savedResults.length} supplier orders accepted: ${orderNumbers}`,
    );
    recordAudit({
      actor: activePermission.role,
      action: "Submitted supplier orders",
      entity: `${orderNumbers} / ${formatBranchMoney(submittedTotal, selectedBranch, { includeBase: true })} / ${savedResults.length} supplier${savedResults.length === 1 ? "" : "s"}`,
      market: selectedBranch.market,
    });
    recordAudit({
      actor: "Restaurant buyer",
      action: supplierRequestSaveFailures.length
        ? "Generated supplier confirmation links with failures"
        : "Generated supplier confirmation links",
      entity: [
        persistedSupplierRequests.map((request) => `${request.supplierName}: ${request.publicToken}`).join(", "),
        supplierRequestSaveFailures.length
          ? `failed: ${supplierRequestSaveFailures.map((failure) => `${failure.request.supplierName} / ${failure.error}`).join(", ")}`
          : "",
      ].filter(Boolean).join(" / "),
      market: selectedBranch.market,
    });
    recordBehavior(
      {
        actor: activePermission.role,
        entity: `${orderNumbers} / ${selectedBranch.name}`,
        market: selectedBranch.market,
        name: "first_order",
        properties: {
          lineCount: enrichedDraft.length,
          supplierCount: savedResults.length,
          totalBam: Number(submittedTotal.toFixed(2)),
        },
        role: "restaurant",
        step: "restaurant_first_order_sent",
      },
      { once: true },
    );
    if (enrichedDraft.some((item) => item.source === "reorder")) {
      const repeatOrderSeconds = behaviorElapsedSeconds(reorderStartedAt);

      recordBehavior({
        actor: activePermission.role,
        entity: `${orderNumbers} / ${selectedBranch.name}`,
        market: selectedBranch.market,
        name: "reorder_completed",
        properties: {
          lineCount: enrichedDraft.length,
          supplierCount: savedResults.length,
          totalBam: Number(submittedTotal.toFixed(2)),
        },
        role: "restaurant",
        step: "restaurant_repeat_order_sent",
      });
      if (repeatOrderSeconds !== null) {
        recordBehavior({
          actor: activePermission.role,
          entity: `${orderNumbers} / ${selectedBranch.name}`,
          market: selectedBranch.market,
          name: "time_to_repeat_order",
          properties: {
            seconds: repeatOrderSeconds,
            supplierCount: savedResults.length,
          },
          role: "restaurant",
          step: "restaurant_repeat_order_timing",
        });
      }
      setReorderStartedAt(null);
    }
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier:
        restaurantKnownSupplierNames.length > 0 ||
        enrichedDraft.some((item) => supplierForDraftItem(item, draftLookupContext) !== "Supplier pending"),
    });
    if (policyNeedsApproval) {
      recordAudit({
        actor: readyApproval ? "Policy engine" : "Policy engine",
        action: readyApproval ? "Used approved branch spend request" : "Created branch approval exception",
        entity: readyApproval
          ? `${readyApproval.id} / ${selectedBranch.name} / ${formatBranchMoney(submittedTotal, selectedBranch, { includeBase: true })}`
          : `${selectedBranch.name} / ${formatBranchMoney(submittedTotal, selectedBranch, { includeBase: true })}`,
        market: selectedBranch.market,
      });
    }
  }

  async function generateExternalInquiry(
    record: ExternalSupplierRecord,
    request?: Partial<ExternalInquiryDraft>,
  ) {
    const primaryProduct = request?.productName ?? record.products[0] ?? "requested products";
    const buyerName = request?.buyerName ?? selectedBranch.name;
    const quantity = request?.quantity ?? "Confirm quantity";
    const neededBy = request?.neededBy ?? "Next available delivery slot";
    const contactDetails = request?.contactDetails ?? branchInquiryContact(selectedBranch);
    const messageBody = `Hello ${record.name},\n\nVeleo is preparing a clean buying request for ${buyerName}.\n\nBusiness: ${buyerName}\nProduct: ${primaryProduct}\nQuantity: ${quantity}\nNeeded by: ${neededBy}\nDelivery market: ${record.market}\nContact: ${contactDetails}\n\nThis request came through Veleo so the restaurant can keep product, quantity, timing, contact, and supplier replies in one purchasing record.\n\nPlease confirm availability, minimum order quantity, delivery terms, pricing, and the best contact for future restaurant orders.\n\nThank you.`;

    setExternalInquiry("Preparing supplier message...");

    const result = await submitInquiry({
      contactHint: record.contactHint,
      deliveryHint: record.deliveryHint,
      externalSupplierId: record.id,
      supplierName: record.name,
      buyerName,
      market: record.market,
      productName: primaryProduct,
      quantity,
      neededBy,
      contactDetails,
      messageBody,
      source: record.source,
    });

    if (!result.ok) {
      setExternalInquiry(`Supplier message not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Backup supplier message failed",
        entity: `${record.name} / ${primaryProduct} / ${readableSaveError(result.error)}`,
        market: record.market,
      });
      return;
    }

    const inquiryRecord: ExternalInquiryRecord = {
      id: result.inquiry.id,
      externalSupplierId: record.id,
      supplierName: record.name,
      buyerName,
      productName: primaryProduct,
      quantity,
      neededBy,
      contactDetails,
      market: record.market,
      messageBody: result.inquiry.messageBody,
      status: "waiting",
      createdAt: "Just now",
    };

    setExternalInquiryLog((items) => [inquiryRecord, ...items]);
    if (record.status !== "converted") {
      const nextStatus =
        record.status === "external_unverified" || record.status === "needs_review"
          ? "ready_to_contact"
          : "invited";
      const existingTask = conversionTasks.find((task) => task.supplierName === record.name);
      const statusResult = await submitExternalSupplierStatus({
        id: record.id,
        nextStep: `Await reply to ${primaryProduct} request from ${buyerName}`,
        owner: existingTask?.owner ?? "Buyer growth loop",
        revenuePotentialEur:
          existingTask?.revenuePotentialEur ?? Math.round((record.confidence / 100) * record.products.length * 15000),
        status: nextStatus,
      });

      if (!statusResult.ok) {
        setDiscoveryActionState(`Supplier message saved, but supplier status was not updated: ${readableSaveError(statusResult.error)}`);
        recordAudit({
          actor: "Buyer growth loop",
          action: "Supplier message status update failed",
          entity: `${record.name} / ${readableSaveError(statusResult.error)}`,
          market: record.market,
        });
      } else {
        setExternalRecords((records) =>
          records.map((item) => (item.id === statusResult.record.id ? statusResult.record : item)),
        );

        if (statusResult.conversionTask) {
          setConversionTasks((tasks) => {
            const savedTask = statusResult.conversionTask;
            const exists = tasks.some((task) => task.id === savedTask?.id || task.supplierName === savedTask?.supplierName);

            return exists
              ? tasks.map((task) =>
                  task.id === savedTask?.id || task.supplierName === savedTask?.supplierName ? savedTask : task,
                )
              : savedTask
                ? [savedTask, ...tasks]
                : tasks;
          });
        }
      }
    }
    setMessageQueue((threads) => [
      {
        id: `msg-inquiry-${result.inquiry.id}`,
        participant: record.name,
        role: "external",
        market: record.market,
        subject: `Inquiry sent: ${primaryProduct}`,
        preview: `${buyerName} requested ${quantity} of ${primaryProduct}, needed ${neededBy}. Contact: ${contactDetails}.`,
        status: "waiting",
        updatedAt: "Just now",
      },
      ...threads,
    ]);
    setExternalInquiry(result.inquiry.messageBody);
    setDiscoveryActionState(`${record.name} inquiry sent and tracked`);
    recordAudit({
      actor: "Buyer growth loop",
      action: "Generated backup supplier message",
      entity: `${record.name} / ${primaryProduct}`,
      market: record.market,
    });
  }

  async function markExternalInquiryReplied(inquiryId: string) {
    const inquiry = externalInquiryLog.find((item) => item.id === inquiryId);

    if (!inquiry || inquiry.status === "replied") {
      return;
    }

    const record = externalRecords.find((item) => item.id === inquiry.externalSupplierId);
    const category = record ? inferExternalCategory(record.products.join(" ")) : inferExternalCategory(inquiry.productName);
    const quoteLead: QuoteRequest = {
      id: `qr-external-${inquiry.id}`,
      buyer: inquiry.buyerName,
      product: inquiry.productName,
      quantity: inquiry.quantity,
      neededBy: inquiry.neededBy,
      status: "waiting",
      suppliersMatched: 1,
      budget: "External reply received",
      supplierName: inquiry.supplierName,
      category,
      deliveryWindow: "Reply received; confirm commercial terms",
      priceRange: "Supplier terms pending review",
      termsNote: "Backup supplier replied. Confirm price, minimum order, and delivery before using in an order.",
    };
    const quoteResult = await submitQuoteRequest({
      buyer: quoteLead.buyer,
      market: inquiry.market,
      product: quoteLead.product,
      quantity: quoteLead.quantity,
      neededBy: quoteLead.neededBy,
      suppliersMatched: quoteLead.suppliersMatched,
      budget: quoteLead.budget,
      supplierName: quoteLead.supplierName,
      category: quoteLead.category,
      deliveryWindow: quoteLead.deliveryWindow,
      priceRange: quoteLead.priceRange,
    });

    if (!quoteResult.ok) {
      setQuoteActionState(`External reply not saved as quote: ${readableSaveError(quoteResult.error)}`);
      recordAudit({
        actor: "Buyer growth loop",
        action: "External reply quote save failed",
        entity: `${inquiry.supplierName} / ${inquiry.productName} / ${readableSaveError(quoteResult.error)}`,
        market: inquiry.market,
      });
      return;
    }

    const savedQuoteLead: QuoteRequest = {
      ...quoteResult.quoteRequest,
      status: "waiting",
      termsNote: quoteLead.termsNote,
    };
    const existingTask = conversionTasks.find((task) => task.supplierName === inquiry.supplierName);
    const statusResult = await submitExternalSupplierStatus({
      id: inquiry.externalSupplierId,
      nextStep: `Verify reply terms for ${inquiry.productName}; convert supplier if price and route are usable.`,
      owner: existingTask?.owner ?? "Buyer growth loop",
      revenuePotentialEur: existingTask?.revenuePotentialEur ?? 18000,
      status: "needs_review",
    });
    const inquiryStatusResult = await updateInquiryStatus(inquiry.id, "replied");

    if (!inquiryStatusResult.ok) {
      setDiscoveryActionState(`Reply quote saved, but inquiry status was not updated: ${readableSaveError(inquiryStatusResult.error)}`);
      recordAudit({
        actor: "Buyer growth loop",
        action: "Supplier message status update failed",
          entity: `${inquiry.supplierName} / ${inquiry.id} / ${readableSaveError(inquiryStatusResult.error)}`,
        market: inquiry.market,
      });
      return;
    }

    setExternalInquiryLog((items) =>
      items.map((item) => (item.id === inquiryId ? { ...item, status: "replied", createdAt: "Replied just now" } : item)),
    );
    if (!statusResult.ok) {
      setDiscoveryActionState(`Reply quote saved, but supplier status was not updated: ${readableSaveError(statusResult.error)}`);
      recordAudit({
        actor: "Buyer growth loop",
        action: "External reply status update failed",
        entity: `${inquiry.supplierName} / ${readableSaveError(statusResult.error)}`,
        market: inquiry.market,
      });
    } else {
      setExternalRecords((records) =>
        records.map((item) => (item.id === statusResult.record.id ? statusResult.record : item)),
      );

      if (statusResult.conversionTask) {
        setConversionTasks((tasks) => {
          const savedTask = statusResult.conversionTask;
          const exists = tasks.some((task) => task.id === savedTask?.id || task.supplierName === savedTask?.supplierName);

          return exists
            ? tasks.map((task) =>
                task.id === savedTask?.id || task.supplierName === savedTask?.supplierName ? savedTask : task,
              )
            : savedTask
              ? [savedTask, ...tasks]
              : tasks;
        });
      }
    }
    setQuoteQueue((requests) =>
      requests.some((request) => request.id === savedQuoteLead.id) ? requests : [savedQuoteLead, ...requests],
    );
    setMessageQueue((threads) => [
      {
        id: `msg-inquiry-reply-${inquiry.id}-${Date.now()}`,
        participant: inquiry.supplierName,
        role: "external",
        market: inquiry.market,
        subject: `Reply received: ${inquiry.productName}`,
        preview: `${inquiry.supplierName} replied to ${inquiry.buyerName}. ${savedQuoteLead.id} is waiting for price, minimum order, and delivery confirmation before order use.`,
        status: "needs_reply",
        updatedAt: "Just now",
        lastAction: "External reply converted into quote review",
      },
      ...threads,
    ]);
    setExternalInquiry(`${inquiry.messageBody}\n\nReply captured: Veleo opened ${savedQuoteLead.id} for price, minimum order, and delivery-term review.`);
    if (statusResult.ok) {
      setDiscoveryActionState(`${inquiry.supplierName} reply captured for review`);
    }
    setQuoteActionState(`${savedQuoteLead.id} opened from backup supplier reply`);
    recordAudit({
      actor: "Buyer growth loop",
      action: "Captured backup supplier reply",
      entity: `${inquiry.supplierName} / ${savedQuoteLead.id} / ${inquiry.productName}`,
      market: inquiry.market,
    });
  }

  async function handleCreateQuoteRequest(product: MarketplaceProduct) {
    if (!activePermission.canSubmitOrders) {
      setQuoteActionState(`${activePermission.role} cannot create restaurant price requests`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked price request creation",
        entity: `${product.name} / ${product.supplierName}`,
        market: product.market,
      });
      return;
    }

    if (product.supplierType === "external") {
      const externalRecord = externalRecords.find((record) => record.name === product.supplierName);

      navigate("Supplier Finder");

      if (externalRecord) {
        await generateExternalInquiry(externalRecord, {
          productName: product.name,
          quantity: product.minOrder,
          neededBy: "Next available delivery slot",
        });
      }

      return;
    }

    setQuoteActionState("Creating quote...");

    const result = await submitQuoteRequest({
      buyer: selectedBranch.name,
      market: product.market || selectedBranch.market,
      product: product.name,
      quantity: product.minOrder,
      neededBy: "Tomorrow 08:00",
      suppliersMatched: product.supplierType === "verified" ? 4 : 1,
      budget: product.priceRange.includes("Quote") ? "Open quote" : product.priceRange,
      supplierName: product.supplierName,
      category: product.category,
      deliveryWindow: product.deliveryWindow,
      priceRange: product.priceRange,
      unitPrice: parseMarketplacePrice(product.priceRange),
    });

    if (!result.ok) {
      const localRequest: QuoteRequest = {
        id: `qr-local-market-${Date.now()}`,
        buyer: selectedBranch.name,
        product: product.name,
        quantity: product.minOrder,
        neededBy: "Tomorrow 08:00",
        status: "sent",
        suppliersMatched: product.supplierType === "verified" ? 4 : 1,
        budget: product.priceRange.includes("Quote") ? "Open quote" : product.priceRange,
        supplierName: product.supplierName,
        category: product.category,
        deliveryWindow: product.deliveryWindow,
        priceRange: product.priceRange,
        unitPrice: parseMarketplacePrice(product.priceRange),
        termsNote: "Pending sync. Restaurant requested a private price from this supplier.",
      };

      upsertLocalQuoteRequest(localRequest);
      setQuoteQueue((requests) => [localRequest, ...requests.filter((request) => request.id !== localRequest.id)]);
      setMessageQueue((threads) => [
        {
          id: `msg-local-market-quote-${localRequest.id}`,
          participant: product.supplierName,
          role: "supplier",
          market: product.market,
          subject: `Quote request: ${product.name}`,
          preview: `${selectedBranch.name} requested ${product.minOrder} for Tomorrow 08:00. Request is pending backend sync, but suppliers can quote it in this workspace.`,
          status: "needs_reply",
          updatedAt: "Just now",
        },
        ...threads,
      ]);
      setQuoteActionState(`${localRequest.id} is in this workspace. Backend sync still needs connection.`);
      markRoleDataSeen("restaurant", {
        orderOrMessage: true,
        supplier: restaurantKnownSupplierNames.length > 0 || Boolean(product.supplierName),
      });
      recordAudit({
        actor: "Restaurant buyer",
        action: "Created local marketplace price request",
        entity: `${product.name} / ${product.supplierName} / ${readableSaveError(result.error)}`,
        market: product.market,
      });
      recordBehavior(
        {
          entity: `${product.name} / ${product.supplierName}`,
          market: product.market,
          name: "first_quote",
          properties: {
            category: product.category,
            quoteRequestId: localRequest.id,
            source: "marketplace",
          },
          role: "restaurant",
          step: "restaurant_private_price_request",
        },
        { once: true },
      );
      return;
    }

    const createdRequest: QuoteRequest = {
      ...result.quoteRequest,
      status: "sent",
    };

    setQuoteQueue((requests) => [createdRequest, ...requests]);
    setMessageQueue((threads) => [
      {
        id: `msg-quote-request-${createdRequest.id}-${Date.now()}`,
        participant: product.supplierName,
        role: "supplier",
        market: product.market,
        subject: `Quote request: ${product.name}`,
        preview: `${selectedBranch.name} requested ${product.minOrder} for Tomorrow 08:00. Supplier should confirm price, minimum order, and delivery terms.`,
        status: "needs_reply",
        updatedAt: "Just now",
      },
      ...threads,
    ]);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Created price request",
      entity: `${product.name} / ${product.supplierName} / ${product.minOrder}`,
      market: product.market,
    });
    recordBehavior(
      {
        entity: `${product.name} / ${product.supplierName}`,
        market: product.market,
        name: "first_quote",
        properties: {
          category: product.category,
          quoteRequestId: createdRequest.id,
          source: "marketplace",
        },
        role: "restaurant",
        step: "restaurant_private_price_request",
      },
      { once: true },
    );
    setQuoteActionState(`${createdRequest.id} sent to ${product.supplierName} and saved`);
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: restaurantKnownSupplierNames.length > 0 || Boolean(product.supplierName),
    });
  }

  async function createQuickQuoteRequest(input: QuickQuoteRequestInput) {
    if (!activePermission.canSubmitOrders) {
      setQuoteActionState(`${activePermission.role} cannot create restaurant price requests`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked quick price request",
        entity: `${input.product} / ${input.quantity} ${input.unit}`,
        market: selectedBranch.market,
      });
      return;
    }

    const cleanProduct = input.product.trim();
    const cleanSupplier = input.supplierName.trim();
    const cleanUnit = input.unit.trim() || "unit";
    const cleanNeededBy = input.neededBy.trim() || "Next delivery";
    const cleanQuantity = Math.max(0, input.quantity || 0);

    if (!cleanProduct || !cleanQuantity) {
      setQuoteActionState("Add an item and quantity before asking suppliers for price");
      return;
    }

    const quantityText = `${formatMoney(cleanQuantity).replace(".00", "")} ${cleanUnit}`;
    setQuoteActionState("Asking suppliers for price...");

    const result = await submitQuoteRequest({
      buyer: selectedBranch.name,
      market: selectedBranch.market,
      product: cleanProduct,
      quantity: quantityText,
      neededBy: cleanNeededBy,
      suppliersMatched: cleanSupplier ? 1 : Math.max(1, restaurantKnownSupplierNames.length || 3),
      budget: "Open price request",
      supplierName: cleanSupplier || undefined,
      category: inferExternalCategory(cleanProduct),
      deliveryWindow: cleanNeededBy,
      priceRange: "Suppliers must quote price, minimum order, and delivery",
    });

    if (!result.ok) {
      const localRequest: QuoteRequest = {
        id: `qr-local-${Date.now()}`,
        buyer: selectedBranch.name,
        product: cleanProduct,
        quantity: quantityText,
        neededBy: cleanNeededBy,
        status: "sent",
        suppliersMatched: cleanSupplier ? 1 : Math.max(1, restaurantKnownSupplierNames.length || 3),
        budget: "Open price request",
        supplierName: cleanSupplier || undefined,
        category: inferExternalCategory(cleanProduct),
        deliveryWindow: cleanNeededBy,
        priceRange: "Suppliers must quote price, minimum order, and delivery",
        termsNote: cleanSupplier
          ? "Pending sync. Restaurant asked this supplier for a private price."
          : "Pending sync. Restaurant asked matched suppliers to compete privately.",
      };
      upsertLocalQuoteRequest(localRequest);
      setQuoteQueue((requests) => [localRequest, ...requests]);
      setMessageQueue((threads) => [
        {
          id: `msg-local-quick-quote-${localRequest.id}`,
          participant: cleanSupplier || "Matched suppliers",
          role: "supplier",
          market: selectedBranch.market,
          subject: `Price request: ${cleanProduct}`,
          preview: `${selectedBranch.name} needs ${quantityText} by ${cleanNeededBy}. Request is pending backend sync, but suppliers can quote it in this workspace.`,
          status: "needs_reply",
          updatedAt: "Just now",
        },
        ...threads,
      ]);
      setActiveNav("Marketplace");
      setQuoteActionState(`${localRequest.id} is in this workspace. Backend sync still needs connection.`);
      markRoleDataSeen("restaurant", {
        orderOrMessage: true,
        supplier: Boolean(cleanSupplier || restaurantKnownSupplierNames.length),
      });
      recordAudit({
        actor: "Restaurant buyer",
        action: "Created local quick price request",
        entity: `${cleanProduct} / ${quantityText} / ${readableSaveError(result.error)}`,
        market: selectedBranch.market,
      });
      recordBehavior(
        {
          entity: `${cleanProduct} / ${cleanSupplier || "matched suppliers"}`,
          market: selectedBranch.market,
          name: "first_quote",
          properties: {
            quoteRequestId: localRequest.id,
            source: "quick_request",
            supplierSpecified: Boolean(cleanSupplier),
          },
          role: "restaurant",
          step: "restaurant_private_price_request",
        },
        { once: true },
      );
      return;
    }

    const createdRequest: QuoteRequest = {
      ...result.quoteRequest,
      status: "sent",
      termsNote: cleanSupplier
        ? "Restaurant asked this supplier for a private price."
        : "Restaurant asked matched suppliers to compete privately.",
    };

    setQuoteQueue((requests) => [createdRequest, ...requests]);
    setMessageQueue((threads) => [
      {
        id: `msg-quick-quote-${createdRequest.id}-${Date.now()}`,
        participant: cleanSupplier || "Matched suppliers",
        role: "supplier",
        market: selectedBranch.market,
        subject: `Price request: ${cleanProduct}`,
        preview: `${selectedBranch.name} needs ${quantityText} by ${cleanNeededBy}. Supplier should quote price, minimum order, delivery, and substitutions.`,
        status: "needs_reply",
        updatedAt: "Just now",
      },
      ...threads,
    ]);
    setActiveNav("Marketplace");
    setQuoteActionState(`${createdRequest.id} sent. Suppliers can now compete on price.`);
    markRoleDataSeen("restaurant", {
      orderOrMessage: true,
      supplier: Boolean(cleanSupplier || restaurantKnownSupplierNames.length),
    });
    recordAudit({
      actor: "Restaurant buyer",
      action: "Created quick price request",
      entity: `${cleanProduct} / ${quantityText} / ${cleanSupplier || "matched suppliers"}`,
      market: selectedBranch.market,
    });
    recordBehavior(
      {
        entity: `${cleanProduct} / ${cleanSupplier || "matched suppliers"}`,
        market: selectedBranch.market,
        name: "first_quote",
        properties: {
          quoteRequestId: createdRequest.id,
          source: "quick_request",
          supplierSpecified: Boolean(cleanSupplier),
        },
        role: "restaurant",
        step: "restaurant_private_price_request",
      },
      { once: true },
    );
  }

  async function actOnPurchaseInsight(insight: PurchaseInsight) {
    const normalizedSupplier = normalizeMarketText(insight.supplier);
    const normalizedCategory = normalizeMarketText(insight.category);
    if (insight.priceBenchmark) {
      recordBehavior({
        entity: `${insight.supplier} / ${insight.category}`,
        market: selectedBranch.market,
        name: "price_benchmark_used",
        properties: {
          benchmarkConfidence: insight.priceBenchmark.confidence,
          benchmarkMedianBam: insight.priceBenchmark.medianBam,
          sampleCount: insight.priceBenchmark.sampleCount,
          supplierCount: insight.priceBenchmark.supplierCount,
        },
        role: "restaurant",
        step: "restaurant_price_benchmark_action",
      });
    }
    const supplierCatalogProduct = catalogProducts.find(
      (product) =>
        normalizeMarketText(product.supplier) === normalizedSupplier &&
        normalizeMarketText(product.category).includes(normalizedCategory.split(" ")[0] ?? normalizedCategory),
    );
    const supplierMarketplaceProduct = searchableMarketplaceProducts.find(
      (product) =>
        normalizeMarketText(product.supplierName) === normalizedSupplier &&
        normalizeMarketText(product.category).includes(normalizedCategory.split(" ")[0] ?? normalizedCategory),
    );
    const backupMarketplaceProduct = searchableMarketplaceProducts
      .filter(
        (product) =>
          normalizeMarketText(product.supplierName) !== normalizedSupplier &&
          normalizeMarketText(product.category).includes(normalizedCategory.split(" ")[0] ?? normalizedCategory),
      )
      .sort((left, right) => supplierOptionScore(right, savedSupplierNames) - supplierOptionScore(left, savedSupplierNames))[0];

    if (insight.status === "over_budget") {
      const amountBam = Math.max(
        selectedBranch.approvalLimit + 1,
        Number((insight.monthSpendEur * BAM_PER_EUR * 0.12).toFixed(2)),
      );
      const existingApproval = approvalQueue.find((request) => request.id === `ap-${insight.id}`);

      if (!existingApproval) {
        const approval: ApprovalRequest = {
          id: `ap-${insight.id}`,
          branch: selectedBranch.name,
          requester: roleLabels.restaurant,
          amountBam,
          policy: `${insight.category} spend is ${insight.variancePct.toFixed(1)}% above control band`,
          due: "Before next reorder",
          status: "needs_approval",
        };

        setApprovalQueue((requests) => [approval, ...requests]);
        setMessageQueue((threads) => [
          {
            id: `msg-spend-control-${insight.id}-${Date.now()}`,
            participant: "Finance control",
            role: "buyer",
            market: selectedBranch.market,
            subject: `Spend control opened: ${insight.category}`,
            preview: `${selectedBranch.name} opened ${approval.id} because ${insight.supplier} is ${insight.variancePct.toFixed(1)}% over policy band. Buyer should approve, reduce quantity, or request alternate terms.`,
            status: "needs_reply",
            updatedAt: "Just now",
            lastAction: "Spend insight became approval control",
          },
          ...threads,
        ]);
        recordAudit({
          actor: "Restaurant buyer",
          action: "Opened spend insight approval",
          entity: `${approval.id} / ${insight.supplier} / ${formatMoney(amountBam)} KM`,
          market: selectedBranch.market,
        });
      }

      setSubmitState(`${insight.category} spend control opened`);
      setActiveNav("Orders");
      return;
    }

    if (insight.status === "price_watch" && backupMarketplaceProduct) {
      await handleCreateQuoteRequest(backupMarketplaceProduct);
      setQuoteActionState(`${backupMarketplaceProduct.supplierName} backup price requested for ${insight.category}`);
      return;
    }

    if (supplierCatalogProduct && supplierCatalogProduct.stockStatus !== "available") {
      findBackupForCatalogProduct(supplierCatalogProduct);
      return;
    }

    const targetProduct = backupMarketplaceProduct ?? supplierMarketplaceProduct;

    if (targetProduct) {
      setMarketplaceCategory(targetProduct.category);
      setMarketplaceMode(marketplaceModeForProduct(targetProduct));
      setSelectedMarketProductId(targetProduct.id);
      setQuery(insight.category);
      setQuoteActionState(`${targetProduct.supplierName} selected from ${insight.category} spend insight`);
      setActiveNav("Marketplace");
      recordAudit({
        actor: "Restaurant buyer",
        action: "Opened supplier comparison from spend insight",
        entity: `${insight.supplier} / ${insight.category}`,
        market: targetProduct.market,
      });
      return;
    }

    setQuery(insight.category);
    captureMarketplaceDemandGap();
  }

  async function runDiscoverySource(source: DiscoverySource) {
    setDiscoveryActionState(`Recording ${source.source} source check`);
    const result = await submitDiscoverySourceRun({ sourceId: source.id });

    if (!result.ok) {
      setDiscoveryActionState(`Discovery run not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Discovery automation",
        action: "Discovery source run failed",
        entity: `${source.source} / ${readableSaveError(result.error)}`,
        market: source.market,
      });
      return;
    }

    const message: MessageThread = {
      id: `msg-discovery-${result.run.id}`,
      participant: "Discovery ops",
      role: "external",
      market: result.source.market,
      subject: `Discovery source checked: ${result.source.source}`,
      preview:
        "Source check was recorded without creating supplier records. Add a public supplier URL to extract products, contact hints, and review evidence.",
      status: "waiting",
      updatedAt: "Just now",
      lastAction: "Source check recorded",
    };
    const messageResult = await submitMessageThread(message);

    setDiscoverySourceQueue((sources) =>
      sources.map((item) => (item.id === result.source.id ? result.source : item)),
    );
    setDiscoveryRuns((runs) => [result.run, ...runs]);

    if (messageResult.ok) {
      setMessageQueue((threads) => [messageResult.message, ...threads]);
    }

    setDiscoveryActionState(`${result.source.source} check saved; add a public URL to create supplier records`);
    recordAudit({
      actor: "Discovery automation",
      action: "Recorded discovery source run",
      entity: `${result.source.source} / 0 supplier records created`,
      market: result.source.market,
    });
  }

  async function savePublicSupplierSource(input: SubmitPublicSupplierSourceInput) {
    setDiscoveryActionState(`Reading public source for ${input.market}`);
    const result = await submitPublicSupplierSource(input);

    if (!result.ok) {
      setDiscoveryActionState(`Source could not be read: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Discovery ops",
        action: "Public supplier source failed",
        entity: input.sourceUrl,
        market: input.market,
      });
      return;
    }

    const record = result.record;
    const existingKey = `${normalizeMarketText(record.name)}::${normalizeMarketText(record.market)}`;
    const created = !externalRecords.some(
      (item) => `${normalizeMarketText(item.name)}::${normalizeMarketText(item.market)}` === existingKey,
    );

    setExternalRecords((records) => {
      const exists = records.some(
        (item) => `${normalizeMarketText(item.name)}::${normalizeMarketText(item.market)}` === existingKey,
      );

      if (exists) {
        return records.map((item) =>
          `${normalizeMarketText(item.name)}::${normalizeMarketText(item.market)}` === existingKey
            ? { ...record, id: item.id, lastSeen: "Just now" }
            : item,
        );
      }

      return [record, ...records];
    });
    setDiscoveryRuns((runs) => [
      {
        id: `run-public-source-${Date.now()}`,
        source: input.sourceType,
        market: input.market,
        recordsFound: 1,
        externalRecordsCreated: created ? 1 : 0,
        status: "completed",
        finishedAt: "Just now",
      },
      ...runs,
    ]);
    setConversionTasks((tasks) => {
      if (!result.conversionTask) {
        return tasks;
      }

      const exists = tasks.some(
        (task) => task.id === result.conversionTask?.id || task.supplierName === result.conversionTask?.supplierName,
      );

      return exists
        ? tasks.map((task) =>
            task.id === result.conversionTask?.id || task.supplierName === result.conversionTask?.supplierName
              ? result.conversionTask
              : task,
          )
        : [result.conversionTask, ...tasks];
    });
    setMessageQueue((threads) => [
      {
        id: `msg-public-source-${record.id}-${Date.now()}`,
        participant: "Discovery ops",
        role: "external",
        market: record.market,
        subject: `Public source structured: ${record.name}`,
        preview: `${record.name} was extracted from ${input.sourceUrl}. Products: ${record.products.join(", ")}. Contact: ${record.contactHint}.`,
        status: "needs_reply",
        updatedAt: "Just now",
        lastAction: "Verify public source record",
      },
      ...threads,
    ]);
    setDiscoveryActionState(
      `${record.name} saved as ${record.status.replace(/_/g, " ")}`,
    );
    recordAudit({
      actor: "Discovery ops",
      action: "Structured public supplier source",
      entity: `${record.name} / ${record.products.join(", ")}`,
      market: record.market,
    });
  }

  async function approveLocalizationRule(ruleId: string) {
    const rule = localizationQueue.find((item) => item.id === ruleId);

    if (!rule || rule.status === "live") {
      return;
    }
    const result = await submitLocalizationRule({ ...rule, status: "live" });

    if (!result.ok) {
      setSubmitState(`Localization not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Platform ops",
        action: "Localization approval save failed",
        entity: `${rule.market} / ${readableSaveError(result.error)}`,
        market: rule.market,
      });
      return;
    }

    const savedRule = result.localizationRule;
    const message: MessageThread = {
      id: `msg-localization-${savedRule.id}-${Date.now()}`,
      participant: "Localization ops",
      role: "buyer",
      market: savedRule.market,
      subject: `Localization live: ${savedRule.market}`,
      preview: `${savedRule.market} is now live with ${savedRule.currency}, ${savedRule.taxMode}, and ${savedRule.language}. Branches can use the market rule for buying controls.`,
      status: "resolved",
      updatedAt: "Just now",
      lastAction: "Localization rule approved",
    };
    void submitMessageThread(message);

    setLocalizationQueue((rules) =>
      rules.map((item) => (item.id === ruleId ? savedRule : item)),
    );
    setMessageQueue((threads) => [
      message,
      ...threads,
    ]);
    recordAudit({
      actor: "Platform ops",
      action: "Approved localization rule",
      entity: `${savedRule.market} / ${savedRule.currency} / ${savedRule.taxMode}`,
      market: savedRule.market,
    });
  }

  async function advanceExternalRecord(record: ExternalSupplierRecord) {
    const nextStatusByCurrent: Record<ExternalSupplierRecord["status"], ExternalSupplierRecord["status"]> = {
      external_unverified: "needs_review",
      needs_review: "ready_to_contact",
      ready_to_contact: "invited",
      invited: "converted",
      converted: "converted",
    };
    const nextStatus = nextStatusByCurrent[record.status];
    const nextStep =
      nextStatus === "ready_to_contact"
        ? "Generate buyer inquiry or send supplier invite"
        : nextStatus === "invited"
          ? "Await supplier admin acceptance"
          : nextStatus === "converted"
            ? "Supplier converted to verified network"
            : "Review public record evidence";
    const existingTask = conversionTasks.find((task) => task.supplierName === record.name);
    const result = await submitExternalSupplierStatus({
      id: record.id,
      nextStep,
      owner: existingTask?.owner ?? (record.confidence >= 78 ? "Discovery ops" : "Supply verification"),
      revenuePotentialEur:
        existingTask?.revenuePotentialEur ?? Math.round((record.confidence / 100) * record.products.length * 18500),
      status: nextStatus,
    });

    if (!result.ok) {
      setDiscoveryActionState(`External supplier status not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Discovery ops",
        action: "External supplier status save failed",
        entity: `${record.name} / ${readableSaveError(result.error)}`,
        market: record.market,
      });
      return;
    }
    const savedRecord = result.record;
    const savedTask = result.conversionTask;

    setExternalRecords((records) =>
      records.map((item) =>
        item.id === savedRecord.id ? savedRecord : item,
      ),
    );
    if (savedTask) {
      setConversionTasks((tasks) => {
        const exists = tasks.some((task) => task.id === savedTask.id || task.supplierName === savedTask.supplierName);
        return exists
          ? tasks.map((task) =>
              task.id === savedTask.id || task.supplierName === savedTask.supplierName ? savedTask : task,
            )
          : [savedTask, ...tasks];
      });
    }

    if (nextStatus === "converted") {
      const convertedCatalogProducts = externalRecordToCatalogProducts(savedRecord);
      const savedProducts = await Promise.all(
        convertedCatalogProducts.map((product) =>
          submitCatalogProduct({
            category: product.category,
            currency: product.currency,
            minimumOrderQuantity: product.lastOrderedKg,
            name: product.name,
            price: product.price,
            stockStatus: product.stockStatus,
            supplierName: product.supplier,
            unit: product.unit,
          }),
        ),
      );
      const productFailure = savedProducts.find((item) => !item.ok);

      if (productFailure && !productFailure.ok) {
        setDiscoveryActionState(`${savedRecord.name} converted, but catalog staging failed: ${readableSaveError(productFailure.error)}`);
        recordAudit({
          actor: "Discovery ops",
          action: "Converted supplier catalog staging failed",
          entity: `${savedRecord.name} / ${readableSaveError(productFailure.error)}`,
          market: savedRecord.market,
        });
      }

      setCatalogProducts((items) => {
        const existingKeys = new Set(
          items.map((item) => `${normalizeMarketText(item.supplier)}::${normalizeMarketText(item.name)}`),
        );
        const newProducts = convertedCatalogProducts.filter(
          (product) =>
            !existingKeys.has(`${normalizeMarketText(product.supplier)}::${normalizeMarketText(product.name)}`),
        );

        return newProducts.length ? [...newProducts, ...items] : items;
      });
      const quoteResult = await submitQuoteRequest({
        buyer: "Network sourcing desk",
        product: titleCaseProductName(savedRecord.products[0] ?? "Converted supplier catalog"),
        quantity: "Opening catalog terms",
        neededBy: "Before first buyer match",
        suppliersMatched: 1,
        budget: "Open quote",
        supplierName: savedRecord.name,
        category: inferExternalCategory(savedRecord.products[0] ?? "Converted supply"),
        deliveryWindow: savedRecord.deliveryHint,
        priceRange: "Supplier to quote",
      });

      if (quoteResult.ok) {
        setQuoteQueue((requests) => [quoteResult.quoteRequest, ...requests]);
      }
    }

    if (nextStatus === "invited" || nextStatus === "converted") {
      const message: MessageThread = {
        id: `msg-external-${Date.now()}`,
        participant: savedRecord.name,
        role: nextStatus === "converted" ? "supplier" : "external",
        market: savedRecord.market,
        subject: nextStatus === "converted" ? "Supplier converted" : "Supplier invite sent",
        preview:
          nextStatus === "converted"
            ? `${savedRecord.name} is now verified in Marketplace with ${savedRecord.products.length} catalog product${savedRecord.products.length === 1 ? "" : "s"} staged for supplier terms.`
            : `${savedRecord.name} received a clean Veleo supplier invite with products and market context.`,
        status: nextStatus === "converted" ? "resolved" : "waiting",
        updatedAt: "Just now",
      };
      const messageResult = await submitMessageThread(message);

      if (messageResult.ok) {
        setMessageQueue((threads) => [
          messageResult.message,
          ...threads,
        ]);
      }
    }

    setDiscoveryActionState(`${savedRecord.name} moved to ${nextStatus.replace("_", " ")}`);
    recordAudit({
      actor: "Discovery ops",
      action: "Advanced external supplier record",
      entity: `${savedRecord.name} / ${nextStatus.replace(/_/g, " ")}`,
      market: savedRecord.market,
    });
  }

  async function advanceConversionTask(taskId: string) {
    const task = conversionTasks.find((item) => item.id === taskId);

    if (!task || task.stage === "converted") {
      return;
    }

    const nextStage: SupplierConversionTask["stage"] =
      task.stage === "review" ? "invited" : "converted";
    const linkedRecord = externalRecords.find((record) => record.name === task.supplierName);
    if (!linkedRecord) {
      setDiscoveryActionState(`Conversion task not advanced: missing external supplier record for ${task.supplierName}`);
      recordAudit({
        actor: "Growth ops",
        action: "Supplier conversion blocked",
        entity: `${task.supplierName} / missing external supplier record`,
        market: task.market,
      });
      return;
    }

    await advanceExternalRecord({
      ...linkedRecord,
      status: nextStage === "converted" ? "invited" : "ready_to_contact",
    });
  }

  async function captureMarketplaceDemandGap() {
    const searchTerm = query.trim();
    const category =
      marketplaceCategory !== "All"
        ? marketplaceCategory
        : inferExternalCategory(searchTerm || "uncategorized supply");
    const productLabel = searchTerm || category;
    const market = selectedBranch.market;
    const avgBudgetEur = Math.max(180, Math.round(bamToEur(selectedBranch.approvalLimit * 0.35)));
    const gapId = `gap-search-${Date.now()}`;
    const demandId = `sd-search-${Date.now()}`;
    const sprintId = `sprint-search-${slugForNav(market)}-${slugForNav(category)}-${Date.now().toString().slice(-5)}`;
    const gap: DemandGap = {
      id: gapId,
      category,
      market,
      failedSearches: 1,
      unavailableOrders: 0,
      suppliersNeeded: 2,
      revenueAtRiskEur: avgBudgetEur * 6,
      recommendedAction: `Source ${productLabel} suppliers for ${branchCity(selectedBranch)} and route verified matches into Marketplace.`,
    };
    const demand: SupplierSearchDemand = {
      id: demandId,
      product: titleCaseProductName(productLabel),
      category,
      market,
      buyerCount: 1,
      failedSearches: 1,
      avgBudgetEur,
      urgency: "now",
      recommendedAction: `Quote ${productLabel} for ${branchCity(selectedBranch)} buyers or offer category coverage.`,
    };
    const coverageRecord: MarketCoverageRecord = {
      id: `cov-search-${slugForNav(market)}-${slugForNav(category)}`,
      market,
      category,
      suppliersLive: 0,
      supplierTarget: 2,
      fillRate: 68,
      risk: "critical",
      nextMove: `Failed restaurant search for ${productLabel}. Source suppliers, verify terms, and route matches into Marketplace.`,
    };
    const sprint: SourcingSprint = {
      id: sprintId,
      title: `${market} ${category} failed-search sprint`,
      market,
      category,
      owner: "Network sourcing desk",
      due: "Today",
      status: "planned",
      potentialEur: Math.round(avgBudgetEur * 9),
    };
    const [gapResult, demandResult, coverageResult, sprintResult] = await Promise.all([
      submitDemandGap(gap),
      submitSupplierDemand(demand),
      submitCoverageRecord(coverageRecord),
      submitSourcingSprint(sprint),
    ]);

    if (!gapResult.ok || !demandResult.ok || !coverageResult.ok || !sprintResult.ok) {
      const error =
        (!gapResult.ok && gapResult.error) ||
        (!demandResult.ok && demandResult.error) ||
        (!coverageResult.ok && coverageResult.error) ||
        (!sprintResult.ok && sprintResult.error) ||
        "network_intelligence_not_saved";

      setDiscoveryActionState(`Demand gap not saved: ${readableSaveError(error)}`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Failed marketplace search save failed",
        entity: `${productLabel} / ${category} / ${readableSaveError(error)}`,
        market,
      });
      return;
    }

    const savedGap = gapResult.demandGap;
    const savedDemand = demandResult.supplierDemand;
    const savedCoverageRecord = coverageResult.coverageRecord;
    const savedSprint = sprintResult.sourcingSprint;

    setDemandGapQueue((gaps) => [savedGap, ...gaps].slice(0, 24));
    setSupplierDemandQueue((signals) => [savedDemand, ...signals].slice(0, 24));
    setCoverageQueue((records) => {
      const hasRecord = records.some((record) => coverageRecordMatches(record, savedCoverageRecord.market, savedCoverageRecord.category));
      const nextRecords = hasRecord ? records : [savedCoverageRecord, ...records];

      return nextRecords.map((record) => {
        if (!coverageRecordMatches(record, savedCoverageRecord.market, savedCoverageRecord.category)) {
          return record;
        }

        return savedCoverageRecord;
      });
    });
    setSourcingSprintQueue((sprints) => {
      const activeSprint = sprints.find(
        (item) =>
          item.status !== "resolved" &&
          normalizeMarketText(item.market) === normalizeMarketText(market) &&
          normalizeMarketText(item.category) === normalizeMarketText(category),
      );

      if (activeSprint) {
        return sprints.map((item) =>
          item.id === activeSprint.id
            ? savedSprint
            : item,
        );
      }

      return [savedSprint, ...sprints];
    });
    setMessageQueue((threads) => [
      {
        id: `msg-demand-gap-${gapId}`,
        participant: "Network sourcing desk",
        role: "supplier",
        market,
        subject: `Failed search captured: ${productLabel}`,
        preview: `${selectedBranch.name} could not find ${productLabel}. Veleo saved the need and will look for suppliers for ${savedGap.category} in ${savedGap.market}.`,
        status: "needs_reply",
        updatedAt: "Just now",
        lastAction: "Restaurant need saved for supplier matching",
      },
      ...threads,
    ]);
    setDiscoveryActionState(`${productLabel} need saved`);
    setNetworkActionState(`${category} sourcing opened from failed search`);
    setQuoteActionState(`${productLabel} routed to supplier demand`);
    recordAudit({
      actor: "Restaurant buyer",
      action: "Captured failed marketplace search",
      entity: `${productLabel} / ${category}`,
      market,
    });
  }

  async function offerDemandCoverage(gap: DemandGap) {
    if (!activePermission.canManageSupplierDesk) {
      setDiscoveryActionState(`${activePermission.role} cannot add supplier availability`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked supplier coverage offer",
        entity: `${gap.category} / ${gap.market}`,
        market: gap.market,
      });
      return;
    }

    const relatedDemand = supplierDemandQueue.find(
      (demand) =>
        normalizeMarketText(demand.category) === normalizeMarketText(gap.category) &&
        (normalizeMarketText(demand.market).includes(normalizeMarketText(gap.market)) ||
          normalizeMarketText(gap.market).includes(normalizeMarketText(demand.market))),
    );
    const productLabel = relatedDemand?.product ?? gap.category;
    const buyerCount = relatedDemand?.buyerCount ?? Math.max(1, Math.ceil(gap.failedSearches / 160));
    const avgBudgetEur = relatedDemand?.avgBudgetEur ?? Math.max(180, Math.round(gap.revenueAtRiskEur / Math.max(8, buyerCount)));
    const quoteLead: QuoteRequest = {
      id: `qr-coverage-${slugForNav(gap.category)}-${Date.now().toString().slice(-5)}`,
      buyer: `${buyerCount} buyers in ${gap.market}`,
      product: productLabel,
        quantity: "Restaurant demand",
      neededBy: relatedDemand?.urgency === "now" ? "Today" : "This week",
      status: "sent",
      suppliersMatched: 1,
      budget: `${formatEur(avgBudgetEur)} avg buyer budget`,
      supplierName: workspaceName,
      category: gap.category,
      deliveryWindow: "Supplier route and minimum order needed",
      priceRange: "Supplier to quote",
    };
    const currentCoverage =
      coverageQueue.find((record) => coverageRecordMatches(record, gap.market, gap.category)) ?? {
        id: `cov-${slugForNav(gap.market)}-${slugForNav(gap.category)}`,
        market: gap.market,
        category: gap.category,
        suppliersLive: 0,
        supplierTarget: Math.max(2, gap.suppliersNeeded),
        fillRate: 72,
        risk: "critical" as const,
        nextMove: "Supplier can serve this need; confirm minimum order, route, and price.",
      };
    const nextSuppliersLive = Math.min(currentCoverage.supplierTarget, currentCoverage.suppliersLive + 1);
    const nextFillRate = Math.min(98.5, Number((currentCoverage.fillRate + 3.8).toFixed(1)));
    const updatedGap: DemandGap = {
      ...gap,
      suppliersNeeded: Math.max(0, gap.suppliersNeeded - 1),
      recommendedAction: `${workspaceName} can supply ${gap.category}. Confirm minimum order, route timing, and restaurant price.`,
    };
    const updatedDemand: SupplierSearchDemand | undefined = relatedDemand
      ? {
          ...relatedDemand,
          matchedSupplier: workspaceName,
          recommendedAction: `Supplier can serve this. Send price terms for ${relatedDemand.product} and publish the product if stock is ready.`,
        }
      : undefined;
    const updatedCoverage: MarketCoverageRecord = {
      ...currentCoverage,
      suppliersLive: nextSuppliersLive,
      fillRate: nextFillRate,
      risk: coverageRiskFor(nextSuppliersLive, currentCoverage.supplierTarget, nextFillRate),
      nextMove: `${workspaceName} can supply this. Confirm minimum order, delivery route, and restaurant price terms.`,
    };
    const [gapResult, demandResult, coverageResult, quoteResult] = await Promise.all([
      submitDemandGap(updatedGap),
      updatedDemand ? submitSupplierDemand(updatedDemand) : Promise.resolve(undefined),
      submitCoverageRecord(updatedCoverage),
      submitQuoteRequest({
        buyer: quoteLead.buyer,
        market: gap.market,
        product: quoteLead.product,
        quantity: quoteLead.quantity,
        neededBy: quoteLead.neededBy,
        suppliersMatched: quoteLead.suppliersMatched,
        budget: quoteLead.budget,
        supplierName: quoteLead.supplierName,
        category: quoteLead.category,
        deliveryWindow: quoteLead.deliveryWindow,
        priceRange: quoteLead.priceRange,
      }),
    ]);

    if (
      !gapResult.ok ||
      (demandResult && !demandResult.ok) ||
      !coverageResult.ok ||
      !quoteResult.ok
    ) {
      const error =
        (!gapResult.ok && gapResult.error) ||
        (demandResult && !demandResult.ok && demandResult.error) ||
        (!coverageResult.ok && coverageResult.error) ||
        (!quoteResult.ok && quoteResult.error) ||
        "coverage_not_saved";

      setDiscoveryActionState(`Supplier availability not saved: ${readableSaveError(error)}`);
      recordAudit({
        actor: workspaceName,
        action: "Supplier availability save failed",
        entity: `${gap.category} / ${readableSaveError(error)}`,
        market: gap.market,
      });
      return;
    }

    const savedGap = gapResult.demandGap;
    const savedDemand = demandResult?.ok ? demandResult.supplierDemand : undefined;
    const savedCoverage = coverageResult.coverageRecord;
    const savedQuoteLead: QuoteRequest = {
      ...quoteResult.quoteRequest,
      status: "sent",
    };

    setDemandGapQueue((gaps) =>
      gaps.map((item) => (item.id === gap.id ? savedGap : item)),
    );
    setSupplierDemandQueue((signals) =>
      signals.map((signal) =>
        signal.id === savedDemand?.id ? savedDemand : signal,
      ),
    );
    setDeliveryAreaQueue((areas) => {
      const existingArea = deliveryAreaForMarket(areas, gap.market);
      const nextAreas = existingArea ? areas : [createDeliveryAreaFromMarket(gap.market), ...areas];
      const targetAreaId = existingArea?.id ?? nextAreas[0]?.id;

      return nextAreas.map((area) =>
        area.id === targetAreaId
          ? {
              ...area,
              status: "capacity_watch",
              days: area.days === "Supplier route pending" ? "Supplier route pending" : area.days,
              minimumOrder: area.minimumOrder === "Confirm minimum" ? "Supplier minimum pending" : area.minimumOrder,
              activeOrders: area.activeOrders ?? 0,
              lastUpdated: `${gap.category} supplier availability added just now`,
            }
          : area,
      );
    });
    setCoverageQueue((records) => {
      const hasRecord = records.some((record) => coverageRecordMatches(record, savedCoverage.market, savedCoverage.category));
      return hasRecord
        ? records.map((record) => (coverageRecordMatches(record, savedCoverage.market, savedCoverage.category) ? savedCoverage : record))
        : [savedCoverage, ...records];
    });
    setQuoteQueue((requests) => [savedQuoteLead, ...requests]);
    setMessageQueue((threads) => [
      {
        id: `msg-coverage-${Date.now()}`,
        participant: "Veleo matching desk",
        role: "supplier",
        market: gap.market,
        subject: `Can supply: ${gap.category}`,
        preview: `${workspaceName} can cover ${gap.category} in ${gap.market}. ${savedQuoteLead.id} is ready for minimum order, price, and delivery terms.`,
        status: "waiting",
        updatedAt: "Just now",
        lastAction: "Supplier availability opened for price terms",
      },
      ...threads,
    ]);
    setDiscoveryActionState(`${gap.category} availability saved for supplier terms`);
    setCatalogActionState(`${savedQuoteLead.id} opened from buyer demand`);
    recordAudit({
      actor: workspaceName,
      action: "Offered demand coverage",
      entity: `${gap.category} / ${savedQuoteLead.id} / ${formatEur(gap.revenueAtRiskEur)} demand`,
      market: gap.market,
    });
  }

  async function activateSupplierOpportunity(opportunity: SupplierOpportunity) {
    if (activatedSupplierOpportunityIds.includes(opportunity.id)) {
      setCatalogActionState(`${opportunity.title} already activated`);
      return;
    }

    if (!activePermission.canManageSupplierDesk) {
      setCatalogActionState(`${activePermission.role} cannot activate supplier revenue opportunities`);
      recordAudit({
        actor: activePermission.role,
        action: "Blocked supplier opportunity activation",
        entity: opportunity.title,
        market: workspaceName,
      });
      return;
    }

    const opportunityText = normalizeMarketText(`${opportunity.title} ${opportunity.detail} ${opportunity.action}`);
    const matchesTokens = (value: string, tokens: string[]) => {
      const normalizedValue = normalizeMarketText(value);
      return tokens.some((token) => normalizedValue.includes(token));
    };
    const opportunityTokens = opportunityText.includes("citrus") || opportunityText.includes("lemon")
      ? ["citrus", "lemon", "lime", "fresh produce", "produce"]
      : opportunityText.includes("non-food") ||
          opportunityText.includes("non food") ||
          opportunityText.includes("napkin") ||
          opportunityText.includes("packaging")
        ? ["non-food", "non food", "napkin", "packaging", "glove", "detergent"]
        : opportunityText.includes("response") || opportunityText.includes("quote")
          ? ["quote", "response"]
          : [];
    const relatedDemand = supplierDemandQueue.find((demand) =>
      matchesTokens(`${demand.product} ${demand.category} ${demand.recommendedAction}`, opportunityTokens),
    );
    const relatedGap = demandGapQueue.find((gap) =>
      matchesTokens(`${gap.category} ${gap.market} ${gap.recommendedAction}`, opportunityTokens),
    );
    const openQuote = quoteQueue.find((request) => isOpenQuoteStatus(request.status));

    if ((opportunityText.includes("response") || opportunityText.includes("quote")) && openQuote) {
      await sendSupplierQuote(openQuote);
      setActivatedSupplierOpportunityIds((ids) => [opportunity.id, ...ids.filter((id) => id !== opportunity.id)]);
      setCatalogActionState(`${opportunity.title} activated through ${openQuote.id}`);
      return;
    }

    if (opportunityText.includes("publish") && relatedDemand) {
      await publishDemandSku(relatedDemand);
      setActivatedSupplierOpportunityIds((ids) => [opportunity.id, ...ids.filter((id) => id !== opportunity.id)]);
      setCatalogActionState(`${opportunity.title} activated through demand-backed SKU`);
      return;
    }

    if (relatedGap) {
      await offerDemandCoverage(relatedGap);
      setActivatedSupplierOpportunityIds((ids) => [opportunity.id, ...ids.filter((id) => id !== opportunity.id)]);
      setCatalogActionState(`${opportunity.title} activated through coverage offer`);
      return;
    }

    if (relatedDemand) {
      createDemandQuoteLead(relatedDemand);
      setActivatedSupplierOpportunityIds((ids) => [opportunity.id, ...ids.filter((id) => id !== opportunity.id)]);
      setCatalogActionState(`${opportunity.title} activated through buyer demand quote`);
      return;
    }

    setActivatedSupplierOpportunityIds((ids) => [opportunity.id, ...ids.filter((id) => id !== opportunity.id)]);
    setCatalogActionState(`${opportunity.title} opened in supplier demand radar`);
    setActiveNav("Supplier Finder");
    recordAudit({
      actor: "Supplier operator",
      action: "Activated supplier revenue opportunity",
      entity: `${opportunity.title} / ${formatEur(opportunity.potentialRevenueEur)}`,
      market: "Supplier demand",
    });
  }

  async function openCoverageSprint(record: MarketCoverageRecord) {
    const activeSprint = sourcingSprintQueue.find(
      (sprint) =>
        sprint.status !== "resolved" &&
        normalizeMarketText(sprint.market) === normalizeMarketText(record.market) &&
        normalizeMarketText(sprint.category) === normalizeMarketText(record.category),
    );

    if (activeSprint) {
      const savedSprintResult = await submitSourcingSprint({ ...activeSprint, status: "running" });
      const savedCoverageResult = await submitCoverageRecord({
        ...record,
        nextMove: `${activeSprint.title} is running. Verify supplier terms and restaurant conversion before closing the gap.`,
      });

      if (!savedSprintResult.ok || !savedCoverageResult.ok) {
        const error =
          (!savedSprintResult.ok && savedSprintResult.error) ||
          (!savedCoverageResult.ok && savedCoverageResult.error) ||
          "sprint_not_saved";

        setNetworkActionState(`Coverage sprint not saved: ${readableSaveError(error)}`);
        recordAudit({
          actor: "Network ops",
          action: "Coverage sprint save failed",
          entity: `${activeSprint.title} / ${readableSaveError(error)}`,
          market: activeSprint.market,
        });
        return;
      }

      setSourcingSprintQueue((sprints) =>
        sprints.map((sprint) =>
          sprint.id === activeSprint.id ? savedSprintResult.sourcingSprint : sprint,
        ),
      );
      setCoverageQueue((records) =>
        records.map((item) =>
          coverageRecordMatches(item, record.market, record.category)
            ? savedCoverageResult.coverageRecord
            : item,
        ),
      );
      setNetworkActionState(`${activeSprint.title} is running`);
      recordAudit({
        actor: "Network ops",
        action: "Activated existing sourcing sprint",
        entity: `${activeSprint.title} / ${formatEur(activeSprint.potentialEur)}`,
        market: activeSprint.market,
      });
      return;
    }

    const sprint: SourcingSprint = {
      id: `sprint-${slugForNav(record.market)}-${slugForNav(record.category)}-${Date.now().toString().slice(-5)}`,
      title: `${record.market} ${record.category} coverage`,
      market: record.market,
      category: record.category,
      owner: record.risk === "critical" ? "Network ops" : "Supply desk",
      due: record.risk === "critical" ? "Today 17:00" : "This week",
      status: "running",
      potentialEur: sprintPotentialForCoverage(record),
    };
    const savedSprintResult = await submitSourcingSprint(sprint);
    const savedCoverageResult = await submitCoverageRecord({
      ...record,
      risk: record.risk === "healthy" ? "watch" : record.risk,
      nextMove: `${sprint.title} opened. Recruit or activate supplier coverage, then confirm response quality.`,
    });

    if (!savedSprintResult.ok || !savedCoverageResult.ok) {
      const error =
        (!savedSprintResult.ok && savedSprintResult.error) ||
        (!savedCoverageResult.ok && savedCoverageResult.error) ||
        "sprint_not_saved";

      setNetworkActionState(`Coverage sprint not saved: ${readableSaveError(error)}`);
      recordAudit({
        actor: "Network ops",
        action: "Coverage sprint save failed",
        entity: `${sprint.title} / ${readableSaveError(error)}`,
        market: sprint.market,
      });
      return;
    }

    const savedSprint = savedSprintResult.sourcingSprint;
    const savedCoverage = savedCoverageResult.coverageRecord;

    setSourcingSprintQueue((sprints) => [savedSprint, ...sprints]);
    setCoverageQueue((records) =>
      records.map((item) =>
        coverageRecordMatches(item, record.market, record.category)
          ? savedCoverage
          : item,
      ),
    );
    setMessageQueue((threads) => [
      {
        id: `msg-sprint-${savedSprint.id}-${Date.now()}`,
        participant: savedSprint.owner,
        role: "supplier",
        market: savedSprint.market,
        subject: `Sourcing sprint opened: ${savedSprint.category}`,
        preview: `${record.market} ${record.category} coverage is ${record.fillRate.toFixed(1)}% with ${record.supplierTarget - record.suppliersLive} supplier gap. Next move: ${record.nextMove}.`,
        status: "needs_reply",
        updatedAt: "Just now",
        lastAction: "Coverage sprint opened",
      },
      ...threads,
    ]);
    setNetworkActionState(`${savedSprint.title} opened`);
    recordAudit({
      actor: "Network ops",
      action: "Opened sourcing sprint",
      entity: `${savedSprint.title} / ${formatEur(savedSprint.potentialEur)}`,
      market: savedSprint.market,
    });
  }

  async function openCountryCoverageSprint(country: OperatingCountry) {
    const countryRecord = countryCoverageRecordFor(country);
    const existingRecord = coverageQueue.find((record) =>
      coverageRecordMatches(record, countryRecord.market, countryRecord.category),
    );
    const record = existingRecord ?? countryRecord;

    setCoverageQueue((records) => {
      const hasRecord = records.some((item) => coverageRecordMatches(item, record.market, record.category));

      if (!hasRecord) {
        return [record, ...records];
      }

      return records.map((item) =>
        coverageRecordMatches(item, record.market, record.category)
          ? {
              ...item,
              fillRate: Math.min(item.fillRate, record.fillRate),
              risk: item.risk === "healthy" ? record.risk : item.risk,
              supplierTarget: Math.max(item.supplierTarget, record.supplierTarget),
              nextMove: country.nextMove,
            }
          : item,
      );
    });
    await openCoverageSprint(record);
  }

  async function advanceSourcingSprint(sprintId: string) {
    const sprint = sourcingSprintQueue.find((item) => item.id === sprintId);

    if (!sprint || sprint.status === "resolved") {
      return;
    }

    const nextStatus: SourcingSprint["status"] = sprint.status === "planned" ? "running" : "resolved";
    const savedSprintResult = await submitSourcingSprint({ ...sprint, status: nextStatus });

    if (!savedSprintResult.ok) {
      setNetworkActionState(`Sourcing sprint not saved: ${readableSaveError(savedSprintResult.error)}`);
      recordAudit({
        actor: "Network ops",
        action: "Sourcing sprint save failed",
        entity: `${sprint.title} / ${readableSaveError(savedSprintResult.error)}`,
        market: sprint.market,
      });
      return;
    }

    const savedSprint = savedSprintResult.sourcingSprint;
    const targetCoverage = coverageQueue.find((record) => coverageRecordMatches(record, sprint.market, sprint.category));
    const updatedCoverage = targetCoverage
      ? nextStatus === "running"
        ? {
            ...targetCoverage,
            nextMove: `${sprint.title} started. Confirm supplier shortlist, quote response, and delivery path.`,
          }
        : (() => {
            const suppliersLive = Math.min(targetCoverage.supplierTarget, targetCoverage.suppliersLive + 1);
            const fillRate = Math.min(99, Number((targetCoverage.fillRate + 6.2).toFixed(1)));

            return {
              ...targetCoverage,
              suppliersLive,
              fillRate,
              risk: coverageRiskFor(suppliersLive, targetCoverage.supplierTarget, fillRate),
              nextMove: `${sprint.title} resolved. Monitor fill rate, restaurant conversion, and supplier response quality.`,
            };
          })()
      : undefined;
    const matchedGap = demandGapQueue.find(
      (gap) =>
        normalizeMarketText(sprint.market).includes(normalizeMarketText(gap.market)) ||
        normalizeMarketText(gap.market).includes(normalizeMarketText(sprint.market)) ||
        normalizeMarketText(sprint.category).includes(normalizeMarketText(gap.category)),
    );
    const updatedGap =
      nextStatus === "resolved" && matchedGap
        ? {
            ...matchedGap,
            suppliersNeeded: Math.max(0, matchedGap.suppliersNeeded - 1),
            recommendedAction: `${sprint.title} resolved. Monitor restaurant conversion and supplier response quality.`,
          }
        : undefined;
    const [coverageResult, gapResult] = await Promise.all([
      updatedCoverage ? submitCoverageRecord(updatedCoverage) : Promise.resolve(undefined),
      updatedGap ? submitDemandGap(updatedGap) : Promise.resolve(undefined),
    ]);

    if ((coverageResult && !coverageResult.ok) || (gapResult && !gapResult.ok)) {
      const error =
        (coverageResult && !coverageResult.ok && coverageResult.error) ||
        (gapResult && !gapResult.ok && gapResult.error) ||
        "network_state_not_saved";

      setNetworkActionState(`Sourcing sprint state not saved: ${readableSaveError(error)}`);
      recordAudit({
        actor: "Network ops",
        action: "Sourcing sprint state save failed",
        entity: `${sprint.title} / ${readableSaveError(error)}`,
        market: sprint.market,
      });
      return;
    }

    setSourcingSprintQueue((sprints) =>
      sprints.map((item) => (item.id === sprintId ? savedSprint : item)),
    );

    if (gapResult?.ok) {
      setDemandGapQueue((gaps) =>
        gaps.map((gap) => (gap.id === gapResult.demandGap.id || gap.id === updatedGap?.id ? gapResult.demandGap : gap)),
      );
    }
    if (coverageResult?.ok) {
      setCoverageQueue((records) =>
        records.map((record) =>
          coverageRecordMatches(record, sprint.market, sprint.category) ? coverageResult.coverageRecord : record,
        ),
      );
    }

    setMessageQueue((threads) => [
      {
        id: `msg-sprint-advance-${sprint.id}-${Date.now()}`,
        participant: sprint.owner,
        role: "supplier",
        market: sprint.market,
        subject: `${nextStatus === "running" ? "Sprint started" : "Sprint resolved"}: ${sprint.title}`,
        preview:
          nextStatus === "running"
            ? `${sprint.owner} started the ${sprint.category} coverage sprint for ${sprint.market}.`
            : `${sprint.owner} resolved the ${sprint.category} sprint. Demand gap tracking has been updated for follow-through.`,
        status: nextStatus === "running" ? "waiting" : "resolved",
        updatedAt: "Just now",
        lastAction: `Sprint ${nextStatus}`,
      },
      ...threads,
    ]);
    setNetworkActionState(`${sprint.title} ${nextStatus}`);
    recordAudit({
      actor: "Network ops",
      action: nextStatus === "running" ? "Started sourcing sprint" : "Resolved sourcing sprint",
      entity: `${sprint.title} / ${formatEur(sprint.potentialEur)}`,
      market: sprint.market,
    });
  }

  function advanceIncident(incidentId: string) {
    const incident = liveIncidentQueue.find((item) => item.id === incidentId);

    if (!incident || incident.status === "resolved") {
      return;
    }

    const nextStatus: LiveIncident["status"] =
      incident.status === "active" ? "mitigating" : "resolved";
    const nextEta = nextStatus === "mitigating" ? "Mitigation active" : "Resolved just now";
    const nextAction =
      nextStatus === "mitigating"
        ? `Mitigation started by ${incident.owner}. Keep buyer and supplier timelines updated.`
        : "Incident resolved. Attach final note to affected buyer and supplier timelines.";

    setIncidentQueue((incidents) => {
      const updatedIncident = {
        ...incident,
        status: nextStatus,
        eta: nextEta,
        action: nextAction,
      };

      return incidents.some((item) => item.id === incidentId)
        ? incidents.map((item) => (item.id === incidentId ? updatedIncident : item))
        : [updatedIncident, ...incidents];
    });
    setMessageQueue((threads) => [
      {
        id: `msg-incident-${incident.id}-${Date.now()}`,
        participant: incident.owner,
        role: "supplier",
        market: incident.market,
        subject: `${nextStatus === "mitigating" ? "Mitigation started" : "Incident resolved"}: ${incident.title}`,
        preview:
          nextStatus === "mitigating"
            ? `${incident.owner} is mitigating ${incident.impact}. Next update: ${nextEta}.`
            : `${incident.title} is resolved for ${incident.market}. Final follow-up is attached to the operations timeline.`,
        status: nextStatus === "resolved" ? "resolved" : "waiting",
        updatedAt: "Just now",
        lastAction: `Incident ${nextStatus}`,
      },
      ...threads,
    ]);
    setNetworkActionState(`${incident.title} ${nextStatus}`);
    recordAudit({
      actor: "Network ops",
      action: nextStatus === "mitigating" ? "Started incident mitigation" : "Resolved incident",
      entity: `${incident.title} / ${incident.impact}`,
      market: incident.market,
    });
  }

  async function toggleSavedSupplier(supplierName: string) {
    const wasSaved = savedSupplierNames.includes(supplierName);
    const supplierProduct = searchableMarketplaceProducts.find((product) => product.supplierName === supplierName);
    const action = wasSaved ? "remove" : "save";

    if (selectedBranch.id === emptyBranchAccount.id) {
      setQuoteActionState("Preferred supplier not saved: configure a branch first");
      recordAudit({
        actor: "Restaurant buyer",
        action: "Preferred supplier save blocked",
        entity: supplierName,
        market: selectedBranch.market,
      });
      return;
    }

    const result = await submitPreferredSupplier({
      action,
      branchId: selectedBranch.id,
      category: supplierProduct?.category,
      market: supplierProduct?.market ?? selectedBranch.market,
      supplierName,
      supplierType: supplierProduct?.supplierType === "external" ? "external" : "verified",
    });

    if (!result.ok) {
      setQuoteActionState(`Preferred supplier not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Restaurant buyer",
        action: "Preferred supplier save failed",
        entity: `${supplierName} / ${readableSaveError(result.error)}`,
        market: supplierProduct?.market ?? selectedBranch.market,
      });
      return;
    }

    setSavedSupplierNames((names) =>
      result.preference.status === "removed"
        ? names.filter((name) => name !== supplierName)
        : names.includes(result.preference.supplierName)
          ? names
          : [result.preference.supplierName, ...names],
    );
    setQuoteActionState(
      wasSaved
        ? `${supplierName} removed from preferred suppliers`
        : `${supplierName} saved as preferred supplier`,
    );
    recordAudit({
      actor: "Restaurant buyer",
      action: wasSaved ? "Removed preferred supplier" : "Saved preferred supplier",
      entity: `${supplierName} / ${selectedBranch.name}`,
      market: supplierProduct?.market ?? selectedBranch.market,
    });

    if (!wasSaved) {
      const preferredMessage: MessageThread = {
        id: `msg-preferred-${slugForNav(supplierName)}-${Date.now()}`,
        participant: supplierName,
        role: supplierProduct?.supplierType === "external" ? "external" : "supplier",
        market: supplierProduct?.market ?? selectedBranch.market,
        subject: `Preferred supplier saved: ${supplierName}`,
        preview: `${supplierName} is now preferred for ${selectedBranch.name}. Veleo will rank it higher in supplier comparisons and repeat buying.`,
        status: "resolved",
        updatedAt: "Just now",
      };

      void submitMessageThread(preferredMessage);
      setMessageQueue((threads) => [
        preferredMessage,
        ...threads,
      ]);
    }
  }

  async function saveRestaurantSupplierContact(input: RestaurantSupplierContactInput) {
    const cleanName = input.name.trim();

    if (!cleanName) {
      setQuoteActionState("Supplier not saved: add a supplier name first");
      return;
    }

    const cleanPhone = input.phone.trim();
    const cleanWhatsapp = input.whatsapp.trim();
    const cleanViber = input.viber.trim();
    const cleanEmail = input.email.trim();
    const cleanUsualOrderText = input.usualOrderText.trim();
    const contactByChannel: Record<RestaurantSupplierContactInput["preferredChannel"], string> = {
      copy: input.contact.trim() || cleanWhatsapp || cleanViber || cleanPhone || cleanEmail,
      email: cleanEmail,
      sms: cleanPhone || cleanWhatsapp || cleanViber,
      viber: cleanViber || cleanPhone || cleanWhatsapp,
      whatsapp: cleanWhatsapp || cleanPhone || cleanViber,
    };
    const cleanContactValue =
      contactByChannel[input.preferredChannel] || input.contact.trim() || cleanPhone || cleanWhatsapp || cleanViber || cleanEmail;
    const nextContact: RestaurantSupplierContact = {
      ...input,
      branchName: selectedBranch.name,
      category: input.category.trim(),
      contact: cleanContactValue,
      contactPerson: input.contactPerson.trim() || undefined,
      deliveryArea: input.deliveryArea.trim(),
      email: cleanEmail || undefined,
      id: `supplier-contact-${slugForNav(cleanName)}-${Date.now()}`,
      name: cleanName,
      phone: cleanPhone || undefined,
      preferredChannel: input.preferredChannel,
      relationshipStatus: "active",
      source: "restaurant_provided",
      usualDays: input.usualDays.trim(),
      usualOrderText: cleanUsualOrderText || undefined,
      viber: cleanViber || undefined,
      whatsapp: cleanWhatsapp || undefined,
      createdAt: currentActionTimestamp(),
    };
    const saveResult = await submitRestaurantSupplierContact(nextContact);
    const savedContact = saveResult.ok ? saveResult.contact : nextContact;
    const normalizedName = normalizeMarketText(cleanName);

    setRestaurantSupplierContacts((contacts) => [
      savedContact,
      ...contacts.filter((contact) => normalizeMarketText(contact.name) !== normalizedName),
    ]);
    setSavedSupplierNames((names) =>
      names.some((name) => normalizeMarketText(name) === normalizedName) ? names : [cleanName, ...names],
    );

    const supplierThread: MessageThread = {
      id: `msg-supplier-contact-${slugForNav(cleanName)}-${Date.now()}`,
      participant: cleanName,
      role: "supplier",
      market: savedContact.deliveryArea || selectedBranch.market,
      subject: `Supplier contact ready: ${cleanName}`,
      preview: [
        `${selectedBranch.name} added ${cleanName} as a current supplier.`,
        savedContact.contactPerson ? `Person: ${savedContact.contactPerson}.` : "",
        savedContact.contact ? `Preferred ${supplierMessageChannelLabel(savedContact.preferredChannel)}: ${savedContact.contact}.` : "Contact can be added later.",
        savedContact.whatsapp ? `WhatsApp: ${savedContact.whatsapp}.` : "",
        savedContact.viber ? `Viber: ${savedContact.viber}.` : "",
        savedContact.phone ? `Phone/SMS: ${savedContact.phone}.` : "",
        savedContact.email ? `Email: ${savedContact.email}.` : "",
        savedContact.category ? `Category: ${savedContact.category}.` : "",
        savedContact.usualDays ? `Usual days: ${savedContact.usualDays}.` : "",
        savedContact.usualOrderText ? `Usual order habit saved.` : "",
      ]
        .filter(Boolean)
        .join(" "),
      status: "waiting",
      updatedAt: "Just now",
      lastAction: "Ready for first order message",
    };

    void submitMessageThread(supplierThread);
    setMessageQueue((threads) => [supplierThread, ...threads]);
    setQuoteActionState(
      saveResult.ok
        ? `${cleanName} saved. Add the order items, then send the supplier message.`
        : `${cleanName} saved locally. Backend sync still needs connection.`,
    );
    recordAudit({
      actor: "Restaurant buyer",
      action: "Added current supplier contact",
      entity: `${cleanName} / ${selectedBranch.name}`,
      market: savedContact.deliveryArea || selectedBranch.market,
    });
    recordBehavior({
      actor: "Restaurant buyer",
      entity: `${cleanName} / ${selectedBranch.name}`,
      market: savedContact.deliveryArea || selectedBranch.market,
      name: "supplier_contact_added",
      properties: {
        category: savedContact.category,
        hasContact: Boolean(savedContact.contact),
        hasUsualOrderText: Boolean(savedContact.usualOrderText),
        usualDays: savedContact.usualDays,
      },
      role: "restaurant",
      step: "restaurant_supplier_contact_added",
    });
    markRoleDataSeen("restaurant", { supplier: true });
  }

  async function resolveMessageThread(threadId: string) {
    const thread = messageQueue.find((item) => item.id === threadId);

    if (!thread) {
      return;
    }

    const updatedThread: MessageThread = {
      ...thread,
      status: "resolved",
      preview: "Follow-up resolved and attached to the buying timeline.",
      updatedAt: "Just now",
      lastAction: "Thread resolved",
    };
    const result = await submitMessageThread(updatedThread);

    if (!result.ok) {
      setSubmitState(`Message update not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: roleLabels[role],
        action: "Message thread resolution failed",
        entity: `${thread.subject} / ${thread.participant} / ${readableSaveError(result.error)}`,
        market: thread.market,
      });
      return;
    }

    setMessageQueue((threads) =>
      threads.map((item) => (item.id === threadId ? result.message : item)),
    );
    recordAudit({
      actor: roleLabels[role],
      action: "Resolved buying message thread",
      entity: `${thread.subject} / ${thread.participant}`,
      market: thread.market,
    });
  }

  async function sendMessageFollowUp(threadId: string) {
    const thread = messageQueue.find((item) => item.id === threadId);

    if (!thread) {
      return;
    }

    const followUp = messageFollowUpTemplate(thread, role);
    const updatedThread: MessageThread = {
      ...thread,
      status: "waiting",
      preview: `Follow-up sent: ${followUp}`,
      lastAction: followUp,
      updatedAt: "Just now",
    };
    const result = await submitMessageThread(updatedThread);

    if (!result.ok) {
      setSubmitState(`Message follow-up not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: roleLabels[role],
        action: "Buying follow-up failed",
        entity: `${thread.subject} / ${thread.participant} / ${readableSaveError(result.error)}`,
        market: thread.market,
      });
      return;
    }

    setMessageQueue((threads) =>
      threads.map((item) => (item.id === threadId ? result.message : item)),
    );
    recordAudit({
      actor: roleLabels[role],
      action: "Sent buying follow-up",
      entity: `${thread.subject} / ${thread.participant}`,
      market: thread.market,
    });
  }

  async function updateConnectorStatus(
    request: ConnectorRequestRecord,
    status: ConnectorRequestRecord["status"],
  ) {
    const statusNote =
      status === "connected"
        ? "Credentials verified and test sync recorded."
        : status === "failed"
          ? "Setup needs attention before automation can rely on this connection."
          : "Connector returned to pending setup.";
    const result = await updateIntegrationConnection({
      requestId: request.requestId,
      status,
      statusNote,
    });

    if (!result.ok) {
      if (request.mode === "local") {
        const localRequest: ConnectorRequestRecord = {
          ...request,
          status,
          statusNote,
          updatedAt: new Date().toISOString(),
        };

        setConnectorRequests((requests) => {
          const nextRequests = requests.map((item) =>
            item.requestId === request.requestId ? localRequest : item,
          );
          saveLocalConnectorRequests(nextRequests.filter((item) => item.mode === "local"));
          return nextRequests;
        });
        setConnectorStatus(`${request.providerName} marked ${status.replace("_", " ")} locally`);
        recordAudit({
          actor: "Platform ops",
          action: "Updated local connector status",
          entity: `${request.providerName} / ${status}`,
          market: request.organizationName,
        });
        return;
      }

      setConnectorStatus(`Connector status not saved: ${readableSaveError(result.error)}`);
      recordAudit({
        actor: "Platform ops",
        action: "Connector status save failed",
        entity: `${request.providerName} / ${readableSaveError(result.error)}`,
        market: request.organizationName,
      });
      return;
    }

    setConnectorRequests((requests) =>
      requests.map((item) => (item.requestId === request.requestId ? result.connectorRequest : item)),
    );
    setConnectorStatus(`${request.providerName} marked ${result.connectorRequest.status.replace("_", " ")}`);
    recordAudit({
      actor: "Platform ops",
      action: "Updated connector status",
      entity: `${request.providerName} / ${result.connectorRequest.status}`,
      market: request.organizationName,
    });
  }

  async function repairIntegrationConnection(integrationName: string) {
    const integration = liveIntegrationStatus.find((item) => item.name === integrationName);

    if (!integration || integration.status === "connected") {
      return;
    }

    await saveConnectorRequest({
      organizationName: "Platform operations",
      role,
      providerName: integration.name,
      systemType: integration.category,
      syncTarget: "Connection review",
      endpointUrl: "",
      authType: "Other",
    });
  }

  function loadOrderAsDraft(order: Order) {
    setDraft(order.items.map((item) => enrichDraftItem(item, draftLookupContext, { supplier: order.supplier, source: "reorder" })));
    setReorderStartedAt(Date.now());
    setSubmitState(`${order.id} loaded as repeat order`);
    setActiveNav("Orders");
  }

  if (!authReady) {
    return (
      <main className="public-shell auth-splash">
        <div className="auth-splash-card">
          <div className="brand-mark">veleo</div>
          <p>Loading your workspace…</p>
        </div>
      </main>
    );
  }

  if (supplierConfirmRequestId) {
    const confirmationRequest =
      publicConfirmationRequest ??
      supplierOrderRequests.find((request) => matchesSupplierConfirmationRequest(request, supplierConfirmRequestId)) ??
      readLocalSupplierOrderRequests().find((request) =>
        matchesSupplierConfirmationRequest(request, supplierConfirmRequestId),
      );

    return (
      <SupplierOrderConfirmationPage
        isLoading={publicConfirmationLoading}
        request={confirmationRequest}
        onBackToLanding={() => {
          window.location.hash = "";
          setSupplierConfirmRequestId("");
        }}
        onClaimSupplierProfile={(supplierName) => {
          const cleanSupplierName = supplierName.trim() || confirmationRequest?.supplierName || "Supplier";
          storageSet(ACCOUNT_BUSINESS_NAME_KEY, cleanSupplierName);
          storageSet(
            PENDING_SIGNUP_KEY,
            JSON.stringify({
              businessName: cleanSupplierName,
              role: "supplier",
            }),
          );
          storageSet("veleo.role", "supplier");
          setAuthNotice("Create your supplier workspace to claim this profile. The restaurant order is already saved.");
          setSupplierConfirmRequestId("");
          setPublicPage("login");
          window.history.replaceState(null, "", "#/login");
        }}
        onRespond={applySupplierOrderResponse}
      />
    );
  }

  if (!sessionRole) {
    return (
      <PublicLanding
        page={publicPage}
        authConfigured={isAuthConfigured()}
        notice={authNotice}
        onBackToLanding={showLanding}
        onAuthenticated={refreshSession}
        onOpenLocalWorkspace={openLocalProofWorkspace}
        onShowLogin={showLogin}
      />
    );
  }

  const sidebarCopy = sidebarWorkspaceCopy(role);

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNavOpen ? "is-open" : ""}`}>
        <button className="mobile-close" type="button" onClick={() => setMobileNavOpen(false)}>
          <X size={20} />
          <span>Close</span>
        </button>
        <div className="brand-block">
          <div className="brand-mark">veleo</div>
          <div className="brand-rule" />
          <p>{sidebarCopy.label}</p>
        </div>

        <nav className="side-nav" aria-label="Primary">
          {activeNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={effectiveNav === item.label ? "active" : ""}
                key={item.label}
                type="button"
                onClick={() => navigate(item.label)}
              >
                <Icon size={18} />
                <span>{navDisplayLabel(item, role)}</span>
              </button>
            );
          })}
        </nav>

        <div className="route-card">
          <RouteLine />
          <div>
            <strong>{sidebarCopy.title}</strong>
            <span>{sidebarCopy.detail}</span>
          </div>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div className="topbar-title">
            <button className="menu-button" type="button" onClick={() => setMobileNavOpen(true)}>
              <Menu size={21} />
              <span className="sr-only">Menu</span>
            </button>
            <div>
              <span className="mobile-brand">veleo</span>
              <p className="eyebrow">{view.kicker}</p>
              <h1>{view.title}</h1>
              <p>{view.description}</p>
            </div>
          </div>

          <div className="topbar-actions">
            <label className="search-box">
              <Search size={17} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholderForRole(role)}
              />
            </label>
            <label className="permission-select">
              <span>Access</span>
              <select
                value={activePermission.id}
                onChange={(event) => {
                  storageSet(permissionTouchedStorageKey(role), "true");
                  setPermissionRoleId(event.target.value);
                }}
                aria-label="Workspace permission role"
              >
                {activePermissionOptions.map((permission) => (
                  <option key={permission.id} value={permission.id}>
                    {permission.role}
                  </option>
                ))}
              </select>
            </label>
            <div className="notification-wrap">
              <button
                className={`icon-button notification-button ${notificationsOpen ? "active" : ""}`}
                type="button"
                aria-label="Notifications"
                aria-expanded={notificationsOpen}
                onClick={() => setNotificationsOpen((open) => !open)}
              >
                <Bell size={19} />
                {urgentNotificationCount ? <span>{urgentNotificationCount}</span> : null}
              </button>
              {notificationsOpen ? (
                <NotificationCenter
                  notifications={operationalNotifications}
                  onAction={runNotificationAction}
                  onSelect={selectNotification}
                />
              ) : null}
            </div>
            <div className="account-chip">
              <span>{workspaceInitials}</span>
              <div>
                <strong>{workspaceName}</strong>
                <small>{roleLabels[role]} / {activePermission.role}</small>
              </div>
            </div>
            <button className="sign-out-button" type="button" onClick={signOut}>
              <LogOut size={17} />
              Sign out
            </button>
          </div>
        </header>

        <GlobalSearchPanel
          query={query}
          results={globalSearchResults}
          onSelect={selectSearchResult}
          onTrackDemand={role === "restaurant" ? captureMarketplaceDemandGap : undefined}
        />

        {roleEntry.shouldShowCommandStrip ? (
          <CommandStrip
            activeNav={effectiveNav}
            draftTotal={draftTotal}
            demandGapCount={demandGapQueue.length}
            externalRecordCount={accountExternalRecords.length}
            fulfillmentCount={accountFulfillmentQueue.length}
            incidentCount={openIncidents.length}
            inquiryReady={Boolean(externalInquiry)}
            issueCount={issueCount}
            lowStockCount={lowStockItems.length}
            messageCount={accountMessages.length}
            networkStats={liveNetworkStats}
            permissionRole={activePermission}
            quoteRequestCount={accountQuoteQueue.filter((request) => isOpenQuoteStatus(request.status)).length}
            invoiceCount={accountInvoiceQueue.length}
            offerCount={liveSupplierOffers.length}
            role={role}
            runCount={discoveryRuns.length}
            onNavigate={navigate}
          />
        ) : null}

        <nav className="mobile-tabbar" aria-label="Mobile workspace">
          {activeNavItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                className={effectiveNav === item.label ? "active" : ""}
                type="button"
                onClick={() => navigate(item.label)}
              >
                <Icon size={18} />
                <span>{navMobileDisplayLabel(item, role)}</span>
              </button>
            );
          })}
        </nav>

        <MobileActionDock
          activeNav={effectiveNav}
          draftItemCount={visibleAccountDraft.length}
          draftTotal={draftTotal}
          invoiceNeedsWorkCount={accountInvoiceQueue.filter((invoice) => invoice.status !== "exported").length}
          issueCount={issueCount}
          openPriceRequestCount={accountQuoteQueue.filter((request) => isOpenQuoteStatus(request.status)).length}
          productFixCount={lowStockItems.length}
          role={role}
          supplierSetupNeeded={supplierSetupNeeded}
          supplierOrderCount={accountFulfillmentQueue.filter(fulfillmentNeedsActionFor).length}
          onNavigate={navigate}
          onSubmitOrder={handleSubmitOrder}
        />

        <section className="workspace-view-motion" key={`${role}-${effectiveNav}`} aria-label={`${effectiveNav} workspace`}>
        <Suspense fallback={<WorkspaceRouteFallback label={view.title} />}>
        {showOverview ? (
          <>
            {role === "admin" ? (
              <>
                <VeleoOperatorCockpit
                  branches={branchBook}
                  bosniaCoverageTargets={liveBosniaCoverageTargets}
                  connectorRequests={connectorRequests}
                  coverageRecords={coverageQueue}
                  demandGaps={demandGapQueue}
                  discrepancies={accountDiscrepancies}
                  doneForYouSetupRequests={doneForYouSetupRequests}
                  externalRecords={accountExternalRecords}
                  fulfillmentOrders={accountFulfillmentQueue}
                  importRuns={importRuns}
                  invoices={accountInvoiceQueue}
                  messageThreads={accountMessages}
                  orders={accountOrders}
                  restaurantSupplierContacts={restaurantSupplierContacts}
                  supplierOrderRequests={supplierOrderRequests}
                  onAdvanceDoneForYouSetup={advanceDoneForYouSetupRequest}
                  onAdvanceExternalRecord={advanceExternalRecord}
                  onAdvanceFulfillment={advanceFulfillmentOrder}
                  onConvertDoneForYouSetup={convertDoneForYouSetupIntoWorkspace}
                  onNavigate={navigate}
                  onOpenCoverageSprint={openCoverageSprint}
                  onResolveInvoiceBlocker={resolveInvoiceBlocker}
                  onSendMessageFollowUp={sendMessageFollowUp}
                />
                <DataImportAndConnectorPanel
                  branches={branchBook}
                  connectorRequests={connectorRequests}
                  connectorStatus={connectorStatus}
                  doneForYouSetupRequests={doneForYouSetupRequests}
                  importRuns={importRuns}
                  importStatus={importStatus}
                  role={role}
                  selectedBranch={selectedBranch}
                  onNavigate={navigate}
                  onImportBuyerOrderGuide={importBuyerOrderGuideFile}
                  onImportSupplierCatalog={importSupplierCatalogFile}
                  onSaveDoneForYouSetupRequest={saveDoneForYouSetupRequest}
                  onSaveConnectorRequest={saveConnectorRequest}
                />
              </>
            ) : null}
            {role === "restaurant" ? (
              <>
                <RestaurantTodayRail
                  draftLineCount={visibleAccountDraft.length}
                  draftTotal={draftTotal}
                  fulfillmentIssueCount={issueCount}
                  invoiceNeedsWorkCount={accountInvoiceQueue.filter((invoice) => invoice.status !== "exported").length}
                  openQuoteCount={accountQuoteQueue.filter((request) => isOpenQuoteStatus(request.status)).length}
                  readyOfferCount={liveSupplierOffers.length + accountQuoteQueue.filter((request) => isReadyQuoteStatus(request.status)).length}
                  repeatOrderCount={accountOrders.filter((order) => order.status !== "issue").length}
                  submitState={submitState}
                  topOfferLabel={
                    liveSupplierOffers[0]
                      ? formatCurrencyAmount(liveSupplierOffers[0].landedPrice, liveSupplierOffers[0].currency)
                      : undefined
                  }
                  onNavigate={navigate}
                  onQuickAdd={addQuickOrderItem}
                  onQuickQuote={createQuickQuoteRequest}
                  onSubmitOrder={handleSubmitOrder}
                />
                <RestaurantOwnerStart
                  approvalCount={accountApprovalQueue.filter((request) => request.status === "needs_approval").length}
                  draftTotal={draftTotal}
                  invoiceCount={accountInvoiceQueue.filter((invoice) => invoice.status !== "exported").length}
                  issueCount={issueCount}
                  offerCount={liveSupplierOffers.length}
                  quoteCount={accountQuoteQueue.length}
                  draftItems={visibleAccountDraft}
                  supplierContacts={selectedBranchSupplierContacts}
                  supplierCount={restaurantKnownSupplierNames.length}
                  onQuickAdd={addQuickOrderItem}
                  onQuickQuote={createQuickQuoteRequest}
                  onAssignSupplier={assignDraftItemSupplier}
                  onNavigate={navigate}
                  onSaveSupplierContact={saveRestaurantSupplierContact}
                  onSubmitOrder={handleSubmitOrder}
                  onUpdateQuantity={updateQuantity}
                />
              </>
            ) : null}
            {role === "supplier" ? (
              supplierSetupNeeded ? (
                <SupplierFirstRunStart
                  inviteOrder={supplierInviteOrder}
                  workspaceName={workspaceName}
                  onConfirmInviteOrder={advanceFulfillmentOrder}
                  onImportSupplierCatalog={importSupplierCatalogFile}
                  onReportInviteIssue={reportFulfillmentIssue}
                  onSaveManualProduct={saveManualSupplierProduct}
                  onNavigate={navigate}
                />
              ) : (
                <SupplierOperatingConsole
                  activatedOpportunityIds={activatedSupplierOpportunityIds}
                  catalogActionState={catalogActionState}
                  catalogProducts={accountCatalogProducts}
                  coverageRecords={coverageQueue}
                  demandGaps={demandGapQueue}
                  deliveryAreas={deliveryAreaQueue}
                  fulfillmentOrders={accountFulfillmentQueue}
                  invoices={accountInvoiceQueue}
                  quoteRequests={accountQuoteQueue}
                  supplierOpportunities={liveSupplierOpportunities}
                  supplierSearchDemand={supplierDemandQueue}
                  sourcingSprints={sourcingSprintQueue}
                  onActivateOpportunity={activateSupplierOpportunity}
                  onAdvanceFulfillment={advanceFulfillmentOrder}
                  onAdjustCatalogPrice={adjustCatalogPrice}
                  onCycleCatalogAvailability={cycleCatalogAvailability}
                  onMarkCatalogAvailable={markCatalogProductAvailable}
                  onCreateDemandQuote={createDemandQuoteLead}
                  onOpenOrders={() => setActiveNav("Orders")}
                  onPublishDemandSku={publishDemandSku}
                  onToggleDeliveryArea={toggleDeliveryAreaCapacity}
                  onCreateInvoice={activePermission.canExportFinance ? createSupplierInvoiceFromOrder : undefined}
                  onDisputeInvoice={disputeInvoice}
                  onExportInvoice={exportInvoice}
                  onMarkInvoicePaid={markInvoicePaid}
                  onReportFulfillmentIssue={reportFulfillmentIssue}
                  onResolveInvoiceBlocker={resolveInvoiceBlocker}
                  onSendQuote={sendSupplierQuote}
                />
              )
            ) : null}
            {roleEntry.shouldShowDashboardActionCenter ? (
              <DashboardActionCenter
                approvalRequests={accountApprovalQueue}
                catalogProducts={accountCatalogProducts}
                conversionTasks={conversionTasks}
                coverageRecords={coverageQueue}
                draftTotal={draftTotal}
                externalRecords={accountExternalRecords}
                fulfillmentOrders={accountFulfillmentQueue}
                invoices={accountInvoiceQueue}
                incidents={liveIncidentQueue}
                messageThreads={accountMessages}
                networkStats={liveNetworkStats}
                quoteRequests={accountQuoteQueue}
                replenishmentSignals={liveReplenishmentSignals}
                role={role}
                submitState={submitState}
                supplierOpportunities={liveSupplierOpportunities}
                supplierOffers={liveSupplierOffers}
                canApproveOrders={activePermission.canApproveOrders}
                onAdvanceConversionTask={advanceConversionTask}
                onAdvanceExternalRecord={advanceExternalRecord}
                onAdvanceFulfillment={advanceFulfillmentOrder}
                onAdvanceIncident={advanceIncident}
                onApproveRequest={approveRequest}
                onFindBackupProduct={findBackupForCatalogProduct}
                onOpenCoverageSprint={openCoverageSprint}
                onSendMessageFollowUp={sendMessageFollowUp}
                onSendQuote={sendSupplierQuote}
                onSubmitOrder={handleSubmitOrder}
                onNavigate={navigate}
              />
            ) : null}
            {role === "admin" ? (
              <>
                <GlobalScaleOverview
                  automationTasks={liveAutomationTasks}
                  networkSnapshot={networkSnapshot}
                  onOpenCountrySprint={openCountryCoverageSprint}
                />
                <EnterpriseAccounts
                  customerAccounts={networkSnapshot.customerAccounts}
                  networkStats={liveNetworkStats}
                />
                <PlatformControlRoom
                  incidents={liveIncidentQueue}
                  marketLanes={liveMarketLanes}
                  trustControls={liveTrustControls}
                  workflowSignals={liveWorkflowSignals}
                  onAdvanceIncident={advanceIncident}
                />
              </>
            ) : null}
            {role === "restaurant" && !restaurantSimpleDashboard ? (
              <>
                <BuyerOperationsCenter
                  actionState={replenishmentState}
                  approvals={accountApprovalQueue}
                  canApprove={activePermission.canApproveOrders}
                  permissionRole={activePermission}
                  insights={livePurchaseInsights}
                  invoices={accountInvoiceQueue}
                  quoteRequests={accountQuoteQueue}
                  signals={liveReplenishmentSignals}
                  supplierOffers={liveSupplierOffers}
                  onAddSignal={addReplenishmentSignal}
                  onAddSupplierOffer={addSupplierOfferToDraft}
                  onActOnInsight={actOnPurchaseInsight}
                  onApprove={approveRequest}
                  onAddInvoice={addManualInvoice}
                  onDisputeInvoice={disputeInvoice}
                  onExportInvoice={exportInvoice}
                  onMarkInvoicePaid={markInvoicePaid}
                  onResolveInvoiceBlocker={resolveInvoiceBlocker}
                />
              </>
            ) : null}
          </>
        ) : null}

        {showMarketplace ? (
          <MarketplaceWorkbench
            allProducts={searchableMarketplaceProducts}
            branches={branchBook}
            categories={marketplaceCategories}
            purchaseInsights={livePurchaseInsights}
            quoteRequests={accountQuoteQueue}
            quoteActionState={quoteActionState}
            savedSupplierNames={accountSavedSupplierNames}
            selectedCategory={marketplaceCategory}
            selectedMode={marketplaceMode}
            selectedProduct={selectedMarketProduct}
            searchQuery={query}
            visibleProducts={visibleMarketplaceProducts}
            helpers={{
              branchCity,
      branchTaxSummary,
      formatBranchMoney,
      marketplaceDecisionFor,
      marketplaceDecisionScore,
      marketplaceProductCanOrder,
      marketplacePriceDisplay,
      marketplaceSupplyStateDetail,
      marketplaceSupplyStateLabel,
      procurementBriefFor,
      productBranchFit,
      supplierOptionScore,
            }}
            onCreateQuoteRequest={handleCreateQuoteRequest}
            onAddProductToDraft={addMarketplaceProductToDraft}
            onCaptureFailedSearch={captureMarketplaceDemandGap}
            onFollowUpQuote={followUpQuoteRequest}
            onUseQuoteInDraft={addQuoteRequestToDraft}
            onSelectBranch={setSelectedBranchId}
            selectedBranch={selectedBranch}
            onSelectCategory={setMarketplaceCategory}
            onSelectMode={setMarketplaceMode}
            onSelectProduct={setSelectedMarketProductId}
            onToggleSavedSupplier={toggleSavedSupplier}
          />
        ) : null}

        {showSupplierConsole ? (
          <SupplierOperatingConsole
            activatedOpportunityIds={activatedSupplierOpportunityIds}
            catalogActionState={catalogActionState}
            catalogProducts={accountCatalogProducts}
            coverageRecords={coverageQueue}
            demandGaps={demandGapQueue}
            deliveryAreas={deliveryAreaQueue}
            fulfillmentOrders={accountFulfillmentQueue}
            invoices={accountInvoiceQueue}
            quoteRequests={accountQuoteQueue}
            supplierOpportunities={liveSupplierOpportunities}
            supplierSearchDemand={supplierDemandQueue}
            sourcingSprints={sourcingSprintQueue}
            onActivateOpportunity={activateSupplierOpportunity}
            onAdvanceFulfillment={advanceFulfillmentOrder}
            onAdjustCatalogPrice={adjustCatalogPrice}
            onCycleCatalogAvailability={cycleCatalogAvailability}
            onMarkCatalogAvailable={markCatalogProductAvailable}
            onCreateDemandQuote={createDemandQuoteLead}
            onOpenOrders={() => setActiveNav("Orders")}
            onPublishDemandSku={publishDemandSku}
            onToggleDeliveryArea={toggleDeliveryAreaCapacity}
            onCreateInvoice={activePermission.canExportFinance ? createSupplierInvoiceFromOrder : undefined}
            onDisputeInvoice={disputeInvoice}
            onExportInvoice={exportInvoice}
            onMarkInvoicePaid={markInvoicePaid}
            onReportFulfillmentIssue={reportFulfillmentIssue}
            onResolveInvoiceBlocker={resolveInvoiceBlocker}
            onSendQuote={sendSupplierQuote}
          />
        ) : null}

        {showExternalDiscovery ? (
          <ExternalDiscoveryView
            actionState={discoveryActionState}
            branches={branchBook}
            conversionTasks={conversionTasks}
            demandGaps={demandGapQueue}
            discoverySources={discoverySourceQueue}
            inquiryLog={externalInquiryLog}
            records={accountExternalRecords}
            role={role}
            runs={discoveryRuns}
            inquiry={externalInquiry}
            selectedBranch={selectedBranch}
            onAdvanceRecord={advanceExternalRecord}
            onAdvanceConversionTask={advanceConversionTask}
            onOfferCoverage={offerDemandCoverage}
            onGenerateInquiry={generateExternalInquiry}
            onMarkInquiryReply={markExternalInquiryReplied}
            onSelectBranch={setSelectedBranchId}
            onRunSource={runDiscoverySource}
            onSubmitPublicSource={savePublicSupplierSource}
          />
        ) : null}

        {showNetworkIntel ? (
          <NetworkIntelView
            actionState={networkActionState}
            bosniaCoverageTargets={liveBosniaCoverageTargets}
            conversionTasks={conversionTasks}
            coverageRecords={coverageQueue}
            demandGaps={demandGapQueue}
            fulfillmentOrders={accountFulfillmentQueue}
            incidents={liveIncidentQueue}
            marketLanes={liveMarketLanes}
            networkSnapshot={networkSnapshot}
            quoteRequests={accountQuoteQueue}
            sourcingSprints={sourcingSprintQueue}
            trustControls={liveTrustControls}
            workflowSignals={liveWorkflowSignals}
            onAdvanceConversionTask={advanceConversionTask}
            onAdvanceFulfillment={advanceFulfillmentOrder}
            onAdvanceIncident={advanceIncident}
            onAdvanceSprint={advanceSourcingSprint}
            onOpenCountrySprint={openCountryCoverageSprint}
            onOpenCoverageSprint={openCoverageSprint}
            onSendQuote={sendSupplierQuote}
          />
        ) : null}

        {showMessages ? (
          <MessagesView
            role={role}
            threads={accountMessages}
            onResolveThread={resolveMessageThread}
            onSendFollowUp={sendMessageFollowUp}
          />
        ) : null}

        {showInvoices ? (
          <InvoiceWorkspace
            canAddInvoice={activePermission.canExportFinance}
            discrepancies={accountDiscrepancies}
            invoices={accountInvoiceQueue}
            permissionLabel={activePermission.role}
            role={role}
            onAddInvoice={addManualInvoice}
            onDisputeInvoice={disputeInvoice}
            onExportInvoice={exportInvoice}
            onMarkInvoicePaid={markInvoicePaid}
            onRecoverIssue={recoverOrderIssue}
            onResolveInvoiceBlocker={resolveInvoiceBlocker}
            onResolveIssue={resolveOrderIssue}
          />
        ) : null}

        {showAnalytics ? (
          <AnalyticsView
            approvalRequests={accountApprovalQueue}
            catalogProducts={accountCatalogProducts}
            conversionTasks={conversionTasks}
            coverageRecords={coverageQueue}
            demandGaps={demandGapQueue}
            externalRecords={accountExternalRecords}
            fulfillmentOrders={accountFulfillmentQueue}
            incidents={liveIncidentQueue}
            invoices={accountInvoiceQueue}
            messageThreads={accountMessages}
            networkSnapshot={networkSnapshot}
            orders={accountOrders}
            purchaseInsights={livePurchaseInsights}
            quoteRequests={accountQuoteQueue}
            role={role}
            supplierOpportunities={liveSupplierOpportunities}
            onActOnPurchaseInsight={actOnPurchaseInsight}
            onActivateSupplierOpportunity={activateSupplierOpportunity}
            onAdvanceConversionTask={advanceConversionTask}
            onAdvanceIncident={advanceIncident}
            onOpenCoverageSprint={openCoverageSprint}
            onResolveMessageThread={resolveMessageThread}
            onSendQuote={sendSupplierQuote}
          />
        ) : null}

        {showOrders ? (
          <RestaurantBuyingCommand
            blockingCount={draftBlockingChecks.length}
            draftEstimateLabel={formatBranchMoney(draftTotal, selectedBranch, { includeBase: true })}
            draftLineCount={accountDraft.length}
            draftSendDisabled={draftSendDisabled}
            draftSupplierCount={draftPackets.length}
            draftTotalLabel={formatBranchMoney(draftTotal, selectedBranch)}
            onComparePrice={() => setActiveNav("Marketplace")}
            onImportUsualList={() => setActiveNav("Settings")}
            onQuickAdd={addQuickOrderItem}
            onQuickQuote={createQuickQuoteRequest}
            onSubmit={handleSubmitOrder}
          />
        ) : null}

        {showBuyerOrders ? (
          <BuyerOperationsCenter
              actionState={replenishmentState}
              approvals={accountApprovalQueue}
              canApprove={activePermission.canApproveOrders}
            permissionRole={activePermission}
            insights={livePurchaseInsights}
            invoices={accountInvoiceQueue}
            quoteRequests={accountQuoteQueue}
            signals={liveReplenishmentSignals}
            supplierOffers={liveSupplierOffers}
            onAddSignal={addReplenishmentSignal}
            onAddSupplierOffer={addSupplierOfferToDraft}
            onActOnInsight={actOnPurchaseInsight}
            onApprove={approveRequest}
            onAddInvoice={addManualInvoice}
            onDisputeInvoice={disputeInvoice}
            onExportInvoice={exportInvoice}
            onMarkInvoicePaid={markInvoicePaid}
            onResolveInvoiceBlocker={resolveInvoiceBlocker}
          />
        ) : null}

        {showBuyerOrders ? (
          <BuyerPurchaseControlCenter
            branchTaxSummary={branchTaxSummary}
            branches={branchBook}
            discrepancies={accountDiscrepancies}
            formatBranchMoney={formatBranchMoney}
            orders={accountOrders}
            onNudgeSupplier={nudgeOrderSupplier}
            onRecoverIssue={recoverOrderIssue}
            onResolveIssue={resolveOrderIssue}
            onUseOrderAsDraft={loadOrderAsDraft}
          />
        ) : null}

        {showSupplierOrders ? (
          <SupplierOrdersView
            fulfillmentOrders={accountFulfillmentQueue}
            invoices={accountInvoiceQueue}
            quoteRequests={accountQuoteQueue}
            onAdvanceFulfillment={advanceFulfillmentOrder}
            onCreateInvoice={activePermission.canExportFinance ? createSupplierInvoiceFromOrder : undefined}
            onDisputeInvoice={disputeInvoice}
            onExportInvoice={exportInvoice}
            onMarkInvoicePaid={markInvoicePaid}
            onReportFulfillmentIssue={reportFulfillmentIssue}
            onResolveInvoiceBlocker={resolveInvoiceBlocker}
            onSendQuote={sendSupplierQuote}
          />
        ) : null}

        {showDelivery ? (
          <section className="operations-grid">
            <Panel
              className="schedule-panel"
              title="Delivery schedule"
              action="View all"
              help="Upcoming deliveries for the buyer account. Use this to catch late routes or supplier issues before service starts."
            >
              <div className="weekday-row">
                {["Mon 19 May", "Tue 20 May", "Wed 21 May", "Thu 22 May", "Fri 23 May"].map(
                  (day, index) => (
                    <button className={index === 0 ? "active" : ""} key={day} type="button">
                      {day}
                    </button>
                  ),
                )}
              </div>
              <div className="schedule-list">
                {accountOrders.slice(0, 4).map((order) => (
                  <article key={order.id}>
                    <time>{order.deliveryAt.replace("Today, ", "")}</time>
                    <Truck size={19} />
                    <div>
                      <strong>{order.city} route</strong>
                      <span>{order.buyer}</span>
                    </div>
                    <StatusBadge status={order.status} />
                  </article>
                ))}
              </div>
            </Panel>

            <Panel
              title="Low stock / alerts"
              action="Review"
              help="Catalog items that need reorder, substitution, or supplier follow-up based on current availability."
            >
              <div className="alert-list">
                {lowStockItems.map((product) => (
                  <StockAlertRow
                    key={product.id}
                    onAddToDraft={() => addCatalogProductToDraft(product)}
                    onFindBackup={() => findBackupForCatalogProduct(product)}
                    product={product}
                  />
                ))}
              </div>
            </Panel>
          </section>
        ) : null}

        {showOrders ? (
          <>
          <section className="work-grid">
            <Panel
              title="Past orders"
              action="History"
              help="Orders you already sent. Repeat a good order, ask a supplier for an update, or fix a problem."
            >
              <div className="filter-row">
                {(["all", "sent", "confirmed", "packed", "in_transit", "delivered", "issue"] as const).map((status) => (
                  <button
                    key={status}
                    className={orderFilter === status ? "active" : ""}
                    type="button"
                    onClick={() => setOrderFilter(status)}
                  >
                    {status === "all" ? "All" : statusLabels[status]}
                  </button>
                ))}
              </div>
              <div className="order-table">
                {filteredOrders.map((order) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    onNudgeSupplier={() => nudgeOrderSupplier(order.id)}
                    onRecoverIssue={() => recoverOrderIssue(order.id)}
                    onResolveIssue={() => resolveOrderIssue(order.id)}
                    supplierRequest={supplierOrderRequests.find((request) => request.orderId === order.id)}
                  />
                ))}
              </div>
            </Panel>

            <Panel
              className="composer-panel"
              title="Today's buying list"
              action={submitState}
              help="Items waiting to be sent. Add items, change amounts, check price, then send."
            >
              <div className="composer-header">
                <div>
                  <p>{draftSuppliers.length > 1 ? "Several suppliers" : "Supplier"}</p>
                  <strong>{draftSupplierSummary}</strong>
                </div>
                <label className="branch-select">
                  <span>Restaurant</span>
                  <select
                    value={selectedBranch.id}
                    onChange={(event) => setSelectedBranchId(event.target.value)}
                    aria-label="Select buying branch"
                  >
                    {branchBook.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </label>
                <div>
                  <span>{accountDraft.length} {accountDraft.length === 1 ? "line" : "lines"}</span>
                  <b>
                    {draftSuppliers.length} {draftSuppliers.length === 1 ? "supplier" : "suppliers"}
                  </b>
                </div>
              </div>

              <div className="branch-context-strip" aria-label="Selected branch budget context">
                <p>
                  <span>Market</span>
                  <strong>{selectedBranch.market}</strong>
                </p>
                <p>
                  <span>Budget remaining</span>
                  <strong>{formatBranchMoney(selectedBranchRemaining, selectedBranch)}</strong>
                </p>
                <p>
                  <span>Approval limit</span>
                  <strong>{formatBranchMoney(selectedBranch.approvalLimit, selectedBranch)}</strong>
                </p>
                <p>
                  <span>Tax profile</span>
                  <strong>{branchTaxSummary(selectedBranch)}</strong>
                </p>
              </div>

              <div className="order-preflight-list" aria-label="List checks">
                {draftPreflightChecks.map((check) => (
                  <OrderPreflightCheckRow key={check.id} check={check} />
                ))}
              </div>

              {draftNeedsBudgetApproval ? (
                <div className={`draft-approval-strip ${draftApprovalRequest?.status ?? "missing"}`}>
                  <div>
                    <strong>
                      {draftApprovalRequest?.status === "approved"
                        ? "Branch approval ready"
                        : draftApprovalRequest?.status === "needs_approval"
                          ? "Approval pending"
                          : "Approval required"}
                    </strong>
                    <span>
                      {draftApprovalRequest
                        ? `${draftApprovalRequest.id} / ${formatBranchMoney(draftApprovalRequest.amountBam, selectedBranch, { includeBase: true })} / ${draftApprovalRequest.due}`
                        : `${selectedBranch.name} needs approval before this order can be sent.`}
                    </span>
                  </div>
                  <button
                    className="secondary-action"
                    type="button"
                    onClick={requestDraftApproval}
                    disabled={draftApprovalRequest?.status === "needs_approval" || draftApprovalRequest?.status === "approved"}
                  >
                    {draftApprovalRequest?.status === "approved"
                      ? "Approved"
                      : draftApprovalRequest?.status === "needs_approval"
                        ? "Pending"
                        : "Request approval"}
                    <LockKeyhole size={16} />
                  </button>
                </div>
              ) : null}

              <div className="draft-packet-list">
                {draftPackets.map((packet) => (
                  <article className="draft-packet" key={packet.supplierName}>
                    <div className="draft-packet-head">
                      <div>
                        <span>Send to supplier</span>
                        <strong>{packet.supplierName}</strong>
                        <small>
                          {packet.items.length} {packet.items.length === 1 ? "item" : "items"} / {packet.categories.join(", ")} / {draftPacketSourceLabel(packet)}
                        </small>
                      </div>
                      <div>
                        <span>Subtotal</span>
                        <b>{formatBranchMoney(packet.total, selectedBranch)}</b>
                      </div>
                    </div>

                    <div className="draft-list">
                    {packet.items.map((item) => {
                      const needsUsualReview = draftItemNeedsUsualReview(item);
                      const savedSupplierHabit = draftItemSavedSupplierHabit(item, draftLookupContext);
                      const assignedSupplier = supplierForDraftItem(item, draftLookupContext);
                      const supplierPending = assignedSupplier === "Supplier pending";
                      const supplierOptions = selectedBranchSupplierContacts.some(
                        (contact) => contact.name === assignedSupplier,
                      )
                        ? selectedBranchSupplierContacts
                        : assignedSupplier === "Supplier pending"
                          ? selectedBranchSupplierContacts
                          : [
                              ...selectedBranchSupplierContacts,
                              {
                                id: `current-${assignedSupplier}`,
                                branchName: selectedBranch.name,
                                category: categoryForDraftItem(item, draftLookupContext),
                                contact: "",
                                createdAt: "",
                                deliveryArea: selectedBranch.market,
                                name: assignedSupplier,
                                relationshipStatus: "needs_review" as const,
                                source: "restaurant_provided" as const,
                                usualDays: "",
                              },
                            ];

                      return (
                        <article
                          className={
                            needsUsualReview
                              ? savedSupplierHabit
                                ? "has-saved-habit"
                                : "needs-usual-review"
                              : undefined
                          }
                          key={item.productId}
                        >
                          <div>
                            <strong>{item.name}</strong>
                            <span>
                              {formatBranchUnitPrice(item.unitPrice, item.unit, selectedBranch)}
                            </span>
                            <span>
                              {categoryForDraftItem(item, draftLookupContext)} / {draftSourceLabel(item)}
                            </span>
                            {supplierOptions.length ? (
                              <label className={`draft-supplier-select${supplierPending ? " needs-supplier" : ""}`}>
                                <span>Supplier</span>
                                <select
                                  aria-label={`Assign supplier for ${item.name}`}
                                  value={supplierPending ? "" : assignedSupplier}
                                  onChange={(event) => assignDraftItemSupplier(item.productId, event.target.value)}
                                >
                                  <option value="">Assign supplier contact</option>
                                  {supplierOptions.map((contact) => (
                                    <option key={contact.id} value={contact.name}>
                                      {contact.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            ) : null}
                            {item.note ? <small>{item.note}</small> : null}
                            {needsUsualReview ? (
                              <em>
                                {savedSupplierHabit
                                  ? "Saved supplier habit found for this shorthand."
                                  : "Confirm supplier understands this usual-item shorthand."}
                              </em>
                            ) : null}
                          </div>
                          <div className="qty-control">
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                              aria-label={`Reduce ${item.name}`}
                            >
                              <Minus size={15} />
                            </button>
                            <input
                              value={item.quantity}
                              onChange={(event) =>
                                updateQuantity(item.productId, Number(event.target.value) || 0)
                              }
                              aria-label={`${item.name} quantity`}
                            />
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                              aria-label={`Increase ${item.name}`}
                            >
                              <Plus size={15} />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                    </div>
                  </article>
                ))}
              </div>

              <div className="composer-total">
                <span>Estimated total for today's list</span>
                <strong>{formatBranchMoney(draftTotal, selectedBranch, { includeBase: true })}</strong>
              </div>
              <button
                className="primary-action full-width"
                type="button"
                onClick={handleSubmitOrder}
                disabled={draftSendDisabled}
              >
                {draftBlockingChecks.length ? "Fix issues before sending" : "Send order"}
                <PackageCheck size={18} />
              </button>
            </Panel>

            <SupplierOrderRequestDesk
              featuredRequestIds={recentSupplierRequestIds}
              requests={supplierOrderRequests.filter((request) => request.restaurantName === selectedBranch.name)}
              onCopyFollowUp={copySupplierFollowUpMessage}
              onCopyMessage={copySupplierOrderMessage}
              onMarkMessageIssue={markSupplierMessageIssue}
              onReviewRequest={() => {
                setOrderFilter("issue");
                navigate("Orders");
              }}
              onShareSheet={shareSupplierOrderMessage}
              onShareMessage={markSupplierMessageShared}
            />
          </section>
          </>
        ) : null}

        {effectiveNav === "Settings" ? (
          <SettingsView
            auditLog={auditQueue}
            branches={branchBook}
            integrations={liveIntegrationStatus}
            connectorRequests={accountConnectorRequests}
            connectorStatus={connectorStatus}
            doneForYouSetupRequests={accountDoneForYouSetupRequests}
            importRuns={accountImportRuns}
            importStatus={importStatus}
            localizationRules={localizationQueue}
            discoverySources={discoverySourceQueue}
            onAdjustBranchBudget={adjustBranchBudgetRule}
            onApproveLocalization={approveLocalizationRule}
            onRepairIntegration={repairIntegrationConnection}
            onRunDiscoverySource={runDiscoverySource}
            role={role}
            selectedBranch={selectedBranch}
            users={workspaceUserQueue}
            onImportBuyerOrderGuide={importBuyerOrderGuideFile}
            onImportSupplierCatalog={importSupplierCatalogFile}
            onNavigate={navigate}
            onProvisionBranchSeat={provisionBranchBuyerSeat}
            onSaveConnectorRequest={saveConnectorRequest}
            onSaveDoneForYouSetupRequest={saveDoneForYouSetupRequest}
            onToggleUserStatus={toggleWorkspaceUserStatus}
            onUpdateConnectorStatus={updateConnectorStatus}
          />
        ) : null}
        </Suspense>
        </section>
      </main>
    </div>
  );
}

function WorkspaceRouteFallback({ label }: { label: string }) {
  return (
    <div className="workspace-route-fallback" role="status" aria-live="polite">
      <span>veleo</span>
      <strong>Loading {label.toLowerCase()}</strong>
    </div>
  );
}

const LANDING_COPY = {
  bs: {
    login: "Prijava",
    eyebrow: "Za restorane · prvo Sarajevo",
    h1: "Naručite od svojih dobavljača za par sekundi.",
    sub: "Napišite narudžbu kako to inače radite — nalijepite je, otkucajte ili pošaljite sliku. Veleo je pretvori u urednu poruku koju dobavljač potvrdi jednim klikom. Bez nove aplikacije za njega. Vaše dobavljače postavljamo besplatno.",
    primaryCta: "Postavite svoje dobavljače — besplatno",
    secondaryCta: "Pogledajte kako radi",
    entryStrip: [
      { title: "Vaši dobavljači", detail: "Postavljamo ih besplatno." },
      { title: "Vaša uobičajena narudžba", detail: "Nalijepite, otkucajte ili slikajte." },
      { title: "Potvrda jednim klikom", detail: "Bez aplikacije za dobavljača." },
    ],
    howEyebrow: "Kako radi",
    howH2: "Od vaše poruke do vaše kuhinje.",
    howSub: "Narudžba ostaje ista kao i do sada. Veleo je sredi i prati odgovor.",
    workflow: ["Vaš dobavljač", "Vaša narudžba", "Dobavljač potvrđuje", "Provjera računa"],
    proofEyebrow: "Stvarna narudžba",
    proofH2: "Uđe zbrkano. Izađe uredno.",
    proofSub: "Jedan dobavljač, jedna narudžba, jedan odgovor.",
    proofMessages: [
      { label: "Prije Veleo-a", text: "Selam Ahbab, ako moze za sutra: 8 glave mladi kupusa, 2 gajbe paradajz, 200g svjeze persun. Hvala puno." },
      { label: "Uredna narudžba", text: "Restoran → Imarn Povrće. Sarajevo, sutra. 8 glavica mladog kupusa / 2 gajbe paradajza / 200 g peršuna." },
      { label: "Odgovor dobavljača", text: "Dobavljač potvrđuje dostavu. Restoran čuva odgovor prije provjere računa." },
    ],
    confirmBadge: "Bez registracije",
    confirmName: "Imarn Povrće potvrđuje",
    confirmMeta: "Sarajevo / sutra ujutro",
    confirmAccepted: "Prihvaćeno",
    confirmItems1: "8 glavica mladog kupusa / 2 gajbe paradajza",
    confirmItems2: "5 kg krastavaca / 200 g peršuna",
    confirmNote: "Restoran ovo vidi prije provjere računa.",
    setupProof: [
      { title: "Postavljanje je besplatno", detail: "Prvi dobavljači i ponovljene narudžbe." },
      { title: "Pošaljite nam kako već radite", detail: "WhatsApp, Viber, slike, spiskovi." },
      { title: "Bosna, rutu po rutu", detail: "Prvo Sarajevo, pa grad po grad." },
    ],
    trustEyebrow: "Povjerenje",
    trustH2: "Jasno od početka.",
    trustSub: "Kontakti, cijene i oglasi ostaju pod kontrolom.",
    trustRules: [
      { title: "Vaši dobavljači ostaju vaši", detail: "Kontakti služe samo vašim narudžbama." },
      { title: "Privatne cijene ostaju privatne", detail: "Samo anonimni rasponi, kasnije." },
      { title: "Bez izmišljene ponude", detail: "Oglasi čekaju provjeru." },
    ],
    finalEyebrow: "Prva 3 mjeseca besplatno",
    finalLine: "Postavite danas jednog pravog dobavljača.",
    finalCta: "Postavite svoje dobavljače — besplatno",
  },
  en: {
    login: "Log in",
    eyebrow: "For restaurants · Sarajevo first",
    h1: "Order from your suppliers in seconds.",
    sub: "Write your order the way you always do — paste it, type it, send a photo. Veleo turns it into a clean message your supplier confirms in one tap. No new app for them. We set up your suppliers for free.",
    primaryCta: "Set up my suppliers — free",
    secondaryCta: "See how it works",
    entryStrip: [
      { title: "Your suppliers", detail: "We set them up free." },
      { title: "Your usual order", detail: "Paste, type, or photo." },
      { title: "One-tap confirm", detail: "No app for the supplier." },
    ],
    howEyebrow: "How it works",
    howH2: "From your message to your kitchen.",
    howSub: "The order stays familiar. Veleo cleans it and tracks the answer.",
    workflow: ["Your supplier", "Your usual order", "Supplier confirms", "Invoice check"],
    proofEyebrow: "Real order",
    proofH2: "Messy in. Clean out.",
    proofSub: "One supplier, one order, one answer.",
    proofMessages: [
      { label: "Before Veleo", text: "Selam Ahbab, ako moze za sutra: 8 glave mladi kupusa, 2 gajbe paradajz, 200g svjeze persun. Hvala puno." },
      { label: "Clean order", text: "Restaurant → Imarn Povrće. Sarajevo, tomorrow. 8 heads young cabbage / 2 crates tomatoes / 200 g parsley." },
      { label: "Supplier reply", text: "Supplier confirms delivery. The restaurant keeps the answer before invoice matching." },
    ],
    confirmBadge: "No account needed",
    confirmName: "Imarn Povrće confirms",
    confirmMeta: "Sarajevo / tomorrow morning",
    confirmAccepted: "Accepted",
    confirmItems1: "8 heads young cabbage / 2 crates tomatoes",
    confirmItems2: "5 kg cucumbers / 200 g parsley",
    confirmNote: "The restaurant sees this before invoice matching.",
    setupProof: [
      { title: "Setup is free", detail: "Your first suppliers and repeat orders." },
      { title: "Send us your habit", detail: "WhatsApp, Viber, photos, lists." },
      { title: "Bosnia, route by route", detail: "Sarajevo first, then city by city." },
    ],
    trustEyebrow: "Trust",
    trustH2: "Clear by default.",
    trustSub: "Contacts, prices, and listings stay controlled.",
    trustRules: [
      { title: "Your suppliers stay yours", detail: "Contacts only serve your orders." },
      { title: "Private prices stay private", detail: "Only anonymous ranges, later." },
      { title: "No fake marketplace", detail: "Listings wait for verification." },
    ],
    finalEyebrow: "First 3 months free",
    finalLine: "Set up one real supplier today.",
    finalCta: "Set up my suppliers — free",
  },
};

function PublicLanding({
  page,
  authConfigured,
  notice,
  onBackToLanding,
  onAuthenticated,
  onOpenLocalWorkspace,
  onShowLogin,
}: {
  page: "landing" | "login";
  authConfigured: boolean;
  notice: string | null;
  onBackToLanding: () => void;
  onAuthenticated: () => void;
  onOpenLocalWorkspace: (role: Role) => void;
  onShowLogin: () => void;
}) {
  const [lang, setLang] = useState<"bs" | "en">(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("veleo.lang") : null;
    return stored === "en" ? "en" : "bs";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("veleo.lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);
  const t = LANDING_COPY[lang];
  const entryIcons = [ClipboardList, TrendingUp, FileSpreadsheet];
  const setupIcons = [Users, MessageSquare, MapPin];
  const trustIcons = [ShieldCheck, LockKeyhole, CheckCircle2];

  if (page === "login") {
    return (
      <WorkspaceLogin
        lang={lang}
        setLang={setLang}
        onBack={onBackToLanding}
        onAuthenticated={onAuthenticated}
        onOpenLocalWorkspace={onOpenLocalWorkspace}
        authConfigured={authConfigured}
        notice={notice}
      />
    );
  }

  return (
    <main className="public-shell">
      <nav className="public-nav" aria-label="Public navigation">
        <div className="public-brand">veleo</div>
        <div className="public-nav-actions">
          <div className="public-lang-toggle" role="group" aria-label="Jezik / Language">
            <button
              type="button"
              className={lang === "bs" ? "active" : ""}
              aria-pressed={lang === "bs"}
              onClick={() => setLang("bs")}
            >
              BS
            </button>
            <button
              type="button"
              className={lang === "en" ? "active" : ""}
              aria-pressed={lang === "en"}
              onClick={() => setLang("en")}
            >
              EN
            </button>
          </div>
          <button className="public-login" type="button" onClick={onShowLogin}>
            {t.login}
            <ChevronRight size={16} />
          </button>
        </div>
      </nav>

      <section className="public-hero" aria-label="Veleo">
        <div className="public-copy">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1>{t.h1}</h1>
          <p>{t.sub}</p>
          <div className="public-hero-actions">
            <button className="public-primary" type="button" onClick={onShowLogin}>
              {t.primaryCta}
              <ChevronRight size={17} />
            </button>
            <a className="public-secondary" href="#how-it-works">
              {t.secondaryCta}
            </a>
          </div>
          <div className="public-entry-strip" aria-label={t.eyebrow}>
            {t.entryStrip.map((point, index) => {
              const Icon = entryIcons[index];
              return (
                <p key={point.title}>
                  <Icon size={18} />
                  <strong>{point.title}</strong>
                  <small>{point.detail}</small>
                </p>
              );
            })}
          </div>
        </div>
      </section>

      <section className="public-section" id="how-it-works" aria-label={t.howH2}>
        <div className="public-section-copy">
          <span>{t.howEyebrow}</span>
          <h2>{t.howH2}</h2>
          <p>{t.howSub}</p>
        </div>
        <div className="public-workflow">
          {t.workflow.map((item, index) => (
            <article key={item}>
              <b>{index + 1}</b>
              <strong>{item}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="public-proof-section" aria-label={t.proofH2}>
        <div className="public-section-copy">
          <span>{t.proofEyebrow}</span>
          <h2>{t.proofH2}</h2>
          <p>{t.proofSub}</p>
        </div>
        <div className="public-proof-stack">
          <div className="public-proof-board">
            {t.proofMessages.map((message, index) => (
              <article key={message.label}>
                <b>{index + 1}</b>
                <span>{message.label}</span>
                <p>{message.text}</p>
              </article>
            ))}
          </div>
          <div className="public-confirmation-proof" aria-label={t.confirmName}>
            <div>
              <span>{t.confirmBadge}</span>
              <strong>{t.confirmName}</strong>
              <small>{t.confirmMeta}</small>
            </div>
            <p>
              <b>{t.confirmAccepted}</b>
              <span>{t.confirmItems1}</span>
            </p>
            <p>
              <b>{t.confirmAccepted}</b>
              <span>{t.confirmItems2}</span>
            </p>
            <em>{t.confirmNote}</em>
          </div>
        </div>
      </section>

      <section className="public-setup-strip" aria-label={t.howEyebrow}>
        {t.setupProof.map((item, index) => {
          const Icon = setupIcons[index];
          return (
            <article key={item.title}>
              <Icon size={22} />
              <strong>{item.title}</strong>
              <p>{item.detail}</p>
            </article>
          );
        })}
      </section>

      <section className="public-trust-section" aria-label={t.trustH2}>
        <div className="public-section-copy">
          <span>{t.trustEyebrow}</span>
          <h2>{t.trustH2}</h2>
          <p>{t.trustSub}</p>
        </div>
        <div className="public-trust-grid">
          {t.trustRules.map((rule, index) => {
            const Icon = trustIcons[index];
            return (
              <article key={rule.title}>
                <Icon size={21} />
                <strong>{rule.title}</strong>
                <p>{rule.detail}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="public-final-cta" aria-label={t.finalLine}>
        <div>
          <span>{t.finalEyebrow}</span>
          <strong>{t.finalLine}</strong>
        </div>
        <button className="public-primary" type="button" onClick={onShowLogin}>
          {t.finalCta}
          <ChevronRight size={17} />
        </button>
      </section>
    </main>
  );
}

const AUTH_MESSAGES = {
  bs: {
    notConfigured: "Nalozi još nisu povezani.",
    invalid: "E-mail ili lozinka nisu tačni.",
    exists: "Taj e-mail već ima nalog — prijavite se.",
    notConfirmed: "Prvo potvrdite e-mail, pa se prijavite.",
    weak: "Odaberite jaču lozinku (najmanje 8 znakova).",
    expired: "Sesija je istekla. Prijavite se ponovo.",
    rateLimit: "Previše e-mailova odjednom. Sačekajte minutu i pokušajte ponovo.",
  },
  en: {
    notConfigured: "Accounts are not connected yet.",
    invalid: "Email or password is incorrect.",
    exists: "That email already has an account — sign in instead.",
    notConfirmed: "Confirm your email first, then sign in.",
    weak: "Choose a stronger password (at least 8 characters).",
    expired: "Your session expired. Sign in again.",
    rateLimit: "Too many emails just now. Wait a minute and try again.",
  },
};

function authMessage(error: string, lang: "bs" | "en" = "en"): string {
  const m = AUTH_MESSAGES[lang] ?? AUTH_MESSAGES.en;
  switch (error) {
    case "auth_not_configured":
      return m.notConfigured;
    case "invalid_grant":
    case "invalid_login_credentials":
    case "Invalid login credentials":
      return m.invalid;
    case "user_already_exists":
    case "User already registered":
      return m.exists;
    case "email_not_confirmed":
    case "Email not confirmed":
      return m.notConfirmed;
    case "weak_password":
      return m.weak;
    case "not_authenticated":
      return m.expired;
    case "over_email_send_rate_limit":
      return m.rateLimit;
    default:
      return error.replace(/_/g, " ");
  }
}

function SupplierOrderRequestDesk({
  featuredRequestIds = [],
  requests,
  onCopyFollowUp,
  onCopyMessage,
  onMarkMessageIssue,
  onReviewRequest,
  onShareSheet,
  onShareMessage,
}: {
  featuredRequestIds?: string[];
  requests: SupplierOrderRequest[];
  onCopyFollowUp: (requestId: string) => void;
  onCopyMessage: (requestId: string) => void;
  onMarkMessageIssue: (
    requestId: string,
    deliveryStatus: Extract<SupplierOrderMessageDeliveryStatus, "needs_follow_up" | "failed">,
    note: string,
  ) => void;
  onReviewRequest?: (request: SupplierOrderRequest) => void;
  onShareSheet: (requestId: string) => void;
  onShareMessage: (requestId: string, channel: SupplierOrderMessageChannel) => void;
}) {
  const sortedRequests = [...requests].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)).slice(0, 6);
  const featuredRequestIdSet = new Set(featuredRequestIds);
  const followUpRequestCount = sortedRequests.filter(
    (request) => request.messageDeliveryStatus === "needs_follow_up" || request.messageDeliveryStatus === "failed",
  ).length;

  return (
    <Panel
      className="supplier-request-desk"
      title="Supplier messages"
      action={
        sortedRequests.length
          ? followUpRequestCount
            ? `${followUpRequestCount} need contact`
            : `${sortedRequests.length} ready`
          : "No messages yet"
      }
      help="Share the clean supplier-ready message through the supplier's normal channel. The VELEO link lets them confirm without an account."
    >
      {sortedRequests.length ? (
        <div className="supplier-request-list">
          {sortedRequests.map((request) => {
            const shareTargets = supplierRequestShareTargets(request);
            const hasDirectShareTarget = shareTargets.some((target) => target.available);
            const preferredShareTarget = shareTargets.find((target) => target.channel === request.preferredChannel);
            const preferredChannelUnavailable =
              Boolean(request.preferredChannel && request.preferredChannel !== "copy" && preferredShareTarget) &&
              !preferredShareTarget?.available;
            const reviewStats = supplierRequestReviewStats(request);
            const followUpDue = supplierFollowUpIsDue(request);
            const followUpDueLabel = request.followUpDueAt
              ? new Date(request.followUpDueAt).toLocaleString()
              : "";
            const needsRestaurantReview = request.status !== "confirmed" && reviewStats.needsReview;
            const reviewSummary = [
              reviewStats.changedLineCount ? `${reviewStats.changedLineCount} changed line(s)` : null,
              reviewStats.unavailableLineCount ? `${reviewStats.unavailableLineCount} unavailable line(s)` : null,
              reviewStats.deliveryPartial ? "partial delivery" : null,
              reviewStats.deliveryUnavailable ? "delivery unavailable" : null,
            ]
              .filter(Boolean)
              .join(" / ");
            const isFeatured = featuredRequestIdSet.has(request.id);

            return (
              <article
                className={`supplier-request-row ${request.status}${isFeatured ? " is-fresh" : ""}`}
                key={request.id}
              >
                <div>
                  <span>{supplierRequestStatusLabel(request.status)}</span>
                  <strong>{request.supplierName}</strong>
                  <small>
                    {request.orderId} / {request.items.length} lines / {formatMoney(request.totalBam)} KM
                  </small>
                  <small>
                    {supplierOrderMessageStateLabel(request)}
                    {request.messageDeliveryChannel ? ` via ${supplierMessageChannelLabel(request.messageDeliveryChannel)}` : ""}
                    {request.messageDeliveryAt ? ` / ${new Date(request.messageDeliveryAt).toLocaleString()}` : ""}
                  </small>
                  {request.messageDeliveryNote ? <small>{request.messageDeliveryNote}</small> : null}
                  {request.followUpDueAt || request.followUpCount ? (
                    <small className={followUpDue ? "supplier-follow-up-due" : undefined}>
                      Follow-up {followUpDue ? "due now" : "due"}{followUpDueLabel ? `: ${followUpDueLabel}` : ""}
                      {request.followUpCount ? ` / ${request.followUpCount} sent` : ""}
                    </small>
                  ) : null}
                  {request.followUpMessage ? (
                    <div className="supplier-follow-up-message" aria-label={`Generated follow-up for ${request.supplierName}`}>
                      <span>Generated follow-up</span>
                      <p>{request.followUpMessage}</p>
                    </div>
                  ) : null}
                  {request.supplierDeliveryAvailability ? (
                    <small>
                      {supplierDeliveryAvailabilityLabel(request.supplierDeliveryAvailability)}
                      {request.supplierDeliveryWindow ? ` / ${request.supplierDeliveryWindow}` : ""}
                    </small>
                  ) : null}
                  {reviewStats.changedLineCount ? <small>{reviewStats.changedLineCount} changed line(s)</small> : null}
                  {reviewStats.unavailableLineCount ? <small>{reviewStats.unavailableLineCount} unavailable line(s)</small> : null}
                  {request.supplierNote ? <small>Supplier note: {request.supplierNote}</small> : null}
                  {!hasDirectShareTarget || preferredChannelUnavailable ? (
                    <div className="supplier-request-channel-alert" role="status">
                      <AlertTriangle size={16} />
                      <div>
                        <strong>Supplier channel missing.</strong>
                        <small>
                          {preferredChannelUnavailable && request.preferredChannel
                            ? `Preferred ${supplierMessageChannelLabel(request.preferredChannel)} needs contact details. `
                            : ""}
                          Add phone, Viber, WhatsApp, or email before sending. Copy remains available.
                        </small>
                      </div>
                    </div>
                  ) : null}
                  {isFeatured ? (
                    <div className="supplier-request-ready-banner" role="status">
                      <Send size={16} />
                      <div>
                        <strong>Send now</strong>
                        <small>Use WhatsApp, Viber, SMS, email, or copy. The link tracks confirmation.</small>
                      </div>
                    </div>
                  ) : null}
                  {needsRestaurantReview ? (
                    <div className="supplier-request-review-alert" role="status">
                      <AlertTriangle size={16} />
                      <div>
                        <strong>Review supplier changes before invoice/payment.</strong>
                        <small>{reviewSummary || "Supplier response needs restaurant review"}</small>
                      </div>
                    </div>
                  ) : null}
                  <p>{request.messageBody}</p>
                </div>
                <div className="supplier-request-actions" aria-label={`Share message for ${request.supplierName}`}>
                  {shareTargets.map((target) =>
                    target.available ? (
                      <a
                        className={target.channel === "whatsapp" ? "primary-channel" : undefined}
                        href={target.href}
                        key={target.channel}
                        target={target.channel === "whatsapp" ? "_blank" : undefined}
                        rel={target.channel === "whatsapp" ? "noreferrer" : undefined}
                        onClick={() => onShareMessage(request.id, target.channel)}
                      >
                        {target.label}
                      </a>
                    ) : (
                      <button
                        className="channel-unavailable"
                        disabled
                        key={target.channel}
                        title={target.reason}
                        type="button"
                      >
                        {target.label}
                      </button>
                    ),
                  )}
                  <button type="button" onClick={() => onShareSheet(request.id)}>
                    Share
                  </button>
                  <button type="button" onClick={() => onCopyMessage(request.id)}>
                    Copy
                  </button>
                  {request.followUpMessage || followUpDue ? (
                    <button type="button" onClick={() => onCopyFollowUp(request.id)}>
                      Copy follow-up
                    </button>
                  ) : null}
                  <a href={request.confirmationUrl} target="_blank" rel="noreferrer">
                    Open link
                  </a>
                  {needsRestaurantReview && onReviewRequest ? (
                    <button
                      className="supplier-request-review-action"
                      type="button"
                      onClick={() => onReviewRequest(request)}
                    >
                      {reviewStats.canAccept ? "Review / accept" : "Find backup"}
                    </button>
                  ) : null}
                </div>
                <div className="supplier-request-followups" aria-label={`Delivery follow-up for ${request.supplierName}`}>
                  <button
                    type="button"
                    onClick={() => onMarkMessageIssue(request.id, "needs_follow_up", "No supplier response yet")}
                  >
                    No response
                  </button>
                  <button
                    type="button"
                    onClick={() => onMarkMessageIssue(request.id, "failed", "Wrong supplier contact")}
                  >
                    Wrong contact
                  </button>
                  <button
                    type="button"
                    onClick={() => onMarkMessageIssue(request.id, "failed", "Unavailable channel")}
                  >
                    Bad channel
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="empty-panel-state">
          <Send size={20} />
          <div>
            <strong>No supplier messages yet.</strong>
            <span>Send today's buying list and VELEO will generate one confirmation link per supplier.</span>
          </div>
        </div>
      )}
    </Panel>
  );
}

function SupplierOrderConfirmationPage({
  isLoading = false,
  request,
  onBackToLanding,
  onClaimSupplierProfile,
  onRespond,
}: {
  isLoading?: boolean;
  request?: SupplierOrderRequest;
  onBackToLanding: () => void;
  onClaimSupplierProfile: (supplierName: string) => void;
  onRespond: (
    requestId: string,
    status: SupplierOrderRequestStatus,
    note: string,
    items: OrderItem[],
    deliveryAvailability: SupplierDeliveryAvailability,
    supplierDeliveryWindow: string,
    trustAction?: SupplierConfirmationTrustAction,
  ) => Promise<SupplierOrderResponseSaveResult> | SupplierOrderResponseSaveResult;
}) {
  const [items, setItems] = useState<OrderItem[]>(() => request?.items ?? []);
  const [deliveryAvailability, setDeliveryAvailability] = useState<SupplierDeliveryAvailability>(
    () => request?.supplierDeliveryAvailability ?? "available",
  );
  const [supplierDeliveryWindow, setSupplierDeliveryWindow] = useState(
    () => request?.supplierDeliveryWindow ?? request?.deliveryWindow ?? "",
  );
  const [note, setNote] = useState(request?.supplierNote ?? "");
  const [responseStatus, setResponseStatus] = useState<SupplierOrderRequestStatus | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);

  useEffect(() => {
    if (!request) {
      return;
    }

    setItems(request.items);
    setDeliveryAvailability(request.supplierDeliveryAvailability ?? "available");
    setSupplierDeliveryWindow(request.supplierDeliveryWindow ?? request.deliveryWindow ?? "");
    setNote(request.supplierNote ?? "");
    setResponseStatus(null);
    setResponseError(null);
  }, [request?.id, request?.publicToken]);

  if (isLoading && !request) {
    return (
      <main className="public-shell supplier-confirm-shell">
        <section className="supplier-confirm-card">
          <div className="public-brand">veleo</div>
          <span>Supplier confirmation</span>
          <h1>Opening the restaurant order.</h1>
          <p>VELEO is checking this supplier link and loading the customer request.</p>
        </section>
      </main>
    );
  }

  if (!request) {
    return (
      <main className="public-shell supplier-confirm-shell">
        <section className="supplier-confirm-card">
          <div className="public-brand">veleo</div>
          <span>Supplier confirmation</span>
          <h1>Order link not found.</h1>
          <p>The restaurant may need to resend the VELEO link or contact you directly.</p>
          <button className="public-primary" type="button" onClick={onBackToLanding}>
            Open VELEO
            <ChevronRight size={17} />
          </button>
        </section>
      </main>
    );
  }

  const activeRequest = request;
  const currentItems = items.length ? items : activeRequest.items;
  const total = currentItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const confirmed = responseStatus ?? activeRequest.status;
  const rejectedCount = currentItems.filter((item) => item.supplierResponse === "rejected").length;
  const changedCount = currentItems.filter((item, index) => {
    const original = activeRequest.items[index];
    return (
      item.supplierResponse === "changed" ||
      item.supplierResponse === "rejected" ||
      (original && (item.quantity !== original.quantity || item.unit !== original.unit || item.unitPrice !== original.unitPrice))
    );
  }).length;

  function updateItem(index: number, patch: Partial<OrderItem>) {
    setItems((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    );
  }

  function updateItemCommercialField(index: number, patch: Partial<OrderItem>) {
    updateItem(index, { ...patch, supplierResponse: "changed" });
  }

  function setLineResponse(index: number, supplierResponse: NonNullable<OrderItem["supplierResponse"]>) {
    const currentItem = currentItems[index];
    if (!currentItem) {
      return;
    }

    if (supplierResponse === "rejected") {
      updateItem(index, {
        originalQuantity: currentItem.originalQuantity ?? currentItem.quantity,
        originalUnitPrice: currentItem.originalUnitPrice ?? currentItem.unitPrice,
        quantity: 0,
        supplierLineNote: currentItem.supplierLineNote || "Unavailable from supplier",
        supplierResponse,
      });
      return;
    }

    updateItem(index, {
      quantity:
        supplierResponse === "available" && currentItem.quantity === 0 && currentItem.originalQuantity
          ? currentItem.originalQuantity
          : currentItem.quantity,
      unitPrice:
        supplierResponse === "available" && currentItem.unitPrice === 0 && currentItem.originalUnitPrice
          ? currentItem.originalUnitPrice
          : currentItem.unitPrice,
      supplierResponse,
    });
  }

  async function submitResponse(
    status: SupplierOrderRequestStatus,
    availabilityOverride?: SupplierDeliveryAvailability,
    noteOverride?: string,
    trustAction: SupplierConfirmationTrustAction = "response",
  ) {
    const selectedDeliveryAvailability = availabilityOverride ?? deliveryAvailability;
    const resolvedStatus: SupplierOrderRequestStatus =
      status === "confirmed" && (selectedDeliveryAvailability !== "available" || rejectedCount > 0 || changedCount > 0)
        ? "changes_requested"
        : status;
    const resolvedDeliveryAvailability: SupplierDeliveryAvailability =
      status === "declined" ? "unavailable" : selectedDeliveryAvailability;
    const cleanDeliveryWindow = supplierDeliveryWindow.trim() || activeRequest.deliveryWindow;

    setResponseError(null);

    const result = await onRespond(
      activeRequest.id,
      resolvedStatus,
      noteOverride ?? note,
      currentItems,
      resolvedDeliveryAvailability,
      cleanDeliveryWindow,
      trustAction,
    );

    if (!result.ok) {
      setResponseError(readableSaveError(result.error));
      return;
    }

    setResponseStatus(resolvedStatus);
  }

  return (
    <main className="public-shell supplier-confirm-shell">
      <section className="supplier-confirm-card">
        <nav className="supplier-confirm-nav" aria-label="Supplier confirmation">
          <div className="public-brand">veleo</div>
          <span>No account needed</span>
        </nav>
        <div className="supplier-confirm-hero">
          <span>Restaurant request</span>
          <h1>{activeRequest.restaurantName} is ordering from {activeRequest.supplierName}</h1>
          <p>
            Confirm the order first. Catalog, prices, delivery areas, and profile setup can come after this customer request.
          </p>
        </div>

        <div className="supplier-confirm-meta" aria-label="Order details">
          <p>
            <span>Order</span>
            <strong>{activeRequest.orderId}</strong>
          </p>
          <p>
            <span>Delivery</span>
            <strong>{activeRequest.deliveryWindow}</strong>
          </p>
          <p>
            <span>City / area</span>
            <strong>{activeRequest.market}</strong>
          </p>
          <p>
            <span>Status</span>
            <strong>{supplierRequestStatusLabel(confirmed)}</strong>
          </p>
        </div>

        <div className="supplier-confirm-items" aria-label="Order lines">
          <div className="supplier-confirm-section-head">
            <span>Customer order</span>
            <strong>Edit only changed lines.</strong>
          </div>
          {currentItems.map((item, index) => (
            <article className={item.supplierResponse === "rejected" ? "rejected" : ""} key={`${item.productId}-${index}`}>
              <div>
                <strong>{item.name}</strong>
                <span>{item.category ?? "Order item"}</span>
                {item.note ? <small>{item.note}</small> : null}
                {item.supplierResponse ? <small>{supplierLineResponseLabel(item.supplierResponse)}</small> : null}
                {item.originalQuantity && item.supplierResponse === "rejected" ? (
                  <small>Requested: {item.originalQuantity} {item.unit}</small>
                ) : null}
              </div>
              <div className="supplier-line-actions" role="group" aria-label={`Line availability for ${item.name}`}>
                {(["available", "changed", "rejected"] as Array<NonNullable<OrderItem["supplierResponse"]>>).map((option) => (
                  <button
                    className={(item.supplierResponse ?? "available") === option ? "active" : ""}
                    key={option}
                    type="button"
                    onClick={() => setLineResponse(index, option)}
                  >
                    {supplierLineResponseLabel(option)}
                  </button>
                ))}
              </div>
              <label>
                <span>Qty</span>
                <input
                  value={item.quantity}
                  inputMode="decimal"
                  onChange={(event) => updateItemCommercialField(index, { quantity: Number(event.target.value) || 0 })}
                />
              </label>
              <label>
                <span>Unit</span>
                <input value={item.unit} onChange={(event) => updateItemCommercialField(index, { unit: event.target.value })} />
              </label>
              <label>
                <span>KM/unit</span>
                <input
                  value={item.unitPrice}
                  inputMode="decimal"
                  onChange={(event) => updateItemCommercialField(index, { unitPrice: Number(event.target.value) || 0 })}
                />
              </label>
              <label>
                <span>Line note</span>
                <input
                  value={item.supplierLineNote ?? ""}
                  onChange={(event) => updateItem(index, { supplierLineNote: event.target.value })}
                  placeholder="Substitute, shortage, today's price..."
                />
              </label>
            </article>
          ))}
        </div>

        <label className="supplier-confirm-note">
          <span>Supplier note</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Example: We can deliver tomorrow at 09:00. Paradajz price is 2.60 KM/kg today."
          />
        </label>

        <div className="supplier-confirm-total">
          <span>
            Estimated total
            {changedCount || rejectedCount ? ` / ${changedCount} changed / ${rejectedCount} unavailable` : ""}
          </span>
          <strong>{formatMoney(total)} KM</strong>
        </div>

        <div className="supplier-confirm-delivery" aria-label="Delivery availability">
          <div>
            <span>Delivery availability</span>
            <strong>{supplierDeliveryAvailabilityLabel(deliveryAvailability)}</strong>
            <small>Mark whether you can deliver this request, a partial order, or nothing for this window.</small>
          </div>
          <div className="supplier-confirm-choice-row" role="group" aria-label="Set delivery availability">
            {(["available", "partial", "unavailable"] as SupplierDeliveryAvailability[]).map((option) => (
              <button
                className={deliveryAvailability === option ? "active" : ""}
                key={option}
                type="button"
                onClick={() => setDeliveryAvailability(option)}
              >
                {supplierDeliveryAvailabilityLabel(option)}
              </button>
            ))}
          </div>
          <label>
            <span>Confirmed delivery window</span>
            <input
              value={supplierDeliveryWindow}
              onChange={(event) => setSupplierDeliveryWindow(event.target.value)}
              placeholder="Example: Sutra 09:00-11:00"
            />
          </label>
        </div>

        <div className="supplier-confirm-data-note" aria-label="How VELEO uses this supplier response">
          <div>
            <span>Visible to</span>
            <strong>Restaurant + VELEO ops</strong>
            <p>Only for this order.</p>
          </div>
          <div>
            <span>Prices</span>
            <strong>Private deal stays private</strong>
            <p>Anonymous ranges only after thresholds.</p>
          </div>
          <div>
            <span>Control</span>
            <strong>Confirm first. Claim later.</strong>
            <p>Change, decline, opt out, or claim.</p>
          </div>
        </div>

        <div className="supplier-confirm-trust" aria-label="Supplier trust controls">
          <div>
            <span>Wrong link or unwanted contact?</span>
            <strong>You can stop this before claiming a profile.</strong>
            <p>
              Use these if the restaurant reached the wrong business, the contact is not yours, or you do not want VELEO
              to send future requests to this supplier contact.
            </p>
          </div>
          <div>
            <button
              className="public-secondary"
              type="button"
              onClick={() => void submitResponse(
                "issue",
                "unavailable",
                "Wrong contact: this order was sent to the wrong supplier or person.",
                "wrong_contact",
              )}
            >
              Wrong contact
            </button>
            <button
              className="public-secondary danger"
              type="button"
              onClick={() => void submitResponse(
                "declined",
                "unavailable",
                "Supplier opted out: do not send future VELEO messages to this contact unless they claim or opt in.",
                "opt_out",
              )}
            >
              Opt out
            </button>
          </div>
        </div>

        <div className="supplier-confirm-actions">
          <button className="public-primary" type="button" onClick={() => void submitResponse("confirmed")}>
            {changedCount || rejectedCount || deliveryAvailability !== "available" ? "Confirm with changes" : "Confirm order"}
            <CheckCircle2 size={17} />
          </button>
          <button className="public-secondary" type="button" onClick={() => void submitResponse("changes_requested")}>
            Send changes
          </button>
          <button
            className="public-secondary danger"
            type="button"
            onClick={() => {
              setDeliveryAvailability("unavailable");
              void submitResponse("declined", "unavailable");
            }}
          >
            Cannot deliver
          </button>
        </div>

        {responseError ? (
          <div className="supplier-confirm-error" role="alert">
            <AlertTriangle size={17} />
            <span>
              Response not saved: {responseError}. Ask the restaurant to resend the link or contact VELEO before treating
              this order as confirmed.
            </span>
          </div>
        ) : null}

        {responseStatus ? (
          <>
            <p className="supplier-confirm-state">
              Response saved: {supplierRequestStatusLabel(responseStatus)}. The restaurant can see this status in VELEO.
            </p>
            <div className="supplier-confirm-claim" aria-label="Claim supplier profile after confirming order">
              <span>Optional next step</span>
              <strong>Claim supplier profile for {activeRequest.supplierName}</strong>
              <p>
                Keep future restaurant requests, delivery terms, price lists, and invoice follow-up under your supplier workspace.
                You can claim now and add catalog details later.
              </p>
              <button className="public-secondary" type="button" onClick={() => onClaimSupplierProfile(activeRequest.supplierName)}>
                Claim supplier profile
                <ChevronRight size={17} />
              </button>
            </div>
          </>
        ) : null}
      </section>
    </main>
  );
}

const LOGIN_COPY = {
  bs: {
    backToOverview: "Nazad na početnu",
    eyebrow: "Pristup radnom prostoru",
    h1SignIn: "Uđite u Veleo.",
    h1SignUp: "Počnite uredno.",
    sub: "Narudžbe, ponude, problemi s dostavom i računi — jedna radna ruta.",
    accessSteps: ["Uvezite svoj spisak", "Naručite ili tražite ponudu", "Uparite račun"],
    roleRestaurant: { title: "Restoran", text: "Dodajte dobavljače. Šaljite uredne narudžbe.", checks: ["Kontakt", "Narudžba", "Potvrda"] },
    roleSupplier: { title: "Dobavljač", text: "Prvo potvrdite potražnju. Postavka kasnije.", checks: ["Upit", "Izmjena", "Preuzmi kasnije"] },
    panelSignInSpan: "Privatni prostor",
    panelSignInStrong: "Prijava",
    panelSignUpSpan: "Novi prostor",
    panelSignUpStrong: "Otvori pristup",
    tabSignIn: "Prijava",
    tabSignUp: "Napravi račun",
    businessName: "Naziv firme",
    businessPlaceholderRestaurant: "Restoran Hot Kola",
    businessPlaceholderSupplier: "Dino mljekara",
    workEmail: "Poslovni e-mail",
    workEmailPlaceholder: "vi@firma.ba",
    methodPassword: "Lozinka",
    methodMagic: "Link na e-mail",
    passwordLabel: "Lozinka",
    passwordPlaceholder: "Najmanje 8 znakova",
    magicHint: "Poslat ćemo vam jednokratni link na e-mail. Bez lozinke.",
    working: "Trenutak...",
    submitSignUpMagic: "Pošalji mi link za registraciju",
    submitSignUpPassword: "Napravi radni prostor",
    submitSignInMagic: "Pošalji mi link za prijavu",
    submitSignInPassword: "Prijava",
    notActive: "Prijava u radni prostor još nije aktivna na ovom okruženju.",
    trust1: "Podaci vaše firme ostaju unutar vašeg prostora.",
    trust2: "Prostori restorana i dobavljača ostaju odvojeni.",
    msgNotOpen: "Pristup nalogu još nije otvoren. Zatražite od Veleo tima da aktivira vaš prostor.",
    msgInvalidEmail: "Unesite ispravan poslovni e-mail.",
    msgNoBusiness: "Unesite naziv firme.",
    msgWeakPassword: "Koristite lozinku s najmanje 8 znakova.",
    msgMagicSignupSent: "Provjerite e-mail — poslali smo link da završite kreiranje prostora.",
    msgConfirmEmail: "Nalog je kreiran. Potvrdite e-mail, pa se prijavite da otvorite svoj prostor.",
    msgMagicSigninSent: "Provjerite e-mail za link za prijavu.",
  },
  en: {
    backToOverview: "Back to overview",
    eyebrow: "Workspace access",
    h1SignIn: "Enter Veleo.",
    h1SignUp: "Start clean.",
    sub: "Orders, quotes, delivery issues, and invoices — one working route.",
    accessSteps: ["Import your list", "Order or get quotes", "Match the invoice"],
    roleRestaurant: { title: "Restaurant", text: "Add suppliers. Send clean orders.", checks: ["Contact", "Order", "Confirmation"] },
    roleSupplier: { title: "Supplier", text: "Confirm demand first. Setup later.", checks: ["Request", "Edit", "Claim later"] },
    panelSignInSpan: "Private workspace",
    panelSignInStrong: "Sign in",
    panelSignUpSpan: "New workspace",
    panelSignUpStrong: "Create access",
    tabSignIn: "Sign in",
    tabSignUp: "Create account",
    businessName: "Business name",
    businessPlaceholderRestaurant: "Restaurant Hot Kola",
    businessPlaceholderSupplier: "Dino Dairy Supply",
    workEmail: "Work email",
    workEmailPlaceholder: "you@company.com",
    methodPassword: "Password",
    methodMagic: "Email link",
    passwordLabel: "Password",
    passwordPlaceholder: "At least 8 characters",
    magicHint: "We'll email you a one-time link. No password needed.",
    working: "Working...",
    submitSignUpMagic: "Email me a signup link",
    submitSignUpPassword: "Create workspace",
    submitSignInMagic: "Email me a sign-in link",
    submitSignInPassword: "Sign in",
    notActive: "Workspace sign-in is not active on this deployment yet.",
    trust1: "Your company data stays inside your workspace.",
    trust2: "Restaurant and supplier spaces stay separate.",
    msgNotOpen: "Account access is not open yet. Ask the Veleo team to activate your workspace.",
    msgInvalidEmail: "Enter a valid work email.",
    msgNoBusiness: "Enter your business name.",
    msgWeakPassword: "Use a password with at least 8 characters.",
    msgMagicSignupSent: "Check your email for a link to finish creating your workspace.",
    msgConfirmEmail: "Account created. Confirm your email, then sign in to open your workspace.",
    msgMagicSigninSent: "Check your email for a sign-in link.",
  },
};

function WorkspaceLogin({
  lang,
  setLang,
  onBack,
  onAuthenticated,
  onOpenLocalWorkspace,
  authConfigured,
  notice,
}: {
  lang: "bs" | "en";
  setLang: (lang: "bs" | "en") => void;
  onBack: () => void;
  onAuthenticated: () => void;
  onOpenLocalWorkspace: (role: Role) => void;
  authConfigured: boolean;
  notice: string | null;
}) {
  const t = LOGIN_COPY[lang];
  const pendingSignupDraft = readStoredJson<{ businessName?: string; role?: "restaurant" | "supplier" } | null>(
    PENDING_SIGNUP_KEY,
    null,
  );
  const [mode, setMode] = useState<"signin" | "signup">(() => (pendingSignupDraft ? "signup" : "signin"));
  const [method, setMethod] = useState<"password" | "magic">("password");
  const [selectedRole, setSelectedRole] = useState<"restaurant" | "supplier">(
    () => pendingSignupDraft?.role ?? "restaurant",
  );
  const [businessName, setBusinessName] = useState(
    () => pendingSignupDraft?.businessName ?? storageGet(ACCOUNT_BUSINESS_NAME_KEY) ?? "",
  );
  const [email, setEmail] = useState(() => storageGet(ACCOUNT_EMAIL_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(notice);

  const accessSteps = t.accessSteps;

  const roleCards: Array<{
    role: "restaurant" | "supplier";
    icon: React.ElementType;
    title: string;
    text: string;
    checks: string[];
  }> = [
    { role: "restaurant", icon: Store, ...t.roleRestaurant },
    { role: "supplier", icon: Factory, ...t.roleSupplier },
  ];

  function persistContact(cleanEmail: string) {
    storageSet(ACCOUNT_EMAIL_KEY, cleanEmail);
    if (businessName.trim()) {
      storageSet(ACCOUNT_BUSINESS_NAME_KEY, businessName.trim());
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) {
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    const cleanBusiness = businessName.trim();

    if (!authConfigured) {
      setStatus(t.msgNotOpen);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setStatus(t.msgInvalidEmail);
      return;
    }
    if (mode === "signup" && !cleanBusiness) {
      setStatus(t.msgNoBusiness);
      return;
    }
    if (method === "password" && password.length < 8) {
      setStatus(t.msgWeakPassword);
      return;
    }

    setBusy(true);
    setStatus(null);
    persistContact(cleanEmail);

    try {
      if (mode === "signup") {
        // Remember the choice so the workspace can be created once a session
        // exists (password signup with confirmation, or magic link, defer it).
        storageSet(
          PENDING_SIGNUP_KEY,
          JSON.stringify({ role: selectedRole, businessName: cleanBusiness }),
        );

        if (method === "magic") {
          const sent = await signInWithMagicLink(cleanEmail);
          setStatus(sent.ok ? t.msgMagicSignupSent : authMessage(sent.error, lang));
          return;
        }

        const created = await signUpWithPassword(cleanEmail, password);
        if (!created.ok) {
          setStatus(authMessage(created.error, lang));
          return;
        }
        if (created.needsConfirmation || !created.session) {
          setStatus(t.msgConfirmEmail);
          return;
        }

        const org = await bootstrapAccount({
          orgName: cleanBusiness,
          orgType: selectedRole,
          email: cleanEmail,
        });
        if (!org.ok) {
          setStatus(authMessage(org.error, lang));
          return;
        }
        storageRemove(PENDING_SIGNUP_KEY);
        onAuthenticated();
        return;
      }

      if (method === "magic") {
        const sent = await signInWithMagicLink(cleanEmail);
        setStatus(sent.ok ? t.msgMagicSigninSent : authMessage(sent.error, lang));
        return;
      }

      const result = await signInWithPassword(cleanEmail, password);
      if (!result.ok) {
        setStatus(authMessage(result.error, lang));
        return;
      }
      onAuthenticated();
    } finally {
      setBusy(false);
    }
  }

  const submitLabel =
    mode === "signup"
      ? method === "magic"
        ? t.submitSignUpMagic
        : t.submitSignUpPassword
      : method === "magic"
        ? t.submitSignInMagic
        : t.submitSignInPassword;

  return (
    <main className="public-shell login-shell">
      <nav className="public-nav" aria-label="Public navigation">
        <button className="public-back" type="button" onClick={onBack}>
          Veleo
        </button>
        <div className="public-nav-actions">
          <div className="public-lang-toggle" role="group" aria-label="Jezik / Language">
            <button
              type="button"
              className={lang === "bs" ? "active" : ""}
              aria-pressed={lang === "bs"}
              onClick={() => setLang("bs")}
            >
              BS
            </button>
            <button
              type="button"
              className={lang === "en" ? "active" : ""}
              aria-pressed={lang === "en"}
              onClick={() => setLang("en")}
            >
              EN
            </button>
          </div>
          <button className="public-login" type="button" onClick={onBack}>
            {t.backToOverview}
          </button>
        </div>
      </nav>

      <section className="login-screen" aria-label="Veleo login">
        <div className="login-screen-copy">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1>{mode === "signup" ? t.h1SignUp : t.h1SignIn}</h1>
          <p>{t.sub}</p>
          <div className="login-route-card" aria-label="Veleo workspace route">
            {accessSteps.map((step, index) => (
              <span key={step}>
                <b>{index + 1}</b>
                {step}
              </span>
            ))}
          </div>
        </div>

        <form className="login-panel" onSubmit={handleSubmit}>
          <div className="login-panel-head">
            <span>{mode === "signup" ? t.panelSignUpSpan : t.panelSignInSpan}</span>
            <strong>{mode === "signup" ? t.panelSignUpStrong : t.panelSignInStrong}</strong>
          </div>

          <div className="auth-tabs" role="tablist" aria-label="Sign in or create account">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signin"}
              className={mode === "signin" ? "active" : ""}
              onClick={() => {
                setMode("signin");
                setStatus(null);
              }}
            >
              {t.tabSignIn}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signup"}
              className={mode === "signup" ? "active" : ""}
              onClick={() => {
                setMode("signup");
                setStatus(null);
              }}
            >
              {t.tabSignUp}
            </button>
          </div>

          {mode === "signup" ? (
            <>
              <div className="login-role-grid compact">
                {roleCards.map((card) => {
                  const Icon = card.icon;
                  const active = selectedRole === card.role;

                  return (
                    <button
                      className={active ? "active" : ""}
                      key={card.role}
                      type="button"
                      onClick={() => setSelectedRole(card.role)}
                    >
                      <Icon size={22} />
                      <strong>{card.title}</strong>
                      <span>{card.text}</span>
                      <small>{card.checks.join(" · ")}</small>
                    </button>
                  );
                })}
              </div>
              <label className="login-field">
                <span>{t.businessName}</span>
                <input
                  value={businessName}
                  onChange={(event) => setBusinessName(event.target.value)}
                  placeholder={selectedRole === "supplier" ? t.businessPlaceholderSupplier : t.businessPlaceholderRestaurant}
                />
              </label>
            </>
          ) : null}

          <label className="login-field">
            <span>{t.workEmail}</span>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              inputMode="email"
              autoComplete="email"
              placeholder={t.workEmailPlaceholder}
            />
          </label>

          <div className="auth-method" role="group" aria-label="Sign-in method">
            <button
              type="button"
              aria-pressed={method === "password"}
              className={method === "password" ? "active" : ""}
              onClick={() => setMethod("password")}
            >
              {t.methodPassword}
            </button>
            <button
              type="button"
              aria-pressed={method === "magic"}
              className={method === "magic" ? "active" : ""}
              onClick={() => setMethod("magic")}
            >
              {t.methodMagic}
            </button>
          </div>

          {method === "password" ? (
            <label className="login-field">
              <span>{t.passwordLabel}</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                placeholder={t.passwordPlaceholder}
              />
            </label>
          ) : (
            <p className="auth-hint">{t.magicHint}</p>
          )}

          <button className="public-primary login-submit" type="submit" disabled={busy}>
            {busy ? t.working : submitLabel}
            <ChevronRight size={17} />
          </button>

          {status ? <p className="login-state">{status}</p> : null}

          {!authConfigured ? (
            <>
              {ALLOW_LOCAL_WORKSPACE_FALLBACK && !authConfigured ? (
                <div className="local-proof-panel" aria-label="Local development proof mode">
                  <span>Local proof mode</span>
                  <div>
                    <button type="button" onClick={() => onOpenLocalWorkspace("restaurant")}>
                      Open restaurant loop
                    </button>
                    <button type="button" onClick={() => onOpenLocalWorkspace("supplier")}>
                      Open supplier desk
                    </button>
                    <button type="button" onClick={() => onOpenLocalWorkspace("admin")}>
                      Open VELEO ops
                    </button>
                  </div>
                  <p>No production access. Local browser only.</p>
                </div>
              ) : null}
              <p className="login-state">
                {t.notActive}
              </p>
            </>
          ) : null}

          <div className="workspace-security-list" aria-label="Workspace access controls">
            <p>
              <ShieldCheck size={15} />
              <span>{t.trust1}</span>
            </p>
            <p>
              <LockKeyhole size={15} />
              <span>{t.trust2}</span>
            </p>
          </div>
        </form>
      </section>
    </main>
  );
}

function workspaceForNav(activeNav: string, role: Role) {
  const copy: Record<string, { kicker: string; title: string; description: string }> = {
    Dashboard: {
      kicker: "Today",
      title:
        role === "restaurant"
          ? "Today"
          : role === "admin"
            ? "Network operations"
            : "Today",
      description:
        role === "restaurant"
          ? "Buy, compare, fix."
          : role === "supplier"
            ? "Quote, confirm, invoice."
            : "Work that needs an answer.",
    },
    Marketplace: {
      kicker: role === "restaurant" ? "Buy" : "Find price",
      title: role === "restaurant" ? "Buy items or ask for price" : "Find suppliers",
      description:
        role === "restaurant"
          ? "Build today's list from live supplier terms."
          : "Price, stock, delivery.",
    },
    Orders: {
      kicker: "Orders",
      title: role === "supplier" ? "Orders to confirm" : role === "restaurant" ? "Orders and issues" : "Order workbench",
      description:
        role === "supplier"
          ? "Confirm, dispatch, invoice."
          : role === "restaurant"
            ? "Track fulfillment, chase suppliers, and clear delivery problems."
            : "Orders and approvals.",
    },
    Invoices: {
      kicker: "Invoices",
      title: role === "supplier" ? "Invoices to send and clear" : "Invoices to match",
      description:
        role === "supplier"
          ? "Send invoices, clear blockers, and keep payment handoff moving."
          : "Match supplier invoices, flag wrong prices, and mark bank-transfer handoff.",
    },
    Suppliers: {
      kicker: role === "supplier" ? "Products" : "Suppliers",
      title: role === "supplier" ? "Products and stock" : "Suppliers",
      description:
        role === "supplier"
          ? "Products, prices, stock."
          : "Availability and replies.",
    },
    "Supplier Finder": {
      kicker:
        role === "admin"
          ? "Coverage"
          : role === "supplier"
            ? "Restaurant leads"
            : "Suppliers",
      title:
        role === "admin"
          ? "Coverage review"
          : role === "supplier"
            ? "Restaurants looking now"
            : "Find backup suppliers",
      description:
        role === "admin"
          ? "Coverage and conversion."
          : role === "supplier"
            ? "Leads you can price."
            : "Compare suppliers when your usual option cannot cover today.",
    },
    "Network Intel": {
      kicker: "Market health",
      title: "Market health",
      description: "Coverage gaps.",
    },
    Messages: {
      kicker: "Inbox",
      title: role === "restaurant" ? "Supplier messages" : role === "supplier" ? "Restaurant messages" : "Messages",
      description:
        role === "restaurant"
          ? "Supplier replies."
          : role === "supplier"
            ? "Restaurant replies."
            : "Replies and follow-up.",
    },
    Analytics: {
      kicker: role === "restaurant" ? "Money" : role === "supplier" ? "Sales" : "Analytics",
      title:
        role === "admin"
          ? "Network analytics"
          : role === "supplier"
            ? "Sales and prices"
            : "Spending and prices",
      description:
        role === "admin"
          ? "Coverage and risk."
          : role === "supplier"
            ? "Sales and readiness."
            : "Spend and prices.",
    },
    Settings: {
      kicker: "Setup",
      title: role === "restaurant" ? "Restaurant setup" : role === "supplier" ? "Supplier setup" : "Network setup",
      description:
        role === "restaurant"
          ? "Imports and access."
          : role === "supplier"
            ? "Catalog and delivery."
            : "Setup and controls.",
    },
  };

  return copy[activeNav] || copy.Dashboard;
}

function GlobalSearchPanel({
  query,
  results,
  onSelect,
  onTrackDemand,
}: {
  query: string;
  results: GlobalSearchResult[];
  onSelect: (result: GlobalSearchResult) => void;
  onTrackDemand?: () => void;
}) {
  const activeQuery = query.trim();

  if (activeQuery.length < 2) {
    return null;
  }

  return (
    <section className="global-search-panel" aria-label="Global search results">
      <div className="global-search-head">
        <div>
          <strong>Search results</strong>
          <span>{results.length ? `${results.length} matches for "${activeQuery}"` : `No matches for "${activeQuery}"`}</span>
        </div>
        <small>Products, suppliers, orders, messages, and network records</small>
      </div>

      {results.length ? (
        <div className="global-search-list">
          {results.map((result) => {
            const Icon = result.icon;

            return (
              <button key={result.id} type="button" onClick={() => onSelect(result)}>
                <Icon size={17} />
                <div>
                  <small>{result.eyebrow}</small>
                  <strong>{result.label}</strong>
                  <span>{result.detail}</span>
                </div>
                <div className="global-search-result-meta">
                  <b>{result.meta}</b>
                  <span>{globalSearchActionFor(result)}</span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="global-search-empty">
          <Search size={18} />
          <span>
            {onTrackDemand
              ? `No supplier or order matched "${activeQuery}". Save it so suppliers can be found or asked for a price.`
              : "Try a product, supplier, city, order ID, category, or status."}
          </span>
          {onTrackDemand ? (
            <button className="global-search-empty-action" type="button" onClick={onTrackDemand}>
              Save this need
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}

function globalSearchActionFor(result: GlobalSearchResult) {
  if (result.eyebrow === "Product" || result.eyebrow === "External option") {
    return result.eyebrow === "External option" ? "Ask supplier" : "Compare terms";
  }

  if (result.eyebrow === "Order") {
    return "Review order";
  }

  if (result.eyebrow === "Catalog") {
    return "Update SKU";
  }

  if (result.eyebrow === "Fulfillment") {
    return "Move order";
  }

  if (result.eyebrow === "Demand signal") {
    return "I can supply this";
  }

  if (result.eyebrow === "Incident") {
    return "Triage";
  }

  if (result.eyebrow === "Coverage") {
    return "Open sprint";
  }

  if (result.eyebrow === "Onboarding") {
    return "Convert supplier";
  }

  if (result.eyebrow === "External record") {
    return "Build message";
  }

  if (result.eyebrow === "Backup supplier") {
    return "Ask supplier";
  }

  if (result.eyebrow === "Message") {
    return "Open thread";
  }

  if (result.eyebrow === "Quote request") {
    return result.nav === "Orders" ? "Send price" : "Review price";
  }

  if (result.eyebrow === "Supplier") {
    return result.nav === "Marketplace" ? "Compare supplier" : "Open supplier";
  }

  return "Open";
}

function NotificationCenter({
  notifications,
  onAction,
  onSelect,
}: {
  notifications: OperationalNotification[];
  onAction: (notification: OperationalNotification) => void;
  onSelect: (notification: OperationalNotification) => void;
}) {
  return (
    <section className="notification-panel" aria-label="Operational notifications">
      <div className="notification-head">
        <div>
          <strong>Operations center</strong>
          <span>{notifications.length} live signal{notifications.length === 1 ? "" : "s"}</span>
        </div>
        <small>Role-aware</small>
      </div>

      <div className="notification-list">
        {notifications.length ? (
          notifications.map((notification) => {
            const Icon = notification.icon;

            return (
              <article
                className={`notification-row ${notification.severity}`}
                key={notification.id}
              >
                <button className="notification-open" type="button" onClick={() => onSelect(notification)}>
                  <span>
                    <Icon size={16} />
                  </span>
                  <div>
                    <strong>{notification.title}</strong>
                    <small>{notification.detail}</small>
                  </div>
                  <b>{notification.meta}</b>
                </button>
                <button className="notification-action" type="button" onClick={() => onAction(notification)}>
                  {notification.actionLabel ?? "Open"}
                  <ChevronRight size={14} />
                </button>
              </article>
            );
          })
        ) : (
          <div className="notification-empty">
            <CheckCircle2 size={18} />
          <span>No urgent work right now.</span>
          </div>
        )}
      </div>
    </section>
  );
}

function CommandStrip({
  activeNav,
  demandGapCount,
  draftTotal,
  externalRecordCount,
  fulfillmentCount,
  incidentCount,
  inquiryReady,
  issueCount,
  lowStockCount,
  messageCount,
  networkStats,
  permissionRole,
  quoteRequestCount,
  invoiceCount,
  offerCount,
  role,
  runCount,
  onNavigate,
}: {
  activeNav: string;
  demandGapCount: number;
  draftTotal: number;
  externalRecordCount: number;
  fulfillmentCount: number;
  incidentCount: number;
  inquiryReady: boolean;
  issueCount: number;
  lowStockCount: number;
  messageCount: number;
  networkStats: NetworkStats;
  permissionRole: PermissionRole;
  quoteRequestCount: number;
  invoiceCount: number;
  offerCount: number;
  role: Role;
  runCount: number;
  onNavigate: (nav: string) => void;
}) {
  const alertCount = issueCount + lowStockCount;
  type CommandCard = {
    label: string;
    value: string;
    help: string;
    action: string;
    nav: string;
    tone?: "critical" | "warning" | "ready";
  };
  const cards: CommandCard[] =
    activeNav === "Supplier Finder" && role === "restaurant"
      ? [
          {
            label: "Supplier choices",
            value: `${externalRecordCount}`,
            help: "Usual suppliers plus backup options you can ask for price.",
            action: "Compare supply",
            nav: "Supplier Finder",
          },
          {
            label: "Supplier message",
            value: inquiryReady ? "Ready" : "Choose supplier",
            help: "Choose a backup supplier and Veleo prepares the message.",
            action: "Prepare message",
            nav: "Supplier Finder",
            tone: inquiryReady ? "ready" : "warning",
          },
          {
            label: "Open alerts",
            value: `${alertCount}`,
            help: "Current stock or order issues that may need backup supply.",
            action: alertCount ? "Resolve alerts" : "Check orders",
            nav: issueCount ? "Orders" : "Marketplace",
            tone: alertCount ? "warning" : "ready",
          },
          {
            label: "Next step",
            value: "Ask supplier",
            help: "Pick a supplier and generate the request.",
            action: "Open finder",
            nav: "Supplier Finder",
          },
        ]
      : activeNav === "Supplier Finder" && role === "supplier"
        ? [
            {
              label: "Restaurant leads",
              value: `${demandGapCount} leads`,
              help: "Categories where restaurants could not find enough suppliers.",
              action: "Review leads",
              nav: "Supplier Finder",
              tone: demandGapCount ? "warning" : "ready",
            },
            {
              label: "Price requests",
              value: `${quoteRequestCount}`,
              help: "Requests currently waiting for supplier response.",
              action: quoteRequestCount ? "Send prices" : "Requests clear",
              nav: "Orders",
              tone: quoteRequestCount ? "warning" : "ready",
            },
            {
              label: "Can supply",
              value: "Add availability",
              help: "Tell restaurants which requests your products can serve.",
              action: "I can supply this",
              nav: "Supplier Finder",
            },
            {
              label: "Next step",
              value: "Pick category",
              help: "Start with the category you can deliver fastest.",
              action: "Open leads",
              nav: "Supplier Finder",
            },
          ]
        : activeNav === "Supplier Finder"
          ? [
              {
                label: "Records to review",
                value: `${externalRecordCount}`,
                help: "Supplier records waiting for verification.",
                action: "Review records",
                nav: "Supplier Finder",
                tone: externalRecordCount ? "warning" : "ready",
              },
              {
                label: "Demand gaps",
                value: `${demandGapCount}`,
                help: "Markets and categories with restaurant search failures.",
                action: "Open gaps",
                nav: "Network Intel",
                tone: demandGapCount ? "warning" : "ready",
              },
              {
                label: "Source runs",
                value: `${runCount}`,
                help: "Discovery runs already logged.",
                action: "Check runs",
                nav: "Supplier Finder",
              },
              {
                label: "Next step",
                value: "Verify record",
                help: "Move useful records toward invite or conversion.",
                action: "Open review",
                nav: "Supplier Finder",
              },
            ]
          : role === "supplier"
            ? [
                {
                  label: "Price requests",
                  value: `${quoteRequestCount}`,
                  help: "Restaurant price requests waiting on supplier response.",
                  action: quoteRequestCount ? "Send prices" : "Requests clear",
                  nav: "Orders",
                  tone: quoteRequestCount ? "warning" : "ready",
                },
                {
                  label: "Fulfillment",
                  value: `${fulfillmentCount}`,
                  help: "Orders that need confirmation, packing, dispatch, or tracking.",
                  action: "Move orders",
                  nav: "Orders",
                  tone: fulfillmentCount ? "critical" : "ready",
                },
                {
                  label: "Products to fix",
                  value: `${lowStockCount}`,
                  help: "Products with low or unavailable stock status.",
                  action: "Fix products",
                  nav: "Suppliers",
                  tone: lowStockCount ? "warning" : "ready",
                },
                {
                  label: "Inbox",
                  value: `${messageCount}`,
                  help: "Restaurant messages, substitutions, and demand coverage threads.",
                  action: "Open inbox",
                  nav: "Messages",
                },
              ]
            : role === "admin"
              ? [
                  {
                    label: "Customers",
                    value: formatCount(networkStats.customers),
                    help: "Active buyer organizations across the network.",
                    action: "View analytics",
                    nav: "Analytics",
                  },
              {
                label: "Supplier records",
                value: `${externalRecordCount}`,
                help: "Supplier records needing verification or conversion.",
                action: "Review supply",
                nav: "Supplier Finder",
                tone: externalRecordCount ? "warning" : "ready",
                  },
                  {
                    label: "Incidents",
                    value: `${incidentCount}`,
                    help: "Open market, supply, or SLA issues.",
                    action: "Open incidents",
                    nav: "Network Intel",
                    tone: incidentCount ? "critical" : "ready",
                  },
                  {
                    label: "Source runs",
                    value: `${runCount}`,
                    help: "Discovery runs already logged.",
                    action: "Check discovery",
                    nav: "Supplier Finder",
                  },
                ]
              : [
                  {
                    label: "Today's list",
                    value: `${formatMoney(draftTotal)} KM`,
                    help: "Current buying list value. Add items, adjust quantities, then send when ready.",
                    action: "Open list",
                    nav: "Orders",
                    tone: draftTotal ? "ready" : undefined,
                  },
                  {
                    label: "Better options",
                    value: `${offerCount}`,
                    help: "Private prices and supplier offers ranked by price, delivery, minimum order, reliability, and usual-supplier preference.",
                    action: offerCount ? "Compare" : "Find price",
                    nav: "Marketplace",
                    tone: offerCount ? "ready" : undefined,
                  },
                  {
                    label: "Invoices",
                    value: `${invoiceCount}`,
                    help: "Supplier invoices waiting to be matched, disputed, exported, or marked paid through existing bank rails.",
                    action: invoiceCount ? "Review" : "Setup",
                    nav: "Orders",
                  },
                  {
                    label: "Inbox",
                    value: `${messageCount + alertCount + quoteRequestCount}`,
                    help: "Supplier replies, quote questions, price changes, delivery issues, and low-stock alerts.",
                    action: "Open inbox",
                    nav: "Messages",
                    tone: alertCount ? "warning" : "ready",
                  },
                ];

  return (
    <section className="command-strip" aria-label="Workspace summary">
      {cards.map((card) => (
        <button
          className={card.tone ? `command-card ${card.tone}` : "command-card"}
          key={card.label}
          type="button"
          onClick={() => onNavigate(card.nav)}
        >
          <span>
            {card.label}
            <Tooltip label={card.help} />
          </span>
          <strong>{card.value}</strong>
          <em>
            {card.action}
            <ChevronRight size={13} />
          </em>
        </button>
      ))}
    </section>
  );
}

function VeleoOperatorCockpit({
  branches,
  bosniaCoverageTargets,
  connectorRequests,
  coverageRecords,
  demandGaps,
  discrepancies,
  doneForYouSetupRequests,
  externalRecords,
  fulfillmentOrders,
  importRuns,
  invoices,
  messageThreads,
  orders,
  restaurantSupplierContacts,
  supplierOrderRequests,
  onAdvanceDoneForYouSetup,
  onAdvanceExternalRecord,
  onAdvanceFulfillment,
  onConvertDoneForYouSetup,
  onNavigate,
  onOpenCoverageSprint,
  onResolveInvoiceBlocker,
  onSendMessageFollowUp,
}: {
  branches: BranchAccount[];
  bosniaCoverageTargets: BosniaCoverageTarget[];
  connectorRequests: ConnectorRequestRecord[];
  coverageRecords: MarketCoverageRecord[];
  demandGaps: DemandGap[];
  discrepancies: DiscrepancyRecord[];
  doneForYouSetupRequests: DoneForYouSetupRequest[];
  externalRecords: ExternalSupplierRecord[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  importRuns: ImportRunRecord[];
  invoices: InvoiceRecord[];
  messageThreads: MessageThread[];
  orders: Order[];
  restaurantSupplierContacts: RestaurantSupplierContact[];
  supplierOrderRequests: SupplierOrderRequest[];
  onAdvanceDoneForYouSetup: (request: DoneForYouSetupRequest) => void;
  onAdvanceExternalRecord: (record: ExternalSupplierRecord) => void;
  onAdvanceFulfillment: (orderId: string) => void;
  onConvertDoneForYouSetup: (request: DoneForYouSetupRequest) => void;
  onNavigate: (label: string) => void;
  onOpenCoverageSprint: (record: MarketCoverageRecord) => void;
  onResolveInvoiceBlocker: (invoiceId: string) => void;
  onSendMessageFollowUp: (threadId: string) => void;
}) {
  const restaurantNames = Array.from(
    new Set(
      [
        ...branches.map((branch) => branch.name),
        ...orders.map((order) => order.buyer),
        ...restaurantSupplierContacts.map((contact) => contact.branchName),
        ...supplierOrderRequests.map((request) => request.restaurantName),
        ...invoices.map((invoice) => invoice.buyer),
      ].filter(Boolean),
    ),
  );
  const setupRows: RestaurantSetupRow[] = restaurantNames.map((name) => {
    const supplierCount = restaurantSupplierContacts.filter((contact) => contact.branchName === name).length;
    const messageCount = supplierOrderRequests.filter((request) => request.restaurantName === name).length;
    const invoiceCount = invoices.filter((invoice) => invoice.buyer === name).length;
    const status: RestaurantSetupRow["status"] = !supplierCount ? "critical" : !messageCount ? "watch" : "ready";
    const nextStep = !supplierCount
      ? "Collect supplier contacts by WhatsApp/Viber"
      : !messageCount
        ? "Paste first usual order and generate supplier message"
        : invoiceCount
          ? "Check invoices against confirmed orders"
          : "Attach first invoice after delivery";

    return {
      id: `setup-${slugForNav(name)}`,
      name,
      supplierCount,
      messageCount,
      invoiceCount,
      status,
      nextStep,
    };
  }).sort((left, right) => {
    const rank = { critical: 0, watch: 1, ready: 2 };
    return rank[left.status] - rank[right.status] || right.supplierCount + right.messageCount - (left.supplierCount + left.messageCount);
  });
  const activatedRestaurants = setupRows.filter((row) => row.status === "ready").length;
  const missingContacts = restaurantSupplierContacts.filter(
    (contact) => !contact.contact || contact.preferredChannel === "copy",
  );
  const contactTrustRepairs = restaurantSupplierContacts.filter(
    (contact) => contact.relationshipStatus !== "active",
  );
  const waitingRequests = supplierOrderRequests.filter((request) => request.status === "sent");
  const overdueSupplierFollowUps = waitingRequests.filter((request) => supplierFollowUpIsDue(request));
  const supplierRepliesNeedingReview = supplierOrderRequests.filter(
    (request) => request.status === "changes_requested" || request.status === "declined" || request.status === "issue",
  );
  const unresolvedMessages = messageThreads.filter((thread) => thread.status !== "resolved");
  const openOrderIssues = [
    ...fulfillmentOrders.filter((order) => order.status === "issue" || order.status === "needs_confirmation"),
  ];
  const invoiceProblems = invoices.filter((invoice) => invoice.status === "blocked");
  const openDiscrepancies = discrepancies.filter((discrepancy) => discrepancy.status === "open");
  const coverageRisks = coverageRecords.filter((record) => record.risk !== "healthy");
  const bosniaCoverageRisks = bosniaCoverageTargets.filter((target) => target.risk !== "healthy");
  const externalNeedsReview = externalRecords.filter((record) => record.status !== "converted");
  const connectorBacklog = connectorRequests.filter((request) => request.status !== "connected");
  const doneForYouBacklog = doneForYouSetupRequests.filter((request) => request.status !== "done");
  const setupBacklog = setupRows.filter((row) => row.status !== "ready");
  const importedRestaurantSetups = importRuns.filter((run) => run.importType === "restaurant_order_guide").length;
  const topMessageThread = unresolvedMessages[0];
  const topWaitingRequest = waitingRequests[0];
  const topFollowUpRequest = overdueSupplierFollowUps[0] ?? topWaitingRequest;
  const topSupplierReply = supplierRepliesNeedingReview[0];
  const topOrderIssue = openOrderIssues[0];
  const topInvoiceProblem = invoiceProblems[0];
  const topDiscrepancy = openDiscrepancies[0];
  const topCoverageRisk = coverageRisks[0];
  const topBosniaTarget = bosniaCoverageRisks[0] ?? bosniaCoverageTargets[0];
  const topDemandGap = demandGaps[0];
  const topExternalRecord = externalNeedsReview[0];
  const topMissingContact = missingContacts[0];
  const topContactTrustRepair = contactTrustRepairs[0];
  const topDoneForYouRequest = doneForYouBacklog[0];
  const candidateActions: Array<OperatorWorkItem | null> = [
    topDoneForYouRequest
      ? {
          id: `operator-done-for-you-${topDoneForYouRequest.requestId}`,
          title: `Set up ${topDoneForYouRequest.branchName || topDoneForYouRequest.organizationName}`,
          detail: `${topDoneForYouRequest.contactChannel} intake: ${topDoneForYouRequest.supplierListText.slice(0, 100) || topDoneForYouRequest.orderHabitsText.slice(0, 100)}`,
          metric: doneForYouSetupStatusLabel(topDoneForYouRequest.status),
          severity: topDoneForYouRequest.status === "new" ? "critical" : "watch",
          button: topDoneForYouRequest.status === "orders_created" ? "Advance" : "Convert",
          icon: ClipboardList,
          onAction: () =>
            topDoneForYouRequest.status === "orders_created"
              ? onAdvanceDoneForYouSetup(topDoneForYouRequest)
              : onConvertDoneForYouSetup(topDoneForYouRequest),
        }
      : null,
    setupBacklog[0]
      ? {
          id: `operator-setup-${setupBacklog[0].id}`,
          title: `${setupBacklog[0].name} setup is not activated`,
          detail: setupBacklog[0].nextStep,
          metric: `${setupBacklog[0].supplierCount} suppliers / ${setupBacklog[0].messageCount} messages`,
          severity: setupBacklog[0].status,
          button: setupBacklog[0].supplierCount ? "Open orders" : "Open setup",
          icon: Store,
          onAction: () => onNavigate(setupBacklog[0].supplierCount ? "Orders" : "Settings"),
        }
      : null,
    topMissingContact
      ? {
          id: `operator-contact-${topMissingContact.id}`,
          title: `Missing channel for ${topMissingContact.name}`,
          detail: `${topMissingContact.branchName} uses this supplier for ${topMissingContact.category || "orders"}. Add phone, Viber, WhatsApp, or email before the next send.`,
          metric: topMissingContact.deliveryArea || "No delivery area",
          severity: "watch",
          button: "Open setup",
          icon: Users,
          onAction: () => onNavigate("Settings"),
        }
      : null,
    topContactTrustRepair
      ? {
          id: `operator-contact-trust-${topContactTrustRepair.id}`,
          title: `Repair supplier contact: ${topContactTrustRepair.name}`,
          detail: `${topContactTrustRepair.branchName} / ${topContactTrustRepair.category || "Supplier contact"} / ${supplierContactRepairReason(topContactTrustRepair)}`,
          metric: supplierContactRelationshipLabel(topContactTrustRepair.relationshipStatus),
          severity: topContactTrustRepair.relationshipStatus === "opted_out" ? "critical" : "watch",
          button: "Open setup",
          icon: ShieldCheck,
          onAction: () => onNavigate("Settings"),
        }
      : null,
    topFollowUpRequest
      ? {
          id: `operator-waiting-${topFollowUpRequest.id}`,
          title: `${topFollowUpRequest.supplierName} has not responded`,
          detail: `${topFollowUpRequest.restaurantName} / ${topFollowUpRequest.orderId} / ${topFollowUpRequest.items.length} lines. Follow up in the supplier's normal channel.`,
          metric: topFollowUpRequest.followUpDueAt
            ? `Due ${new Date(topFollowUpRequest.followUpDueAt).toLocaleString()}`
            : supplierRequestStatusLabel(topFollowUpRequest.status),
          severity: supplierFollowUpIsDue(topFollowUpRequest) ? "critical" : "watch",
          button: "Open inbox",
          icon: Send,
          onAction: () => onNavigate("Messages"),
        }
      : null,
    topSupplierReply
      ? {
          id: `operator-reply-${topSupplierReply.id}`,
          title: `${topSupplierReply.supplierName} replied with ${supplierRequestStatusLabel(topSupplierReply.status).toLowerCase()}`,
          detail: `${topSupplierReply.restaurantName} / ${topSupplierReply.orderId}${topSupplierReply.supplierNote ? ` / ${topSupplierReply.supplierNote}` : ""}`,
          metric: formatMoney(topSupplierReply.totalBam),
          severity: topSupplierReply.status === "declined" || topSupplierReply.status === "issue" ? "critical" : "watch",
          button: "Open orders",
          icon: MessageSquare,
          onAction: () => onNavigate("Orders"),
        }
      : null,
    topOrderIssue
      ? {
          id: `operator-order-${topOrderIssue.id}`,
          title: `${topOrderIssue.buyer} order needs supplier action`,
          detail: `${topOrderIssue.supplierName}: ${topOrderIssue.nextAction}`,
          metric: formatEur(topOrderIssue.valueEur),
          severity: topOrderIssue.status === "issue" ? "critical" : "watch",
          button: "Advance",
          icon: Truck,
          onAction: () => onAdvanceFulfillment(topOrderIssue.id),
        }
      : null,
    topInvoiceProblem || topDiscrepancy
      ? {
          id: `operator-invoice-${topInvoiceProblem?.id ?? topDiscrepancy?.id}`,
          title: topInvoiceProblem
            ? `Invoice ${topInvoiceProblem.id} is blocked`
            : `Invoice mismatch: ${topDiscrepancy?.supplierName}`,
          detail: topInvoiceProblem
            ? `${topInvoiceProblem.buyer} / ${topInvoiceProblem.supplierName ?? "Supplier"} / ${topInvoiceProblem.due}`
            : `${topDiscrepancy?.buyerName} / ${topDiscrepancy?.detail}`,
          metric: topInvoiceProblem ? formatEur(topInvoiceProblem.amountEur) : topDiscrepancy?.type.replace(/_/g, " ") ?? "Mismatch",
          severity: "critical",
          button: topInvoiceProblem ? "Resolve" : "Open invoices",
          icon: FileSpreadsheet,
          onAction: () =>
            topInvoiceProblem ? onResolveInvoiceBlocker(topInvoiceProblem.id) : onNavigate("Invoices"),
        }
      : null,
    topCoverageRisk || topDemandGap
      ? {
          id: `operator-coverage-${topCoverageRisk?.id ?? topDemandGap?.id}`,
          title: topCoverageRisk
            ? `${topCoverageRisk.market} / ${topCoverageRisk.category} coverage risk`
            : `${topDemandGap?.market} / ${topDemandGap?.category} demand gap`,
          detail: topCoverageRisk?.nextMove ?? topDemandGap?.recommendedAction ?? "Open supplier coverage.",
          metric: topCoverageRisk
            ? `${topCoverageRisk.suppliersLive}/${topCoverageRisk.supplierTarget} suppliers`
            : formatEur(topDemandGap?.revenueAtRiskEur ?? 0),
          severity: topCoverageRisk?.risk === "critical" || (topDemandGap?.revenueAtRiskEur ?? 0) > 100000 ? "critical" : "watch",
          button: topCoverageRisk ? "Open sprint" : "Open intel",
          icon: Gauge,
          onAction: () => (topCoverageRisk ? onOpenCoverageSprint(topCoverageRisk) : onNavigate("Network Intel")),
        }
      : null,
    topBosniaTarget
      ? {
          id: `operator-bosnia-coverage-${topBosniaTarget.id}`,
          title: `${topBosniaTarget.city} ${topBosniaTarget.category} coverage`,
          detail: topBosniaTarget.nextSupplierAction,
          metric: `${topBosniaTarget.suppliersLive}/${topBosniaTarget.supplierTarget} suppliers`,
          severity:
            topBosniaTarget.risk === "critical" ? "critical" : topBosniaTarget.risk === "watch" ? "watch" : "ready",
          button: "Open map",
          icon: MapPin,
          onAction: () => onNavigate("Network Intel"),
        }
      : null,
    topExternalRecord
      ? {
          id: `operator-external-${topExternalRecord.id}`,
          title: `Verify ${topExternalRecord.name}`,
          detail: `${topExternalRecord.market}: ${topExternalRecord.products.slice(0, 3).join(", ") || "Products need review"}`,
          metric: `${topExternalRecord.confidence}% confidence`,
          severity: topExternalRecord.status === "needs_review" ? "critical" : "watch",
          button: "Advance",
          icon: Search,
          onAction: () => onAdvanceExternalRecord(topExternalRecord),
        }
      : null,
    topMessageThread
      ? {
          id: `operator-message-${topMessageThread.id}`,
          title: `${topMessageThread.participant} needs follow-up`,
          detail: `${topMessageThread.subject} / ${topMessageThread.preview}`,
          metric: topMessageThread.status.replace(/_/g, " "),
          severity: topMessageThread.status === "needs_reply" ? "critical" : "watch",
          button: "Follow up",
          icon: Inbox,
          onAction: () => onSendMessageFollowUp(topMessageThread.id),
        }
      : null,
  ];
  const severityRank: Record<OperatorWorkItem["severity"], number> = { critical: 0, watch: 1, ready: 2 };
  const workItems = candidateActions
    .filter((item): item is OperatorWorkItem => Boolean(item))
    .sort((left, right) => severityRank[left.severity] - severityRank[right.severity])
    .slice(0, 8);
  const supplierCallRequests = supplierOrderRequests.filter(
    (request) =>
      request.status === "sent" &&
      (supplierFollowUpIsDue(request) ||
        request.messageDeliveryStatus === "failed" ||
        request.messageDeliveryStatus === "needs_follow_up"),
  );
  const callSheetCandidates: OperatorCallSheetItem[] = [
    ...supplierCallRequests.map((request) => {
      const followUpDue = supplierFollowUpIsDue(request);
      const channelIssue =
        request.messageDeliveryStatus === "failed" || request.messageDeliveryStatus === "needs_follow_up";

      return {
        id: `call-supplier-${request.id}`,
        title: request.supplierName,
        detail: `${request.restaurantName} / ${request.orderId} / ${request.items.length} order lines`,
        channel: supplierMessageChannelLabel(request.preferredChannel ?? request.messageDeliveryChannel ?? "copy"),
        target: request.supplierContact || request.supplierName,
        reason: channelIssue
          ? request.messageDeliveryStatus === "failed"
            ? "Message failed"
            : "Channel missing or needs manual follow-up"
          : "Supplier no response",
        severity: followUpDue || request.messageDeliveryStatus === "failed" ? ("critical" as const) : ("watch" as const),
        button: "Open messages",
        icon: Send,
        onAction: () => onNavigate("Messages"),
      };
    }),
    ...missingContacts.map((contact) => ({
      id: `call-contact-${contact.id}`,
      title: contact.name,
      detail: `${contact.branchName} / ${contact.category || "Supplier contact"} / ${contact.deliveryArea || "Delivery area missing"}`,
      channel: "Ask restaurant",
      target: contact.contactPerson || contact.branchName,
      reason: "Add phone, Viber, WhatsApp, or email",
      severity: "watch" as const,
      button: "Open setup",
      icon: Users,
      onAction: () => onNavigate("Settings"),
    })),
    ...contactTrustRepairs.map((contact) => ({
      id: `call-contact-trust-${contact.id}`,
      title: contact.name,
      detail: `${contact.branchName} / ${contact.category || "Supplier contact"} / ${contact.deliveryArea || "Delivery area missing"}`,
      channel: "Trust repair",
      target: contact.contactPerson || contact.contact || contact.branchName,
      reason: supplierContactRepairReason(contact),
      severity: contact.relationshipStatus === "opted_out" ? ("critical" as const) : ("watch" as const),
      button: "Open setup",
      icon: ShieldCheck,
      onAction: () => onNavigate("Settings"),
    })),
    ...doneForYouBacklog.map((request) => ({
      id: `call-intake-${request.requestId}`,
      title: request.branchName || request.organizationName,
      detail: `${request.city || "City pending"} / ${doneForYouSetupStatusLabel(request.status)} / ${request.preferredSetupWindow || "No setup window"}`,
      channel: doneForYouSetupChannelLabel(request.contactChannel),
      target: request.contactValue || request.contactName || "Contact pending",
      reason: "Turn intake into suppliers and first repeat order",
      severity: request.status === "new" ? ("critical" as const) : ("watch" as const),
      button: request.status === "orders_created" ? "Advance" : "Convert",
      icon: ClipboardList,
      onAction: () =>
        request.status === "orders_created" ? onAdvanceDoneForYouSetup(request) : onConvertDoneForYouSetup(request),
    })),
    ...supplierRepliesNeedingReview.map((request) => ({
      id: `call-review-${request.id}`,
      title: request.restaurantName,
      detail: `${request.supplierName} replied ${supplierRequestStatusLabel(request.status).toLowerCase()}${request.supplierNote ? ` / ${request.supplierNote}` : ""}`,
      channel: "Restaurant ops",
      target: request.restaurantName,
      reason: "Review supplier changes before invoice/payment",
      severity: request.status === "declined" || request.status === "issue" ? ("critical" as const) : ("watch" as const),
      button: "Open orders",
      icon: MessageSquare,
      onAction: () => onNavigate("Orders"),
    })),
    ...invoiceProblems.map((invoice) => ({
      id: `call-invoice-${invoice.id}`,
      title: invoice.buyer,
      detail: `${invoice.supplierName ?? "Supplier pending"} / ${invoice.id} / ${invoice.due}`,
      channel: "Restaurant ops",
      target: invoice.buyer,
      reason: "Blocked invoice needs order/supplier check",
      severity: "critical" as const,
      button: "Resolve",
      icon: FileSpreadsheet,
      onAction: () => onResolveInvoiceBlocker(invoice.id),
    })),
    ...openDiscrepancies.map((discrepancy) => ({
      id: `call-discrepancy-${discrepancy.id}`,
      title: discrepancy.buyerName,
      detail: `${discrepancy.supplierName} / ${discrepancy.detail}`,
      channel: "Restaurant ops",
      target: discrepancy.buyerName,
      reason: discrepancy.type.replace(/_/g, " "),
      severity: "critical" as const,
      button: "Open invoices",
      icon: AlertTriangle,
      onAction: () => onNavigate("Invoices"),
    })),
  ];
  const operatorCallSheet = Array.from(
    new Map(callSheetCandidates.map((item) => [item.id, item])).values(),
  )
    .sort((left, right) => severityRank[left.severity] - severityRank[right.severity])
    .slice(0, 8);
  const criticalCount = workItems.filter((item) => item.severity === "critical").length;
  const messageRows = supplierOrderRequests
    .slice()
    .sort((left, right) => {
      const rank: Record<SupplierOrderRequestStatus, number> = {
        declined: 0,
        issue: 1,
        changes_requested: 2,
        sent: 3,
        confirmed: 4,
      };
      return rank[left.status] - rank[right.status] || right.updatedAt.localeCompare(left.updatedAt);
    })
    .slice(0, 6);

  return (
    <section className="operator-cockpit-grid" aria-label="VELEO operator cockpit">
      <Panel
        className="operator-command-panel"
        title="VELEO operator cockpit"
        action={`${criticalCount} urgent / ${workItems.length} queued`}
        help="Service-led back office for cold-start Bosnia onboarding: restaurant setup, supplier contacts, supplier confirmation links, order exceptions, invoice mismatches, and coverage work."
      >
        <div className="operator-kpi-grid">
          <p>
            <strong>{activatedRestaurants}/{setupRows.length || 0}</strong>
            <span>restaurants activated</span>
          </p>
          <p>
            <strong>{missingContacts.length}</strong>
            <span>supplier contacts missing channel</span>
          </p>
          <p>
            <strong>{contactTrustRepairs.length}</strong>
            <span>supplier contact trust repairs</span>
          </p>
          <p>
            <strong>{overdueSupplierFollowUps.length}/{waitingRequests.length}</strong>
            <span>supplier follow-ups due</span>
          </p>
          <p>
            <strong>{invoiceProblems.length + openDiscrepancies.length}</strong>
            <span>invoice/order exceptions</span>
          </p>
        </div>
        <div className="operator-work-list">
          {workItems.length ? (
            workItems.map((item) => <OperatorWorkRow item={item} key={item.id} />)
          ) : (
            <p className="data-empty-state">No operator work is open from saved cold-start records.</p>
          )}
        </div>
      </Panel>

      <Panel
        title="Operator call sheet"
        action={`${operatorCallSheet.length} next contacts`}
        help="One ranked list of who VELEO should contact next: missing supplier channels, wrong-contact or opt-out repairs, no-response supplier links, setup intakes, supplier replies, and invoice blockers."
      >
        <div className="operator-call-sheet">
          {operatorCallSheet.length ? (
            operatorCallSheet.map((item) => <OperatorCallSheetRow item={item} key={item.id} />)
          ) : (
            <p className="data-empty-state">No operator calls are needed from the current cold-start records.</p>
          )}
        </div>
      </Panel>

      <Panel
        title="Restaurant setup ledger"
        action={`${setupBacklog.length} not activated`}
        help="Tracks whether each restaurant has current suppliers, a generated supplier message, and invoice matching started."
      >
        <div className="restaurant-setup-ledger">
          {setupRows.length ? (
            setupRows.slice(0, 8).map((row) => <RestaurantSetupStatusRow row={row} key={row.id} />)
          ) : (
            <p className="data-empty-state">No restaurant account, branch, order, or supplier contact has been loaded yet.</p>
          )}
        </div>
      </Panel>

      <Panel
        title="Supplier message follow-up"
        action={`${overdueSupplierFollowUps.length} due / ${supplierOrderRequests.length} links`}
        help="No-login supplier confirmation links and their current response state. This is the heartbeat of the cold-start loop."
      >
        <div className="operator-message-ledger">
          {messageRows.length ? (
            messageRows.map((request) => (
              <article className={`operator-message-row ${request.status}`} key={request.id}>
                <div>
                  <strong>{request.supplierName}</strong>
                  <span>{request.restaurantName} / {request.orderId}</span>
                  <small>{request.items.length} lines / {request.deliveryWindow}</small>
                </div>
                <b>{supplierRequestStatusLabel(request.status)}</b>
                <button className="row-action" type="button" onClick={() => onNavigate("Orders")}>
                  Open
                  <ChevronRight size={15} />
                </button>
              </article>
            ))
          ) : (
            <p className="data-empty-state">No supplier confirmation link has been generated yet.</p>
          )}
        </div>
      </Panel>

      <Panel
        title="Setup inputs"
        action={`${importedRestaurantSetups} imports / ${connectorBacklog.length} connectors`}
        help="Operator view of done-for-you setup inputs: typed/imported order guides, software connection backlog, and current supplier coverage data."
      >
        <div className="operator-input-list">
          <p>
            <span>Restaurant order-guide imports</span>
            <strong>{importedRestaurantSetups}</strong>
          </p>
          <p>
            <span>Pending software connections</span>
            <strong>{connectorBacklog.length}</strong>
          </p>
          <p>
            <span>WhatsApp/Viber setup intakes</span>
            <strong>{doneForYouBacklog.length}</strong>
          </p>
          <p>
            <span>Wrong-contact / opt-out repairs</span>
            <strong>{contactTrustRepairs.length}</strong>
          </p>
          <p>
            <span>Coverage risks</span>
            <strong>{coverageRisks.length}</strong>
          </p>
          <p>
            <span>Bosnia city/category gaps</span>
            <strong>{bosniaCoverageRisks.length}</strong>
          </p>
          <p>
            <span>Supplier records to review</span>
            <strong>{externalNeedsReview.length}</strong>
          </p>
        </div>
      </Panel>

      <Panel
        title="Done-for-you setup intake"
        action={`${doneForYouBacklog.length} open`}
        help="Restaurants can send supplier lists, screenshots, and order habits by WhatsApp/Viber. VELEO operators turn them into supplier contacts and repeat orders."
      >
        <div className="done-for-you-list">
          {doneForYouSetupRequests.length ? (
            doneForYouSetupRequests.slice(0, 6).map((request) => (
              <DoneForYouSetupRequestRow
                key={request.requestId}
                request={request}
                onAdvance={request.status === "done" ? undefined : () => onAdvanceDoneForYouSetup(request)}
                onConvert={
                  request.status === "done" || request.status === "orders_created"
                    ? undefined
                    : () => onConvertDoneForYouSetup(request)
                }
              />
            ))
          ) : (
            <p className="data-empty-state">No WhatsApp/Viber setup intake has been saved yet.</p>
          )}
        </div>
      </Panel>

      <Panel
        title="Bosnia acquisition map"
        action={`${bosniaCoverageRisks.length} city/category gaps`}
        help="City/category acquisition map for Bosnia: Sarajevo produce, Mostar meat, Tuzla packaging, and Banja Luka beverages."
      >
        <div className="bosnia-coverage-map">
          {bosniaCoverageTargets.map((target) => (
            <BosniaCoverageTargetRow key={target.id} target={target} />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function BosniaCoverageTargetRow({ target }: { target: BosniaCoverageTarget }) {
  const supplierGap = Math.max(0, target.supplierTarget - target.suppliersLive);

  return (
    <article className={`bosnia-coverage-row ${target.risk}`}>
      <span>
        <MapPin size={17} />
      </span>
      <div>
        <strong>{target.city} {target.category}</strong>
        <small>{target.nextSupplierAction}</small>
        <p>{target.sourceSignals.join(" / ")}</p>
      </div>
      <div>
        <b>{target.suppliersLive}/{target.supplierTarget}</b>
        <small>{target.fillRate.toFixed(1)}% fill</small>
      </div>
      <div>
        <b>{supplierGap ? `${supplierGap} to find` : "Covered"}</b>
        <small>{target.restaurantContacts} contacts / {target.externalCandidates} candidates</small>
      </div>
      <div>
        <b>{target.acquisitionOwner}</b>
        <small>{target.primaryChannel}</small>
      </div>
    </article>
  );
}

function doneForYouSetupStatusLabel(status: DoneForYouSetupRequest["status"]) {
  const labels: Record<DoneForYouSetupRequest["status"], string> = {
    contacts_created: "Contacts created",
    done: "Done",
    in_review: "In review",
    new: "New intake",
    orders_created: "Orders created",
  };

  return labels[status];
}

function DoneForYouSetupRequestRow({
  request,
  onAdvance,
  onConvert,
}: {
  request: DoneForYouSetupRequest;
  onAdvance?: () => void;
  onConvert?: () => void;
}) {
  const preview = request.supplierListText || request.orderHabitsText || "No setup notes saved.";

  return (
    <article className={`done-for-you-row ${request.status}`}>
      <span>
        <ClipboardList size={17} />
      </span>
      <div>
        <strong>{request.branchName || request.organizationName}</strong>
        <small>
          {request.city || "No city"} / {request.contactChannel} / {request.contactValue || "contact pending"}
        </small>
        <p>{preview.slice(0, 180)}</p>
      </div>
      <div>
        <b>{doneForYouSetupStatusLabel(request.status)}</b>
        <small>{request.preferredSetupWindow || request.createdAt}</small>
      </div>
      {onAdvance || onConvert ? (
        <div className="row-button-group done-for-you-actions">
          {onConvert ? (
            <button className="row-action" type="button" onClick={onConvert}>
              Convert
              <ChevronRight size={15} />
            </button>
          ) : null}
          {onAdvance ? (
            <button className="row-action secondary" type="button" onClick={onAdvance}>
              Advance
              <ChevronRight size={15} />
            </button>
          ) : null}
        </div>
      ) : (
        <b>Complete</b>
      )}
    </article>
  );
}

function OperatorWorkRow({ item }: { item: OperatorWorkItem }) {
  const Icon = item.icon;

  return (
    <article className={`operator-work-row ${item.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{item.title}</strong>
        <small>{item.detail}</small>
      </div>
      <b>{item.metric}</b>
      <button className="row-action" type="button" onClick={item.onAction}>
        {item.button}
        <ChevronRight size={15} />
      </button>
    </article>
  );
}

function OperatorCallSheetRow({ item }: { item: OperatorCallSheetItem }) {
  const Icon = item.icon;

  return (
    <article className={`operator-call-row ${item.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{item.title}</strong>
        <small>{item.detail}</small>
      </div>
      <p>
        <span>{item.channel}</span>
        <b>{item.target}</b>
      </p>
      <p>
        <span>Reason</span>
        <b>{item.reason}</b>
      </p>
      <button className="row-action" type="button" onClick={item.onAction}>
        {item.button}
        <ChevronRight size={15} />
      </button>
    </article>
  );
}

function RestaurantSetupStatusRow({ row }: { row: RestaurantSetupRow }) {
  return (
    <article className={`restaurant-setup-row ${row.status}`}>
      <div>
        <strong>{row.name}</strong>
        <small>{row.nextStep}</small>
      </div>
      <p>
        <span>Suppliers</span>
        <b>{row.supplierCount}</b>
      </p>
      <p>
        <span>Messages</span>
        <b>{row.messageCount}</b>
      </p>
      <p>
        <span>Invoices</span>
        <b>{row.invoiceCount}</b>
      </p>
    </article>
  );
}

function GlobalScaleOverview({
  automationTasks,
  networkSnapshot,
  onOpenCountrySprint,
}: {
  automationTasks: AutomationTask[];
  networkSnapshot: NetworkOperatingSnapshot;
  onOpenCountrySprint: (country: OperatingCountry) => void;
}) {
  const { operatingCountries, regionHealth, stats: networkStats } = networkSnapshot;
  const priorityCountries = operatingCountries.filter((country) => country.priority !== "healthy");
  const averageCountryFillRate =
    operatingCountries.length
      ? operatingCountries.reduce((sum, country) => sum + country.fillRate, 0) / operatingCountries.length
      : 0;

  return (
    <>
      <section className="scale-grid" aria-label="Network operations summary">
        <Panel
          title="Network scale"
          action={`${networkStats.countries} countries`}
          help="Top-level operating scale loaded from saved marketplace, order, supplier, and workspace records."
        >
          <div className="network-kpi-grid">
            <p>
              <strong>{formatCount(networkStats.customers)}</strong>
              <span>customer accounts</span>
            </p>
            <p>
              <strong>{formatCount(networkStats.suppliers)}</strong>
              <span>supplier accounts</span>
            </p>
            <p>
              <strong>{networkStats.automatedOrders}%</strong>
              <span>orders automated</span>
            </p>
            <p>
              <strong>{networkStats.supportSla}%</strong>
              <span>support SLA</span>
            </p>
          </div>
        </Panel>

        <Panel
          className="region-panel"
          title="Regional health"
          action="Global live"
          help="Regional rollup across countries. Watch regions need supplier coverage, SLA, or issue-rate intervention."
        >
          <div className="region-list">
            {regionHealth.length ? (
              regionHealth.map((region) => (
                <RegionRow key={region.id} region={region} />
              ))
            ) : (
              <p className="data-empty-state">Import accounts, orders, or supplier records to build regional health.</p>
            )}
          </div>
        </Panel>

        <Panel
          title="Automation queue"
          action="Edge jobs"
          help="Background jobs keeping supplier matching, price checks, and delivery SLA monitoring current."
        >
          <div className="automation-list">
            {automationTasks.length ? (
              automationTasks.map((task) => (
                <AutomationRow key={task.id} task={task} />
              ))
            ) : (
              <p className="data-empty-state">Live workspace records have not produced automation work yet.</p>
            )}
          </div>
        </Panel>
      </section>

      <section className="country-overview-grid" aria-label="Country operating coverage">
        <Panel
          title="Country coverage watch"
          action={`${priorityCountries.length} need action`}
          help="Countries where fill rate, demand pressure, or supplier density requires operator attention."
        >
          <div className="country-priority-list">
            {priorityCountries.length ? (
              priorityCountries.slice(0, 6).map((country) => (
                <CountryCoverageRow
                  key={country.id}
                  country={country}
                  compact
                  onOpenSprint={() => onOpenCountrySprint(country)}
                />
              ))
            ) : (
              <p className="data-empty-state">No country coverage watch is open from saved records.</p>
            )}
          </div>
        </Panel>

        <Panel
          title="Coverage benchmark"
          action={`${averageCountryFillRate.toFixed(1)}% avg fill`}
          help="Global operating benchmark across the country ledger. This keeps the 42-country claim tied to supply performance."
        >
          <div className="coverage-benchmark-grid">
            <p>
              <strong>{operatingCountries.length}</strong>
              <span>countries modeled</span>
            </p>
            <p>
              <strong>{priorityCountries.length}</strong>
              <span>coverage watches</span>
            </p>
            <p>
              <strong>{formatCount(operatingCountries.reduce((sum, country) => sum + country.suppliers, 0))}</strong>
              <span>country supplier links</span>
            </p>
          </div>
        </Panel>
      </section>
    </>
  );
}

function DashboardActionCenter({
  approvalRequests,
  catalogProducts,
  conversionTasks,
  coverageRecords,
  draftTotal,
  externalRecords,
  fulfillmentOrders,
  invoices,
  incidents,
  messageThreads,
  networkStats,
  quoteRequests,
  replenishmentSignals,
  role,
  submitState,
  supplierOpportunities,
  supplierOffers,
  canApproveOrders,
  onAdvanceConversionTask,
  onAdvanceExternalRecord,
  onAdvanceFulfillment,
  onAdvanceIncident,
  onApproveRequest,
  onFindBackupProduct,
  onOpenCoverageSprint,
  onSendMessageFollowUp,
  onSendQuote,
  onSubmitOrder,
  onNavigate,
}: {
  approvalRequests: ApprovalRequest[];
  catalogProducts: Product[];
  conversionTasks: SupplierConversionTask[];
  coverageRecords: MarketCoverageRecord[];
  draftTotal: number;
  externalRecords: ExternalSupplierRecord[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  invoices: InvoiceRecord[];
  incidents: LiveIncident[];
  messageThreads: MessageThread[];
  networkStats: NetworkStats;
  quoteRequests: QuoteRequest[];
  replenishmentSignals: ReplenishmentSignal[];
  role: Role;
  submitState: string;
  supplierOpportunities: SupplierOpportunity[];
  supplierOffers: SupplierOffer[];
  canApproveOrders: boolean;
  onAdvanceConversionTask: (taskId: string) => void;
  onAdvanceExternalRecord: (record: ExternalSupplierRecord) => void;
  onAdvanceFulfillment: (orderId: string) => void;
  onAdvanceIncident: (incidentId: string) => void;
  onApproveRequest: (requestId: string) => void;
  onFindBackupProduct: (product: Product) => void;
  onOpenCoverageSprint: (record: MarketCoverageRecord) => void;
  onSendMessageFollowUp: (threadId: string) => void;
  onSendQuote: (request: QuoteRequest) => void;
  onSubmitOrder: () => void;
  onNavigate: (label: string) => void;
}) {
  const needsApproval = approvalRequests.filter((request) => request.status === "needs_approval");
  const stockoutSignals = replenishmentSignals.filter((signal) => signal.risk === "stockout");
  const weakCatalog = catalogProducts.filter((product) => product.stockStatus !== "available");
  const openQuotes = quoteRequests.filter((request) => isOpenQuoteStatus(request.status));
  const readyQuotes = quoteRequests.filter((request) => isReadyQuoteStatus(request.status));
  const invoiceNeedsWork = invoices.filter((invoice) => invoice.status !== "exported");
  const fulfillmentNeedsAction = fulfillmentOrders.filter(fulfillmentNeedsActionFor);
  const fulfillmentValue = fulfillmentOrders.reduce((sum, order) => sum + order.valueEur, 0);
  const unresolvedMessages = messageThreads.filter((thread) => thread.status !== "resolved");
  const topInboxBlocker = unresolvedMessages
    .slice()
    .sort((left, right) => {
      const leftPriority = left.status === "needs_reply" ? 0 : 1;
      const rightPriority = right.status === "needs_reply" ? 0 : 1;

      return leftPriority - rightPriority;
    })[0];
  const openIncidents = incidents.filter((incident) => incident.status !== "resolved");
  const coverageRisks = coverageRecords.filter((record) => record.risk !== "healthy");
  const conversionOpen = conversionTasks.filter((task) => task.stage !== "converted");
  const externalNeedsReview = externalRecords.filter((record) => record.status !== "converted");
  const topOpportunity = supplierOpportunities
    .slice()
    .sort((left, right) => right.potentialRevenueEur - left.potentialRevenueEur)[0];
  const topStockoutProduct = stockoutSignals[0]
    ? catalogProducts.find((product) => product.id === stockoutSignals[0].productId)
    : weakCatalog[0];

  const inboxAction = topInboxBlocker
    ? {
        icon: MessageSquare,
        title: `${topInboxBlocker.status === "needs_reply" ? "Reply to" : "Follow up"} ${topInboxBlocker.participant}`,
        detail: `${topInboxBlocker.subject} / ${topInboxBlocker.market}. ${topInboxBlocker.preview}`,
        button: topInboxBlocker.status === "needs_reply" ? "Send reply" : "Send follow-up",
        onClick: () => onSendMessageFollowUp(topInboxBlocker.id),
      }
    : null;

  const roleActions =
    role === "supplier"
      ? [
          {
            icon: Send,
            title: openQuotes[0] ? `Send price to ${openQuotes[0].buyer}` : "No price requests waiting",
            detail: openQuotes[0]
              ? `${openQuotes[0].product} / ${openQuotes[0].quantity} / needed ${openQuotes[0].neededBy}.`
              : "Every restaurant request is either priced or waiting on the restaurant.",
            button: openQuotes[0] ? "Send price" : "Open requests",
            onClick: () => (openQuotes[0] ? onSendQuote(openQuotes[0]) : onNavigate("Orders")),
          },
          {
            icon: PackageCheck,
            title: fulfillmentNeedsAction[0] ? `Move ${fulfillmentNeedsAction[0].buyer}` : "Fulfillment board clear",
            detail: fulfillmentNeedsAction[0]
              ? `${fulfillmentNeedsAction[0].id} / ${fulfillmentNeedsAction[0].nextAction}`
              : "All active fulfillment work is already in transit or tracked.",
            button: fulfillmentNeedsAction[0] ? "Advance order" : "Open board",
            onClick: () =>
              fulfillmentNeedsAction[0]
                ? onAdvanceFulfillment(fulfillmentNeedsAction[0].id)
                : onNavigate("Orders"),
          },
          {
            icon: TrendingUp,
            title: topOpportunity?.title ?? "Find restaurants to sell to",
            detail: topOpportunity
              ? `${topOpportunity.action} / ${formatEur(topOpportunity.potentialRevenueEur)} upside.`
              : "No new restaurant lead is open right now.",
            button: "View leads",
            onClick: () => onNavigate("Supplier Finder"),
          },
        ]
      : role === "admin"
        ? [
            {
              icon: AlertTriangle,
              title: openIncidents[0] ? openIncidents[0].title : "No live incidents",
              detail: openIncidents[0]
                ? `${openIncidents[0].impact} / ${openIncidents[0].action}`
                : "Market incident queue is clear.",
              button: openIncidents[0] ? "Advance incident" : "Open intel",
              onClick: () =>
                openIncidents[0]
                  ? onAdvanceIncident(openIncidents[0].id)
                  : onNavigate("Network Intel"),
            },
            {
              icon: Search,
              title: `${externalNeedsReview.length} supplier records need work`,
              detail: `${conversionOpen.length} conversion tasks remain open across verified and backup supplier records.`,
              button: conversionOpen[0]
                ? conversionOpen[0].stage === "review"
                  ? "Invite supplier"
                  : "Convert supplier"
                : externalNeedsReview[0]
                  ? "Advance record"
                  : "Review",
              onClick: () =>
                conversionOpen[0]
                  ? onAdvanceConversionTask(conversionOpen[0].id)
                  : externalNeedsReview[0]
                    ? onAdvanceExternalRecord(externalNeedsReview[0])
                    : onNavigate("Supplier Finder"),
            },
            {
              icon: ShieldCheck,
              title: `${coverageRisks.length} coverage risks`,
              detail: coverageRisks[0]
                ? `${coverageRisks[0].market} / ${coverageRisks[0].category}: ${coverageRisks[0].nextMove}`
                : "Coverage matrix is healthy across tracked markets.",
              button: coverageRisks[0] ? "Open sprint" : "Open intel",
              onClick: () =>
                coverageRisks[0]
                  ? onOpenCoverageSprint(coverageRisks[0])
                  : onNavigate("Network Intel"),
            },
          ]
        : [
            {
              icon: ClipboardList,
              title: draftTotal ? "Send today's buying list" : "Start today's buying list",
              detail: draftTotal
                ? `${formatMoney(draftTotal)} KM is ready to review. Current state: ${submitState}.`
                : "Add the items you need for service, then send the order from one place.",
              button: draftTotal ? "Send order" : "Open list",
              onClick: onSubmitOrder,
            },
            {
              icon: TrendingUp,
              title: supplierOffers[0]
                ? `Best current offer: ${supplierOffers[0].productName}`
                : readyQuotes[0]
                  ? `Quote ready: ${readyQuotes[0].product}`
                  : "Compare suppliers before you buy",
              detail: supplierOffers[0]
                ? `${supplierOffers[0].supplierName}: ${formatCurrencyAmount(supplierOffers[0].landedPrice, supplierOffers[0].currency)}. ${supplierOffers[0].rankReason}.`
                : readyQuotes[0]
                  ? `${readyQuotes[0].supplierName ?? "Supplier"} quoted ${readyQuotes[0].budget}. Compare it before adding to today's list.`
                  : "Import an order guide or search an item to create private price requests from suppliers.",
              button: supplierOffers.length || readyQuotes.length ? "Compare offers" : "Find price",
              onClick: () => onNavigate("Marketplace"),
            },
            {
              icon: FileSpreadsheet,
              title: invoiceNeedsWork[0]
                ? `Match invoice ${invoiceNeedsWork[0].id}`
                : "Invoices are clear",
              detail: invoiceNeedsWork[0]
                ? `${invoiceNeedsWork[0].buyer} / ${formatEur(invoiceNeedsWork[0].amountEur)} / ${invoiceMatchLabel(invoiceNeedsWork[0])}.`
                : "Supplier invoices can be matched to orders and prepared for bank transfer without Veleo holding funds.",
              button: invoiceNeedsWork[0] ? "Review invoice" : "Open invoices",
              onClick: () => onNavigate("Orders"),
            },
            {
              icon: Search,
              title: topStockoutProduct ? `Find another supplier for ${topStockoutProduct.name}` : "Find supplier options",
              detail: stockoutSignals[0]
                ? `${stockoutSignals[0].branch} has ${stockoutSignals[0].currentCoverageDays.toFixed(1)} days covered.`
                : topStockoutProduct
                  ? `${topStockoutProduct.supplier} is ${stockLabels[topStockoutProduct.stockStatus].toLowerCase()}. Find a backup before the next order.`
                  : "Search suppliers before the next item becomes unavailable.",
              button: topStockoutProduct ? "Find supplier" : "Find supplier",
              onClick: () =>
                topStockoutProduct
                  ? onFindBackupProduct(topStockoutProduct)
                  : onNavigate("Supplier Finder"),
            },
            {
              icon: AlertTriangle,
              title: needsApproval[0]
                ? canApproveOrders
                  ? `Approve spend for ${needsApproval[0].branch}`
                  : `Spend review for ${needsApproval[0].branch}`
                : fulfillmentNeedsAction[0]
                  ? `Delivery issue: ${fulfillmentNeedsAction[0].buyer}`
                  : "No delivery issues blocking service",
              detail: needsApproval[0]
                ? `${formatMoney(needsApproval[0].amountBam)} KM / ${needsApproval[0].policy}`
                : fulfillmentNeedsAction[0]
                  ? fulfillmentNeedsAction[0].nextAction
                  : "Current orders are not blocked by approval, shortage, or route issue.",
              button: needsApproval[0] && canApproveOrders ? "Approve" : "Open orders",
              onClick: () =>
                needsApproval[0] && canApproveOrders
                  ? onApproveRequest(needsApproval[0].id)
                  : onNavigate("Orders"),
            },
          ];
  const actions = inboxAction ? [inboxAction, ...roleActions].slice(0, 4) : roleActions;

  const postureCards =
    role === "supplier"
      ? [
          {
            icon: Send,
            label: "Open quotes",
            value: `${openQuotes.length}`,
            note: `${quoteRequests.length - openQuotes.length} quoted or waiting`,
          },
          {
            icon: Truck,
            label: "Orders value",
            value: formatEur(fulfillmentValue),
            note: `${fulfillmentNeedsAction.length} orders need action`,
          },
          {
            icon: Boxes,
            label: "Products to fix",
            value: `${weakCatalog.length}`,
            note: `${catalogProducts.length - weakCatalog.length}/${catalogProducts.length} products ready`,
          },
          {
            icon: MessageSquare,
            label: "Inbox",
            value: `${unresolvedMessages.length}`,
            note: "Restaurant replies and new leads",
          },
        ]
      : role === "admin"
        ? [
            {
              icon: Building2,
              label: "Customers",
              value: formatCount(networkStats.customers),
              note: `${networkStats.countries} countries live`,
            },
            {
              icon: Factory,
              label: "Supplier base",
              value: formatCount(networkStats.suppliers),
              note: `${externalNeedsReview.length} supplier records in motion`,
            },
            {
              icon: AlertTriangle,
              label: "Incidents",
              value: `${openIncidents.length}`,
              note: `${coverageRisks.length} market coverage risks`,
            },
            {
              icon: ShieldCheck,
              label: "Controls",
              value: `${networkStats.supportSla}%`,
              note: "Support SLA and audit posture",
            },
          ]
        : [
            {
              icon: ClipboardList,
              label: "Today's list",
              value: `${formatMoney(draftTotal)} KM`,
              note: "Items ready to review",
            },
            {
              icon: TrendingUp,
              label: "Better offers",
              value: `${supplierOffers.length}`,
              note: supplierOffers[0]
                ? `${supplierOffers[0].supplierName} leads on ${supplierOffers[0].productName}`
                : "Create private price requests to compare",
            },
            {
              icon: FileSpreadsheet,
              label: "Invoices",
              value: `${invoiceNeedsWork.length}`,
              note: "Need match, dispute review, or finance handoff",
            },
            {
              icon: MessageSquare,
              label: "Inbox",
              value: `${unresolvedMessages.length}`,
              note: "Supplier replies and substitutions",
            },
          ];

  return (
    <>
      <section className="dashboard-action-grid" aria-label="Dashboard actions">
        <Panel
          title={role === "restaurant" ? "What needs doing" : "Next actions"}
          action={`${actions.length} open`}
          help={
            role === "restaurant"
              ? "The restaurant work that needs attention today: buying list, supplier replies, low stock, delivery issues, and approvals."
              : "The work that needs attention first: orders, price requests, product fixes, restaurant leads, and replies."
          }
        >
          <div className="action-list">
            {actions.map((action) => (
              <DashboardActionRow key={action.title} action={action} />
            ))}
          </div>
        </Panel>
        <Panel
          title={role === "restaurant" ? "At a glance" : role === "supplier" ? "Sales snapshot" : "Network snapshot"}
          action="Live"
          help={
            role === "restaurant"
              ? "Current buying list, invoices, supplier offers, and messages."
              : role === "supplier"
                ? "Price requests, orders, product fixes, and restaurant messages."
                : "Current customer, supplier, incident, and control status."
          }
        >
          <div className="metric-grid compact">
            {postureCards.map((card) => (
              <MetricCard
                key={card.label}
                icon={card.icon}
                label={card.label}
                value={card.value}
                note={card.note}
              />
            ))}
          </div>
        </Panel>
      </section>
    </>
  );
}

function DashboardActionRow({
  action,
}: {
  action: {
    icon: React.ElementType;
    title: string;
    detail: string;
    button: string;
    onClick: () => void;
  };
}) {
  const Icon = action.icon;

  return (
    <article className="dashboard-action-row">
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{action.title}</strong>
        <small>{action.detail}</small>
      </div>
      <button className="row-action" type="button" onClick={action.onClick}>
        {action.button}
        <ChevronRight size={15} />
      </button>
    </article>
  );
}

function PlatformControlRoom({
  incidents,
  marketLanes,
  trustControls,
  workflowSignals,
  onAdvanceIncident,
}: {
  incidents: LiveIncident[];
  marketLanes: MarketLane[];
  trustControls: TrustControl[];
  workflowSignals: WorkflowSignal[];
  onAdvanceIncident: (incidentId: string) => void;
}) {
  const openIncidentCount = incidents.filter((incident) => incident.status !== "resolved").length;

  return (
    <section className="control-grid" aria-label="Platform control room">
      <Panel
        title="Live incident triage"
        action={`${openIncidentCount} open`}
        help="Open operational failures that can damage fulfillment, pricing, or buyer trust."
      >
        <div className="incident-list">
          {incidents.map((incident) => (
            <IncidentRow
              key={incident.id}
              incident={incident}
              onAdvance={() => onAdvanceIncident(incident.id)}
            />
          ))}
        </div>
      </Panel>

      <Panel
        title="Market lanes"
        action="Expansion"
        help="Regional routes and expansion lanes where Veleo tracks readiness, risk, and supplier coverage."
      >
        <div className="lane-list">
          {marketLanes.map((lane) => (
            <MarketLaneRow key={lane.id} lane={lane} />
          ))}
        </div>
      </Panel>

      <Panel
        title="Buying workflow"
        action="Today"
        help="Live workflow stages across requests, quotes, orders, delivery, and issue resolution."
      >
        <div className="workflow-list">
          {workflowSignals.map((signal) => (
            <WorkflowRow key={signal.id} signal={signal} />
          ))}
        </div>
      </Panel>

      <Panel
        title="Trust controls"
        action="Audit ready"
        help="Controls that prove orders, supplier changes, messages, and account actions are traceable."
      >
        <div className="trust-list">
          {trustControls.map((control) => (
            <TrustControlRow key={control.id} control={control} />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function SupplierOperatingConsole({
  activatedOpportunityIds,
  catalogActionState,
  catalogProducts,
  coverageRecords,
  demandGaps,
  deliveryAreas,
  fulfillmentOrders,
  invoices,
  quoteRequests,
  supplierOpportunities,
  supplierSearchDemand,
  sourcingSprints,
  onActivateOpportunity,
  onAdvanceFulfillment,
  onAdjustCatalogPrice,
  onCycleCatalogAvailability,
  onMarkCatalogAvailable,
  onCreateDemandQuote,
  onOpenOrders,
  onPublishDemandSku,
  onToggleDeliveryArea,
  onCreateInvoice,
  onDisputeInvoice,
  onExportInvoice,
  onMarkInvoicePaid,
  onReportFulfillmentIssue,
  onResolveInvoiceBlocker,
  onSendQuote,
}: {
  activatedOpportunityIds: string[];
  catalogActionState: string;
  catalogProducts: Product[];
  coverageRecords: MarketCoverageRecord[];
  demandGaps: DemandGap[];
  deliveryAreas: DeliveryArea[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  invoices: InvoiceRecord[];
  quoteRequests: QuoteRequest[];
  supplierOpportunities: SupplierOpportunity[];
  supplierSearchDemand: SupplierSearchDemand[];
  sourcingSprints: SourcingSprint[];
  onActivateOpportunity: (opportunity: SupplierOpportunity) => void;
  onAdvanceFulfillment: (orderId: string) => void;
  onAdjustCatalogPrice: (productId: string, delta: number) => void;
  onCycleCatalogAvailability: (productId: string) => void;
  onMarkCatalogAvailable: (productId: string) => void;
  onCreateDemandQuote: (demand: SupplierSearchDemand) => void;
  onOpenOrders: () => void;
  onPublishDemandSku: (demand: SupplierSearchDemand) => void;
  onToggleDeliveryArea: (areaId: string) => void;
  onCreateInvoice?: (orderId: string) => void;
  onDisputeInvoice: (invoiceId: string) => void;
  onExportInvoice: (invoiceId: string) => void;
  onMarkInvoicePaid: (invoiceId: string) => void;
  onReportFulfillmentIssue: (orderId: string) => void;
  onResolveInvoiceBlocker: (invoiceId: string) => void;
  onSendQuote: (request: QuoteRequest, override?: SupplierQuoteOverride) => void;
}) {
  const [discountOverrides, setDiscountOverrides] = useState<Record<string, number>>({});
  const availableSkus = catalogProducts.filter((product) => product.stockStatus === "available").length;
  const lowOrUnavailableSkus = catalogProducts.length - availableSkus;
  const openQuotes = quoteRequests.filter((request) => isOpenQuoteStatus(request.status)).length;
  const quotedRequests = quoteRequests.length - openQuotes;
  const fulfillmentValue = fulfillmentOrders.reduce((sum, order) => sum + order.valueEur, 0);
  const fulfillmentNeedsAction = fulfillmentOrders.filter(fulfillmentNeedsActionFor).length;
  const invoicedOrderIds = new Set(invoices.map((invoice) => invoice.orderId).filter(Boolean));
  const invoiceValue = invoices.reduce((sum, invoice) => sum + invoice.amountEur, 0);
  const blockedInvoices = invoices.filter((invoice) => invoice.status === "blocked").length;
  const capacityWatchAreas = deliveryAreas.filter((area) => area.status === "capacity_watch").length;
  const firstWeakSku = catalogProducts.find((product) => product.stockStatus !== "available");
  const firstOpenQuote = quoteRequests.find((request) => isOpenQuoteStatus(request.status));
  const firstCapacityWatchArea = deliveryAreas.find((area) => area.status === "capacity_watch");
  const firstBlockedInvoice = invoices.find((invoice) => invoice.status === "blocked");
  const firstReadyInvoice = invoices.find((invoice) => invoice.status === "ready");
  const firstFulfillmentAction = fulfillmentOrders.find(fulfillmentNeedsActionFor);
  const hottestDemand = supplierSearchDemand
    .filter((demand) => demand.urgency !== "monitor")
    .slice()
    .sort(
      (left, right) =>
        right.avgBudgetEur + right.failedSearches * 3 - (left.avgBudgetEur + left.failedSearches * 3),
    )[0];
  const topUnactivatedOpportunity = supplierOpportunities
    .filter((opportunity) => !activatedOpportunityIds.includes(opportunity.id))
    .slice()
    .sort((left, right) => {
      const urgencyRank = { now: 0, this_week: 1, monitor: 2 };

      return (
        urgencyRank[left.urgency] - urgencyRank[right.urgency] ||
        right.potentialRevenueEur - left.potentialRevenueEur
      );
    })[0];
  const topOpportunity = supplierOpportunities
    .slice()
    .sort((left, right) => right.potentialRevenueEur - left.potentialRevenueEur)[0];
  const catalogReadinessPct = catalogProducts.length
    ? Math.round((availableSkus / catalogProducts.length) * 100)
    : 100;
  const quoteResponsePct = quoteRequests.length
    ? Math.round((quotedRequests / quoteRequests.length) * 100)
    : 100;
  const discountableQuotes: SupplierDiscountCandidate[] = quoteRequests
    .filter((request) => isOpenQuoteStatus(request.status))
    .slice(0, 4)
    .map((request) => {
      const terms = resolveSupplierQuoteTerms(request, catalogProducts);
      const baseline = Number((terms.unitPrice * 1.08).toFixed(2));
      const discountPercent = 6;
      const discountPrice = Number((baseline * (1 - discountPercent / 100)).toFixed(2));
      const savings = Number((baseline - discountPrice).toFixed(2));

      return {
        request,
        baseline,
        discountPercent,
        discountPrice,
        savings,
        currency: terms.priceRange.includes("EUR") ? "EUR" : "BAM",
        unit: terms.priceRange.split("/")[1]?.trim() || parseMarketplaceQuantity(request.quantity).unit,
      };
    });
  const setQuoteDiscount = (requestId: string, nextDiscount: number) => {
    setDiscountOverrides((current) => ({
      ...current,
      [requestId]: Math.max(0, Math.min(40, nextDiscount)),
    }));
  };
  const discountOffers = discountableQuotes.map((offer) => {
    const discountPercent = discountOverrides[offer.request.id] ?? offer.discountPercent;
    const discountPrice = Number((offer.baseline * (1 - discountPercent / 100)).toFixed(2));
    const savings = Number((offer.baseline - discountPrice).toFixed(2));

    return {
      ...offer,
      discountPercent,
      discountPrice,
      savings,
    };
  });
  const fulfillmentFlowPct = fulfillmentOrders.length
    ? Math.round(((fulfillmentOrders.length - fulfillmentNeedsAction) / fulfillmentOrders.length) * 100)
    : 100;
  const financeHandoffPct = invoices.length
    ? Math.round(((invoices.length - blockedInvoices) / invoices.length) * 100)
    : 100;
  const averageDeliveryPerformance = deliveryAreas.length
    ? deliveryAreas.reduce((sum, area) => sum + area.performance, 0) / deliveryAreas.length
    : 100;
  const deliveryCapacityPct = Math.max(
    0,
    Math.round(averageDeliveryPerformance - capacityWatchAreas * 6),
  );
  const supplierHealthScore = Math.round(
    catalogReadinessPct * 0.26 +
      quoteResponsePct * 0.22 +
      fulfillmentFlowPct * 0.22 +
      financeHandoffPct * 0.15 +
      deliveryCapacityPct * 0.15,
  );
  const healthStatusFor = (score: number): SupplierHealthDriver["status"] =>
    score >= 88 ? "ready" : score >= 72 ? "watch" : "critical";
  const supplierHealthDrivers: SupplierHealthDriver[] = [
    {
      id: "catalog",
      label: "Products ready",
      value: `${catalogReadinessPct}%`,
      detail: `${availableSkus}/${catalogProducts.length} products available. Restaurants stop ordering when stock is stale.`,
      status: healthStatusFor(catalogReadinessPct),
      button: firstWeakSku ? "Mark ready" : "Products clear",
      disabled: !firstWeakSku,
      icon: Boxes,
      onAction: () => firstWeakSku && onMarkCatalogAvailable(firstWeakSku.id),
    },
    {
      id: "quote-response",
      label: "Price response",
      value: `${quoteResponsePct}%`,
      detail: `${openQuotes} open price request${openQuotes === 1 ? "" : "s"} still need price, minimum order, or delivery terms.`,
      status: healthStatusFor(quoteResponsePct),
      button: firstOpenQuote ? "Send price" : "Requests clear",
      disabled: !firstOpenQuote,
      icon: Send,
      onAction: () => firstOpenQuote && onSendQuote(firstOpenQuote),
    },
    {
      id: "fulfillment",
      label: "Fulfillment flow",
      value: `${fulfillmentFlowPct}%`,
      detail: `${fulfillmentNeedsAction} order${fulfillmentNeedsAction === 1 ? "" : "s"} need confirmation, packing, dispatch, or issue handling.`,
      status: healthStatusFor(fulfillmentFlowPct),
      button: firstFulfillmentAction ? "Move order" : "Orders clear",
      disabled: !firstFulfillmentAction,
      icon: Truck,
      onAction: () => firstFulfillmentAction && onAdvanceFulfillment(firstFulfillmentAction.id),
    },
    {
      id: "finance",
      label: "Invoice handoff",
      value: `${financeHandoffPct}%`,
      detail: blockedInvoices
        ? `${blockedInvoices} invoice issue${blockedInvoices === 1 ? "" : "s"} can delay restaurant reconciliation.`
        : `${formatEur(invoiceValue)} in invoices are ready or prepared.`,
      status: healthStatusFor(financeHandoffPct),
      button: firstBlockedInvoice ? "Clear issue" : firstReadyInvoice ? "Prepare" : "Invoices clear",
      disabled: !firstBlockedInvoice && !firstReadyInvoice,
      icon: FileSpreadsheet,
      onAction: () =>
        firstBlockedInvoice
          ? onResolveInvoiceBlocker(firstBlockedInvoice.id)
          : firstReadyInvoice
            ? onExportInvoice(firstReadyInvoice.id)
            : undefined,
    },
    {
      id: "delivery",
      label: "Delivery capacity",
      value: `${deliveryCapacityPct}%`,
      detail: capacityWatchAreas
        ? `${capacityWatchAreas} route${capacityWatchAreas === 1 ? "" : "s"} are on capacity watch before new demand is accepted.`
        : `${deliveryAreas.length} delivery area${deliveryAreas.length === 1 ? "" : "s"} are inside capacity plan.`,
      status: healthStatusFor(deliveryCapacityPct),
      button: firstCapacityWatchArea ? "Reopen route" : "Capacity ok",
      disabled: !firstCapacityWatchArea,
      icon: MapPin,
      onAction: () => firstCapacityWatchArea && onToggleDeliveryArea(firstCapacityWatchArea.id),
    },
  ];
  const supplierRevenueCandidates: Array<SupplierRevenueAction | null> = [
    firstOpenQuote
      ? {
          id: `quote-${firstOpenQuote.id}`,
          title: `Price ${firstOpenQuote.product}`,
          detail: `${firstOpenQuote.buyer} needs ${firstOpenQuote.quantity}. Confirm price, minimum order, and delivery window before the restaurant moves on.`,
          metric: supplierSafeQuoteMetric(firstOpenQuote),
          severity: "high",
          button: "Send price",
          icon: Send,
          onAction: () => onSendQuote(firstOpenQuote),
        }
      : null,
    firstFulfillmentAction
      ? {
          id: `fulfillment-${firstFulfillmentAction.id}`,
          title:
            firstFulfillmentAction.status === "needs_confirmation"
              ? `Confirm ${firstFulfillmentAction.buyer}`
              : firstFulfillmentAction.buyer,
          detail: firstFulfillmentAction.nextAction,
          metric: formatEur(firstFulfillmentAction.valueEur),
          severity: firstFulfillmentAction.status === "issue" ? "high" : "watch",
          button:
            firstFulfillmentAction.status === "needs_confirmation"
              ? "Confirm"
              : firstFulfillmentAction.status === "packed"
                ? "Dispatch"
                : "Advance",
          icon: Truck,
          onAction: () => onAdvanceFulfillment(firstFulfillmentAction.id),
        }
      : null,
    firstBlockedInvoice || firstReadyInvoice
      ? {
          id: `invoice-${(firstBlockedInvoice ?? firstReadyInvoice)?.id}`,
          title: firstBlockedInvoice ? "Clear invoice issue" : "Prepare ready invoice",
          detail: firstBlockedInvoice
            ? `${firstBlockedInvoice.buyer} invoice needs correction before the restaurant can reconcile it.`
            : `${firstReadyInvoice?.buyer ?? "Restaurant"} invoice can be prepared for bank transfer.`,
          metric: formatEur((firstBlockedInvoice ?? firstReadyInvoice)?.amountEur ?? 0),
          severity: firstBlockedInvoice ? "high" : "ready",
          button: firstBlockedInvoice ? "Clear issue" : "Prepare",
          icon: FileSpreadsheet,
          onAction: () =>
            firstBlockedInvoice
              ? onResolveInvoiceBlocker(firstBlockedInvoice.id)
              : firstReadyInvoice
                ? onExportInvoice(firstReadyInvoice.id)
                : undefined,
        }
      : null,
    firstWeakSku
      ? {
          id: `sku-${firstWeakSku.id}`,
          title: `Refresh ${firstWeakSku.name}`,
          detail: `${firstWeakSku.category} is ${stockLabels[firstWeakSku.stockStatus].toLowerCase()}. Update availability before restaurants look elsewhere.`,
          metric: `${availableSkus}/${catalogProducts.length} ready`,
          severity: firstWeakSku.stockStatus === "unavailable" ? "high" : "watch",
          button: "Mark ready",
          icon: Boxes,
          onAction: () => onMarkCatalogAvailable(firstWeakSku.id),
        }
      : null,
    topUnactivatedOpportunity
      ? {
          id: `opportunity-${topUnactivatedOpportunity.id}`,
          title: topUnactivatedOpportunity.title,
          detail: topUnactivatedOpportunity.detail,
          metric: formatEur(topUnactivatedOpportunity.potentialRevenueEur),
          severity: topUnactivatedOpportunity.urgency === "now" ? "high" : "watch",
          button: "Activate",
          icon: TrendingUp,
          onAction: () => onActivateOpportunity(topUnactivatedOpportunity),
        }
      : null,
    hottestDemand
      ? {
          id: `demand-${hottestDemand.id}`,
          title: `Serve ${hottestDemand.product}`,
          detail: `${formatCount(hottestDemand.failedSearches)} failed restaurant searches in ${hottestDemand.market}. ${hottestDemand.recommendedAction}`,
          metric: formatEur(hottestDemand.avgBudgetEur),
          severity: hottestDemand.urgency === "now" ? "high" : "watch",
          button: "Quote demand",
          icon: Search,
          onAction: () => onCreateDemandQuote(hottestDemand),
        }
      : null,
    firstCapacityWatchArea
      ? {
          id: `capacity-${firstCapacityWatchArea.id}`,
          title: `${firstCapacityWatchArea.market} capacity`,
          detail: `${firstCapacityWatchArea.days} / ${firstCapacityWatchArea.minimumOrder}. Reopen or tighten route capacity before accepting more demand.`,
          metric: `${firstCapacityWatchArea.performance}%`,
          severity: "watch",
          button: "Reopen route",
          icon: MapPin,
          onAction: () => onToggleDeliveryArea(firstCapacityWatchArea.id),
        }
      : null,
  ];
  const supplierRevenueQueue = supplierRevenueCandidates
    .filter((action): action is SupplierRevenueAction => Boolean(action))
    .slice(0, 6);
  const supplierActions = [
    {
      id: "availability",
      title: "Refresh product availability",
      detail: `${lowOrUnavailableSkus} product${lowOrUnavailableSkus === 1 ? "" : "s"} are low or unavailable. Fresh availability helps restaurants order from you.`,
      metric: `${availableSkus}/${catalogProducts.length} ready`,
      severity: lowOrUnavailableSkus > 2 ? "high" : "watch",
      icon: Boxes,
      button: firstWeakSku ? "Mark ready" : "Products clear",
      disabled: !firstWeakSku,
      onAction: () => firstWeakSku && onMarkCatalogAvailable(firstWeakSku.id),
    },
    {
      id: "quotes",
      title: "Answer price requests",
      detail: `${openQuotes} restaurant request${openQuotes === 1 ? "" : "s"} still need pricing, minimum order, or delivery confirmation.`,
      metric: `${quotedRequests} quoted`,
      severity: openQuotes > 2 ? "high" : "watch",
      icon: Send,
      button: firstOpenQuote ? "Send price" : "Requests clear",
      disabled: !firstOpenQuote,
      onAction: () => firstOpenQuote && onSendQuote(firstOpenQuote),
    },
    {
      id: "capacity",
      title: "Protect delivery capacity",
      detail: capacityWatchAreas
        ? `${capacityWatchAreas} delivery area is near capacity. Update minimum order and dispatch windows before accepting more demand.`
        : "All delivery areas are inside the current capacity plan.",
      metric: `${deliveryAreas.length} areas`,
      severity: capacityWatchAreas ? "watch" : "ready",
      icon: Truck,
      button: firstCapacityWatchArea ? "Reopen route" : "Capacity ok",
      disabled: !firstCapacityWatchArea,
      onAction: () => firstCapacityWatchArea && onToggleDeliveryArea(firstCapacityWatchArea.id),
    },
    {
      id: "finance",
      title: "Clear invoice issues",
      detail: blockedInvoices
        ? `${blockedInvoices} invoice needs correction and can slow restaurant reconciliation.`
        : "Invoices are ready or prepared for bank-transfer handoff.",
      metric: formatEur(invoiceValue),
      severity: blockedInvoices ? "high" : "ready",
      icon: FileSpreadsheet,
      button: firstBlockedInvoice ? "Clear issue" : firstReadyInvoice ? "Prepare invoice" : "Invoices clear",
      disabled: !firstBlockedInvoice && !firstReadyInvoice,
      onAction: () =>
        firstBlockedInvoice
          ? onResolveInvoiceBlocker(firstBlockedInvoice.id)
          : firstReadyInvoice
            ? onExportInvoice(firstReadyInvoice.id)
            : undefined,
    },
  ];

  return (
    <>
      <SupplierWinCommand
        activeOrders={fulfillmentNeedsAction}
        discountOffer={discountOffers[0]}
        hottestDemand={hottestDemand}
        openQuotes={openQuotes}
        primaryAction={supplierRevenueQueue[0]}
        onAdjustDiscount={discountOffers[0] ? (nextDiscount) => setQuoteDiscount(discountOffers[0].request.id, nextDiscount) : undefined}
        onCreateDemandQuote={onCreateDemandQuote}
        onOpenQuotes={onOpenOrders}
        onSendOffer={onSendQuote}
      />

      <SupplierPrivateOfferDesk
        offers={discountOffers}
        onAdjustDiscount={setQuoteDiscount}
        onSendOffer={onSendQuote}
      />

      <SupplierRevenueCommandQueue actions={supplierRevenueQueue} />

      <SupplierHealthLedger score={supplierHealthScore} drivers={supplierHealthDrivers} />

      <section className="supplier-console-grid" aria-label="Supplier operating console">
        <Panel
          title="Products to fix"
          action={catalogActionState}
          help="Supplier-controlled product rows. Update availability, price, and stock so restaurants can trust your offer before requesting."
        >
          <div className="catalog-manager-list">
            {catalogProducts.length ? (
              catalogProducts.slice(0, 5).map((product) => (
                <CatalogProductRow
                  key={product.id}
                  product={product}
                  onAdjustPrice={onAdjustCatalogPrice}
                  onCycleAvailability={onCycleCatalogAvailability}
                />
              ))
            ) : (
              <div className="empty-state compact">
                <Boxes size={18} />
                <strong>Add your first product</strong>
                <span>Upload a price list or save one product so restaurants can ask for a real price.</span>
              </div>
            )}
          </div>
        </Panel>

        <Panel
          title="Price requests"
          action={`${openQuotes} open`}
          help="Restaurant requests waiting for private supplier pricing, minimum order, delivery confirmation, or a question back."
        >
          <div className="quote-list">
            {quoteRequests.length ? (
              quoteRequests.map((request) => (
                <QuoteRequestRow
                  audience="supplier"
                  key={request.id}
                  request={request}
                  onSendQuote={isReadyQuoteStatus(request.status) ? undefined : () => onSendQuote(request)}
                />
              ))
            ) : (
              <div className="empty-state compact">
                <Send size={18} />
                <strong>No restaurant price requests yet</strong>
                <span>Keep products and delivery areas ready so the next request can become an order.</span>
              </div>
            )}
          </div>
        </Panel>

        <Panel
          title="Better price chances"
          action={`${discountOffers.length} chances`}
          help="Send a private better price when it can help win the order. Restaurants compare total price, delivery, minimum order, and reliability."
        >
          <div className="discount-offer-list">
            {discountOffers.length ? (
              discountOffers.map((offer) => {
                const requestQuantity = parseMarketplaceQuantity(offer.request.quantity);
                const quantity = Math.max(1, requestQuantity.quantity || 1);
                const buyerPays = Number((offer.discountPrice * quantity).toFixed(2));
                const buyerSaves = Number((offer.savings * quantity).toFixed(2));
                const winReason =
                  buyerSaves > 0
                    ? `Restaurant sees ${formatCurrencyAmount(buyerSaves, offer.currency)} saved on this request.`
                    : "Restaurant sees a private price and delivery terms before rival replies.";

                return (
                  <article className="discount-offer-row" key={offer.request.id}>
                    <span>
                      <TrendingUp size={17} />
                    </span>
                    <div>
                      <strong>{offer.request.product}</strong>
                      <small>{offer.request.buyer} / {offer.request.quantity}</small>
                      <div className="discount-price-grid" aria-label={`Private offer math for ${offer.request.product}`}>
                        <p>
                          <small>List</small>
                          <b>{formatCurrencyAmount(offer.baseline, offer.currency)}/{offer.unit}</b>
                        </p>
                        <p className="highlight">
                          <small>Private offer</small>
                          <b>{formatCurrencyAmount(offer.discountPrice, offer.currency)}/{offer.unit}</b>
                        </p>
                        <p>
                          <small>Restaurant saves</small>
                          <b>{formatCurrencyAmount(offer.savings, offer.currency)}/{offer.unit}</b>
                        </p>
                      </div>
                      <div className="discount-outcome-strip" aria-label={`Buyer outcome for ${offer.request.product}`}>
                        <p className="primary">
                          <small>Restaurant pays</small>
                          <b>{formatCurrencyAmount(buyerPays, offer.currency)}</b>
                        </p>
                        <p className="save">
                          <small>You save them</small>
                          <b>{formatCurrencyAmount(buyerSaves, offer.currency)}</b>
                        </p>
                        <p>
                          <small>Needed</small>
                          <b>{offer.request.neededBy}</b>
                        </p>
                      </div>
                      <small className="discount-win-note">{winReason}</small>
                      <div className="discount-control" aria-label={`Discount percent for ${offer.request.product}`}>
                        <button
                          type="button"
                          onClick={() => setQuoteDiscount(offer.request.id, offer.discountPercent - 1)}
                          aria-label={`Lower ${offer.request.product} discount`}
                        >
                          -
                        </button>
                        <strong>{offer.discountPercent}%</strong>
                        <button
                          type="button"
                          onClick={() => setQuoteDiscount(offer.request.id, offer.discountPercent + 1)}
                          aria-label={`Raise ${offer.request.product} discount`}
                        >
                          +
                        </button>
                      </div>
                      <div className="discount-preset-row" aria-label={`Quick discounts for ${offer.request.product}`}>
                        {supplierDiscountPresets.map((preset) => (
                          <button
                            className={offer.discountPercent === preset ? "active" : ""}
                            key={preset}
                            type="button"
                            onClick={() => setQuoteDiscount(offer.request.id, preset)}
                          >
                            {preset ? `${preset}%` : "List"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      className="row-action"
                      type="button"
                      title="Send this better private price to the restaurant. Rival suppliers do not see it."
                      onClick={() =>
                        onSendQuote(offer.request, {
                          discountPercent: offer.discountPercent,
                          listPrice: offer.baseline,
                        })
                      }
                    >
                      Send better price
                    </button>
                  </article>
                );
              })
            ) : (
              <div className="empty-state compact">
                <CheckCircle2 size={18} />
                <strong>No better-price chances right now</strong>
                <span>When a restaurant asks for a price, you can send a sharper private offer here.</span>
              </div>
            )}
          </div>
        </Panel>

        <Panel
          title="Restaurants to win"
          action="Open"
          help="Real restaurant demand this supplier can win by updating products, delivery, or price."
        >
          <div className="opportunity-list">
            {supplierOpportunities.map((opportunity) => (
              <SupplierOpportunityRow
                key={opportunity.id}
                isActivated={activatedOpportunityIds.includes(opportunity.id)}
                opportunity={opportunity}
                onActivate={() => onActivateOpportunity(opportunity)}
              />
            ))}
          </div>
        </Panel>

        <Panel
          title="Product readiness"
          action={`${lowOrUnavailableSkus} need updates`}
          help="Product readiness, price response, customer opportunities, and order value."
        >
          <div className="catalog-health-grid">
            <PostureCard
              icon={PackageCheck}
              label="Products ready"
              value={`${availableSkus}/${catalogProducts.length}`}
              note={`${lowOrUnavailableSkus} products low or unavailable`}
            />
            <PostureCard
              icon={Clock3}
              label="Price replies"
              value={`${quotedRequests}/${quoteRequests.length}`}
              note={`${openQuotes} restaurant requests still open`}
            />
            <PostureCard
              icon={TrendingUp}
              label="Demand upside"
              value={formatEur(topOpportunity?.potentialRevenueEur ?? 0)}
              note={topOpportunity ? topOpportunity.action : "No active demand action"}
            />
            <PostureCard
              icon={CheckCircle2}
              label="Orders value"
              value={formatEur(fulfillmentValue)}
              note={`${fulfillmentNeedsAction} orders need supplier action`}
            />
          </div>
        </Panel>

        <Panel
          title="Orders to confirm"
          action={`${supplierActions.length} checks`}
          help="The next orders, prices, invoices, products, and delivery limits that need attention."
        >
          <div className="supplier-action-list">
            {supplierActions.map((action) => (
              <SupplierActionRow key={action.id} action={action} />
            ))}
          </div>
        </Panel>
      </section>

      <SupplierDemandIntelligence
        coverageRecords={coverageRecords}
        demandGaps={demandGaps}
        sourcingSprints={sourcingSprints}
        supplierSearchDemand={supplierSearchDemand}
        onCreateDemandQuote={onCreateDemandQuote}
        onPublishDemandSku={onPublishDemandSku}
      />

      <section className="supplier-ops-grid">
        <Panel
          title="Orders to confirm"
          action="Today"
          help="Orders that need supplier confirmation, packing, dispatch, or tracking updates before the restaurant has to chase."
        >
          <div className="fulfillment-list">
            {fulfillmentOrders.length ? (
              fulfillmentOrders.map((order) => (
                <SupplierFulfillmentRow
                  key={order.id}
                  order={order}
                  hasInvoice={invoicedOrderIds.has(order.buyerOrderId ?? order.id)}
                  onAdvance={() => onAdvanceFulfillment(order.id)}
                  onCreateInvoice={onCreateInvoice ? () => onCreateInvoice(order.id) : undefined}
                  onReportIssue={() => onReportFulfillmentIssue(order.id)}
                />
              ))
            ) : (
              <div className="empty-state compact">
                <Truck size={18} />
                <strong>No orders to confirm yet</strong>
                <span>Respond to price requests and keep delivery capacity current so orders can arrive cleanly.</span>
              </div>
            )}
          </div>
        </Panel>

        <Panel
          title="Invoice status"
          action="Match and export"
          help="Invoices tied to orders so restaurants can match, dispute, export, or mark paid through their bank."
        >
          <div className="invoice-list">
            {invoices.length ? (
              invoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  onDispute={() => onDisputeInvoice(invoice.id)}
                  onExport={() => onExportInvoice(invoice.id)}
                  onMarkPaid={() => onMarkInvoicePaid(invoice.id)}
                  onResolveBlocker={() => onResolveInvoiceBlocker(invoice.id)}
                />
              ))
            ) : (
              <div className="empty-state compact">
                <FileSpreadsheet size={18} />
                <strong>No invoices waiting</strong>
                <span>After delivery, create an invoice from the order so the restaurant can match it.</span>
              </div>
            )}
          </div>
        </Panel>

        <Panel
          title="Delivery areas"
          action="Capacity"
          help="Places this supplier can deliver to, and which routes are too full for more orders."
        >
          <div className="delivery-area-list">
            {deliveryAreas.length ? (
              deliveryAreas.map((area) => (
                <DeliveryAreaRow
                  key={area.id}
                  area={area}
                  onToggleCapacity={() => onToggleDeliveryArea(area.id)}
                />
              ))
            ) : (
              <div className="empty-state compact">
                <MapPin size={18} />
                <strong>Add a delivery area</strong>
                <span>Restaurants need to know where you deliver before they send quote requests or orders.</span>
              </div>
            )}
          </div>
        </Panel>
      </section>
    </>
  );
}

function SupplierDemandIntelligence({
  coverageRecords,
  demandGaps,
  sourcingSprints,
  supplierSearchDemand,
  onCreateDemandQuote,
  onPublishDemandSku,
}: {
  coverageRecords: MarketCoverageRecord[];
  demandGaps: DemandGap[];
  sourcingSprints: SourcingSprint[];
  supplierSearchDemand: SupplierSearchDemand[];
  onCreateDemandQuote: (demand: SupplierSearchDemand) => void;
  onPublishDemandSku: (demand: SupplierSearchDemand) => void;
}) {
  const urgentCoverage = coverageRecords.filter((record) => record.risk !== "healthy");
  const totalDemandAtRisk = demandGaps.reduce((sum, gap) => sum + gap.revenueAtRiskEur, 0);
  const hotDemand = supplierSearchDemand.filter((demand) => demand.urgency !== "monitor");
  const supplierPricingRules = coldStartPricingRules.filter((rule) => rule.audience === "supplier");

  return (
    <section className="supplier-intel-grid" aria-label="Restaurant demand for supplier">
      <Panel
        title="Restaurant leads"
        action={`${hotDemand.length} hot`}
        help="Products restaurants searched for but could not buy cleanly. Send a price or add the product so restaurants can order from you."
      >
        <div className="supplier-search-demand-list">
          {supplierSearchDemand.map((demand) => (
            <SupplierSearchDemandRow
              key={demand.id}
              demand={demand}
              onCreateQuote={() => onCreateDemandQuote(demand)}
              onPublishSku={() => onPublishDemandSku(demand)}
            />
          ))}
        </div>
      </Panel>

      <Panel
        title="Restaurant demand radar"
        action={formatEur(totalDemandAtRisk)}
        help="Restaurant searches and unavailable-order value that suppliers can turn into new revenue."
      >
        <div className="demand-signal-list">
          {demandGaps.map((gap) => (
            <SupplierDemandSignalRow key={gap.id} gap={gap} />
          ))}
        </div>
      </Panel>

      <Panel
        title="Supplier pricing logic"
        action="Demand first"
        help="Supplier billing should come after proof: confirmed demand, qualified leads, order volume, visibility, and automation."
      >
        <div className="supplier-pricing-list">
          {supplierPricingRules.map((rule) => (
            <article key={rule.id} className={rule.status}>
              <span>{rule.status === "free_now" ? "Free now" : "Paid later"}</span>
              <strong>{rule.title}</strong>
              <p>{rule.basis}</p>
              <small>{rule.trigger}</small>
            </article>
          ))}
        </div>
      </Panel>

      <Panel
          title="Where restaurants need suppliers"
          action={`${urgentCoverage.length} weak areas`}
          help="Cities and categories where restaurants need more suppliers. Use this to find new business."
      >
        <div className="coverage-list">
          {urgentCoverage.map((record) => (
            <CoverageGapRow key={record.id} record={record} />
          ))}
        </div>
      </Panel>

      <Panel
          title="New business pushes"
          action="Open"
          help="Focused pushes to win restaurant demand in a specific city and category."
      >
        <div className="sprint-list">
          {sourcingSprints.map((sprint) => (
            <SourcingSprintRow key={sprint.id} sprint={sprint} />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function SupplierRevenueCommandQueue({ actions }: { actions: SupplierRevenueAction[] }) {
  const highCount = actions.filter((action) => action.severity === "high").length;

  if (!actions.length) {
    return null;
  }

  return (
    <section className="supplier-revenue-grid" aria-label="Supplier new business">
      <Panel
        title="Orders you can win"
        action={highCount ? `${highCount} urgent` : `${actions.length} ready`}
        help="The next restaurant request, lead, product fix, order, invoice, or delivery limit that can turn into sales."
      >
        <div className="supplier-revenue-list">
          {actions.map((action) => (
            <SupplierRevenueActionRow action={action} key={action.id} />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function SupplierWinCommand({
  activeOrders,
  discountOffer,
  hottestDemand,
  openQuotes,
  primaryAction,
  onAdjustDiscount,
  onCreateDemandQuote,
  onOpenQuotes,
  onSendOffer,
}: {
  activeOrders: number;
  discountOffer?: SupplierDiscountCandidate;
  hottestDemand?: SupplierSearchDemand;
  openQuotes: number;
  primaryAction?: SupplierRevenueAction;
  onAdjustDiscount?: (discountPercent: number) => void;
  onCreateDemandQuote: (demand: SupplierSearchDemand) => void;
  onOpenQuotes: () => void;
  onSendOffer: (request: QuoteRequest, override?: SupplierQuoteOverride) => void;
}) {
  const discountQuantity = discountOffer
    ? Math.max(1, parseMarketplaceQuantity(discountOffer.request.quantity).quantity || 1)
    : 0;
  const discountBuyerPays = discountOffer
    ? Number((discountOffer.discountPrice * discountQuantity).toFixed(2))
    : 0;
  const discountBuyerSaves = discountOffer
    ? Number((discountOffer.savings * discountQuantity).toFixed(2))
    : 0;
  const discountUnitLabel = discountOffer ? `${discountOffer.currency}/${discountOffer.unit}` : "";
  const discountWinReason = discountOffer
    ? `${discountOffer.discountPercent}% below list. Private to this restaurant.`
    : "";
  const primaryLabel = discountOffer
    ? "Send better price"
    : primaryAction
      ? primaryAction.button
      : hottestDemand
        ? "Price this lead"
        : "Open price requests";
  const headline = discountOffer
    ? `Win ${discountOffer.request.buyer}.`
    : primaryAction
      ? primaryAction.title
      : hottestDemand
        ? `Restaurants need ${hottestDemand.product}.`
        : "Respond, confirm, invoice.";
  const winSteps = [
    {
      label: "Send price",
      detail: discountOffer ? `${discountOffer.discountPercent}% private` : "Price + delivery",
    },
    {
      label: "Confirm order",
      detail: "Accept + pack",
    },
    {
      label: "Send invoice",
      detail: "Match + resolve",
    },
  ];
  const runPrimaryAction = discountOffer
    ? () =>
        onSendOffer(discountOffer.request, {
          discountPercent: discountOffer.discountPercent,
          listPrice: discountOffer.baseline,
        })
    : primaryAction
      ? primaryAction.onAction
      : hottestDemand
      ? () => onCreateDemandQuote(hottestDemand)
      : onOpenQuotes;

  return (
    <section className="supplier-win-command" aria-label="Supplier win orders">
      <div className="supplier-win-copy">
        <span>Today</span>
        <h2>{headline}</h2>
        <div className="supplier-win-flow" aria-label="Supplier order win flow">
          {winSteps.map((step, index) => (
            <p key={step.label}>
              <b>{index + 1}</b>
              <span>{step.label}</span>
              <small>{step.detail}</small>
            </p>
          ))}
        </div>
        {discountOffer ? (
          <>
            <div className="supplier-win-offer" aria-label={`Private offer summary for ${discountOffer.request.product}`}>
              <p>
                <span>Restaurant</span>
                <strong>{discountOffer.request.buyer}</strong>
              </p>
              <p>
                <span>Item</span>
                <strong>{discountOffer.request.product}</strong>
              </p>
              <p className="pay">
                <span>Restaurant pays</span>
                <strong>{formatMoney(discountBuyerPays)} {discountOffer.currency}</strong>
              </p>
              <p className="save">
                <span>Restaurant saves</span>
                <strong>{formatMoney(discountBuyerSaves)} {discountOffer.currency}</strong>
              </p>
              <p>
                <span>Needed</span>
                <strong>{discountOffer.request.neededBy}</strong>
              </p>
            </div>
            <div className="supplier-win-discount-control" aria-label={`Set private discount for ${discountOffer.request.product}`}>
              <button
                type="button"
                onClick={() => onAdjustDiscount?.(discountOffer.discountPercent - 1)}
                aria-label={`Lower ${discountOffer.request.product} discount`}
              >
                -
              </button>
              <p>
                <span>Private discount</span>
                <strong>{discountOffer.discountPercent}%</strong>
              </p>
              <button
                type="button"
                onClick={() => onAdjustDiscount?.(discountOffer.discountPercent + 1)}
                aria-label={`Raise ${discountOffer.request.product} discount`}
              >
                +
              </button>
            </div>
            <div className="supplier-win-preset-row" aria-label={`Quick private discounts for ${discountOffer.request.product}`}>
              {supplierDiscountPresets.map((preset) => (
                <button
                  className={discountOffer.discountPercent === preset ? "active" : ""}
                  key={preset}
                  type="button"
                  onClick={() => onAdjustDiscount?.(preset)}
                >
                  {preset ? `${preset}%` : "List price"}
                </button>
              ))}
            </div>
            <div className="supplier-win-price-story" aria-label={`Private price outcome for ${discountOffer.request.product}`}>
              <p>
                <span>List price</span>
                <strong>{formatMoney(discountOffer.baseline)} {discountUnitLabel}</strong>
              </p>
              <p>
                <span>Your price</span>
                <strong>{formatMoney(discountOffer.discountPrice)} {discountUnitLabel}</strong>
              </p>
              <p className="save">
                <span>Saved per unit</span>
                <strong>{formatMoney(discountOffer.savings)} {discountUnitLabel}</strong>
              </p>
              <p className="reason">
                <span>Why it can win</span>
                <strong>{discountWinReason}</strong>
              </p>
            </div>
          </>
        ) : null}
      </div>
      <div className="supplier-win-metrics" aria-label="Supplier revenue status">
        <p>
          <span>Price requests</span>
          <strong>{openQuotes}</strong>
        </p>
        <p className={discountOffer ? "highlight" : ""}>
          <span>Better price</span>
          <strong>
            {discountOffer
              ? `${discountOffer.discountPercent}%`
              : "Only when needed"}
          </strong>
        </p>
        <p>
          <span>Orders to move</span>
          <strong>{activeOrders}</strong>
        </p>
      </div>
      <div className="supplier-win-actions">
        <button
          className="primary-action"
          type="button"
          onClick={runPrimaryAction}
        >
          {primaryLabel}
            {discountOffer ? <TrendingUp size={18} /> : primaryAction ? <ChevronRight size={18} /> : <Send size={18} />}
        </button>
        {discountOffer ? (
          <small>
            Private offer.
          </small>
        ) : primaryAction ? (
          <small>{primaryAction.detail}</small>
        ) : (
          <small>Price, confirm, invoice.</small>
        )}
      </div>
    </section>
  );
}

function SupplierRevenueActionRow({ action }: { action: SupplierRevenueAction }) {
  const Icon = action.icon;

  return (
    <article className={`supplier-revenue-row ${action.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{action.title}</strong>
        <small>{action.detail}</small>
      </div>
      <b>{action.metric}</b>
      <button
        className="row-action"
        type="button"
        onClick={action.onAction}
        title={`${action.button}: ${action.title}`}
      >
        {action.button}
        <ChevronRight size={15} />
      </button>
    </article>
  );
}

function SupplierHealthLedger({
  drivers,
  score,
}: {
  drivers: SupplierHealthDriver[];
  score: number;
}) {
  const status = score >= 88 ? "ready" : score >= 72 ? "watch" : "critical";
  const statusCopy =
    status === "ready"
      ? "Ready"
      : status === "watch"
        ? "Needs fixing"
        : "Losing orders";

  return (
    <section className={`supplier-health-ledger ${status}`} aria-label="Supplier readiness ledger">
      <div className="supplier-health-head">
        <div>
          <span>Can restaurants order?</span>
          <strong>{score}% usable</strong>
          <small>{statusCopy}. Fix anything that stops a restaurant from ordering: products, prices, delivery, invoices, or order status.</small>
        </div>
        <b>{statusCopy}</b>
      </div>
      <div className="supplier-health-list">
        {drivers.map((driver) => (
          <article className={driver.status} key={driver.id}>
            <span>
              <driver.icon size={16} />
            </span>
            <div>
              <strong>{driver.label}</strong>
              <small>{driver.detail}</small>
            </div>
            <b>{driver.value}</b>
            <button
              className="row-action"
              type="button"
              disabled={driver.disabled}
              onClick={driver.onAction}
              title={driver.disabled ? `${driver.label} is clear` : driver.detail}
            >
              {driver.button}
              {driver.disabled ? <CheckCircle2 size={15} /> : <ChevronRight size={15} />}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function SupplierOrdersView({
  fulfillmentOrders,
  invoices,
  quoteRequests,
  onAdvanceFulfillment,
  onCreateInvoice,
  onDisputeInvoice,
  onExportInvoice,
  onMarkInvoicePaid,
  onReportFulfillmentIssue,
  onResolveInvoiceBlocker,
  onSendQuote,
}: {
  fulfillmentOrders: SupplierFulfillmentOrder[];
  invoices: InvoiceRecord[];
  quoteRequests: QuoteRequest[];
  onAdvanceFulfillment: (orderId: string) => void;
  onCreateInvoice?: (orderId: string) => void;
  onDisputeInvoice: (invoiceId: string) => void;
  onExportInvoice: (invoiceId: string) => void;
  onMarkInvoicePaid: (invoiceId: string) => void;
  onReportFulfillmentIssue: (orderId: string) => void;
  onResolveInvoiceBlocker: (invoiceId: string) => void;
  onSendQuote: (request: QuoteRequest) => void;
}) {
  const activeOrders = fulfillmentOrders.filter(fulfillmentNeedsActionFor).length;
  const openQuotes = quoteRequests.filter((request) => isOpenQuoteStatus(request.status)).length;
  const quoteSlaSignals = quoteRequests
    .slice()
    .sort((left, right) => supplierQuoteSlaPriority(left) - supplierQuoteSlaPriority(right))
    .slice(0, 4)
    .map(supplierQuoteSlaSignalFor);
  const invoicedOrderIds = new Set(invoices.map((invoice) => invoice.orderId).filter(Boolean));

  return (
    <section className="supplier-ops-grid" aria-label="Supplier order operations">
      <Panel
        title="Price requests"
        action={`${openQuotes} waiting`}
        help="Restaurants waiting for price, minimum order, delivery window, or invoice terms."
      >
        <div className="quote-sla-list">
          {quoteSlaSignals.length ? (
            quoteSlaSignals.map((signal) => (
              <SupplierQuoteSlaRow
                key={signal.request.id}
                signal={signal}
                onSendQuote={() => onSendQuote(signal.request)}
              />
            ))
          ) : (
            <div className="empty-state compact">
              <Send size={18} />
              <strong>No prices waiting</strong>
              <span>Keep products available so restaurant requests can be answered quickly.</span>
            </div>
          )}
        </div>
      </Panel>

      <Panel
        title="Fulfillment board"
        action={`${activeOrders} need action`}
        help="Supplier-side order queue. Move each order through confirmation, packing, and dispatch."
      >
        <div className="fulfillment-list">
          {fulfillmentOrders.length ? (
            fulfillmentOrders.map((order) => (
              <SupplierFulfillmentRow
                key={order.id}
                order={order}
                hasInvoice={invoicedOrderIds.has(order.buyerOrderId ?? order.id)}
                onAdvance={() => onAdvanceFulfillment(order.id)}
                onCreateInvoice={onCreateInvoice ? () => onCreateInvoice(order.id) : undefined}
                onReportIssue={() => onReportFulfillmentIssue(order.id)}
              />
            ))
          ) : (
            <div className="empty-state compact">
              <Truck size={18} />
              <strong>No supplier orders yet</strong>
              <span>Send prices and keep catalog coverage ready so restaurants can place orders.</span>
            </div>
          )}
        </div>
      </Panel>

      <Panel
        title="Price requests"
        action={`${openQuotes} open`}
        help="Restaurant price requests that need your response before the restaurant decides."
      >
        <div className="quote-list">
          {quoteRequests.length ? (
            quoteRequests.map((request) => (
              <QuoteRequestRow
                audience="supplier"
                key={request.id}
                request={request}
                onSendQuote={isReadyQuoteStatus(request.status) ? undefined : () => onSendQuote(request)}
              />
            ))
          ) : (
            <div className="empty-state compact">
              <Send size={18} />
              <strong>No restaurant requests yet</strong>
              <span>Add products and delivery areas so restaurants can request prices from you.</span>
            </div>
          )}
        </div>
      </Panel>

      <Panel
        title="Invoices and delivery"
        action="Finance-ready"
        help="Finance and delivery records tied to supplier fulfillment work."
      >
        <div className="invoice-list">
          {invoices.length ? (
            invoices.map((invoice) => (
              <InvoiceRow
                key={invoice.id}
                invoice={invoice}
                onDispute={() => onDisputeInvoice(invoice.id)}
                onExport={() => onExportInvoice(invoice.id)}
                onMarkPaid={() => onMarkInvoicePaid(invoice.id)}
                onResolveBlocker={() => onResolveInvoiceBlocker(invoice.id)}
              />
            ))
          ) : (
            <div className="empty-state compact">
              <FileSpreadsheet size={18} />
              <strong>No invoices yet</strong>
              <span>Create an invoice from a delivered order so matching and issue handling can start.</span>
            </div>
          )}
        </div>
      </Panel>
    </section>
  );
}

function ExternalDiscoveryView({
  actionState,
  branches,
  conversionTasks,
  demandGaps,
  discoverySources,
  inquiry,
  inquiryLog,
  records,
  role,
  runs,
  selectedBranch,
  onAdvanceRecord,
  onAdvanceConversionTask,
  onOfferCoverage,
  onGenerateInquiry,
  onMarkInquiryReply,
  onSelectBranch,
  onRunSource,
  onSubmitPublicSource,
}: {
  actionState: string;
  branches: BranchAccount[];
  conversionTasks: SupplierConversionTask[];
  demandGaps: DemandGap[];
  discoverySources: DiscoverySource[];
  inquiry: string;
  inquiryLog: ExternalInquiryRecord[];
  records: ExternalSupplierRecord[];
  role: Role;
  runs: DiscoveryRunRecord[];
  selectedBranch: BranchAccount;
  onAdvanceRecord: (record: ExternalSupplierRecord) => void;
  onAdvanceConversionTask: (taskId: string) => void;
  onOfferCoverage: (gap: DemandGap) => void;
  onGenerateInquiry: (record: ExternalSupplierRecord, request?: Partial<ExternalInquiryDraft>) => void;
  onMarkInquiryReply: (inquiryId: string) => Promise<void>;
  onSelectBranch: (branchId: string) => void;
  onRunSource: (source: DiscoverySource) => void;
  onSubmitPublicSource: (input: SubmitPublicSupplierSourceInput) => Promise<void>;
}) {
  const isAdmin = role === "admin";
  const isSupplier = role === "supplier";
  const supplierAction = actionState.includes("coverage") ? "New lead saved" : `${demandGaps.length} leads`;
  const recordAction = isAdmin ? actionState : `${records.length} options`;
  const [selectedInquiryRecordId, setSelectedInquiryRecordId] = useState(records[0]?.id ?? "");
  const selectedInquiryRecord =
    records.find((record) => record.id === selectedInquiryRecordId) ?? records[0];
  const [inquiryDraft, setInquiryDraft] = useState<ExternalInquiryDraft>(() =>
    defaultExternalInquiryDraft(records[0], selectedBranch),
  );
  const [publicSourceUrl, setPublicSourceUrl] = useState("");
  const [publicSourceMarket, setPublicSourceMarket] = useState(selectedBranch.market);
  const [publicSourceType, setPublicSourceType] = useState("Public catalog page");
  const [publicSourceState, setPublicSourceState] = useState("Ready");
  const missingInquiryFields = missingExternalInquiryFields(inquiryDraft);
  const inquiryReady = Boolean(selectedInquiryRecord) && missingInquiryFields.length === 0;
  const verificationRank: Record<ExternalVerificationAction["severity"], number> = {
    critical: 0,
    watch: 1,
    ready: 2,
  };
  const verificationActions: ExternalVerificationAction[] = records
    .map((record) => {
      const matchedDemand = demandGaps.find((gap) => externalRecordMatchesDemand(record, gap));
      const matchedConversion = conversionTasks.find((task) => task.supplierName === record.name);
      const inquiries = inquiryLog.filter((item) => item.externalSupplierId === record.id);
      const waitingInquiry = inquiries.find((item) => item.status !== "replied");
      const demandValue = matchedDemand?.revenueAtRiskEur ?? matchedConversion?.revenuePotentialEur ?? 0;
      const severity: ExternalVerificationAction["severity"] =
        record.status === "converted"
          ? "ready"
          : record.status === "ready_to_contact" || record.status === "invited" || demandValue > 180000
            ? "critical"
            : "watch";

      return {
        record,
        title: record.name,
        detail: matchedDemand
          ? `${matchedDemand.market} / ${matchedDemand.category}: ${matchedDemand.recommendedAction}`
          : matchedConversion
            ? `${matchedConversion.market}: ${matchedConversion.nextStep}`
            : `${record.source}: ${record.products.join(", ")}`,
        metric: demandValue ? formatEur(demandValue) : `${record.confidence}% confidence`,
        secondary: waitingInquiry
          ? `Waiting reply: ${waitingInquiry.productName}`
          : matchedConversion
            ? `${matchedConversion.stage} / ${matchedConversion.owner}`
            : `${inquiries.length} tracked message${inquiries.length === 1 ? "" : "s"}`,
        severity,
        button:
          record.status === "converted"
            ? "Converted"
            : record.status === "invited"
              ? "Convert"
              : record.status === "ready_to_contact"
                ? "Invite"
                : "Review",
        inquiryButton: selectedInquiryRecord?.id === record.id ? "Selected" : "Load message",
        icon: record.status === "converted" ? CheckCircle2 : matchedDemand ? TrendingUp : RadioTower,
      };
    })
    .sort(
      (left, right) =>
        verificationRank[left.severity] - verificationRank[right.severity] ||
        right.record.confidence - left.record.confidence,
    )
    .slice(0, 6);

  function updateInquiryDraft(field: keyof ExternalInquiryDraft, value: string) {
    setInquiryDraft((draft) => ({ ...draft, [field]: value }));
  }

  function selectInquiryRecord(record: ExternalSupplierRecord) {
    setSelectedInquiryRecordId(record.id);
    setInquiryDraft((draft) => ({
      ...draft,
      productName: record.products[0] ?? draft.productName,
    }));
  }

  function selectInquiryBranch(branchId: string) {
    const branch = branches.find((item) => item.id === branchId);

    onSelectBranch(branchId);

    if (branch) {
      setInquiryDraft((draft) => ({
        ...draft,
        buyerName: branch.name,
        contactDetails: branchInquiryContact(branch),
      }));
    }
  }

  function generateSelectedInquiry() {
    if (selectedInquiryRecord && inquiryReady) {
      onGenerateInquiry(selectedInquiryRecord, inquiryDraft);
    }
  }

  async function submitPublicSource(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!publicSourceUrl.trim()) {
      setPublicSourceState("Paste a public supplier, catalog, or directory URL first.");
      return;
    }

    setPublicSourceState("Fetching and structuring public source");
    await onSubmitPublicSource({
      sourceUrl: publicSourceUrl.trim(),
      market: publicSourceMarket.trim() || selectedBranch.market,
      sourceType: publicSourceType,
    });
    setPublicSourceState("Source submitted. Check supplier records and run history.");
  }

  return (
    <>
      {isAdmin ? (
        <ExternalVerificationCockpit
          actions={verificationActions}
          onAdvanceRecord={onAdvanceRecord}
          onSelectRecord={selectInquiryRecord}
        />
      ) : null}

      {isSupplier ? (
        <section className="external-grid">
          <Panel
            title="Restaurant demand you can cover"
            action={supplierAction}
            help="These are categories where restaurants searched, failed, or hit unavailable stock. Use this to pick demand you can serve."
          >
            <div className="gap-list">
              {demandGaps.map((gap) => (
                <SupplierDemandGapRow
                  key={gap.id}
                  gap={gap}
                  onOffer={() => onOfferCoverage(gap)}
                />
              ))}
            </div>
          </Panel>

          <Panel
            title="Tell restaurants you can supply this"
            action="Supplier growth"
            help="This does not create an order. It makes your supplier account available when restaurants ask for that category."
          >
            <div className="inquiry-box compact">
              <p>
                Pick a restaurant need and click I can supply this. This opens a thread so
                future requests in that category can reach your supplier account.
              </p>
              <ul className="plain-checklist">
                <li>Supplier chooses a category and city they can serve.</li>
                <li>Restaurant requests stay clean: item, quantity, delivery window, contact.</li>
                <li>Matched requests move to Messages or Price Requests for the supplier.</li>
              </ul>
            </div>
          </Panel>
        </section>
      ) : (
        <section className="external-grid">
          <Panel
            title={isAdmin ? "Supplier records to verify" : "Backup supplier options"}
            action={recordAction}
            help={
              isAdmin
                ? "These records come from discovery sources. Admins verify them before invite or conversion."
                : "Pick a backup supplier and Veleo prepares a clear message with item, amount, delivery time, and contact details."
            }
          >
            <div className="external-list">
              {records.map((record) => (
                <ExternalSupplierRow
                  key={record.id}
                  isSelected={selectedInquiryRecord?.id === record.id}
                  record={record}
                  role={role}
                  onAdvance={() => onAdvanceRecord(record)}
                  onGenerate={() => selectInquiryRecord(record)}
                />
              ))}
            </div>
          </Panel>

          <Panel
            title={isAdmin ? "Supplier outreach workspace" : "Message to supplier"}
            action={inquiryReady ? (inquiry ? "Ready" : "Ready to generate") : `Needs ${missingInquiryFields.length || 1}`}
            help={
              isAdmin
                ? "Prepare the supplier outreach message and track the reply."
                : "No rewriting. Confirm the details once, then generate the message to ask this supplier for price, stock, delivery, and minimum order."
            }
          >
            <div className="inquiry-box">
              <div className="inquiry-builder">
                <div className="inquiry-builder-target">
                  <span>Selected supplier</span>
                  <strong>{selectedInquiryRecord?.name ?? "Choose supplier"}</strong>
                  <small>{selectedInquiryRecord?.market ?? "No supplier selected"}</small>
                </div>
                <label>
                  <span>
                    Buyer branch
                    <Tooltip label="Sets the business name and contact line used in the supplier message." />
                  </span>
                  <select value={selectedBranch.id} onChange={(event) => selectInquiryBranch(event.target.value)}>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} / {branch.market}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>
                    Business
                    <Tooltip label="The restaurant, hotel, cafe, or branch name the supplier sees first." />
                  </span>
                  <input
                    value={inquiryDraft.buyerName}
                    onChange={(event) => updateInquiryDraft("buyerName", event.target.value)}
                  />
                </label>
                <label>
                  <span>
                    Product
                    <Tooltip label="The exact item the supplier needs to price and confirm." />
                  </span>
                  <input
                    value={inquiryDraft.productName}
                    onChange={(event) => updateInquiryDraft("productName", event.target.value)}
                  />
                </label>
                <label>
                  <span>
                    Quantity
                    <Tooltip label="Use a clear amount and unit, such as 25 kg, 8 cases, or 300 packs." />
                  </span>
                  <input
                    value={inquiryDraft.quantity}
                    onChange={(event) => updateInquiryDraft("quantity", event.target.value)}
                  />
                </label>
                <label>
                  <span>
                    Needed by
                    <Tooltip label="The delivery deadline or receiving window the supplier must answer against." />
                  </span>
                  <input
                    value={inquiryDraft.neededBy}
                    onChange={(event) => updateInquiryDraft("neededBy", event.target.value)}
                  />
                </label>
                <label>
                  <span>
                    Contact
                    <Tooltip label="The contact line Veleo attaches so the supplier can reply cleanly." />
                  </span>
                  <input
                    value={inquiryDraft.contactDetails}
                    onChange={(event) => updateInquiryDraft("contactDetails", event.target.value)}
                  />
                </label>
                <div className={`inquiry-readiness ${inquiryReady ? "ready" : "blocked"}`}>
                  <strong>{inquiryReady ? "Message ready" : "Finish the message details"}</strong>
                  <small>
                    {inquiryReady
                      ? "Veleo will create a tracked supplier message and a follow-up thread."
                      : `Missing ${missingInquiryFields.join(", ")} before Veleo can generate the message.`}
                  </small>
                </div>
                <button
                  className="primary-action full-width"
                  type="button"
                  onClick={generateSelectedInquiry}
                  disabled={!inquiryReady}
                  title={
                    inquiryReady
                      ? "Generate and track the supplier message"
                      : "Complete the required inquiry fields first"
                  }
                >
                  Generate supplier message
                  <Send size={17} />
                </button>
              </div>
              <div className="inquiry-preview">
                {inquiry ? (
                  <pre>{inquiry}</pre>
                ) : (
                  <p>
                    Select a supplier, confirm product, quantity, delivery time, and contact, then generate
                    the message Veleo will track for you.
                  </p>
                )}
              </div>
              <ExternalInquiryOpsQueue inquiryLog={inquiryLog} onMarkReply={onMarkInquiryReply} />
              <div className="inquiry-timeline">
                <div>
                  <strong>Supplier replies</strong>
                  <span>{inquiryLog.length} tracked message{inquiryLog.length === 1 ? "" : "s"}</span>
                </div>
                {inquiryLog.length ? (
                  <div className="inquiry-timeline-list">
                    {inquiryLog.slice(0, 4).map((item) => (
                      <ExternalInquiryLogRow
                        key={item.id}
                        item={item}
                        onMarkReply={item.status === "replied" ? undefined : () => void onMarkInquiryReply(item.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <p>No backup supplier message has been tracked yet.</p>
                )}
              </div>
            </div>
          </Panel>
        </section>
      )}

      {isAdmin ? (
        <section className="external-ops-grid">
          <Panel
            title="Add public supplier source"
            action={publicSourceState}
            help="Fetch a real public page, extract supplier/product/contact signals, and save it as an external unverified record for review."
          >
            <form className="public-source-form" onSubmit={(event) => void submitPublicSource(event)}>
              <label className="data-field">
                <span>Public URL</span>
                <input
                  value={publicSourceUrl}
                  onChange={(event) => setPublicSourceUrl(event.target.value)}
                  placeholder="https://supplier.example/catalog"
                />
              </label>
              <label className="data-field">
                <span>Market</span>
                <input
                  value={publicSourceMarket}
                  onChange={(event) => setPublicSourceMarket(event.target.value)}
                  placeholder="Sarajevo, BA"
                />
              </label>
              <label className="data-field">
                <span>Source type</span>
                <select
                  value={publicSourceType}
                  onChange={(event) => setPublicSourceType(event.target.value)}
                >
                  <option>Public catalog page</option>
                  <option>Business directory</option>
                  <option>Importer list</option>
                  <option>Search result</option>
                  <option>Manual source</option>
                </select>
              </label>
              <button className="primary-action full-width" type="submit">
                Fetch and structure source
                <RadioTower size={16} />
              </button>
            </form>
          </Panel>

          <Panel
            title="Discovery source runs"
            action="Run or pause"
            help="Source runs are admin-only. They refresh candidate suppliers from directories, catalog pages, and importer lists."
          >
            <div className="discovery-source-run-list">
              {discoverySources.map((source) => (
                <DiscoverySourceRunRow
                  key={source.id}
                  source={source}
                  onRun={() => onRunSource(source)}
                />
              ))}
            </div>
          </Panel>

          <Panel title="Run history" action={`${runs.length} runs`} help="Recent source runs and how many supplier records they created.">
            <div className="discovery-run-list">
              {runs.slice(0, 4).map((run) => (
                <DiscoveryRunRecordRow key={run.id} run={run} />
              ))}
            </div>
          </Panel>

          <Panel title="Conversion pipeline" action="Supplier growth" help="Admin view for turning verified records into real supplier accounts.">
            <div className="conversion-list">
              {conversionTasks.slice(0, 5).map((task) => (
                <SupplierConversionTaskRow
                  key={task.id}
                  onAdvance={() => onAdvanceConversionTask(task.id)}
                  task={task}
                />
              ))}
            </div>
          </Panel>
        </section>
      ) : null}
    </>
  );
}

function ExternalVerificationCockpit({
  actions,
  onAdvanceRecord,
  onSelectRecord,
}: {
  actions: ExternalVerificationAction[];
  onAdvanceRecord: (record: ExternalSupplierRecord) => void;
  onSelectRecord: (record: ExternalSupplierRecord) => void;
}) {
  const criticalCount = actions.filter((action) => action.severity === "critical").length;

  return (
    <section className="external-verification-grid" aria-label="External supplier verification cockpit">
      <Panel
        title="External supplier verification cockpit"
        action={`${criticalCount} urgent / ${actions.length} records`}
        help="Admin work queue for turning public supplier records into buyer inquiries, verified supplier accounts, and conversion tasks."
      >
        <div className="external-verification-list">
          {actions.map((action) => (
            <ExternalVerificationActionRow
              action={action}
              key={action.record.id}
              onAdvance={() => onAdvanceRecord(action.record)}
              onSelect={() => onSelectRecord(action.record)}
            />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function ExternalVerificationActionRow({
  action,
  onAdvance,
  onSelect,
}: {
  action: ExternalVerificationAction;
  onAdvance: () => void;
  onSelect: () => void;
}) {
  const Icon = action.icon;
  const isConverted = action.record.status === "converted";

  return (
    <article className={`external-verification-row ${action.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{action.title}</strong>
        <small>{action.record.market} / {action.record.source}</small>
        <p>{action.detail}</p>
      </div>
      <div>
        <b>{action.metric}</b>
        <small>{action.secondary}</small>
        <StatusPill value={action.record.status === "converted" ? "Verified" : action.record.status} />
      </div>
      <div className="row-button-group external-verification-actions">
        <button type="button" onClick={onSelect} title={`Load ${action.record.name} into the supplier message workspace`}>
          {action.inquiryButton}
          <ExternalLink size={15} />
        </button>
        <button
          type="button"
          onClick={onAdvance}
          disabled={isConverted}
          title={isConverted ? "Supplier is already converted" : `${action.button} ${action.record.name}`}
        >
          {action.button}
          {isConverted ? <CheckCircle2 size={15} /> : <ChevronRight size={15} />}
        </button>
      </div>
    </article>
  );
}

function NetworkIntelView({
  actionState,
  bosniaCoverageTargets,
  conversionTasks,
  coverageRecords,
  demandGaps,
  fulfillmentOrders,
  incidents,
  marketLanes,
  networkSnapshot,
  quoteRequests,
  sourcingSprints,
  trustControls,
  workflowSignals,
  onAdvanceConversionTask,
  onAdvanceFulfillment,
  onAdvanceIncident,
  onAdvanceSprint,
  onOpenCountrySprint,
  onOpenCoverageSprint,
  onSendQuote,
}: {
  actionState: string;
  bosniaCoverageTargets: BosniaCoverageTarget[];
  conversionTasks: SupplierConversionTask[];
  coverageRecords: MarketCoverageRecord[];
  demandGaps: DemandGap[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  incidents: LiveIncident[];
  marketLanes: MarketLane[];
  networkSnapshot: NetworkOperatingSnapshot;
  quoteRequests: QuoteRequest[];
  sourcingSprints: SourcingSprint[];
  trustControls: TrustControl[];
  workflowSignals: WorkflowSignal[];
  onAdvanceConversionTask: (taskId: string) => void;
  onAdvanceFulfillment: (orderId: string) => void;
  onAdvanceIncident: (incidentId: string) => void;
  onAdvanceSprint: (sprintId: string) => void;
  onOpenCountrySprint: (country: OperatingCountry) => void;
  onOpenCoverageSprint: (record: MarketCoverageRecord) => void;
  onSendQuote: (request: QuoteRequest) => void;
}) {
  const { operatingCountries } = networkSnapshot;
  const urgentCoverage = coverageRecords.filter((record) => record.risk !== "healthy");
  const bosniaCoverageRisks = bosniaCoverageTargets.filter((target) => target.risk !== "healthy");
  const activeSprints = sourcingSprints.filter((sprint) => sprint.status !== "resolved");
  const openIncidents = incidents.filter((incident) => incident.status !== "resolved");
  const openConversions = conversionTasks.filter((task) => task.stage !== "converted");
  const quoteStatusRank: Record<QuoteRequest["status"], number> = {
    waiting: 0,
    sent: 1,
    draft: 2,
    quoted: 3,
    accepted: 4,
    closed: 5,
  };
  const openQuoteRequests = quoteRequests
    .filter((request) => isOpenQuoteStatus(request.status))
    .sort((left, right) => quoteStatusRank[left.status] - quoteStatusRank[right.status]);
  const quotedRate = quoteRequests.length
    ? Math.round(((quoteRequests.length - openQuoteRequests.length) / quoteRequests.length) * 100)
    : 100;
  const fulfillmentStatusRank: Record<SupplierFulfillmentOrder["status"], number> = {
    issue: 0,
    needs_confirmation: 1,
    packed: 2,
    in_transit: 3,
    confirmed: 4,
    delivered: 5,
  };
  const activeFulfillmentOrders = fulfillmentOrders
    .filter(fulfillmentNeedsActionFor)
    .sort((left, right) => fulfillmentStatusRank[left.status] - fulfillmentStatusRank[right.status]);
  const fulfillmentIssueCount = fulfillmentOrders.filter((order) => order.status === "issue").length;
  const fulfillmentReadyRate = fulfillmentOrders.length
    ? Math.round(
        (fulfillmentOrders.filter((order) => order.status === "delivered" || order.status === "in_transit").length /
          fulfillmentOrders.length) *
          100,
      )
    : 100;
  const quoteSlaRequest = openQuoteRequests[0];
  const fulfillmentSlaOrder = activeFulfillmentOrders[0];
  const incidentSla = openIncidents[0];
  const conversionSla = openConversions[0];
  const networkSlaActions: NetworkSlaAction[] = [
    {
      id: "network-sla-quotes",
      title: "Price response SLA",
      detail: quoteSlaRequest
        ? `${quoteSlaRequest.buyer} is waiting on ${quoteSlaRequest.product} / ${quoteSlaRequest.quantity}.`
        : "All restaurant price requests have supplier terms attached.",
      metric: `${quotedRate}% quoted`,
      secondary: `${openQuoteRequests.length} open / ${quoteRequests.length} total`,
      severity: openQuoteRequests.length ? (quotedRate < 70 ? "critical" : "watch") : "ready",
      button: quoteSlaRequest ? "Send price" : "Clear",
      icon: Send,
      disabled: !quoteSlaRequest,
      onAction: () => quoteSlaRequest && onSendQuote(quoteSlaRequest),
    },
    {
      id: "network-sla-fulfillment",
      title: "Fulfillment SLA",
      detail: fulfillmentSlaOrder
        ? `${fulfillmentSlaOrder.buyer}: ${fulfillmentSlaOrder.nextAction}`
        : "Supplier fulfillment board has no open operational blocker.",
      metric: `${fulfillmentReadyRate}% moving`,
      secondary: `${activeFulfillmentOrders.length} active / ${fulfillmentIssueCount} issues`,
      severity: fulfillmentIssueCount ? "critical" : activeFulfillmentOrders.length ? "watch" : "ready",
      button: fulfillmentSlaOrder ? "Advance order" : "Clear",
      icon: Truck,
      disabled: !fulfillmentSlaOrder,
      onAction: () => fulfillmentSlaOrder && onAdvanceFulfillment(fulfillmentSlaOrder.id),
    },
    {
      id: "network-sla-incidents",
      title: "Incident SLA",
      detail: incidentSla ? `${incidentSla.market}: ${incidentSla.action}` : "No unresolved incident is blocking market operations.",
      metric: incidentSla ? incidentSla.eta : "Clear",
      secondary: `${openIncidents.length} open incidents`,
      severity: incidentSla?.severity === "critical" ? "critical" : incidentSla ? "watch" : "ready",
      button: incidentSla ? (incidentSla.status === "active" ? "Mitigate" : "Resolve") : "Clear",
      icon: AlertTriangle,
      disabled: !incidentSla,
      onAction: () => incidentSla && onAdvanceIncident(incidentSla.id),
    },
    {
      id: "network-sla-conversions",
      title: "Supplier conversion SLA",
      detail: conversionSla ? `${conversionSla.supplierName}: ${conversionSla.nextStep}` : "Conversion queue is clear.",
      metric: conversionSla ? formatEur(conversionSla.revenuePotentialEur) : "Clear",
      secondary: `${openConversions.length} open conversions`,
      severity: conversionSla?.stage === "review" ? "watch" : conversionSla ? "ready" : "ready",
      button: conversionSla ? (conversionSla.stage === "review" ? "Invite" : "Convert") : "Clear",
      icon: Users,
      disabled: !conversionSla,
      onAction: () => conversionSla && onAdvanceConversionTask(conversionSla.id),
    },
  ];
  const countryCoverageQueue = operatingCountries
    .slice()
    .sort((left, right) => {
      const priorityRank = { priority: 0, watch: 1, healthy: 2 };
      return (
        priorityRank[left.priority] - priorityRank[right.priority] ||
        right.demandIndex - left.demandIndex ||
        left.fillRate - right.fillRate
      );
    });
  const priorityCountryCount = countryCoverageQueue.filter((country) => country.priority !== "healthy").length;
  const marketExpansionActions: MarketExpansionAction[] = countryCoverageQueue.slice(0, 8).map((country) => {
    const normalizedGap = normalizeMarketText(country.topGap);
    const matchedDemand =
      demandGaps.find(
        (gap) => normalizeMarketText(gap.category) === normalizedGap && countryMarketMatches(country, gap.market),
      ) ?? demandGaps.find((gap) => normalizeMarketText(gap.category) === normalizedGap);
    const matchedCoverage =
      coverageRecords.find(
        (record) =>
          normalizeMarketText(record.category) === normalizedGap &&
          countryMarketMatches(country, record.market),
      ) ??
      coverageRecords.find((record) => countryMarketMatches(country, record.market) && record.risk !== "healthy") ??
      coverageRecords.find((record) => normalizeMarketText(record.category) === normalizedGap);
    const matchedSprint = activeSprints.find(
      (sprint) =>
        countryMarketMatches(country, sprint.market) ||
        normalizeMarketText(sprint.category).includes(normalizedGap) ||
        normalizedGap.includes(normalizeMarketText(sprint.category)),
    );
    const matchedConversion = openConversions.find(
      (task) => countryMarketMatches(country, task.market) || normalizeMarketText(task.nextStep).includes(normalizedGap),
    );
    const revenueAtRisk = matchedDemand?.revenueAtRiskEur ?? matchedSprint?.potentialEur ?? 0;
    const severity: MarketExpansionAction["severity"] =
      matchedCoverage?.risk === "critical" || country.priority === "priority" || revenueAtRisk > 180000
        ? "critical"
        : matchedCoverage?.risk === "watch" || country.priority === "watch" || country.demandIndex >= 70
          ? "watch"
          : "ready";
    let button = "Open country";
    let icon: React.ElementType = Globe2;
    let onAction = () => onOpenCountrySprint(country);

    if (matchedCoverage && matchedCoverage.risk !== "healthy") {
      button = "Open coverage";
      icon = Gauge;
      onAction = () => onOpenCoverageSprint(matchedCoverage);
    } else if (matchedSprint) {
      button = matchedSprint.status === "planned" ? "Start sprint" : "Advance sprint";
      icon = Zap;
      onAction = () => onAdvanceSprint(matchedSprint.id);
    } else if (matchedConversion) {
      button = matchedConversion.stage === "review" ? "Invite supplier" : "Convert supplier";
      icon = Users;
      onAction = () => onAdvanceConversionTask(matchedConversion.id);
    }

    return {
      id: `expansion-${country.id}`,
      title: `${country.country} / ${country.topGap}`,
      detail: matchedDemand
        ? `${matchedDemand.market}: ${matchedDemand.recommendedAction}`
        : matchedCoverage
          ? `${matchedCoverage.market}: ${matchedCoverage.nextMove}`
          : country.nextMove,
      metric: matchedDemand ? formatEur(matchedDemand.revenueAtRiskEur) : `${country.fillRate.toFixed(1)}% fill`,
      secondary: matchedCoverage
        ? `${matchedCoverage.suppliersLive}/${matchedCoverage.supplierTarget} suppliers`
        : `${formatCount(country.customers)} customers / ${formatCount(country.suppliers)} suppliers`,
      severity,
      button,
      country,
      icon,
      onAction,
    };
  });
  const severityRank: Record<NetworkHealthAction["severity"], number> = { critical: 0, watch: 1, ready: 2 };
  const coverageActions: NetworkHealthAction[] = urgentCoverage.map((record) => ({
    id: `coverage-${record.id}`,
    title: `${record.market} / ${record.category}`,
    detail: record.nextMove,
    metric: `${record.suppliersLive}/${record.supplierTarget} suppliers`,
    severity: record.risk === "critical" ? "critical" : "watch",
    button: "Open sprint",
    icon: Gauge,
    onAction: () => onOpenCoverageSprint(record),
  }));
  const demandActions: NetworkHealthAction[] = demandGaps.flatMap((gap) => {
      const matchedCoverage =
        coverageRecords.find((record) => coverageRecordMatches(record, gap.market, gap.category)) ??
        coverageRecords.find((record) => normalizeMarketText(record.category) === normalizeMarketText(gap.category));

      if (!matchedCoverage) {
        return [];
      }

      const action: NetworkHealthAction = {
        id: `demand-${gap.id}`,
        title: `${gap.category} demand gap`,
        detail: `${gap.market}: ${gap.recommendedAction}`,
        metric: formatEur(gap.revenueAtRiskEur),
        severity: gap.revenueAtRiskEur > 180000 ? "critical" : "watch",
        button: "Route coverage",
        icon: TrendingUp,
        onAction: () => onOpenCoverageSprint(matchedCoverage),
      };

      return [action];
    });
  const sprintActions: NetworkHealthAction[] = activeSprints.map((sprint) => ({
    id: `sprint-${sprint.id}`,
    title: sprint.title,
    detail: `${sprint.market} / ${sprint.category} / owner ${sprint.owner}`,
    metric: formatEur(sprint.potentialEur),
    severity: sprint.status === "running" ? "watch" : "critical",
    button: sprint.status === "planned" ? "Start" : "Resolve",
    icon: Zap,
    onAction: () => onAdvanceSprint(sprint.id),
  }));
  const conversionActions: NetworkHealthAction[] = openConversions.slice(0, 4).map((task) => ({
    id: `conversion-${task.id}`,
    title: task.supplierName,
    detail: `${task.market}: ${task.nextStep}`,
    metric: formatEur(task.revenuePotentialEur),
    severity: task.stage === "review" ? "watch" : "ready",
    button: task.stage === "review" ? "Invite" : "Convert",
    icon: Users,
    onAction: () => onAdvanceConversionTask(task.id),
  }));
  const incidentActions: NetworkHealthAction[] = openIncidents.map((incident) => ({
    id: `incident-${incident.id}`,
    title: incident.title,
    detail: `${incident.market}: ${incident.action}`,
    metric: incident.severity,
    severity: incident.severity === "critical" ? "critical" : "watch",
    button: incident.status === "active" ? "Mitigate" : "Resolve",
    icon: AlertTriangle,
    onAction: () => onAdvanceIncident(incident.id),
  }));
  const networkActions = [
    ...coverageActions,
    ...demandActions,
    ...sprintActions,
    ...conversionActions,
    ...incidentActions,
  ]
    .sort((left, right) => severityRank[left.severity] - severityRank[right.severity])
    .slice(0, 8);

  return (
    <>
      <NetworkSlaControl actions={networkSlaActions} />

      <MarketExpansionCockpit actions={marketExpansionActions} />

      <MarketHealthActionQueue actions={networkActions} />

      <section className="bosnia-acquisition-grid" aria-label="Bosnia city/category acquisition map">
        <Panel
          title="Bosnia city/category acquisition map"
          action={`${bosniaCoverageRisks.length} gaps`}
          help="Track supplier coverage by city and category: Sarajevo produce, Mostar meat, Tuzla packaging, and Banja Luka beverages. Use this to decide who VELEO calls next."
        >
          <div className="bosnia-coverage-map">
            {bosniaCoverageTargets.map((target) => (
              <BosniaCoverageTargetRow key={target.id} target={target} />
            ))}
          </div>
        </Panel>
      </section>

      <section className="network-intel-grid" aria-label="Market coverage">
        <Panel
          title="Demand gaps"
          action={`${demandGaps.length} categories`}
          help="Markets and categories where buyers could not source enough supply. Use this to decide which supplier coverage matters next."
        >
          <div className="gap-list">
            {demandGaps.map((gap) => (
              <DemandGapRow key={gap.id} gap={gap} />
            ))}
          </div>
        </Panel>

        <Panel
          title="Coverage matrix"
          action={actionState === "Network ready" ? `${urgentCoverage.length} risks` : actionState}
          help="Live supplier coverage by market. Critical and watch rows show where admin should recruit, verify, or activate backup suppliers."
        >
          <div className="coverage-list">
            {coverageRecords.map((record) => (
              <CoverageGapRow
                key={record.id}
                onOpenSprint={record.risk === "healthy" ? undefined : () => onOpenCoverageSprint(record)}
                record={record}
              />
            ))}
          </div>
        </Panel>
      </section>

      <section className="country-ledger-grid" aria-label="Country coverage ledger">
        <Panel
          title="Country coverage ledger"
          action={`${operatingCountries.length} countries`}
          help="Country-level operating model behind the regional rollups: customers, suppliers, fill rate, demand pressure, top gap, and next move."
        >
          <div className="country-ledger-list">
            {countryCoverageQueue.map((country) => (
              <CountryCoverageRow
                key={country.id}
                country={country}
                onOpenSprint={country.priority === "healthy" ? undefined : () => onOpenCountrySprint(country)}
              />
            ))}
          </div>
        </Panel>

        <Panel
          title="Expansion pressure"
          action={`${priorityCountryCount} watches`}
          help="The countries most likely to create missed orders unless Veleo recruits or activates supplier coverage."
        >
          <div className="country-pressure-list">
            {countryCoverageQueue.slice(0, 8).map((country) => (
              <CountryPressureRow
                key={country.id}
                country={country}
                onOpenSprint={country.priority === "healthy" ? undefined : () => onOpenCountrySprint(country)}
              />
            ))}
          </div>
        </Panel>
      </section>

      <section className="network-priority-grid" aria-label="Network priorities">
        <Panel
          title="Sourcing sprints"
          action={`${activeSprints.length} active`}
          help="Short focused campaigns for opening supplier coverage in a market/category with revenue at risk."
        >
          <div className="sprint-list">
            {sourcingSprints.map((sprint) => (
              <SourcingSprintRow
                key={sprint.id}
                onAdvance={() => onAdvanceSprint(sprint.id)}
                sprint={sprint}
              />
            ))}
          </div>
        </Panel>

        <Panel
          title="Onboarding priorities"
          action={`${conversionTasks.length} suppliers`}
          help="Supplier accounts most worth converting next, ranked by market fit, revenue potential, and missing buyer coverage."
        >
          <div className="conversion-list">
            {conversionTasks.slice(0, 5).map((task) => (
              <SupplierConversionTaskRow
                key={task.id}
                onAdvance={() => onAdvanceConversionTask(task.id)}
                task={task}
              />
            ))}
          </div>
        </Panel>

        <Panel
          title="Incident command"
          action={`${incidents.filter((incident) => incident.status !== "resolved").length} open`}
          help="Operations issues that can break fulfillment, pricing, or buyer trust. Resolve these before they become missed orders."
        >
          <div className="incident-list">
            {incidents.map((incident) => (
              <IncidentRow
                key={incident.id}
                incident={incident}
                onAdvance={() => onAdvanceIncident(incident.id)}
              />
            ))}
          </div>
        </Panel>
      </section>

      <PlatformControlRoom
        incidents={incidents}
        marketLanes={marketLanes}
        trustControls={trustControls}
        workflowSignals={workflowSignals}
        onAdvanceIncident={onAdvanceIncident}
      />
    </>
  );
}

function NetworkSlaControl({ actions }: { actions: NetworkSlaAction[] }) {
  const urgentCount = actions.filter((action) => action.severity !== "ready").length;

  return (
    <section className="network-sla-grid" aria-label="Network SLA control">
      <Panel
        title="Network SLA control"
        action={`${urgentCount} watches / ${actions.length} lanes`}
        help="Admin view of the operating promises that protect buyer trust: quote response, fulfillment progress, incident resolution, and supplier conversion."
      >
        <div className="network-sla-list">
          {actions.map((action) => (
            <NetworkSlaActionRow action={action} key={action.id} />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function NetworkSlaActionRow({ action }: { action: NetworkSlaAction }) {
  const Icon = action.icon;

  return (
    <article className={`network-sla-row ${action.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{action.title}</strong>
        <small>{action.detail}</small>
      </div>
      <div>
        <b>{action.metric}</b>
        <small>{action.secondary}</small>
      </div>
      <button
        className="row-action"
        type="button"
        disabled={action.disabled}
        onClick={action.onAction}
        title={action.disabled ? `${action.title} is clear` : `${action.button}: ${action.detail}`}
      >
        {action.button}
        {action.disabled ? <CheckCircle2 size={15} /> : <ChevronRight size={15} />}
      </button>
    </article>
  );
}

function MarketExpansionCockpit({ actions }: { actions: MarketExpansionAction[] }) {
  const criticalCount = actions.filter((action) => action.severity === "critical").length;

  return (
    <section className="market-expansion-grid" aria-label="Market expansion cockpit">
      <Panel
        title="Market expansion cockpit"
        action={`${criticalCount} priority / ${actions.length} markets`}
        help="Country-level control for deciding which market, supplier gap, and category should get coverage work next."
      >
        <div className="market-expansion-list">
          {actions.map((action) => (
            <MarketExpansionActionRow action={action} key={action.id} />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function MarketExpansionActionRow({ action }: { action: MarketExpansionAction }) {
  const Icon = action.icon;

  return (
    <article className={`market-expansion-row ${action.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{action.title}</strong>
        <small>{action.country.region} / {action.country.code}</small>
        <p>{action.detail}</p>
      </div>
      <div>
        <b>{action.metric}</b>
        <small>{action.secondary}</small>
      </div>
      <button
        className="row-action"
        type="button"
        onClick={action.onAction}
        title={`${action.button}: ${action.title}`}
      >
        {action.button}
        <ChevronRight size={15} />
      </button>
    </article>
  );
}

function MarketHealthActionQueue({ actions }: { actions: NetworkHealthAction[] }) {
  const criticalCount = actions.filter((action) => action.severity === "critical").length;

  return (
    <section className="network-action-grid" aria-label="Market health action queue">
      <Panel
        title="Market health action queue"
        action={`${criticalCount} critical / ${actions.length} queued`}
        help="A ranked admin worklist combining coverage gaps, failed demand, sourcing sprints, supplier conversion, and live incidents."
      >
        <div className="network-action-list">
          {actions.map((action) => (
            <NetworkHealthActionRow action={action} key={action.id} />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function NetworkHealthActionRow({ action }: { action: NetworkHealthAction }) {
  const Icon = action.icon;

  return (
    <article className={`network-action-row ${action.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{action.title}</strong>
        <small>{action.detail}</small>
      </div>
      <b>{action.metric}</b>
      <button
        className="row-action"
        type="button"
        onClick={action.onAction}
        title={`${action.button}: ${action.title}`}
      >
        {action.button}
        <ChevronRight size={15} />
      </button>
    </article>
  );
}

function MessagesView({
  role,
  threads,
  onResolveThread,
  onSendFollowUp,
}: {
  role: Role;
  threads: MessageThread[];
  onResolveThread: (threadId: string) => void;
  onSendFollowUp: (threadId: string) => void;
}) {
  const [selectedThreadId, setSelectedThreadId] = useState(threads[0]?.id ?? "");
  const selectedThread = threads.find((thread) => thread.id === selectedThreadId) ?? threads[0];
  const needsReply = threads.filter((thread) => thread.status === "needs_reply").length;
  const waiting = threads.filter((thread) => thread.status === "waiting").length;
  const resolved = threads.filter((thread) => thread.status === "resolved").length;
  const selectedIsResolved = selectedThread?.status === "resolved";
  const followUpBody = selectedThread ? messageFollowUpTemplate(selectedThread, role) : "";
  const messageOperations = threads
    .filter((thread) => thread.status !== "resolved")
    .sort((left, right) => {
      const leftPriority = left.status === "needs_reply" ? 0 : 1;
      const rightPriority = right.status === "needs_reply" ? 0 : 1;
      return leftPriority - rightPriority;
    })
    .slice(0, 4)
    .map(messageOperationFor);
  const actionLabel =
    selectedThread?.status === "needs_reply"
      ? selectedThread.id === "msg-104"
        ? "Approve substitution"
        : "Resolve request"
      : selectedThread?.status === "waiting"
        ? "Mark follow-up sent"
        : "Resolved";

  return (
    <>
      <section className="message-command-grid" aria-label="Message operations">
        <article className={needsReply ? "high" : "ready"}>
          <span>Needs reply</span>
          <strong>{needsReply}</strong>
          <small>Buyer or supplier decision required</small>
        </article>
        <article className={waiting ? "watch" : "ready"}>
          <span>Waiting</span>
          <strong>{waiting}</strong>
          <small>Follow-up or supplier reply pending</small>
        </article>
        <article className="ready">
          <span>Resolved</span>
          <strong>{resolved}</strong>
          <small>Attached to buying history</small>
        </article>
      </section>

      <MessageOperationsQueue
        operations={messageOperations}
        onResolveThread={onResolveThread}
        onSelectThread={setSelectedThreadId}
        onSendFollowUp={onSendFollowUp}
      />

      <section className="messages-grid" aria-label="Messages">
        <Panel
          title="Conversation inbox"
          action={`${threads.length} threads`}
          help="Buyer, supplier, substitution, quote, and external inquiry threads in one operational queue."
        >
          <div className="message-list">
            {threads.map((thread) => (
              <MessageThreadRow
                key={thread.id}
                isSelected={selectedThread?.id === thread.id}
                thread={thread}
                onSelect={() => setSelectedThreadId(thread.id)}
              />
            ))}
          </div>
        </Panel>

        <Panel
          title="Request workspace"
          action={selectedIsResolved ? "Resolved" : "Actionable"}
          help="Each message stays connected to a restaurant request, price, substitution, or external supplier inquiry."
        >
          <div className="message-detail">
            <Inbox size={22} />
            <div>
              <p className="eyebrow">{selectedThread?.role ?? "thread"} / {selectedThread?.market ?? "market"}</p>
              <h2>{selectedThread?.subject ?? "Request workspace"}</h2>
              <p>{selectedThread?.preview ?? "Select a conversation to see the operational context."}</p>
            </div>
            <div className="message-reply-box">
              <span>Generated follow-up</span>
              <p>{followUpBody || "Select a thread to generate the next supplier or buyer follow-up."}</p>
              {selectedThread?.lastAction ? <small>Last sent: {selectedThread.lastAction}</small> : null}
            </div>
            <div className="message-context-grid">
              <p>
                <span>Participant</span>
                <strong>{selectedThread?.participant ?? "None selected"}</strong>
              </p>
              <p>
                <span>Status</span>
                <strong>{selectedThread?.status.replace(/_/g, " ") ?? "No status"}</strong>
              </p>
              <p>
                <span>Updated</span>
                <strong>{selectedThread?.updatedAt ?? "Not available"}</strong>
              </p>
            </div>
            <button
              className="secondary-action"
              type="button"
              onClick={() => selectedThread && onSendFollowUp(selectedThread.id)}
              disabled={!selectedThread || selectedIsResolved}
            >
              Send follow-up
              <Send size={17} />
            </button>
            <button
              className="primary-action"
              type="button"
              onClick={() => selectedThread && onResolveThread(selectedThread.id)}
              disabled={!selectedThread || selectedIsResolved}
            >
              {actionLabel}
              {selectedIsResolved ? <CheckCircle2 size={17} /> : <Send size={17} />}
            </button>
          </div>
        </Panel>
      </section>
    </>
  );
}

function MessageOperationsQueue({
  operations,
  onResolveThread,
  onSelectThread,
  onSendFollowUp,
}: {
  operations: MessageOperationSignal[];
  onResolveThread: (threadId: string) => void;
  onSelectThread: (threadId: string) => void;
  onSendFollowUp: (threadId: string) => void;
}) {
  return (
    <section className="message-ops-queue" aria-label="Message operations queue">
      <div className="message-ops-head">
        <div>
          <strong>Conversation work queue</strong>
          <span>Open requests ranked before the full inbox.</span>
        </div>
        <b>{operations.length} open</b>
      </div>
      {operations.length ? (
        <div className="message-ops-list">
          {operations.map((operation) => (
            <article className={operation.severity} key={operation.thread.id}>
              <span>
                <MessageSquare size={16} />
              </span>
              <div>
                <strong>{operation.title}</strong>
                <small>{operation.detail}</small>
              </div>
              <b>{operation.metric}</b>
              <div className="row-button-group message-actions">
                <button type="button" onClick={() => onSelectThread(operation.thread.id)}>
                  Open
                </button>
                <button type="button" onClick={() => onSendFollowUp(operation.thread.id)}>
                  {operation.primaryAction}
                </button>
                <button type="button" onClick={() => onResolveThread(operation.thread.id)}>
                  {operation.closeAction}
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="message-ops-empty">
          <CheckCircle2 size={17} />
          <span>No open buying conversations need action.</span>
        </div>
      )}
    </section>
  );
}

function CountryCoverageRow({
  country,
  compact = false,
  onOpenSprint,
}: {
  country: OperatingCountry;
  compact?: boolean;
  onOpenSprint?: () => void;
}) {
  return (
    <article className={`country-coverage-row ${country.priority} ${compact ? "compact" : ""}`}>
      <span>{country.code}</span>
      <div>
        <strong>{country.country}</strong>
        <small>{country.region} / gap: {country.topGap}</small>
        {compact ? null : <p>{country.nextMove}</p>}
      </div>
      <div>
        <b>{formatCount(country.customers)}</b>
        <small>{formatCount(country.suppliers)} suppliers</small>
      </div>
      <div>
        <b>{country.fillRate.toFixed(1)}%</b>
        <small>{country.demandIndex} demand</small>
      </div>
      {onOpenSprint ? (
        <button
          className="row-action"
          type="button"
          onClick={onOpenSprint}
          title={`Open sourcing sprint for ${country.country} / ${country.topGap}`}
        >
          Open sprint
          <Zap size={15} />
        </button>
      ) : null}
    </article>
  );
}

function CountryPressureRow({
  country,
  onOpenSprint,
}: {
  country: OperatingCountry;
  onOpenSprint?: () => void;
}) {
  return (
    <article className={`country-pressure-row ${country.priority}`}>
      <div>
        <strong>{country.country}</strong>
        <span>{country.topGap}</span>
      </div>
      <div className="pressure-meter" aria-label={`${country.country} demand pressure ${country.demandIndex}`}>
        <span style={{ width: `${country.demandIndex}%` }} />
      </div>
      <p>{country.nextMove}</p>
      {onOpenSprint ? (
        <button
          className="row-action"
          type="button"
          onClick={onOpenSprint}
          title={`Open country sourcing sprint for ${country.topGap}`}
        >
          Open sprint
          <Zap size={15} />
        </button>
      ) : null}
    </article>
  );
}

function RegionRow({ region }: { region: RegionHealth }) {
  return (
    <article className={`region-row ${region.status}`}>
      <div>
        <strong>{region.region}</strong>
        <span>{region.markets}</span>
      </div>
      <div>
        <b>{formatCount(region.customers)}</b>
        <span>{region.orderVolume}</span>
      </div>
      <div>
        <b>{region.onTimeRate}%</b>
        <span>{region.issueRate}% issue rate</span>
      </div>
    </article>
  );
}

function OrderPreflightCheckRow({ check }: { check: DraftPreflightCheck }) {
  const Icon = check.icon;

  return (
    <article className={`order-preflight-row ${check.status}`}>
      <span>
        <Icon size={15} />
      </span>
      <div>
        <strong>{check.title}</strong>
        <small>{check.detail}</small>
      </div>
      <b>{check.value}</b>
    </article>
  );
}

function AutomationRow({ task }: { task: AutomationTask }) {
  return (
    <article className={`automation-row ${task.status}`}>
      <span>
        {task.status === "running" ? <Zap size={16} /> : <AlertTriangle size={16} />}
      </span>
      <div>
        <strong>{task.title}</strong>
        <small>{task.scope}</small>
      </div>
      <b>{task.volume}</b>
    </article>
  );
}

function IncidentRow({
  incident,
  onAdvance,
}: {
  incident: LiveIncident;
  onAdvance?: () => void;
}) {
  const actionLabel =
    incident.status === "active" ? "Mitigate" : incident.status === "mitigating" ? "Resolve" : "Resolved";

  return (
    <article className={`incident-row ${incident.severity} ${incident.status}`}>
      <span>
        {incident.status === "resolved" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
      </span>
      <div>
        <strong>{incident.title}</strong>
        <small>{incident.market} / {incident.impact}</small>
        <small>{incident.status.replace("_", " ")}</small>
      </div>
      <div>
        <b>{incident.eta}</b>
        <small>{incident.owner}</small>
      </div>
      {onAdvance ? (
        <button
          className="row-action"
          type="button"
          onClick={onAdvance}
          disabled={incident.status === "resolved"}
          title="Move this incident through mitigation and resolution"
        >
          {actionLabel}
          <ChevronRight size={15} />
        </button>
      ) : null}
      <p>{incident.action}</p>
    </article>
  );
}

function MarketLaneRow({ lane }: { lane: MarketLane }) {
  return (
    <article className={`lane-row ${lane.status}`}>
      <div>
        <strong>{lane.market}</strong>
        <span>{lane.segment}</span>
      </div>
      <div>
        <b>{formatEur(lane.gmvEur)}</b>
        <span>{formatCount(lane.customers)} customers</span>
      </div>
      <div>
        <b>{lane.fillRate}%</b>
        <span>fill rate / +{lane.marginLift}% margin</span>
      </div>
      <p>{lane.nextMove}</p>
    </article>
  );
}

function WorkflowRow({ signal }: { signal: WorkflowSignal }) {
  return (
    <article className="workflow-row">
      <div className="workflow-meter" aria-label={`${signal.automation}% automated`}>
        <span style={{ width: `${signal.automation}%` }} />
      </div>
      <div>
        <strong>{signal.name}</strong>
        <small>{signal.stage}</small>
      </div>
      <b>{signal.volume}</b>
      <p>{signal.outcome}</p>
    </article>
  );
}

function TrustControlRow({ control }: { control: TrustControl }) {
  return (
    <article className={`trust-row ${control.status}`}>
      <span>
        {control.status === "ready" ? <ShieldCheck size={16} /> : <LockKeyhole size={16} />}
      </span>
      <div>
        <strong>{control.label}</strong>
        <small>{control.detail}</small>
      </div>
      <b>{control.value}</b>
    </article>
  );
}

function SupplierQuoteSlaRow({
  signal,
  onSendQuote,
}: {
  signal: SupplierQuoteSlaSignal;
  onSendQuote: () => void;
}) {
  const Icon =
    signal.severity === "critical" ? AlertTriangle : signal.severity === "watch" ? Clock3 : CheckCircle2;
  const isQuoted = isReadyQuoteStatus(signal.request.status);

  return (
    <article className={`quote-sla-row ${signal.severity}`}>
      <span className="quote-sla-icon">
        <Icon size={17} aria-hidden="true" />
      </span>
      <div>
        <strong>{signal.title}</strong>
        <span>
          {signal.request.product} / {signal.request.quantity} / {signal.request.neededBy}
        </span>
        <small>{signal.detail}</small>
      </div>
      <div className="quote-sla-owner">
        <b>{signal.owner}</b>
        <span>{signal.metric}</span>
      </div>
      <StatusPill value={signal.request.status} />
      <button
        className="row-action"
        type="button"
        disabled={isQuoted}
        onClick={onSendQuote}
        title={isQuoted ? "Supplier terms have already been sent" : signal.detail}
      >
        {signal.button}
        {isQuoted ? <CheckCircle2 size={15} /> : <Send size={15} />}
      </button>
    </article>
  );
}

function SupplierPrivateOfferDesk({
  offers,
  onAdjustDiscount,
  onSendOffer,
}: {
  offers: SupplierDiscountCandidate[];
  onAdjustDiscount: (requestId: string, discountPercent: number) => void;
  onSendOffer: (request: QuoteRequest, override?: SupplierQuoteOverride) => void;
}) {
  if (!offers.length) {
    return (
      <section className="supplier-private-desk empty" aria-label="Private offer desk">
        <div>
          <span>Private offers</span>
          <strong>No price requests right now.</strong>
          <p>When a restaurant asks for a price, reply here with price, delivery window, minimum order, and invoice terms.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="supplier-private-desk" aria-label="Private offer desk">
      <div className="supplier-private-head">
        <div>
          <span>Private prices</span>
          <strong>Answer requests with one clear offer.</strong>
        </div>
        <small>
          Only the restaurant sees your offer.
          <Tooltip label="Suppliers do not see competitor prices before quoting. Restaurants compare private offers by landed cost, delivery, minimum order, reliability, and fit." />
        </small>
      </div>
      <div className="supplier-private-list">
        {offers.map((offer) => {
          const requestQuantity = parseMarketplaceQuantity(offer.request.quantity);
          const quantity = Math.max(1, requestQuantity.quantity || 1);
          const buyerPays = Number((offer.discountPrice * quantity).toFixed(2));
          const buyerSaves = Number((offer.savings * quantity).toFixed(2));

          return (
            <article className="supplier-private-card" key={offer.request.id}>
              <div className="supplier-private-request">
                <span>Restaurant</span>
                <strong>{offer.request.buyer}</strong>
                <small>{offer.request.product} / {offer.request.quantity} / {offer.request.neededBy}</small>
              </div>
              <div className="supplier-private-math">
                <p>
                  <span>Restaurant pays</span>
                  <strong>{formatCurrencyAmount(buyerPays, offer.currency)}</strong>
                  <small>{formatCurrencyAmount(offer.discountPrice, offer.currency)}/{offer.unit}</small>
                </p>
                <p className="save">
                  <span>Restaurant saves</span>
                  <strong>{formatCurrencyAmount(buyerSaves, offer.currency)}</strong>
                  <small>{offer.discountPercent}% private discount</small>
                </p>
                <p>
                  <span>Needed by</span>
                  <strong>{offer.request.neededBy}</strong>
                  <small>{offer.request.deliveryWindow ?? "Confirm delivery window"}</small>
                </p>
              </div>
              <div className="supplier-private-control" aria-label={`Private discount for ${offer.request.product}`}>
                <button
                  type="button"
                  onClick={() => onAdjustDiscount(offer.request.id, offer.discountPercent - 1)}
                  aria-label={`Lower ${offer.request.product} discount`}
                >
                  <Minus size={15} />
                </button>
                <b>{offer.discountPercent}%</b>
                <button
                  type="button"
                  onClick={() => onAdjustDiscount(offer.request.id, offer.discountPercent + 1)}
                  aria-label={`Raise ${offer.request.product} discount`}
                >
                  <Plus size={15} />
                </button>
              </div>
              <div className="supplier-private-presets" aria-label={`Quick discounts for ${offer.request.product}`}>
                {supplierDiscountPresets.map((preset) => (
                  <button
                    className={offer.discountPercent === preset ? "active" : ""}
                    key={preset}
                    type="button"
                    onClick={() => onAdjustDiscount(offer.request.id, preset)}
                  >
                    {preset ? `${preset}%` : "List"}
                  </button>
                ))}
              </div>
              <button
                className="primary-action"
                type="button"
                title="Send this private price to the restaurant. Rival suppliers do not see it."
                onClick={() =>
                  onSendOffer(offer.request, {
                    discountPercent: offer.discountPercent,
                    listPrice: offer.baseline,
                  })
                }
              >
                Send better price
                <Send size={16} />
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function SupplierActionRow({
  action,
}: {
  action: {
    title: string;
    detail: string;
    metric: string;
    severity: string;
    icon: React.ElementType;
    button?: string;
    disabled?: boolean;
    onAction?: () => void;
  };
}) {
  const Icon = action.icon;

  return (
    <article className={`supplier-action-row ${action.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{action.title}</strong>
        <small>{action.detail}</small>
      </div>
      <b>{action.metric}</b>
      {action.button ? (
        <button
          className="row-action"
          type="button"
          disabled={action.disabled}
          onClick={action.onAction}
          title={action.disabled ? "No direct action is currently needed" : action.detail}
        >
          {action.button}
          <ChevronRight size={15} />
        </button>
      ) : null}
    </article>
  );
}

function CatalogProductRow({
  product,
  onAdjustPrice,
  onCycleAvailability,
}: {
  product: Product;
  onAdjustPrice: (productId: string, delta: number) => void;
  onCycleAvailability: (productId: string) => void;
}) {
  return (
    <article className={`catalog-manager-row ${product.stockStatus}`}>
      <span>
        <PackageCheck size={17} />
      </span>
      <div>
        <strong>{product.name}</strong>
        <small>
          {product.category} / {product.unit} / {product.supplier}
        </small>
      </div>
      <div>
        <b>{formatMoney(product.price)} KM</b>
        <StockBadge status={product.stockStatus} />
      </div>
      <div className="row-button-group">
        <button type="button" onClick={() => onAdjustPrice(product.id, -0.1)} aria-label={`Lower ${product.name} price`}>
          <Minus size={15} />
        </button>
        <button type="button" onClick={() => onAdjustPrice(product.id, 0.1)} aria-label={`Raise ${product.name} price`}>
          <Plus size={15} />
        </button>
        <button type="button" onClick={() => onCycleAvailability(product.id)}>
          Status
        </button>
      </div>
    </article>
  );
}

function SupplierOpportunityRow({
  isActivated = false,
  opportunity,
  onActivate,
}: {
  isActivated?: boolean;
  opportunity: SupplierOpportunity;
  onActivate?: () => void;
}) {
  return (
    <article className={`opportunity-row ${opportunity.urgency}`}>
      <span>
        <TrendingUp size={17} />
      </span>
      <div>
        <strong>{opportunity.title}</strong>
        <small>{opportunity.detail}</small>
        <p>{opportunity.action}</p>
      </div>
      <b>{formatEur(opportunity.potentialRevenueEur)}</b>
      {onActivate ? (
        <button
          className="row-action"
          type="button"
          disabled={isActivated}
          onClick={onActivate}
          title={
            isActivated
              ? "This opportunity has already been opened for follow-up"
              : "Turn this restaurant demand into a price, product, or lead follow-up"
          }
        >
          {isActivated ? "Activated" : "Activate"}
          {isActivated ? <CheckCircle2 size={15} /> : <Zap size={15} />}
        </button>
      ) : null}
    </article>
  );
}

function SupplierSearchDemandRow({
  demand,
  onCreateQuote,
  onPublishSku,
}: {
  demand: SupplierSearchDemand;
  onCreateQuote: () => void;
  onPublishSku: () => void;
}) {
  return (
    <article className={`supplier-search-demand-row ${demand.urgency}`}>
      <span>
        <Search size={17} />
      </span>
      <div>
        <strong>{demand.product}</strong>
        <small>{demand.category} / {demand.market}</small>
        <p>{demand.recommendedAction}</p>
      </div>
      <div>
        <b>{formatEur(demand.avgBudgetEur)}</b>
        <small>{formatCount(demand.failedSearches)} failed searches</small>
      </div>
      <div className="row-button-group demand-actions">
        <button type="button" onClick={onCreateQuote} title="Create a supplier price request from this restaurant demand">
          Quote
          <ChevronRight size={15} />
        </button>
        <button type="button" onClick={onPublishSku} title="Publish this searched product into the live supplier catalog">
          Publish SKU
          <PackageCheck size={15} />
        </button>
      </div>
    </article>
  );
}

function SupplierDemandSignalRow({ gap }: { gap: DemandGap }) {
  return (
    <article className="demand-signal-row">
      <span>
        <Search size={17} />
      </span>
      <div>
        <strong>{gap.category}</strong>
        <small>{gap.market}</small>
        <p>{gap.recommendedAction}</p>
      </div>
      <div>
        <b>{formatEur(gap.revenueAtRiskEur)}</b>
        <small>{formatCount(gap.failedSearches)} failed searches</small>
      </div>
    </article>
  );
}

function CoverageGapRow({
  onOpenSprint,
  record,
}: {
  onOpenSprint?: () => void;
  record: MarketCoverageRecord;
}) {
  return (
    <article className={`coverage-row ${record.risk}`}>
      <span>
        <Gauge size={17} />
      </span>
      <div>
        <strong>{record.category}</strong>
        <small>{record.market}</small>
        <p>{record.nextMove}</p>
      </div>
      <div>
        <b>{record.suppliersLive}/{record.supplierTarget}</b>
        <small>{record.fillRate.toFixed(1)}% fill rate</small>
      </div>
      {onOpenSprint ? (
        <button
          className="row-action"
          type="button"
          onClick={onOpenSprint}
          title="Open a sourcing sprint for this weak market and category"
        >
          Open sprint
          <Zap size={15} />
        </button>
      ) : null}
    </article>
  );
}

function SourcingSprintRow({
  onAdvance,
  sprint,
}: {
  onAdvance?: () => void;
  sprint: SourcingSprint;
}) {
  const actionLabel =
    sprint.status === "planned" ? "Start" : sprint.status === "running" ? "Resolve" : "Resolved";

  return (
    <article className={`sprint-row ${sprint.status}`}>
      <span>
        <Zap size={17} />
      </span>
      <div>
        <strong>{sprint.title}</strong>
        <small>{sprint.market} / {sprint.owner}</small>
        <p>{sprint.category} coverage due {sprint.due}</p>
      </div>
      <b>{formatEur(sprint.potentialEur)}</b>
      {onAdvance ? (
        <button
          className="row-action"
          type="button"
          onClick={onAdvance}
          disabled={sprint.status === "resolved"}
          title="Move this sourcing sprint to the next operating state"
        >
          {actionLabel}
          <ChevronRight size={15} />
        </button>
      ) : null}
    </article>
  );
}

function SupplierFulfillmentRow({
  hasInvoice = false,
  order,
  onAdvance,
  onCreateInvoice,
  onReportIssue,
}: {
  hasInvoice?: boolean;
  order: SupplierFulfillmentOrder;
  onAdvance: () => void;
  onCreateInvoice?: () => void;
  onReportIssue?: () => void;
}) {
  const actionLabel =
    order.status === "needs_confirmation"
      ? "Confirm"
      : order.status === "confirmed"
        ? "Mark packed"
      : order.status === "packed"
        ? "Dispatch"
        : order.status === "in_transit"
          ? "Mark delivered"
          : order.status === "issue"
            ? "Issue open"
          : "Delivered";
  const canAdvance = order.status !== "delivered" && order.status !== "issue";
  const canReportIssue = Boolean(onReportIssue) && order.status !== "delivered" && order.status !== "issue";
  const canCreateInvoice = Boolean(onCreateInvoice) && !hasInvoice && order.status !== "issue";

  return (
    <article className={`fulfillment-row ${order.status}`}>
      <span>
        <Truck size={17} />
      </span>
      <div>
        <strong>{order.buyer}</strong>
        <small>
          {order.id} / {order.deliveryWindow}
        </small>
        {order.supplierName || order.market ? (
          <small>
            {[order.supplierName, order.market, order.itemCount ? `${order.itemCount} lines` : undefined]
              .filter(Boolean)
              .join(" / ")}
          </small>
        ) : null}
        <p>{order.nextAction}</p>
      </div>
      <div>
        <b>{formatEur(order.valueEur)}</b>
        <StatusPill value={order.status} />
      </div>
      <div className="row-button-group fulfillment-actions">
        <button
          className="row-action"
          type="button"
          disabled={!canAdvance}
          onClick={onAdvance}
          title={
            canAdvance
              ? "Move this order to the next supplier fulfillment status"
              : order.status === "issue"
                ? "Resolve the buyer issue before advancing fulfillment"
                : "Delivered orders are closed"
          }
        >
          {actionLabel}
          <ChevronRight size={15} />
        </button>
        {onReportIssue ? (
          <button
            className="row-action danger"
            type="button"
            disabled={!canReportIssue}
            onClick={onReportIssue}
            title={
              canReportIssue
                ? "Tell the buyer there is a shortage, delay, or substitution issue"
                : "No new issue can be reported for this order status"
            }
          >
            Report issue
            <AlertTriangle size={15} />
          </button>
        ) : null}
        {onCreateInvoice ? (
          <button
            className="row-action secondary"
            type="button"
            disabled={!canCreateInvoice}
            onClick={onCreateInvoice}
            title={
              hasInvoice
                ? "An invoice is already tied to this order"
                : order.status === "issue"
                  ? "Resolve the order issue before sending an invoice"
                  : "Send an invoice from this order so the restaurant can match it before bank transfer"
            }
          >
            {hasInvoice ? "Invoiced" : "Send invoice"}
            <FileSpreadsheet size={15} />
          </button>
        ) : null}
      </div>
    </article>
  );
}

function InvoiceRow({
  invoice,
  ownerLabel,
  relatedIssueCount,
  onDispute,
  onExport,
  onMarkPaid,
  onResolveBlocker,
}: {
  invoice: InvoiceRecord;
  ownerLabel?: string;
  relatedIssueCount?: number;
  onDispute?: () => void;
  onExport?: () => void;
  onMarkPaid?: () => void;
  onResolveBlocker?: () => void;
}) {
  const canDispute = Boolean(onDispute) && invoice.status !== "blocked" && invoice.status !== "paid";
  const canExport =
    Boolean(onExport) && invoice.status === "ready" && (!invoice.paymentReadiness || invoice.paymentReadiness === "ready");
  const canMarkPaid = Boolean(onMarkPaid) && invoice.status === "exported";
  const canResolve = invoice.status === "blocked" && Boolean(onResolveBlocker);
  const matchLabel = invoiceMatchLabel(invoice);
  const issueCount = relatedIssueCount ?? (invoice.status === "blocked" ? 1 : 0);
  const owner =
    ownerLabel ??
    (invoice.status === "blocked"
      ? "Buyer and supplier"
      : invoice.status === "paid"
        ? "Closed"
        : invoice.status === "exported"
          ? "Finance"
          : invoice.orderId
            ? "Finance"
            : "Buyer");
  const nextStep =
    invoice.status === "paid"
      ? "Paid through the bank and closed for this workflow."
      : invoice.status === "blocked"
      ? `Fix before payment: ${invoiceMismatchCopy(invoice)}.`
      : invoice.status === "exported"
        ? "Matched and prepared for bank transfer."
        : invoice.paymentReadiness === "needs_order"
          ? "Attach the order before preparing payment."
        : invoice.orderId
          ? "Check supplier, amount, and order number before preparing payment."
          : "Add the order number before this can be matched.";

  return (
    <article className={`invoice-row ${invoice.status}`}>
      <span>
        <FileSpreadsheet size={17} />
      </span>
      <div>
        <strong>{invoice.supplierName ?? invoice.id}</strong>
        <small>
          {[invoice.id, invoice.buyer, invoice.orderId ?? "No order link"].filter(Boolean).join(" / ")}
        </small>
        {invoice.market ? <small>{invoice.market}</small> : null}
        {invoice.fileUrl ? (
          <small>
            <a href={invoice.fileUrl} target="_blank" rel="noreferrer">
              Open file
            </a>
          </small>
        ) : null}
      </div>
      <div className="invoice-match-summary" aria-label={`Invoice match state for ${invoice.id}`}>
        <p>
          <span>Match</span>
          <b>{matchLabel}</b>
        </p>
        <p>
          <span>Amount</span>
          <b>{formatEur(invoice.amountEur)}</b>
          {invoice.expectedAmountEur !== undefined ? (
            <small>
              Expected {formatEur(invoice.expectedAmountEur)}
              {invoice.amountDeltaEur ? ` / delta ${formatEur(invoice.amountDeltaEur)}` : ""}
            </small>
          ) : null}
        </p>
        <p>
          <span>Due</span>
          <b>{invoice.due}</b>
          {invoice.issuedAt ? <small>{invoice.issuedAt}</small> : null}
        </p>
        <p>
          <span>Owner</span>
          <b>{owner}</b>
          <small>{issueCount ? `${issueCount} open issue${issueCount === 1 ? "" : "s"}` : "No open issues"}</small>
        </p>
        <p>
          <span>Capture</span>
          <b>{invoiceCaptureLabel(invoice)}</b>
          <small>{invoice.orderAttachmentStatus === "attached" ? "Order attached" : "Order missing"}</small>
        </p>
        <p>
          <span>Mismatches</span>
          <b>{invoice.mismatchTypes?.length ?? 0}</b>
          <small>{invoiceMismatchCopy(invoice)}</small>
        </p>
      </div>
      <div className="invoice-next-step">
        <StatusPill value={invoice.status} />
        <small>{nextStep}</small>
      </div>
      <div className="row-button-group invoice-actions">
        {canResolve ? (
          <button
            className="row-action"
            type="button"
            onClick={onResolveBlocker}
            title="Clear the invoice issue and move this invoice back to ready"
          >
            Clear issue
            <CheckCircle2 size={15} />
          </button>
        ) : null}
        {onExport && !canResolve ? (
          <button
            className="row-action"
            type="button"
            disabled={!canExport}
            onClick={onExport}
            title={
              canExport
                ? "Prepare this invoice for bank transfer"
                : invoice.status === "exported"
                  ? "This invoice is prepared. Mark it paid after the bank transfer is complete"
                  : invoice.status === "paid"
                    ? "This invoice is already paid"
                    : "Clear the invoice issue before preparing payment"
            }
          >
            {invoice.status === "exported" ? "Prepared" : invoice.status === "paid" ? "Paid" : "Prepare"}
            <ArrowUpRight size={15} />
          </button>
        ) : null}
        {onMarkPaid ? (
          <button
            className="row-action"
            type="button"
            disabled={!canMarkPaid}
            onClick={onMarkPaid}
            title={
              canMarkPaid
                ? "Mark this invoice paid after the bank transfer is complete"
                : invoice.status === "paid"
                  ? "This invoice is already marked paid"
                  : "Prepare this invoice before marking it paid"
            }
          >
            Mark paid
            <CheckCircle2 size={15} />
          </button>
        ) : null}
        {onDispute ? (
          <button
            className="row-action danger"
            type="button"
            disabled={!canDispute}
            onClick={onDispute}
            title={
              canDispute
                ? "Flag wrong price, missing goods, or a matching problem before payment"
                : invoice.status === "paid"
                  ? "Paid invoices are closed"
                  : "This invoice is already disputed"
            }
          >
            Flag issue
            <AlertTriangle size={15} />
          </button>
        ) : null}
      </div>
    </article>
  );
}

function DeliveryAreaRow({
  area,
  onToggleCapacity,
}: {
  area: DeliveryArea;
  onToggleCapacity?: () => void;
}) {
  return (
    <article className={`delivery-area-row ${area.status}`}>
      <span>
        <MapPin size={17} />
      </span>
      <div>
        <strong>{area.market}</strong>
        <small>
          {area.days} / Minimum {area.minimumOrder}
        </small>
      </div>
      <div>
        <b>{area.performance.toFixed(1)}%</b>
        <span>
          delivery SLA{typeof area.activeOrders === "number" ? ` / ${area.activeOrders} active` : ""}
        </span>
        {area.lastUpdated ? <small>{area.lastUpdated}</small> : null}
      </div>
      <StatusPill value={area.status} />
      {onToggleCapacity ? (
        <button className="row-action" type="button" onClick={onToggleCapacity}>
          {area.status === "capacity_watch" ? "Reopen" : "Watch"}
          <Gauge size={15} />
        </button>
      ) : null}
    </article>
  );
}

function ExternalInquiryOpsQueue({
  inquiryLog,
  onMarkReply,
}: {
  inquiryLog: ExternalInquiryRecord[];
  onMarkReply: (inquiryId: string) => Promise<void>;
}) {
  const signals = inquiryLog.slice(0, 3).map(externalInquirySignalFor);

  return (
    <div className="inquiry-ops-queue" aria-label="Supplier reply queue">
      <div className="inquiry-ops-head">
        <strong>Supplier replies</strong>
        <span>{signals.length ? `${signals.length} active message${signals.length === 1 ? "" : "s"}` : "No supplier messages yet"}</span>
      </div>
      {signals.length ? (
        <div className="inquiry-ops-list">
          {signals.map((signal) => (
            <article className={signal.status} key={signal.id}>
              <div>
                <strong>{signal.title}</strong>
                <small>{signal.detail}</small>
              </div>
              <b>{signal.metric}</b>
              <button
                className="row-action"
                type="button"
                disabled={signal.status === "done"}
                onClick={() => void onMarkReply(signal.id)}
                title={
                  signal.status === "done"
                    ? "Supplier reply has already been captured into the quote workflow"
                    : "Capture supplier reply and create a price check"
                }
              >
                {signal.button}
                {signal.status === "done" ? <CheckCircle2 size={15} /> : <ChevronRight size={15} />}
              </button>
            </article>
          ))}
        </div>
      ) : (
        <p>Generate a supplier message to track replies, price checks, and follow-up.</p>
      )}
    </div>
  );
}

function ExternalSupplierRow({
  isSelected = false,
  record,
  role,
  onAdvance,
  onGenerate,
}: {
  isSelected?: boolean;
  record: ExternalSupplierRecord;
  role: Role;
  onAdvance: () => void;
  onGenerate: () => void;
}) {
  const nextAction =
    record.status === "converted"
      ? "Converted"
      : record.status === "invited"
        ? "Convert"
        : record.status === "ready_to_contact"
          ? "Invite"
          : "Review";

  return (
    <article className={`external-row ${record.status} ${isSelected ? "selected" : ""}`}>
      <div>
        <strong>{record.name}</strong>
        <span>
          {record.market} / {role === "admin" ? record.source : record.deliveryHint}
        </span>
        <p>{record.products.join(", ")}</p>
      </div>
      <div>
        <b>{role === "admin" ? `${record.confidence}%` : "Contact found"}</b>
        <span>{role === "admin" ? record.lastSeen : record.contactHint}</span>
        <StatusPill
          value={
            record.status === "converted"
              ? "Verified"
              : role === "admin"
                ? record.status
                : "Ask first"
          }
        />
      </div>
      <div className="external-actions">
        <button
          type="button"
          title={role === "admin" ? "Load this supplier into the outreach builder" : "Use this supplier in the message form"}
          onClick={onGenerate}
        >
          {isSelected ? "Selected" : "Use"}
          <ExternalLink size={15} />
        </button>
        {role === "admin" ? (
          <button
            type="button"
            title="Move this supplier record through the admin verification pipeline"
            onClick={onAdvance}
            disabled={record.status === "converted"}
          >
            {nextAction}
            <ChevronRight size={15} />
          </button>
        ) : null}
      </div>
    </article>
  );
}

function ExternalInquiryLogRow({
  item,
  onMarkReply,
}: {
  item: ExternalInquiryRecord;
  onMarkReply?: () => void;
}) {
  return (
    <article className={`inquiry-log-row ${item.status}`}>
      <div>
        <strong>{item.supplierName}</strong>
        <span>{item.buyerName} / {item.market}</span>
      </div>
      <div>
        <b>{item.productName}</b>
        <span>{item.quantity} / needed {item.neededBy}</span>
      </div>
      <div>
        <StatusPill value={item.status} />
        <small>{item.createdAt}</small>
        {onMarkReply ? (
          <button
            className="row-action"
            type="button"
            title="Capture the supplier reply and open a quote review item"
            onClick={onMarkReply}
          >
            Mark reply
            <MessageSquare size={15} />
          </button>
        ) : null}
      </div>
    </article>
  );
}

function DiscoverySourceRunRow({
  source,
  onRun,
}: {
  source: DiscoverySource;
  onRun: () => void;
}) {
  return (
    <article className={`discovery-source-run-row ${source.status}`}>
      <span>
        <RadioTower size={17} />
      </span>
      <div>
        <strong>{source.source}</strong>
        <small>
          {source.market} / {source.cadence}
        </small>
      </div>
      <div>
        <b>{source.recordsFound}</b>
        <span>{source.verifiedYield}% yield</span>
      </div>
      <button className="row-action" type="button" onClick={onRun}>
        Run source
        <Zap size={15} />
      </button>
    </article>
  );
}

function DiscoveryRunRecordRow({ run }: { run: DiscoveryRunRecord }) {
  return (
    <article className={`discovery-run-row ${run.status}`}>
      <span>
        <Activity size={17} />
      </span>
      <div>
        <strong>{run.source}</strong>
        <small>{run.market}</small>
      </div>
      <div>
        <b>{run.recordsFound}</b>
        <span>{run.externalRecordsCreated} created</span>
      </div>
      <StatusPill value={run.status} />
    </article>
  );
}

function SupplierConversionTaskRow({
  onAdvance,
  task,
}: {
  onAdvance?: () => void;
  task: SupplierConversionTask;
}) {
  const actionLabel =
    task.stage === "review" ? "Invite" : task.stage === "invited" ? "Convert" : "Converted";

  return (
    <article className={`conversion-row ${task.stage}`}>
      <span>
        <Users size={17} />
      </span>
      <div>
        <strong>{task.supplierName}</strong>
        <small>
          {task.market} / {task.owner}
        </small>
        <p>{task.nextStep}</p>
      </div>
      <div>
        <b>{formatEur(task.revenuePotentialEur)}</b>
        <StatusPill value={task.stage} />
      </div>
      {onAdvance ? (
        <button
          className="row-action"
          type="button"
          onClick={onAdvance}
          disabled={task.stage === "converted"}
          title="Move this supplier conversion task to the next stage"
        >
          {actionLabel}
          {task.stage === "converted" ? <CheckCircle2 size={15} /> : <ChevronRight size={15} />}
        </button>
      ) : null}
    </article>
  );
}

function SupplierDemandGapRow({ gap, onOffer }: { gap: DemandGap; onOffer: () => void }) {
  return (
    <article className="supplier-demand-row">
      <div>
        <strong>{gap.category}</strong>
        <span>{gap.market}</span>
      </div>
      <div className="supplier-demand-metrics">
        <p>
          <b>{formatCount(gap.failedSearches)}</b>
          <span>failed searches</span>
        </p>
        <p>
          <b>{gap.suppliersNeeded}</b>
          <span>suppliers needed</span>
        </p>
        <p>
          <b>{formatEur(gap.revenueAtRiskEur)}</b>
          <span>buyer demand</span>
        </p>
      </div>
      <button
        className="row-action"
        type="button"
        title="Tell restaurants you can supply this category"
        onClick={onOffer}
      >
        I can supply this
        <ArrowUpRight size={15} />
      </button>
      <p>
        Restaurants in {gap.market} need more reliable {gap.category.toLowerCase()} supply.
        Use this if you can send price and delivery terms this week.
      </p>
    </article>
  );
}

function DemandGapRow({ gap }: { gap: DemandGap }) {
  return (
    <article className="gap-row">
      <div>
        <strong>{gap.category}</strong>
        <span>{gap.market}</span>
      </div>
      <div className="gap-metrics">
        <p><b>{formatCount(gap.failedSearches)}</b><span>failed searches</span></p>
        <p><b>{gap.unavailableOrders}</b><span>unavailable orders</span></p>
        <p><b>{gap.suppliersNeeded}</b><span>suppliers needed</span></p>
        <p><b>{formatEur(gap.revenueAtRiskEur)}</b><span>at risk</span></p>
      </div>
      <p>{gap.recommendedAction}</p>
    </article>
  );
}

function MessageThreadRow({
  thread,
  isSelected = false,
  onSelect,
}: {
  thread: MessageThread;
  isSelected?: boolean;
  onSelect?: () => void;
}) {
  return (
    <article className={`message-row ${thread.status} ${isSelected ? "selected" : ""}`}>
      <button type="button" onClick={onSelect}>
        <span>
          <MessageSquare size={17} />
        </span>
        <div>
          <strong>{thread.subject}</strong>
          <small>{thread.participant} / {thread.market}</small>
          <p>{thread.preview}</p>
        </div>
        <div>
          <b>{thread.updatedAt}</b>
          <StatusPill value={thread.status} />
        </div>
      </button>
    </article>
  );
}

function statusPillLabel(value: string) {
  const normalized = value.toLowerCase();
  const labels: Record<string, string> = {
    external_unverified: "Ask first",
    "external-unverified": "Ask first",
    needs_review: "Needs check",
    "needs-review": "Needs check",
    ready_to_contact: "Ready to ask",
    "ready-to-contact": "Ready to ask",
    quote_only: "Ask price",
    "quote-only": "Ask price",
    in_stock: "In stock",
    "in-stock": "In stock",
    in_transit: "On the way",
    "in-transit": "On the way",
    capacity_watch: "Route full",
    "capacity-watch": "Route full",
    backup: "Backup",
    external: "Backup",
  };

  return labels[normalized] ?? value.replace(/_/g, " ");
}

function StatusPill({ value }: { value: string }) {
  return (
    <span className={`status-pill ${value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      {statusPillLabel(value)}
    </span>
  );
}

function EnterpriseAccounts({
  customerAccounts,
  networkStats,
}: {
  customerAccounts: CustomerAccount[];
  networkStats: NetworkStats;
}) {
  return (
    <section className="account-grid">
      <Panel title="Restaurant accounts" action={`${customerAccounts.length} tracked accounts`}>
        <div className="account-list">
          {customerAccounts.length ? (
            customerAccounts.map((account) => (
              <AccountRow key={account.id} account={account} />
            ))
          ) : (
            <p className="data-empty-state">Create restaurant accounts or import buying lists to populate account readiness.</p>
          )}
        </div>
      </Panel>
      <Panel title="Global operating posture" action="Board view">
        <div className="posture-grid">
          <PostureCard icon={Globe2} label="Markets live" value={`${networkStats.countries}`} note="Localized catalogs and currencies" />
          <PostureCard icon={Building2} label="Restaurant accounts" value={`${networkStats.enterpriseAccounts}`} note="Groups, branches, and distributors" />
          <PostureCard icon={Activity} label="Support SLA" value={`${networkStats.supportSla}%`} note="First response within target" />
          <PostureCard icon={ArrowUpRight} label="Monthly GMV" value={formatEur(networkStats.monthlyGmvEur)} note="Saved order value in the last 30 days" />
        </div>
      </Panel>
    </section>
  );
}

function AccountRow({ account }: { account: CustomerAccount }) {
  return (
    <article className={`account-row ${account.risk}`}>
      <div>
        <strong>{account.name}</strong>
        <span>{account.type} / {account.region} / owner {account.owner}</span>
      </div>
      <div className="health-meter" aria-label={`${account.health}% account readiness`}>
        <span style={{ width: `${account.health}%` }} />
      </div>
      <b>{formatEur(account.monthlySpendEur)}</b>
      <p>{account.nextAction}</p>
    </article>
  );
}

function PostureCard({
  icon: Icon,
  label,
  value,
  note,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  note: string;
}) {
  return (
    <article className="posture-card">
      <Icon size={18} />
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{note}</p>
    </article>
  );
}

function AnalyticsView({
  approvalRequests,
  catalogProducts,
  conversionTasks,
  coverageRecords,
  demandGaps,
  externalRecords,
  fulfillmentOrders,
  incidents,
  invoices,
  messageThreads,
  networkSnapshot,
  orders: analyticsOrders,
  purchaseInsights,
  quoteRequests: analyticsQuoteRequests,
  role,
  supplierOpportunities,
  onActOnPurchaseInsight,
  onActivateSupplierOpportunity,
  onAdvanceConversionTask,
  onAdvanceIncident,
  onOpenCoverageSprint,
  onResolveMessageThread,
  onSendQuote,
}: {
  approvalRequests: ApprovalRequest[];
  catalogProducts: Product[];
  conversionTasks: SupplierConversionTask[];
  coverageRecords: MarketCoverageRecord[];
  demandGaps: DemandGap[];
  externalRecords: ExternalSupplierRecord[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  incidents: LiveIncident[];
  invoices: InvoiceRecord[];
  messageThreads: MessageThread[];
  networkSnapshot: NetworkOperatingSnapshot;
  orders: Order[];
  purchaseInsights: PurchaseInsight[];
  quoteRequests: QuoteRequest[];
  role: Role;
  supplierOpportunities: SupplierOpportunity[];
  onActOnPurchaseInsight: (insight: PurchaseInsight) => void | Promise<void>;
  onActivateSupplierOpportunity: (opportunity: SupplierOpportunity) => void;
  onAdvanceConversionTask: (taskId: string) => void;
  onAdvanceIncident: (incidentId: string) => void;
  onOpenCoverageSprint: (record: MarketCoverageRecord) => void;
  onResolveMessageThread: (threadId: string) => void;
  onSendQuote: (request: QuoteRequest) => void;
}) {
  const { regionHealth } = networkSnapshot;
  const totalOrderSpend = analyticsOrders.reduce((sum, order) => sum + order.total, 0);
  const supplierSpend = Object.entries(
    analyticsOrders.reduce<Record<string, number>>((totals, order) => {
      totals[order.supplier] = (totals[order.supplier] ?? 0) + order.total;
      return totals;
    }, {}),
  )
    .map(([label, value]) => ({
      label,
      detail: `${analyticsOrders.filter((order) => order.supplier === label).length} orders`,
      amount: value,
      value: `${formatMoney(value)} KM`,
    }))
    .sort((left, right) => right.amount - left.amount);
  const orderPeak = Math.max(...analyticsOrders.map((order) => order.total), 1);
  const openQuotes = analyticsQuoteRequests.filter((request) => isOpenQuoteStatus(request.status));
  const fulfillmentValue = fulfillmentOrders.reduce((sum, order) => sum + order.valueEur, 0);
  const invoiceValue = invoices.reduce((sum, invoice) => sum + invoice.amountEur, 0);
  const blockedInvoices = invoices.filter((invoice) => invoice.status === "blocked");
  const weakCatalog = catalogProducts.filter((product) => product.stockStatus !== "available");
  const unresolvedMessages = messageThreads.filter((thread) => thread.status !== "resolved");
  const coverageRisks = coverageRecords.filter((record) => record.risk !== "healthy");
  const openConversions = conversionTasks.filter((task) => task.stage !== "converted");
  const topPurchaseInsight = purchaseInsights
    .slice()
    .sort((left, right) => Math.abs(right.variancePct) - Math.abs(left.variancePct))[0];
  const topOpenQuote = openQuotes[0];
  const topSupplierOpportunity = supplierOpportunities
    .slice()
    .sort((left, right) => {
      const urgencyRank = { now: 0, this_week: 1, monitor: 2 };

      return (
        urgencyRank[left.urgency] - urgencyRank[right.urgency] ||
        right.potentialRevenueEur - left.potentialRevenueEur
      );
    })[0];
  const topCoverageRisk = coverageRisks
    .slice()
    .sort((left, right) => {
      const riskRank = { critical: 0, watch: 1, healthy: 2 };

      return riskRank[left.risk] - riskRank[right.risk] || left.fillRate - right.fillRate;
    })[0];
  const topConversion = openConversions
    .slice()
    .sort((left, right) => right.revenuePotentialEur - left.revenuePotentialEur)[0];
  const topMessage = unresolvedMessages[0];
  const topIncident = incidents.find((incident) => incident.status !== "resolved");
  const buyerAnalyticsCandidates: Array<AnalyticsAction | null> = [
    topPurchaseInsight
      ? {
          id: `buyer-insight-${topPurchaseInsight.id}`,
          title: `${topPurchaseInsight.category} spend decision`,
          detail: `${topPurchaseInsight.supplier}: ${topPurchaseInsight.detail}`,
          metric: `${topPurchaseInsight.variancePct.toFixed(1)}%`,
          severity: topPurchaseInsight.status === "over_budget" ? "high" : "watch",
          button:
            topPurchaseInsight.status === "over_budget"
              ? "Open control"
              : topPurchaseInsight.status === "price_watch"
                ? "Ask backup price"
                : "Compare",
          icon: FileSpreadsheet,
          onAction: () => onActOnPurchaseInsight(topPurchaseInsight),
        }
      : null,
    topMessage
      ? {
          id: `buyer-message-${topMessage.id}`,
          title: topMessage.subject,
          detail: `${topMessage.participant} / ${topMessage.market}`,
          metric: topMessage.status.replace(/_/g, " "),
          severity: topMessage.status === "needs_reply" ? "high" : "watch",
          button: "Resolve",
          icon: MessageSquare,
          onAction: () => onResolveMessageThread(topMessage.id),
        }
      : null,
  ];
  const buyerAnalyticsActions = buyerAnalyticsCandidates.filter(
    (action): action is AnalyticsAction => Boolean(action),
  );
  const supplierAnalyticsCandidates: Array<AnalyticsAction | null> = [
    topOpenQuote
      ? {
          id: `supplier-quote-${topOpenQuote.id}`,
          title: `Price ${topOpenQuote.product}`,
          detail: `${topOpenQuote.buyer} / ${topOpenQuote.quantity} / ${topOpenQuote.neededBy}`,
          metric: supplierSafeQuoteMetric(topOpenQuote),
          severity: "high",
          button: "Send price",
          icon: Send,
          onAction: () => onSendQuote(topOpenQuote),
        }
      : null,
    topSupplierOpportunity
      ? {
          id: `supplier-opportunity-${topSupplierOpportunity.id}`,
          title: topSupplierOpportunity.title,
          detail: topSupplierOpportunity.action,
          metric: formatEur(topSupplierOpportunity.potentialRevenueEur),
          severity: topSupplierOpportunity.urgency === "now" ? "high" : "watch",
          button: "Activate",
          icon: TrendingUp,
          onAction: () => onActivateSupplierOpportunity(topSupplierOpportunity),
        }
      : null,
  ];
  const supplierAnalyticsActions = supplierAnalyticsCandidates.filter(
    (action): action is AnalyticsAction => Boolean(action),
  );
  const adminAnalyticsCandidates: Array<AnalyticsAction | null> = [
    topCoverageRisk
      ? {
          id: `admin-coverage-${topCoverageRisk.id}`,
          title: `${topCoverageRisk.market} / ${topCoverageRisk.category}`,
          detail: topCoverageRisk.nextMove,
          metric: `${topCoverageRisk.fillRate.toFixed(1)}% fill`,
          severity: topCoverageRisk.risk === "critical" ? "high" : "watch",
          button: "Open sprint",
          icon: Gauge,
          onAction: () => onOpenCoverageSprint(topCoverageRisk),
        }
      : null,
    topConversion
      ? {
          id: `admin-conversion-${topConversion.id}`,
          title: topConversion.supplierName,
          detail: `${topConversion.market}: ${topConversion.nextStep}`,
          metric: formatEur(topConversion.revenuePotentialEur),
          severity: "watch",
          button: topConversion.stage === "review" ? "Invite" : "Convert",
          icon: Users,
          onAction: () => onAdvanceConversionTask(topConversion.id),
        }
      : null,
    topIncident
      ? {
          id: `admin-incident-${topIncident.id}`,
          title: topIncident.title,
          detail: `${topIncident.market}: ${topIncident.action}`,
          metric: topIncident.severity,
          severity: topIncident.severity === "critical" ? "high" : "watch",
          button: topIncident.status === "active" ? "Mitigate" : "Resolve",
          icon: AlertTriangle,
          onAction: () => onAdvanceIncident(topIncident.id),
        }
      : null,
  ];
  const adminAnalyticsActions = adminAnalyticsCandidates.filter(
    (action): action is AnalyticsAction => Boolean(action),
  );

  if (role === "supplier") {
    const fulfillmentPeak = Math.max(...fulfillmentOrders.map((order) => order.valueEur), 1);

    return (
      <>
        <AnalyticsActionQueue actions={supplierAnalyticsActions} role={role} />
        <section className="report-grid" aria-label="Supplier analytics">
          <Panel title="Sales performance" action={`${openQuotes.length} quotes open`}>
            <div className="insight-list">
              <p>
                <strong>{formatEur(fulfillmentValue)}</strong>
                <span>order value currently in motion</span>
              </p>
              <p>
                <strong>{catalogProducts.length - weakCatalog.length}/{catalogProducts.length}</strong>
                <span>products ready for restaurants</span>
              </p>
              <p>
                <strong>{blockedInvoices.length}</strong>
                <span>invoice issues across {formatEur(invoiceValue)} invoice value</span>
              </p>
            </div>
          </Panel>

          <Panel title="Order value trend" action={`${fulfillmentOrders.length} orders`}>
            <div className="report-chart" aria-label="Order value by order">
              {fulfillmentOrders.map((order) => (
                <span key={order.id} style={{ height: `${Math.max(18, (order.valueEur / fulfillmentPeak) * 100)}%` }} />
              ))}
            </div>
          </Panel>

          <Panel title="Revenue moves" action={`${supplierOpportunities.length} signals`}>
            <div className="analytics-row-list">
              {supplierOpportunities.map((opportunity) => (
                <AnalyticsRow
                  key={opportunity.id}
                  label={opportunity.title}
                  detail={opportunity.action}
                  value={formatEur(opportunity.potentialRevenueEur)}
                />
              ))}
            </div>
          </Panel>
        </section>
      </>
    );
  }

  if (role === "admin") {
    return (
      <>
        <AnalyticsActionQueue actions={adminAnalyticsActions} role={role} />
        <section className="report-grid" aria-label="Network analytics">
          <Panel title="Market coverage" action={`${coverageRisks.length} risks`}>
            <div className="analytics-row-list">
              {coverageRecords.map((record) => (
                <AnalyticsRow
                  key={record.id}
                  label={`${record.market} / ${record.category}`}
                  detail={record.nextMove}
                  value={`${record.fillRate.toFixed(1)}%`}
                />
              ))}
            </div>
          </Panel>

          <Panel title="Regional service" action={`${regionHealth.length} regions`}>
            <div className="report-chart" aria-label="Regional on-time service">
              {regionHealth.map((region) => (
                <span key={region.id} style={{ height: `${Math.max(18, region.onTimeRate)}%` }} />
              ))}
            </div>
          </Panel>

          <Panel title="Expansion decisions" action={`${openConversions.length} conversions`}>
            <div className="decision-list">
              {demandGaps.map((gap) => (
                <p key={gap.id}>{gap.market}: {gap.recommendedAction}</p>
              ))}
              <p>{externalRecords.length} external supplier records are available for sourcing review.</p>
            </div>
          </Panel>
        </section>
      </>
    );
  }

  return (
    <>
      <AnalyticsActionQueue actions={buyerAnalyticsActions} role={role} />
      <section className="report-grid" aria-label="Buyer analytics">
        <Panel title="Spend concentration" action={`${formatMoney(totalOrderSpend)} KM`}>
          <div className="analytics-row-list">
            {supplierSpend.map((item) => (
              <AnalyticsRow key={item.label} label={item.label} detail={item.detail} value={item.value} />
            ))}
          </div>
        </Panel>

        <Panel title="Order value trend" action={`${analyticsOrders.length} recent`}>
          <div className="report-chart" aria-label="Recent order value">
            {analyticsOrders.map((order) => (
              <span key={order.id} style={{ height: `${Math.max(18, (order.total / orderPeak) * 100)}%` }} />
            ))}
          </div>
        </Panel>

        <Panel title="Spend and control decisions" action={`${approvalRequests.length} approvals`}>
          <div className="insight-list compact-insights">
            <p>
              <strong>{purchaseInsights.length}</strong>
              <span>active spend insights across supplier categories</span>
            </p>
            <p>
              <strong>{unresolvedMessages.length}</strong>
              <span>open supplier or branch message threads</span>
            </p>
            <p>
              <strong>{analyticsOrders.filter((order) => order.status === "issue").length}</strong>
              <span>orders with service or supplier issue status</span>
            </p>
          </div>
          <div className="decision-list">
            {purchaseInsights.map((insight) => (
              <p key={insight.id}>{insight.supplier}: {insight.detail}</p>
            ))}
          </div>
        </Panel>
      </section>
    </>
  );
}

function AnalyticsActionQueue({ actions, role }: { actions: AnalyticsAction[]; role: Role }) {
  if (!actions.length) {
    return null;
  }

  const highCount = actions.filter((action) => action.severity === "high").length;

  return (
    <section className="analytics-action-grid" aria-label={`${role} analytics action queue`}>
      <Panel
        title="Analytics action queue"
        action={`${highCount} urgent / ${actions.length} actions`}
        help="Metric-backed actions generated from the analytics view so reporting leads directly to buying work."
      >
        <div className="analytics-action-list">
          {actions.map((action) => (
            <AnalyticsActionRow action={action} key={action.id} />
          ))}
        </div>
      </Panel>
    </section>
  );
}

function AnalyticsActionRow({ action }: { action: AnalyticsAction }) {
  const Icon = action.icon;

  return (
    <article className={`analytics-action-row ${action.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{action.title}</strong>
        <small>{action.detail}</small>
      </div>
      <b>{action.metric}</b>
      <button
        className="row-action"
        type="button"
        onClick={() => void action.onAction()}
        title={`${action.button}: ${action.title}`}
      >
        {action.button}
        <ChevronRight size={15} />
      </button>
    </article>
  );
}

function AnalyticsRow({
  label,
  detail,
  value,
}: {
  label: string;
  detail: string;
  value: string;
}) {
  return (
    <article className="analytics-row">
      <div>
        <strong>{label}</strong>
        <span>{detail}</span>
      </div>
      <b>{value}</b>
    </article>
  );
}

function SettingsView({
  auditLog,
  branches,
  connectorRequests,
  connectorStatus,
  discoverySources,
  doneForYouSetupRequests,
  importRuns,
  importStatus,
  integrations,
  localizationRules,
  onAdjustBranchBudget,
  onApproveLocalization,
  onImportBuyerOrderGuide,
  onImportSupplierCatalog,
  onNavigate,
  onRepairIntegration,
  onRunDiscoverySource,
  onSaveConnectorRequest,
  onSaveDoneForYouSetupRequest,
  onUpdateConnectorStatus,
  role,
  selectedBranch,
  users,
  onProvisionBranchSeat,
  onToggleUserStatus,
}: {
  auditLog: AuditEvent[];
  branches: BranchAccount[];
  connectorRequests: ConnectorRequestRecord[];
  connectorStatus: string;
  discoverySources: DiscoverySource[];
  doneForYouSetupRequests: DoneForYouSetupRequest[];
  importRuns: ImportRunRecord[];
  importStatus: string;
  integrations: IntegrationStatus[];
  localizationRules: LocalizationRule[];
  onAdjustBranchBudget: (
    branchId: string,
    field: "weeklyBudget" | "approvalLimit",
    delta: number,
  ) => void;
  onApproveLocalization: (ruleId: string) => void;
  onImportBuyerOrderGuide: (file: File) => Promise<boolean>;
  onImportSupplierCatalog: (file: File, supplierName: string) => Promise<boolean>;
  onNavigate: (label: string) => void;
  onRepairIntegration: (integrationName: string) => Promise<void>;
  onRunDiscoverySource: (source: DiscoverySource) => void;
  onSaveConnectorRequest: (input: SubmitIntegrationRequestInput) => Promise<void>;
  onSaveDoneForYouSetupRequest: (input: SubmitDoneForYouSetupRequestInput) => Promise<void>;
  onUpdateConnectorStatus: (
    request: ConnectorRequestRecord,
    status: ConnectorRequestRecord["status"],
  ) => Promise<void>;
  role: Role;
  selectedBranch: BranchAccount;
  users: WorkspaceUser[];
  onProvisionBranchSeat: () => void;
  onToggleUserStatus: (userId: string) => void;
}) {
  const liveConnections = integrations.filter((integration) => integration.status === "connected").length;
  const degradedConnections = integrations.length - liveConnections;
  const connectedConnectorCount = connectorRequests.filter((request) => request.status === "connected").length;
  const pendingConnectors = connectorRequests.filter((request) => request.status === "pending_setup").length;
  const failedConnectors = connectorRequests.filter((request) => request.status === "failed").length;
  const totalConnectionRecords = integrations.length + connectorRequests.length;
  const connectionRiskCount = degradedConnections + pendingConnectors + failedConnectors;
  const branchBudgetRisks = branches.filter(
    (branch) => branch.status === "watch" || branchSpendRatio(branch) > 0.85,
  );
  const liveLocalization = localizationRules.filter((rule) => rule.status === "live").length;
  const reviewLocalization = localizationRules.filter((rule) => rule.status !== "live");
  const reviewSources = discoverySources.filter((source) => source.status !== "running");
  const approvalRoles = permissionRoles.filter((permission) => permission.canApproveOrders).length;
  const activeUsers = users.filter((user) => user.status === "active").length;
  const invitedUsers = users.filter((user) => user.status === "invited").length;
  const suspendedUsers = users.filter((user) => user.status === "suspended").length;
  const branchById = new Map(branches.map((branch) => [branch.id, branch]));
  const permissionById = new Map(permissionRoles.map((permission) => [permission.id, permission]));
  const branchGovernanceActions: BranchGovernanceAction[] = [
    ...branches
      .slice()
      .sort((left, right) => branchSpendRatio(right) - branchSpendRatio(left))
      .slice(0, 5)
      .map((branch) => {
        const spentPct = branchSpendPercent(branch);
        const remaining = branch.weeklyBudget - branch.spentThisWeek;
        const pressureHigh = spentPct >= 95 || remaining < branch.approvalLimit * 0.35;
        const severity: BranchGovernanceAction["severity"] = pressureHigh
          ? "high"
          : branch.status === "watch"
            ? "watch"
            : "ready";

        return {
          id: `branch-governance-${branch.id}`,
          title: branch.name,
          detail: `${formatBranchMoney(remaining, branch)} remains before weekly branch budget is consumed. Approval limit is ${formatBranchMoney(branch.approvalLimit, branch)}.`,
          metric: `${spentPct}% used`,
          severity,
          button: pressureHigh ? "Add budget" : "Raise limit",
          icon: Building2,
          onAction: () =>
            pressureHigh
              ? onAdjustBranchBudget(branch.id, "weeklyBudget", 500)
              : onAdjustBranchBudget(branch.id, "approvalLimit", 100),
        };
      }),
    ...(invitedUsers || suspendedUsers
      ? [
          {
            id: "branch-governance-access",
            title: "Access seats",
            detail: `${invitedUsers} invited and ${suspendedUsers} suspended users need owner review before branch teams can buy or approve cleanly.`,
            metric: `${activeUsers}/${users.length} active`,
            severity: suspendedUsers ? "high" : "watch",
            button: "Prepare seat",
            icon: ShieldCheck,
            onAction: onProvisionBranchSeat,
          } satisfies BranchGovernanceAction,
        ]
      : []),
    ...(reviewLocalization[0]
      ? [
          {
            id: `branch-governance-localization-${reviewLocalization[0].id}`,
            title: `${reviewLocalization[0].market} localization`,
            detail: `${reviewLocalization[0].currency} / ${reviewLocalization[0].taxMode} / ${reviewLocalization[0].language}. Approve before branch buying uses this market rule.`,
            metric: reviewLocalization[0].status,
            severity: "watch",
            button: "Approve",
            icon: Globe2,
            onAction: () => onApproveLocalization(reviewLocalization[0].id),
          } satisfies BranchGovernanceAction,
        ]
      : []),
  ].slice(0, 7);
  const scaleChecks = [
    {
      id: "branches",
      title: "Branch budget coverage",
      metric: branches.length ? `${branches.length - branchBudgetRisks.length}/${branches.length} healthy` : "No branches",
      detail: `${branchBudgetRisks.length} branch${branchBudgetRisks.length === 1 ? "" : "es"} need spend review before approval spikes.`,
      status: branchBudgetRisks.length || !branches.length ? "watch" : "ready",
      icon: Building2,
    },
    {
      id: "roles",
      title: "Permission separation",
      metric: users.length ? `${activeUsers}/${users.length} active` : "No users",
      detail: `${approvalRoles} role${approvalRoles === 1 ? "" : "s"} can approve orders; ${invitedUsers} invite${invitedUsers === 1 ? "" : "s"} pending and ${suspendedUsers} suspended.`,
      status: suspendedUsers || !users.length ? "watch" : "ready",
      icon: ShieldCheck,
    },
    {
      id: "markets",
      title: "Localization readiness",
      metric: localizationRules.length ? `${liveLocalization}/${localizationRules.length} live` : "No rules",
      detail: reviewLocalization.length
        ? `${reviewLocalization.map((rule) => rule.market).join(", ")} need tax or currency review.`
        : "All active market rules are live.",
      status: reviewLocalization.length || !localizationRules.length ? "watch" : "ready",
      icon: Globe2,
    },
    {
      id: "connections",
      title: "Data connection health",
      metric: totalConnectionRecords
        ? `${liveConnections + connectedConnectorCount}/${totalConnectionRecords} live`
        : "No connectors",
      detail: connectionRiskCount
        ? `${connectionRiskCount} connection record${connectionRiskCount === 1 ? "" : "s"} need credentials, owner review, or repair before automation can rely on them.`
        : "All data connections are live.",
      status: connectionRiskCount || !totalConnectionRecords ? "watch" : "ready",
      icon: Activity,
    },
  ];
  const controlRisks = [
    ...branchBudgetRisks.map((branch) => ({
      id: `branch-${branch.id}`,
      title: branch.name,
      detail: `${formatCurrencyAmount(branch.spentThisWeek, "BAM")} of ${formatCurrencyAmount(branch.weeklyBudget, "BAM")} used this week.`,
      action: "Review approval limit and next repeat list",
      metric: `${branchSpendPercent(branch)}%`,
      severity: "watch",
      icon: Building2,
    })),
    ...connectorRequests.map((request) => ({
      id: `connector-request-${request.requestId}`,
      title: `${request.providerName} connector`,
      detail: `${request.organizationName} / ${request.systemType} / ${request.syncTarget}`,
      action: "Assign credentials and test a real sync before this connection can be marked live",
      metric: request.status.replace("_", " "),
      severity: "watch",
      icon: RadioTower,
    })),
    ...integrations
      .filter((integration) => integration.status !== "connected")
      .map((integration) => ({
        id: `integration-${integration.name}`,
        title: `${integration.name} ${integration.status}`,
        detail: `${integration.category} / ${integration.lastSync}`,
        action: integration.status === "degraded" ? "Retry or assign owner" : "Confirm rollout plan",
        metric: integration.status,
        severity: integration.status === "degraded" ? "high" : "watch",
        icon: Activity,
        button: integration.status === "degraded" ? "Repair" : "Activate",
        onAction: () => onRepairIntegration(integration.name),
      })),
    ...reviewLocalization.map((rule) => ({
      id: `localization-${rule.id}`,
      title: rule.market,
      detail: `${rule.currency} / ${rule.taxMode} / ${rule.language}`,
      action: "Approve localization before market expansion",
      metric: rule.status,
      severity: "watch",
      icon: Globe2,
      button: "Approve",
      onAction: () => onApproveLocalization(rule.id),
    })),
    ...reviewSources.map((source) => ({
      id: `source-${source.id}`,
      title: source.source,
      detail: `${source.market} / ${source.cadence} / ${source.verifiedYield}% verified yield`,
      action: "Review source quality and verification path",
      metric: source.status,
      severity: source.status === "paused" ? "high" : "watch",
      icon: RadioTower,
      button: source.status === "paused" ? "Resume" : "Run",
      onAction: () => onRunDiscoverySource(source),
    })),
    ...users
      .filter((user) => user.status !== "active")
      .map((user) => {
        const branch = branchById.get(user.branchId);
        const permission = permissionById.get(user.roleId);

        return {
          id: `user-${user.id}`,
          title: user.name,
          detail: `${permission?.role ?? user.roleId} / ${branch?.name ?? "Unassigned branch"} / ${user.lastSeen}`,
          action:
            user.status === "invited"
              ? "Confirm the person before they place or approve orders"
              : "Review whether this access should stay paused",
          metric: user.status,
          severity: user.status === "suspended" ? "high" : "watch",
          icon: LockKeyhole,
        };
      }),
  ].slice(0, 8);

  const setupPanel = (
    <DataImportAndConnectorPanel
      branches={branches}
      connectorRequests={connectorRequests}
      connectorStatus={connectorStatus}
      doneForYouSetupRequests={doneForYouSetupRequests}
      importRuns={importRuns}
      importStatus={importStatus}
      onNavigate={onNavigate}
      role={role}
      selectedBranch={selectedBranch}
      onImportBuyerOrderGuide={onImportBuyerOrderGuide}
      onImportSupplierCatalog={onImportSupplierCatalog}
      onSaveConnectorRequest={onSaveConnectorRequest}
      onSaveDoneForYouSetupRequest={onSaveDoneForYouSetupRequest}
    />
  );

  if (role !== "admin") {
    return <>{setupPanel}</>;
  }

  return (
    <>
      {setupPanel}

      <BranchGovernanceQueue actions={branchGovernanceActions} />

      <section className="settings-overview-grid" aria-label="Workspace administration">
        <Panel title="Workspace state" action={`${branches.length} branches`}>
          <div className="settings-summary-list">
            <article>
              <Building2 size={18} />
              <div>
                <strong>Branch operating rules</strong>
                <span>Budgets, approval limits, and delivery windows are managed per location.</span>
              </div>
            </article>
            <article>
              <ShieldCheck size={18} />
              <div>
                <strong>{activeUsers}/{users.length} active access seats</strong>
                <span>Buyer, finance, supplier operator, and admin scopes stay separated by branch.</span>
              </div>
            </article>
            <article>
              <Globe2 size={18} />
              <div>
                <strong>{localizationRules.length} market rules</strong>
                <span>Currency, tax mode, and language settings are ready for Balkan expansion.</span>
              </div>
            </article>
          </div>
        </Panel>

        <Panel title="Controls queue" action={`${liveConnections}/${integrations.length} live`}>
          <div className="settings-control-list">
            <p>
              <b>Connections</b>
            <span>
              {liveConnections + connectedConnectorCount} connected, {connectionRiskCount} needing setup or review.
            </span>
            </p>
            <p>
              <b>Discovery sources</b>
              <span>{discoverySources.length} supplier feeds checked by cadence and yield.</span>
            </p>
            <p>
              <b>Access queue</b>
              <span>{invitedUsers} invited, {suspendedUsers} suspended, {activeUsers} active.</span>
            </p>
            <p>
              <b>Audit</b>
              <span>{auditLog.length} recent account actions visible for review.</span>
            </p>
          </div>
        </Panel>
      </section>

      <section className="settings-readiness-grid" aria-label="Scale readiness">
        <Panel
          title="Scale readiness"
          action={`${scaleChecks.filter((check) => check.status === "ready").length}/${scaleChecks.length} ready`}
          help="Operational readiness across branches, permissions, localization, and data connections."
        >
          <div className="scale-check-list">
            {scaleChecks.map((check) => (
              <ScaleCheckRow key={check.id} check={check} />
            ))}
          </div>
        </Panel>

        <Panel
          title="Control risks"
          action={`${controlRisks.length} open`}
          help="The specific controls that can slow expansion, approvals, automation, or supplier discovery."
        >
          <div className="control-risk-list">
            {controlRisks.map((risk) => (
              <ControlRiskRow key={risk.id} risk={risk} />
            ))}
          </div>
        </Panel>
      </section>

      <section className="admin-grid">
        <Panel
          title="Workspace users"
          action={`${activeUsers}/${users.length} active`}
          help="Access seats are attached to a branch and permission role so restaurants can control who buys, approves, or exports."
        >
          <div className="workspace-user-toolbar">
            <div>
              <b>{selectedBranch.name}</b>
              <span>{selectedBranch.market} / next access seat uses the selected branch context</span>
            </div>
            <button
              className="secondary-action"
              type="button"
              onClick={onProvisionBranchSeat}
              title="Prepare a buyer seat for the selected branch and add it to the access queue"
            >
              Prepare seat
              <Plus size={16} />
            </button>
          </div>
          <div className="workspace-user-list">
            {users.map((user) => (
              <WorkspaceUserRow
                branch={branchById.get(user.branchId)}
                key={user.id}
                onToggleStatus={() => onToggleUserStatus(user.id)}
                permission={permissionById.get(user.roleId)}
                user={user}
              />
            ))}
          </div>
        </Panel>

        <Panel title="Branch budgets" action="Multi-branch">
          <div className="branch-list">
            {branches.map((branch) => (
              <BranchRow
                key={branch.id}
                branch={branch}
                onAdjustBudget={onAdjustBranchBudget}
              />
            ))}
          </div>
        </Panel>
      </section>

      <section className="admin-grid secondary">
        <Panel title="Roles and permissions" action="RBAC">
          <div className="permission-list">
            {permissionRoles.map((permission) => (
              <PermissionRow
                key={permission.id}
                permission={permission}
                users={users.filter((user) => user.roleId === permission.id)}
              />
            ))}
          </div>
        </Panel>

        <Panel title="Localization and tax" action="Global">
          <div className="localization-list">
            {localizationRules.map((rule) => (
              <LocalizationRow
                key={rule.id}
                rule={rule}
                onApprove={rule.status === "live" ? undefined : () => onApproveLocalization(rule.id)}
              />
            ))}
          </div>
        </Panel>

        <Panel title="Discovery source operations" action="Always-on">
          <div className="discovery-source-list">
            {discoverySources.map((source) => (
              <DiscoverySourceRow
                key={source.id}
                source={source}
                onRun={() => onRunDiscoverySource(source)}
              />
            ))}
          </div>
        </Panel>
      </section>

      <section className="admin-grid tertiary">
        <Panel title="Audit log" action="Immutable">
          <div className="audit-list">
            {auditLog.map((event) => (
              <AuditEventRow key={event.id} event={event} />
            ))}
          </div>
        </Panel>

        <Panel title="Data connections" action={`${pendingConnectors} pending`}>
          {integrations.length || connectorRequests.length ? (
            <div className="integration-list">
              {connectorRequests.map((request) => (
                <ConnectorRequestRow
                  key={request.id}
                  request={request}
                  onMarkConnected={() => onUpdateConnectorStatus(request, "connected")}
                  onMarkFailed={() => onUpdateConnectorStatus(request, "failed")}
                  onReturnToPending={() => onUpdateConnectorStatus(request, "pending_setup")}
                />
              ))}
              {integrations.map((integration) => (
                <IntegrationRow
                  key={integration.name}
                  integration={integration}
                  onRepair={
                    integration.status === "connected" ? undefined : () => onRepairIntegration(integration.name)
                  }
                />
              ))}
            </div>
          ) : (
            <p className="data-empty-state">
              Save a real POS, ERP, accounting, inventory, SFTP, spreadsheet, or API connection.
            </p>
          )}
        </Panel>
      </section>
    </>
  );
}

function BranchGovernanceQueue({ actions }: { actions: BranchGovernanceAction[] }) {
  return (
    <section className="branch-governance-queue" aria-label="Branch governance queue">
      <div className="branch-governance-head">
        <div>
          <span>Branch governance</span>
          <strong>Budget, access, and localization controls</strong>
          <small>Controls that keep multi-branch buying inside budget, approval scope, and market rules.</small>
        </div>
        <b>{actions.length} checks</b>
      </div>
      <div className="branch-governance-list">
        {actions.map((action) => {
          const Icon = action.icon;

          return (
            <article className={action.severity} key={action.id}>
              <span>
                <Icon size={16} />
              </span>
              <div>
                <strong>{action.title}</strong>
                <small>{action.detail}</small>
              </div>
              <b>{action.metric}</b>
              <button className="row-action" type="button" onClick={action.onAction}>
                {action.button}
                <ChevronRight size={15} />
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ScaleCheckRow({
  check,
}: {
  check: {
    title: string;
    metric: string;
    detail: string;
    status: string;
    icon: React.ElementType;
  };
}) {
  const Icon = check.icon;

  return (
    <article className={`scale-check-row ${check.status}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{check.title}</strong>
        <small>{check.detail}</small>
      </div>
      <b>{check.metric}</b>
    </article>
  );
}

function ControlRiskRow({
  risk,
}: {
  risk: {
    title: string;
    detail: string;
    action: string;
    metric: string;
    severity: string;
    icon: React.ElementType;
    button?: string;
    onAction?: () => void;
  };
}) {
  const Icon = risk.icon;

  return (
    <article className={`control-risk-row ${risk.severity}`}>
      <span>
        <Icon size={17} />
      </span>
      <div>
        <strong>{risk.title}</strong>
        <small>{risk.detail}</small>
        <p>{risk.action}</p>
      </div>
      <StatusPill value={risk.metric} />
      {risk.button && risk.onAction ? (
        <button className="row-action" type="button" onClick={risk.onAction}>
          {risk.button}
          <ChevronRight size={15} />
        </button>
      ) : null}
    </article>
  );
}

function BranchRow({
  branch,
  onAdjustBudget,
}: {
  branch: BranchAccount;
  onAdjustBudget: (
    branchId: string,
    field: "weeklyBudget" | "approvalLimit",
    delta: number,
  ) => void;
}) {
  const spentPct = Math.min(100, Math.round((branch.spentThisWeek / branch.weeklyBudget) * 100));

  return (
    <article className={`branch-row ${branch.status}`}>
      <div>
        <strong>{branch.name}</strong>
        <span>{branch.market} / {branch.tier} / {branch.users} users</span>
      </div>
      <div className="budget-meter" aria-label={`${spentPct}% budget used`}>
        <span style={{ width: `${spentPct}%` }} />
      </div>
      <div>
        <b>{formatBranchMoney(branch.spentThisWeek, branch)} / {formatBranchMoney(branch.weeklyBudget, branch)}</b>
        <span>{formatBranchMoney(branch.approvalLimit, branch)} approval limit / {branchTaxSummary(branch)}</span>
      </div>
      <div className="row-button-group branch-budget-actions">
        <button
          type="button"
          onClick={() => onAdjustBudget(branch.id, "approvalLimit", -100)}
          title={`Lower approval limit for ${branch.name}`}
        >
          Limit
          <Minus size={15} />
        </button>
        <button
          type="button"
          onClick={() => onAdjustBudget(branch.id, "approvalLimit", 100)}
          title={`Raise approval limit for ${branch.name}`}
        >
          Limit
          <Plus size={15} />
        </button>
        <button
          type="button"
          onClick={() => onAdjustBudget(branch.id, "weeklyBudget", 500)}
          title={`Add weekly budget room for ${branch.name}`}
        >
          Budget
          <Plus size={15} />
        </button>
      </div>
    </article>
  );
}

function WorkspaceUserRow({
  branch,
  permission,
  user,
  onToggleStatus,
}: {
  branch?: BranchAccount;
  permission?: PermissionRole;
  user: WorkspaceUser;
  onToggleStatus: () => void;
}) {
  const actionLabel = user.status === "active" ? "Suspend" : "Activate";

  return (
    <article className={`workspace-user-row ${user.status}`}>
      <div>
        <strong>{user.name}</strong>
        <span>{user.email}</span>
        <small>{user.focus}</small>
      </div>
      <div>
        <b>{permission?.role ?? user.roleId}</b>
        <span>{branch?.name ?? "Unassigned branch"}</span>
      </div>
      <div>
        <StatusPill value={user.status} />
        <span>{user.lastSeen}</span>
      </div>
      <button
        className="user-action-button"
        type="button"
        onClick={onToggleStatus}
        title={
          user.status === "active"
            ? "Suspend this user's workspace access"
            : "Reactivate this user's workspace access"
        }
      >
        {actionLabel}
        {user.status === "active" ? <LockKeyhole size={15} /> : <CheckCircle2 size={15} />}
      </button>
    </article>
  );
}

function PermissionRow({ permission, users }: { permission: PermissionRole; users: WorkspaceUser[] }) {
  const activeUsers = users.filter((user) => user.status === "active").length;
  const pendingUsers = users.filter((user) => user.status !== "active").length;

  return (
    <article className="permission-row">
      <div>
        <strong>{permission.role}</strong>
        <span>{permission.scope}</span>
      </div>
      <div>
        <b>{activeUsers}/{users.length || permission.users} active</b>
        <span>{pendingUsers ? `${pendingUsers} pending or paused` : permission.approvalLimit}</span>
        <span>{permissionCapabilitySummary(permission)}</span>
      </div>
      <StatusPill value={permission.canApproveOrders ? "Can approve" : "Review only"} />
    </article>
  );
}

function LocalizationRow({
  rule,
  onApprove,
}: {
  rule: LocalizationRule;
  onApprove?: () => void;
}) {
  return (
    <article className={`localization-row ${rule.status}`}>
      <div>
        <strong>{rule.market}</strong>
        <span>{rule.language} / {rule.taxMode}</span>
      </div>
      <b>{rule.currency}</b>
      <StatusPill value={rule.status} />
      {onApprove ? (
        <button
          className="row-action"
          type="button"
          onClick={onApprove}
          title={`Approve ${rule.market} localization for live buying controls`}
        >
          Approve
          <CheckCircle2 size={15} />
        </button>
      ) : null}
    </article>
  );
}

function DiscoverySourceRow({
  source,
  onRun,
}: {
  source: DiscoverySource;
  onRun: () => void;
}) {
  return (
    <article className={`discovery-source-row ${source.status}`}>
      <div>
        <strong>{source.source}</strong>
        <span>{source.market} / {source.cadence} / last run {source.lastRun}</span>
      </div>
      <div>
        <b>{formatCount(source.recordsFound)}</b>
        <span>{source.verifiedYield}% verified yield</span>
      </div>
      <StatusPill value={source.status} />
      <button
        className="row-action"
        type="button"
        onClick={onRun}
        title={`Run ${source.source} to refresh supplier records for ${source.market}`}
      >
        {source.status === "paused" ? "Resume" : "Run"}
        <Zap size={15} />
      </button>
    </article>
  );
}

function AuditEventRow({ event }: { event: AuditEvent }) {
  return (
    <article className="audit-row">
      <span>
        <ShieldCheck size={16} />
      </span>
      <div>
        <strong>{event.action}</strong>
        <small>{event.actor} / {event.entity} / {event.market}</small>
        {event.behaviorEvent ? (
          <small>{event.behaviorEvent.replace(/_/g, " ")} / {event.behaviorStep ?? "workspace"}</small>
        ) : null}
      </div>
      <b>{event.createdAt}</b>
    </article>
  );
}

function ConnectorRequestRow({
  request,
  onMarkConnected,
  onMarkFailed,
  onReturnToPending,
}: {
  request: ConnectorRequestRecord;
  onMarkConnected?: () => void;
  onMarkFailed?: () => void;
  onReturnToPending?: () => void;
}) {
  return (
    <article className={`integration-row ${request.status}`}>
      <div>
        <strong>{request.providerName}</strong>
        <span>{request.organizationName} / {request.systemType} / {request.syncTarget}</span>
      </div>
      <b>{request.status.replace("_", " ")}</b>
      <small>
        {request.authType}
        {request.endpointUrl ? ` / ${request.endpointUrl}` : ""} / {request.statusNote || request.updatedAt || request.createdAt}
      </small>
      {onMarkConnected || onMarkFailed || onReturnToPending ? (
        <div className="connector-status-actions">
          {request.status !== "connected" && onMarkConnected ? (
            <button
              className="row-action"
              type="button"
              onClick={onMarkConnected}
              title="Record this connector as connected only after credentials and a test sync are verified"
            >
              Mark verified
              <CheckCircle2 size={15} />
            </button>
          ) : null}
          {request.status !== "failed" && onMarkFailed ? (
            <button
              className="row-action"
              type="button"
              onClick={onMarkFailed}
              title="Record that this connector setup needs attention"
            >
              Needs work
              <AlertTriangle size={15} />
            </button>
          ) : null}
          {request.status !== "pending_setup" && onReturnToPending ? (
            <button
              className="row-action"
              type="button"
              onClick={onReturnToPending}
              title="Return this connector to pending setup"
            >
              Pending
              <RotateCcw size={15} />
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function IntegrationRow({
  integration,
  onRepair,
}: {
  integration: IntegrationStatus;
  onRepair?: () => void;
}) {
  return (
    <article className={`integration-row ${integration.status}`}>
      <div>
        <strong>{integration.name}</strong>
        <span>{integration.category}</span>
      </div>
      <b>{integration.status.replace("_", " ")}</b>
      <small>{integration.lastSync}</small>
      {onRepair ? (
        <button className="row-action" type="button" onClick={onRepair}>
          {integration.status === "planned" ? "Activate" : "Repair"}
          <ChevronRight size={15} />
        </button>
      ) : null}
    </article>
  );
}

function MobileActionDock({
  activeNav,
  draftItemCount,
  draftTotal,
  invoiceNeedsWorkCount,
  issueCount,
  openPriceRequestCount,
  productFixCount,
  role,
  supplierOrderCount,
  supplierSetupNeeded,
  onNavigate,
  onSubmitOrder,
}: {
  activeNav: string;
  draftItemCount: number;
  draftTotal: number;
  invoiceNeedsWorkCount: number;
  issueCount: number;
  openPriceRequestCount: number;
  productFixCount: number;
  role: Role;
  supplierOrderCount: number;
  supplierSetupNeeded: boolean;
  onNavigate: (label: string) => void;
  onSubmitOrder: () => void;
}) {
  if (role === "admin") {
    return null;
  }

  type MobileDockAction = {
    label: string;
    activeOn: string[];
    value: string;
    tone: "primary" | "warning" | "money" | "success" | "plain";
    onClick: () => void;
  };

  let actions: MobileDockAction[];
  const hasDraftItems = draftItemCount > 0;

  if (role === "restaurant") {
    actions = [
      {
        label: hasDraftItems ? "Send order" : "Make list",
        activeOn: ["Orders"],
        value: hasDraftItems
          ? draftTotal > 0
            ? `${formatMoney(draftTotal)} KM ready`
            : `${draftItemCount} line${draftItemCount === 1 ? "" : "s"} ready`
          : "Add items",
        tone: "primary",
        onClick: hasDraftItems ? onSubmitOrder : () => onNavigate("Orders"),
      },
      {
        label: "Compare price",
        activeOn: ["Marketplace"],
        value: openPriceRequestCount ? `${openPriceRequestCount} waiting` : "Ask suppliers",
        tone: "money",
        onClick: () => onNavigate("Marketplace"),
      },
      issueCount
        ? {
            label: "Fix problem",
            activeOn: ["Orders"],
            value: `${issueCount} open`,
            tone: "warning",
            onClick: () => onNavigate("Orders"),
          }
        : {
            label: "Invoices",
            activeOn: ["Invoices"],
            value: invoiceNeedsWorkCount ? `${invoiceNeedsWorkCount} check` : "All clear",
            tone: "success",
            onClick: () => onNavigate("Invoices"),
          },
    ];
  } else if (supplierSetupNeeded) {
    actions = [
      {
        label: "Upload prices",
        activeOn: ["Settings"],
        value: "Start selling",
        tone: "primary",
        onClick: () => onNavigate("Settings"),
      },
      {
        label: "Connect",
        activeOn: ["Settings"],
        value: "Software",
        tone: "plain",
        onClick: () => onNavigate("Settings"),
      },
      {
        label: "Restaurant leads",
        activeOn: ["Supplier Finder"],
        value: "Find buyers",
        tone: "plain",
        onClick: () => onNavigate("Supplier Finder"),
      },
    ];
  } else {
    const primarySupplierAction: MobileDockAction = openPriceRequestCount
      ? {
          label: "Send prices",
          activeOn: ["Orders"],
          value: `${openPriceRequestCount} waiting`,
          tone: "primary",
          onClick: () => onNavigate("Orders"),
        }
      : supplierOrderCount
        ? {
            label: "Move orders",
            activeOn: ["Orders"],
            value: `${supplierOrderCount} need action`,
            tone: "primary",
            onClick: () => onNavigate("Orders"),
          }
        : {
            label: "Find buyers",
            activeOn: ["Supplier Finder"],
            value: "Restaurant leads",
            tone: "primary",
            onClick: () => onNavigate("Supplier Finder"),
          };

    actions = [
      primarySupplierAction,
      {
        label: "Products",
        activeOn: ["Suppliers"],
        value: productFixCount ? `${productFixCount} fix` : "Ready",
        tone: productFixCount ? "warning" : "plain",
        onClick: () => onNavigate("Suppliers"),
      },
      {
        label: "Invoices",
        activeOn: ["Invoices"],
        value: invoiceNeedsWorkCount ? `${invoiceNeedsWorkCount} check` : "Ready",
        tone: invoiceNeedsWorkCount ? "warning" : "success",
        onClick: () => onNavigate("Invoices"),
      },
    ];
  }

  return (
    <div className="mobile-action-dock" aria-label="Fast actions">
      {actions.map((action, index) => {
        const isActive = action.activeOn.includes(activeNav);

        return (
          <button
            className={`${action.tone} ${isActive ? "active" : ""} ${index === 0 ? "is-main" : ""}`}
            aria-current={isActive ? "page" : undefined}
            key={action.label}
            type="button"
            onClick={action.onClick}
          >
            <span>{action.label}</span>
            <b>{action.value}</b>
          </button>
        );
      })}
    </div>
  );
}

function Panel({
  title,
  action,
  onAction,
  help,
  className = "",
  children,
}: {
  title: string;
  action?: string;
  onAction?: () => void;
  help?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`panel ${className}`}>
      <div className="panel-title">
        <h2>
          {title}
          {help ? <Tooltip label={help} /> : null}
        </h2>
        {action ? (
          onAction ? (
            <button className="panel-action" type="button" onClick={onAction}>
              {action}
            </button>
          ) : (
            <span className="panel-action" aria-label={action}>
              {action}
            </span>
          )
        ) : null}
      </div>
      {children}
    </section>
  );
}

function Tooltip({ label }: { label: string }) {
  return (
    <span className="tooltip" tabIndex={0} aria-label={label}>
      <Info size={14} aria-hidden="true" />
      <span className="tooltip-bubble" role="tooltip">
        {label}
      </span>
    </span>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  note,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  note: string;
}) {
  return (
    <article className="metric-card">
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{note}</small>
      </div>
      <div className="metric-icon">
        <Icon size={21} />
      </div>
    </article>
  );
}

function StockAlertRow({
  onAddToDraft,
  onFindBackup,
  product,
}: {
  onAddToDraft: () => void;
  onFindBackup: () => void;
  product: Product;
}) {
  return (
    <article className={`stock-alert-row ${product.stockStatus}`}>
      <StockIcon status={product.stockStatus} />
      <div>
        <strong>{product.name}</strong>
        <span>
          {product.supplier} / {product.lastOrderedKg} {product.unit} last order
        </span>
      </div>
      <StockBadge status={product.stockStatus} />
      <div className="row-button-group stock-alert-actions">
        <button
          type="button"
          onClick={onAddToDraft}
          title={`Add ${product.name} to today's buying list`}
        >
          Add item
          <Plus size={15} />
        </button>
        <button
          type="button"
          onClick={onFindBackup}
          title={`Find backup suppliers for ${product.category}`}
        >
          Backup
          <Search size={15} />
        </button>
      </div>
    </article>
  );
}

function OrderRow({
  order,
  onNudgeSupplier,
  onRecoverIssue,
  onResolveIssue,
  supplierRequest,
}: {
  order: Order;
  onNudgeSupplier: () => void;
  onRecoverIssue: () => void;
  onResolveIssue: () => void;
  supplierRequest?: SupplierOrderRequest;
}) {
  const canNudgeSupplier = order.status !== "delivered" && order.status !== "issue";
  const supplierReviewStats = supplierRequestReviewStats(supplierRequest);
  const supplierReviewNeeded = order.status === "issue" && supplierReviewStats.needsReview;
  const changedLines = supplierRequest?.items.filter((item) => item.supplierResponse === "changed") ?? [];
  const unavailableLines = supplierRequest?.items.filter((item) => item.supplierResponse === "rejected") ?? [];
  const reviewLines = [...changedLines, ...unavailableLines].slice(0, 4);

  return (
    <article className={`order-row${supplierReviewNeeded ? " has-review" : ""}`}>
      <strong>{order.id}</strong>
      <div>
        <b>{order.buyer}</b>
        <span>{order.supplier}</span>
      </div>
      <span>{order.city}</span>
      <span>{order.deliveryAt}</span>
      <StatusBadge status={order.status} />
      <b>{formatMoney(order.total)} KM</b>
      {order.status === "issue" ? (
        <div className="row-button-group order-actions">
          <button
            className="order-row-action recovery"
            type="button"
            onClick={onRecoverIssue}
            title={
              supplierReviewNeeded && !supplierReviewStats.canAccept
                ? "Create a replacement list because this supplier reply cannot be accepted as confirmed"
                : "Create a replacement list from backup suppliers"
            }
          >
            {supplierReviewNeeded && !supplierReviewStats.canAccept ? "Find backup" : "Replace"}
            <RotateCcw size={15} />
          </button>
          {supplierReviewNeeded ? (
            supplierReviewStats.canAccept ? (
              <button
                className="order-row-action accept"
                type="button"
                onClick={onResolveIssue}
                title="Accept the supplier's changed quantities, prices, delivery window, and notes"
              >
                Accept changes
                <CheckCircle2 size={15} />
              </button>
            ) : null
          ) : (
            <button
              className="order-row-action issue"
              type="button"
              onClick={onResolveIssue}
              title="Mark this issue resolved and notify the supplier"
            >
              Resolve
              <CheckCircle2 size={15} />
            </button>
          )}
        </div>
      ) : (
        <button
          className="order-row-action"
          type="button"
          onClick={onNudgeSupplier}
          disabled={!canNudgeSupplier}
          title={
            canNudgeSupplier
              ? "Ask the supplier for an order status update"
              : "Delivered orders already have a closed fulfillment record"
          }
        >
          Nudge
          <Send size={15} />
        </button>
      )}
      {supplierReviewNeeded && supplierRequest ? (
        <div className="order-row-review" role="status">
          <div className="order-row-review-head">
            <AlertTriangle size={17} />
            <div>
              <strong>Supplier response needs review before invoice/payment.</strong>
              <span>
                {supplierRequest.supplierName} / {supplierRequestStatusLabel(supplierRequest.status)}
                {supplierRequest.supplierDeliveryAvailability
                  ? ` / ${supplierDeliveryAvailabilityLabel(supplierRequest.supplierDeliveryAvailability)}`
                  : ""}
                {supplierRequest.supplierDeliveryWindow ? ` / ${supplierRequest.supplierDeliveryWindow}` : ""}
              </span>
            </div>
          </div>
          {supplierRequest.supplierNote ? <p>Supplier note: {supplierRequest.supplierNote}</p> : null}
          {reviewLines.length ? (
            <ul>
              {reviewLines.map((item, index) => {
                const quantityChanged =
                  typeof item.originalQuantity === "number" && item.originalQuantity !== item.quantity;
                const priceChanged =
                  typeof item.originalUnitPrice === "number" && item.originalUnitPrice !== item.unitPrice;
                const responseLabel = item.supplierResponse
                  ? supplierLineResponseLabel(item.supplierResponse)
                  : "Needs review";

                return (
                  <li key={`${item.productId}-${index}`}>
                    <b>{item.name}</b>
                    <span>
                      {responseLabel}
                      {quantityChanged ? ` / ${item.originalQuantity} -> ${item.quantity} ${item.unit}` : ` / ${item.quantity} ${item.unit}`}
                      {priceChanged
                        ? ` / ${formatMoney(item.originalUnitPrice ?? 0)} -> ${formatMoney(item.unitPrice)} KM`
                        : ""}
                      {item.supplierLineNote ? ` / ${item.supplierLineNote}` : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>
              {supplierReviewStats.canAccept
                ? "Delivery or order status changed. Accept the supplier reply or replace it before matching invoices."
                : "The supplier declined or cannot deliver. Find a backup before matching invoices."}
            </p>
          )}
        </div>
      ) : null}
    </article>
  );
}

function StatusBadge({ status }: { status: OrderStatus }) {
  return <span className={`status-badge ${status}`}>{statusLabels[status]}</span>;
}

function StockBadge({ status }: { status: StockStatus }) {
  return <span className={`stock-badge ${status}`}>{stockLabels[status]}</span>;
}

function StockIcon({ status }: { status: StockStatus }) {
  if (status === "unavailable") {
    return (
      <span className="stock-icon danger">
        <AlertTriangle size={17} />
      </span>
    );
  }

  if (status === "low") {
    return (
      <span className="stock-icon warning">
        <Boxes size={17} />
      </span>
    );
  }

  return (
    <span className="stock-icon good">
      <PackageCheck size={17} />
    </span>
  );
}

function RouteLine() {
  return (
    <svg viewBox="0 0 230 150" aria-hidden="true" className="route-line">
      <path
        d="M18 112 C50 78, 80 138, 103 92 S136 24, 165 55 S193 121, 218 76"
        fill="none"
        stroke="currentColor"
        strokeDasharray="8 8"
        strokeLinecap="round"
        strokeWidth="3"
      />
      <circle cx="18" cy="112" r="5" fill="currentColor" />
      <circle cx="103" cy="92" r="5" fill="currentColor" />
      <circle cx="165" cy="55" r="5" fill="currentColor" />
      <Truck x="18" y="30" width="48" height="48" strokeWidth="1.6" />
    </svg>
  );
}

export default App;
